/* eslint-env es2020 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import ProtonWebSDK from "@proton/web-sdk";

import "./dashboard.css";
import APP_LOGO from "./assets/nch_logo_512.png";

// Constants
import { CONTRACT, TX_CONTRACT, DEFAULT_RPC, FALLBACK_RPCS, LS_OPEN_CARDS_KEY, LS_TIPS_KEY, LS_ADMIN_UI_MODE_KEY, LS_CONTRACT_VIEW_AS_KEY, APP_NAME } from "./constants";

// Utils
import {
  safeParseJson,
  parseRequiredImmutablePairs,
  buildRequiredImmutableTextFromPairs,
  parseRequiredMutablePairs,
  buildRequiredMutableTextFromPairs,
  requiredMutableEntriesFromPairs,
  requiredMutableArraysEqual,
  validateRequiredMutablePairs,
  poolSupportsRequiredMutable,
  poolSupportsClaimScope,
  poolClaimScopeNeedsSetpoolscope,
  CLAIM_SCOPE_NFT,
  CLAIM_SCOPE_ACCOUNT,
  claimScopeLabel,
  serializeError,
} from "./utils/jsonUtils";
import { epochSecToLocalDateValue, epochSecToLocalTimeValue, localDateTimePartsToEpochSec, toReadableTime } from "./utils/dateTimeUtils";
import { alphaCompare, normalizeRpcEndpoint, uniq, pick, asArrayOfNames } from "./utils/stringUtils";
import { parseAssetString, symbolCodeFrom, parseSymbolParts, assetPlaceholderForSymbol, amountPlaceholderForSymbol, normalizeAssetInputForSymbol, formatAssetInt, remainingAssetString } from "./utils/assetUtils";
import { downloadCsv } from "./utils/csvUtils";
import { loadImageRgbForPdf, buildPdfFromLinesWithHeader } from "./utils/pdfUtils";
import {
  parseImmutableRequirements,
  matchesRequirements,
  matchesPoolAssetRequirements,
  fetchAllAtomicAssets,
  atomicCollectionExists,
  mapLimit,
} from "./utils/atomicUtils";
import { fetchAllRows, derivePoolScope, nameToUint64, postWalletUiCleanup } from "./utils/chainUtils";

// Components
import { SimpleTable } from "./components/SimpleTable";
import { TermsGate } from "./components/TermsGate";
import { TransactionBanner } from "./components/TransactionBanner";
import { ClaimPreviewModal } from "./components/ClaimPreviewModal";
import RewardsLaunchWizard from "./components/RewardsLaunchWizard";
import { REWARDS_WIZARD_STEPS, REWARDS_WIZARD_STEP_COUNT } from "./rewardsWizardStepsMeta";
import ClaimRewardsPermissionGuide from "./components/ClaimRewardsPermissionGuide";

// Hooks
import { useWallet } from "./hooks/useWallet";

// All utility functions have been moved to separate modules (see imports above)

export default function NftRewardsDashboard() {
  // === Terms of Use gating ===
  // Update this string whenever the Terms change. Users will be asked to re-accept.
  const TERMS_VERSION = "2026-01-04-DE2";
  const TERMS_STORAGE_KEY = `nr_terms_accept_${TERMS_VERSION}`;

  const [termsGateOpen, setTermsGateOpen] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(() => {
    try {
      return localStorage.getItem(TERMS_STORAGE_KEY) === "yes";
    } catch {
      return false;
    }
  });
  const [rpc] = useState(DEFAULT_RPC);

  // Wallet state (using hook for some logic, but keeping local state for extended options)
  const [link, setLink] = useState(null);
  const [session, setSession] = useState(null);
  const [auth, setAuth] = useState(null);

  const actor = auth?.actor ? String(auth.actor) : "";
  const isContract = actor && actor.toLowerCase() === CONTRACT.toLowerCase();
  const loggedIn = !!auth;

  const [contractViewAsProjectId, setContractViewAsProjectId] = useState(() => {
    if (typeof window === "undefined") return "";
    try {
      const raw = window.localStorage.getItem(LS_CONTRACT_VIEW_AS_KEY);
      const o = safeParseJson(raw || "");
      if (o && typeof o === "object") return String(o.projectId || "").trim();
    } catch {
      // ignore
    }
    return "";
  });
  const [contractViewAsAccount, setContractViewAsAccount] = useState(() => {
    if (typeof window === "undefined") return "";
    try {
      const raw = window.localStorage.getItem(LS_CONTRACT_VIEW_AS_KEY);
      const o = safeParseJson(raw || "");
      if (o && typeof o === "object") return String(o.account || "").trim();
    } catch {
      // ignore
    }
    return "";
  });

  // Use wallet hook for logging and some utilities
  const {
    logAuthEvent,
  } = useWallet();

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [accessGateMsg, setAccessGateMsg] = useState("");

  // If a wallet session is restored or connected and Terms were not accepted yet,
  // require acceptance before showing the dashboard.
  useEffect(() => {
    if (actor && !termsAccepted) {
      setTermsChecked(false);
      setTermsGateOpen(true);
    }
  }, [actor, termsAccepted]);

  // Transaction banner (shown in header)
  const [txBanner, setTxBanner] = useState(null); // { ok: boolean, message: string, txid?: string }

  // After creating/updating entities, we want to jump to the exact row that was just written.
  // Using refs avoids race conditions with effects that auto-select the first item.
  const pendingSelectPoolKeyRef = useRef("");
  const pendingSelectReqRef = useRef({ poolKey: "", reqKey: "" });
  // When the user selects "(new rule)" we must not auto-snap back to an existing rule.
  const preferNewReqRef = useRef(false);
  const preferNewSurveyRef = useRef(false);
  const preferNewPromotionRef = useRef(false);

  // Core on-chain state
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);

  const projectsById = useMemo(() => {
    const m = new Map();
    (projects || []).forEach((p) => m.set(String(p?.projectId), p));
    return m;
  }, [projects]);

  
  const [projVaults, setProjVaults] = useState([]);

  const vaultByProjectId = useMemo(() => {
    const m = new Map();
    (projVaults || []).forEach((v) => m.set(String(v?.projectId), v));
    return m;
  }, [projVaults]);

  /** Contract picks a project + allowed account: UI matches that user; txs still sign as the contract. */
  const contractViewAsActive = useMemo(() => {
    if (!isContract) return false;
    const pid = String(contractViewAsProjectId || "").trim();
    const acc = String(contractViewAsAccount || "").trim();
    if (!pid || !acc) return false;
    const p = (projects || []).find((x) => String(x.projectId) === pid);
    if (!p) return false;
    const raw = p.allowedAccounts ?? p.allowed_accounts;
    const allowed = Array.isArray(raw) ? raw.map((x) => String(x)) : [];
    return allowed.includes(acc);
  }, [isContract, contractViewAsProjectId, contractViewAsAccount, projects]);

  const contractGodMode = isContract && !contractViewAsActive;

  const effectiveActorForFilters = useMemo(() => {
    if (contractViewAsActive) return String(contractViewAsAccount || "").trim();
    return String(actor || "").trim();
  }, [contractViewAsActive, contractViewAsAccount, actor]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.localStorage) return;
    try {
      if (!isContract) {
        window.localStorage.removeItem(LS_CONTRACT_VIEW_AS_KEY);
        return;
      }
      const pid = String(contractViewAsProjectId || "").trim();
      const acc = String(contractViewAsAccount || "").trim();
      if (pid && acc) {
        window.localStorage.setItem(LS_CONTRACT_VIEW_AS_KEY, JSON.stringify({ projectId: pid, account: acc }));
      } else {
        window.localStorage.removeItem(LS_CONTRACT_VIEW_AS_KEY);
      }
    } catch {
      // ignore
    }
  }, [isContract, contractViewAsProjectId, contractViewAsAccount]);

  useEffect(() => {
    if (!isContract) return;
    const pid = String(contractViewAsProjectId || "").trim();
    const acc = String(contractViewAsAccount || "").trim();
    if (!pid || !acc) return;
    const p = (projects || []).find((x) => String(x.projectId) === pid);
    if (!p) return;
    const raw = p.allowedAccounts ?? p.allowed_accounts;
    const allowed = Array.isArray(raw) ? raw.map((x) => String(x)) : [];
    if (!allowed.includes(acc)) setContractViewAsAccount("");
  }, [isContract, contractViewAsProjectId, contractViewAsAccount, projects]);

  const viewAsProjectRow = useMemo(() => {
    const pid = String(contractViewAsProjectId || "").trim();
    if (!pid) return null;
    return (projects || []).find((p) => String(p.projectId) === pid) || null;
  }, [projects, contractViewAsProjectId]);

  const viewAsAllowedAccounts = useMemo(() => {
    if (!viewAsProjectRow) return [];
    const raw = viewAsProjectRow.allowedAccounts ?? viewAsProjectRow.allowed_accounts;
    const arr = Array.isArray(raw) ? raw.map((x) => String(x).trim()).filter(Boolean) : [];
    return [...new Set(arr)].sort((a, b) => a.localeCompare(b));
  }, [viewAsProjectRow]);

  const contractViewAsProjectOptions = useMemo(() => {
    return [...(projects || [])].sort((a, b) => Number(a.projectId) - Number(b.projectId));
  }, [projects]);

  // Advanced: Transaction JSON (hidden by default)
    const [showTxJson, setShowTxJson] = useState(() => {
    if (typeof window !== "undefined" && window.localStorage) {
      const v = window.localStorage.getItem("nr_showTxJson_v1");
      if (v === "1") return true;
      if (v === "0") return false;
    }
    return false;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.localStorage) return;
    try {
      window.localStorage.setItem("nr_showTxJson_v1", showTxJson ? "1" : "0");
    } catch {
      // ignore
    }
  }, [showTxJson]);

  const [txJsonText, setTxJsonText] = useState("");
  const [lastSignedTxJson, setLastSignedTxJson] = useState("");

    const defaultOpenCards = {
    allowedTokens: false,
    ramBudgets: false,
    ramFees: false,
    deposits: false,
    ramQuick: false,
    manualDeposits: false,
    project: false,
    pool: false,
    depNfts: false,
    claimCalc: false,
    claims: false,
    wallets: false,
    exclAssets: false,
    promotions: false,
    surveyVotes: false,
    surveys: false,
    surveyPoolForm: true,
    promotionPoolForm: true,
    billing: false,
    promotionFilters: false,
    surveyFilters: false,
    streaks: false,
    streakFilters: false,
    streakPoolForm: true,
    streakStates: false,
    poolCreatorFee: false,
    poolSelect: false,
    poolReset: false,
    stakeSnap: false,
    stakeElig: false,
    permissionGuide: false,
  };

  const [openCards, setOpenCards] = useState(() => {
    if (typeof window !== "undefined" && window.localStorage) {
      const saved = safeParseJson(window.localStorage.getItem(LS_OPEN_CARDS_KEY) || "");
      if (saved && typeof saved === "object") return {...defaultOpenCards,...saved };
    }
    return {...defaultOpenCards };
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.localStorage) return;
    try {
      window.localStorage.setItem(LS_OPEN_CARDS_KEY, JSON.stringify(openCards));
    } catch {
      // ignore
    }
  }, [openCards]);

  const [dismissedTips, setDismissedTips] = useState(() => {
    if (typeof window !== "undefined" && window.localStorage) {
      const saved = safeParseJson(window.localStorage.getItem(LS_TIPS_KEY) || "");
      if (saved && typeof saved === "object") return saved;
    }
    return {};
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.localStorage) return;
    try {
      window.localStorage.setItem(LS_TIPS_KEY, JSON.stringify(dismissedTips));
    } catch {
      // ignore
    }
  }, [dismissedTips]);

  const [adminUiMode, setAdminUiModeState] = useState(() => {
    if (typeof window === "undefined") return "full";
    try {
      const v = window.localStorage.getItem(LS_ADMIN_UI_MODE_KEY);
      return v === "wizard" || v === "reset" ? v : "full";
    } catch {
      return "full";
    }
  });
  const setAdminUiMode = useCallback((mode) => {
    const m = mode === "wizard" || mode === "reset" ? mode : "full";
    setAdminUiModeState(m);
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        window.localStorage.setItem(LS_ADMIN_UI_MODE_KEY, m);
      }
    } catch {
      // ignore
    }
  }, []);
  const [wizardStep, setWizardStep] = useState(1);
  const prevWizardStepRef = useRef(0);
  const prevAdminUiModeRef = useRef(adminUiMode);

  const toggleCard = useCallback((key) => {
    setOpenCards((s) => ({...s, [key]: !s[key] }));
  }, []);

  // Global expand/collapse (includes Advanced JSON section via showTxJson)
  const allBoxesExpanded = useMemo(() => {
    const vals = Object.values(openCards || {});
    const cardsAllOpen = vals.length ? vals.every(Boolean) : true;
    return cardsAllOpen && !!showTxJson;
  }, [openCards, showTxJson]);

  const toggleAllBoxes = () => {
    const nextOpen = !allBoxesExpanded;
    setOpenCards((s) => {
      const next = {};
      Object.keys(s || {}).forEach((k) => {
        next[k] = nextOpen;
      });
      return next;
    });
    setShowTxJson(nextOpen);
  };

  const [allowTokens, setAllowTokens] = useState([]);
  const [ramBudgets, setRamBudgets] = useState([]);
  const [ramFees, setRamFees] = useState([]);
  const [deposits, setDeposits] = useState([]);

  // Monthly consolidated invoice / credit note (PDF)
  const [billingMonth, setBillingMonth] = useState(() => String(new Date().getMonth() + 1));
  const [billingYear, setBillingYear] = useState(() => String(new Date().getFullYear()));
  const [billingBusy, setBillingBusy] = useState(false);
  const [billingError, setBillingError] = useState("");
  const [billingPdfUrl, setBillingPdfUrl] = useState("");
  const [billingPdfName, setBillingPdfName] = useState("");
  const [billingDebug, setBillingDebug] = useState("");

  // Build-time toggle for PDF debug output (set in .env.local or build env).
  // CRA exposes REACT_APP_* vars; VITE_* is accepted for legacy naming.
  const PDF_DEBUG_ENABLED = (() => {
    const raw = String(process.env.REACT_APP_PDF_DEBUG || process.env.VITE_PDF_DEBUG || "").trim().toLowerCase();
    return raw === "1" || raw === "true" || raw === "yes";
  })();

  useEffect(() => {
    return () => {
      try {
        if (billingPdfUrl) URL.revokeObjectURL(billingPdfUrl);
      } catch {
        // ignore
      }
    };
  }, [billingPdfUrl]);

  const [pools, setPools] = useState([]);
  const [poolMutRows, setPoolMutRows] = useState([]);
  const [poolScopeRows, setPoolScopeRows] = useState([]);
  const [reqNfts, setReqNfts] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [surveyVotes, setSurveyVotes] = useState([]);
  const [nauths, setNauths] = useState([]);
  const [nauthsLoading, setNauthsLoading] = useState(false);

  // Selections
  const allowedProjects = useMemo(() => {
    if (!isContract) {
      const a = String(actor || "").trim();
      if (!a) return [];
      return (projects || []).filter((p) => (p.allowedAccounts || []).map((x) => String(x)).includes(a));
    }
    if (!contractViewAsActive) return projects || [];
    const pid = String(contractViewAsProjectId || "").trim();
    const p = (projects || []).find((x) => String(x.projectId) === pid);
    return p ? [p] : [];
  }, [projects, actor, isContract, contractViewAsActive, contractViewAsProjectId]);

  const [selectedProjectId, setSelectedProjectId] = useState("");
  const selectedProject = useMemo(() => {
    const id = String(selectedProjectId || "");
    return (allowedProjects || []).find((p) => String(p.projectId) === id) || null;
  }, [allowedProjects, selectedProjectId]);

  useEffect(() => {
    if (!contractViewAsActive) return;
    const pid = String(contractViewAsProjectId || "").trim();
    if (pid && String(selectedProjectId) !== pid) setSelectedProjectId(pid);
  }, [contractViewAsActive, contractViewAsProjectId, selectedProjectId]);

  const [selectedPoolHash, setSelectedPoolHash] = useState("");
  const selectedPool = useMemo(() => {
    const h = String(selectedPoolHash || "");
    return (pools || []).find((p) => String(p.poolHash) === h) || null;
  }, [pools, selectedPoolHash]);

  // Check if actions are active for the current account and selected project
  const actionPermissions = useMemo(() => {
    const pid = String(selectedProjectId || "").trim();
    const nauthsArray = Array.isArray(nauths) ? nauths : [];
    
    // Helper to check if an action is active
    const isActionActive = (actionName) => {
      if (contractGodMode) return true;
      
      // Check if action exists in nauths and is active
      const found = nauthsArray.find((r) => {
        const rAction = String(r?.action || "").trim().toLowerCase();
        const actionLower = String(actionName || "").toLowerCase();
        // active can be true, 1, "1", or truthy; treat only explicit false/0 as inactive
        const rActive = r?.active !== false && r?.active !== 0 && r?.active !== "0";
        const rProjectId = String(r?.projectId ?? "").trim();
        
        // If projectId is specified in nauths, check if it matches
        if (rProjectId && pid) {
          return rAction === actionLower && rActive && rProjectId === pid;
        }
        // No projectId in nauths = global permission for this action
        return rAction === actionLower && rActive;
      });
      
      return !!found;
    };
    
    return {
      setpool: isActionActive("setpool"),
      setpoolmut: isActionActive("setpoolmut"),
      setpoolscope: isActionActive("setpoolscope"),
      setreqnft: isActionActive("setreqnft"),
      setexcl: isActionActive("setexcl"),
      setpromo: isActionActive("setpromo"),
      setsurvey: isActionActive("setsurvey"),
      setstreak: isActionActive("setstreak"),
      setcrefee: isActionActive("setcrefee"),
      clrclaims: isActionActive("clrclaims"),
      delpool: isActionActive("delpool"),
      setstaksnap: isActionActive("setstaksnap"),
      delstaksnap: isActionActive("delstaksnap"),
      clrstaksnap: isActionActive("clrstaksnap"),
      delstakelig: isActionActive("delstakelig"),
    };
  }, [nauths, selectedProjectId, contractGodMode]);

  // Editable forms
  const [projectForm, setProjectForm] = useState({
    projectId: "",
    projectName: "",
    homepageUrl: "",
    projectImageUrl: "",
    projectDescription: "",
    allowedAccountsText: "[]",
  });

  const [poolForm, setPoolForm] = useState({
    poolKey: "",
    projectId: "",
    collectionName: "",
    rewardAmount: "",
    rewardTokenSymbolRaw: "",
    rewardTokenContract: "",
    rewardWallet: CONTRACT,
    active: true,
    startTimeSec: "0",
    endTimeSec: "0",
    cooldownSec: "0",
    requiredImmutableText: "[]",
    requiredMutableText: "[]",
    poolDescription: "",
    poolImageUrl: "",
    poolType: 1,
    claimScope: CLAIM_SCOPE_NFT,
    creatorFeeEnabled: false,
  });

  const [creatorFeeForm, setCreatorFeeForm] = useState({
    feePercent: "0",
    receiver: "",
  });

  const [poolCreatorFees, setPoolCreatorFees] = useState([]);
  const [creFeeFormAction, setCreFeeFormAction] = useState({
    poolKey: "",
    projectId: "",
    feePercent: "0",
    receiver: "",
  });

  // Pool Reset (clrclaims action)
  const [resetPoolForm, setResetPoolForm] = useState({
    poolKey: "",
    maxRows: "500",
  });
  const [resetPoolResult, setResetPoolResult] = useState(null);
  const [resetPoolConfirmOpen, setResetPoolConfirmOpen] = useState(false);
  const [deletePoolConfirmOpen, setDeletePoolConfirmOpen] = useState(false);

    const [poolReqPairs, setPoolReqPairs] = useState(() =>
    parseRequiredImmutablePairs(poolForm.requiredImmutableText, 5)
  );
  const poolReqPairsSyncRef = useRef(false);

  useEffect(() => {
    if (poolReqPairsSyncRef.current) {
      poolReqPairsSyncRef.current = false;
      return;
    }
    setPoolReqPairs(parseRequiredImmutablePairs(poolForm.requiredImmutableText, 5));
  }, [poolForm.requiredImmutableText]);

  function onPoolReqPairChange(idx, field, value) {
    setPoolReqPairs((prev) => {
      const next = (prev || []).map((p, i) => (i === idx ? { ...p, [field]: value } : p));
      const built = buildRequiredImmutableTextFromPairs(next);
      poolReqPairsSyncRef.current = true;
      setPoolForm((s) => ({ ...s, requiredImmutableText: built }));
      return next;
    });
  }

  function addPoolReqPairRow() {
    setPoolReqPairs((prev) => {
      const cur = Array.isArray(prev) ? prev : [];
      if (cur.length >= 5) return cur;
      const next = cur.concat([{ key: "", value: "" }]);
      const built = buildRequiredImmutableTextFromPairs(next);
      poolReqPairsSyncRef.current = true;
      setPoolForm((s) => ({ ...s, requiredImmutableText: built }));
      return next;
    });
  }

function removePoolReqPairRow() {
    setPoolReqPairs((prev) => {
      const cur = Array.isArray(prev) ? prev : [];
      if (!cur.length) return cur;
      const next = cur.slice(0, -1);
      const built = buildRequiredImmutableTextFromPairs(next);
      poolReqPairsSyncRef.current = true;
      setPoolForm((s) => ({ ...s, requiredImmutableText: built }));
      return next;
    });
  }

  const poolSupportsMutable = useMemo(
    () => poolSupportsRequiredMutable(poolForm.poolType, poolForm.collectionName),
    [poolForm.poolType, poolForm.collectionName]
  );

  const poolSupportsScope = useMemo(
    () => poolSupportsClaimScope(poolForm.poolType),
    [poolForm.poolType]
  );

  const [poolMutPairs, setPoolMutPairs] = useState(() =>
    parseRequiredMutablePairs(poolForm.requiredMutableText, 5)
  );
  const poolMutPairsSyncRef = useRef(false);

  useEffect(() => {
    if (poolMutPairsSyncRef.current) {
      poolMutPairsSyncRef.current = false;
      return;
    }
    setPoolMutPairs(parseRequiredMutablePairs(poolForm.requiredMutableText, 5));
  }, [poolForm.requiredMutableText]);

  function onPoolMutPairChange(idx, field, value) {
    setPoolMutPairs((prev) => {
      const next = (prev || []).map((p, i) => (i === idx ? { ...p, [field]: value } : p));
      const built = buildRequiredMutableTextFromPairs(next);
      poolMutPairsSyncRef.current = true;
      setPoolForm((s) => ({ ...s, requiredMutableText: built }));
      return next;
    });
  }

  function addPoolMutPairRow() {
    setPoolMutPairs((prev) => {
      const cur = Array.isArray(prev) ? prev : [];
      if (cur.length >= 5) return cur;
      const next = cur.concat([{ key: "", value: "" }]);
      const built = buildRequiredMutableTextFromPairs(next);
      poolMutPairsSyncRef.current = true;
      setPoolForm((s) => ({ ...s, requiredMutableText: built }));
      return next;
    });
  }

  function removePoolMutPairRow() {
    setPoolMutPairs((prev) => {
      const cur = Array.isArray(prev) ? prev : [];
      if (!cur.length) return cur;
      const next = cur.slice(0, -1);
      const built = buildRequiredMutableTextFromPairs(next);
      poolMutPairsSyncRef.current = true;
      setPoolForm((s) => ({ ...s, requiredMutableText: built }));
      return next;
    });
  }

  const requiredMutableForSelectedPool = useMemo(() => {
    if (!selectedPool) return [];
    const h = String(selectedPool.poolHash ?? "").trim();
    const row = (poolMutRows || []).find((r) => String(r.poolHash ?? r.poolhash ?? "") === h);
    const arr = row?.requiredMutable ?? row?.required_mutable;
    return Array.isArray(arr) ? arr.map(String) : [];
  }, [selectedPool, poolMutRows]);

  const claimScopeForSelectedPool = useMemo(() => {
    if (!selectedPool) return CLAIM_SCOPE_NFT;
    const h = String(selectedPool.poolHash ?? "").trim();
    const row = (poolScopeRows || []).find((r) => String(r.poolHash ?? r.poolhash ?? "") === h);
    const raw = row?.claimScope ?? row?.claim_scope;
    const n = Number(raw);
    return n === CLAIM_SCOPE_ACCOUNT ? CLAIM_SCOPE_ACCOUNT : CLAIM_SCOPE_NFT;
  }, [selectedPool, poolScopeRows]);

// New-entry helpers
  const preferNewPoolRef = useRef(false);
  const justCopiedPoolRef = useRef(null);
  const [poolCheck, setPoolCheck] = useState(null); // { ok, errors:[], warnings:[] }
  const [surveyCheck, setSurveyCheck] = useState(null); // { ok, errors:[], warnings:[] }
  const [promotionCheck, setPromotionCheck] = useState(null); // { ok, errors:[], warnings:[] }
  const [reqCheck, setReqCheck] = useState(null); // { ok, errors:[], warnings:[] }

  const [reqForm, setReqForm] = useState({
    poolKey: "",
    reqKey: "default",
    collectionName: "",
    requiredImmutableText: "[]",
    nftcount: "1",
    active: true,
  });
  const [reqPairs, setReqPairs] = useState(() =>
    parseRequiredImmutablePairs(reqForm.requiredImmutableText, 5)
  );
  const reqPairsSyncRef = useRef(false);

  useEffect(() => {
    if (reqPairsSyncRef.current) {
      reqPairsSyncRef.current = false;
      return;
    }
    setReqPairs(parseRequiredImmutablePairs(reqForm.requiredImmutableText, 5));
  }, [reqForm.requiredImmutableText]);

  function onReqPairChange(idx, field, value) {
    setReqPairs((prev) => {
      const next = (prev || []).map((p, i) => (i === idx ? { ...p, [field]: value } : p));
      const built = buildRequiredImmutableTextFromPairs(next);
      reqPairsSyncRef.current = true;
      setReqForm((s) => ({ ...s, requiredImmutableText: built }));
      return next;
    });
  }

  function addReqPairRow() {
    setReqPairs((prev) => {
      const cur = Array.isArray(prev) ? prev : [];
      if (cur.length >= 5) return cur;
      const next = cur.concat([{ key: "", value: "" }]);
      const built = buildRequiredImmutableTextFromPairs(next);
      reqPairsSyncRef.current = true;
      setReqForm((s) => ({ ...s, requiredImmutableText: built }));
      return next;
    });
  }


function removeReqPairRow() {
    setReqPairs((prev) => {
      const cur = Array.isArray(prev) ? prev : [];
      if (!cur.length) return cur;
      const next = cur.slice(0, -1);
      const built = buildRequiredImmutableTextFromPairs(next);
      reqPairsSyncRef.current = true;
      setReqForm((s) => ({ ...s, requiredImmutableText: built }));
      return next;
    });
  }

  const [selectedReqId, setSelectedReqId] = useState("");
  const selectedReqRow = useMemo(() => {
    const id = String(selectedReqId || "");
    return (reqNfts || []).find((r) => String(r.reqId) === id) || null;
  }, [reqNfts, selectedReqId]);

  // New entries (computed AFTER selectedReqId is initialized to avoid TDZ errors)
  const isNewPool = !!selectedProject && !selectedPool;
  const isNewRule = !!selectedPool && !String(selectedReqId || "");

  // Claims / wallets
  const [claims, setClaims] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [claimsLoading, setClaimsLoading] = useState(false);
  const [walletsLoading, setWalletsLoading] = useState(false);
  const [claimFilterAccount, setClaimFilterAccount] = useState("");
  const [claimFilterAsset, setClaimFilterAsset] = useState("");
  const [walletFilterAccount, setWalletFilterAccount] = useState("");

  const refetchClaimsWalletsForSelectedPool = useCallback(async () => {
    setError("");
    if (!selectedPool) {
      setClaims([]);
      setWallets([]);
      return;
    }
    const scope = derivePoolScope(selectedPool);
    if (!scope) {
      setClaims([]);
      setWallets([]);
      return;
    }
    setClaimsLoading(true);
    setWalletsLoading(true);
    try {
      const [cRows, wRows] = await Promise.all([
        fetchAllRows(rpc, { code: CONTRACT, scope, table: "nclaims", limit: 1000 }),
        fetchAllRows(rpc, { code: CONTRACT, scope, table: "nwallets", limit: 1000, index_position: 2, key_type: "i64" }),
      ]);
      const filterActor = (rows) => {
        const arr = Array.isArray(rows) ? rows : [];
        if (contractGodMode) return arr;
        const a = String(effectiveActorForFilters || "").trim();
        if (!a) return [];
        return arr.filter((r) => {
          if (r && Object.prototype.hasOwnProperty.call(r, "actor")) return String(r.actor) === a;
          return true;
        });
      };
      setClaims(filterActor(cRows));
      setWallets(filterActor(wRows));
    } catch (e) {
      setError(serializeError(e).message);
    } finally {
      setClaimsLoading(false);
      setWalletsLoading(false);
    }
  }, [selectedPool, rpc, contractGodMode, effectiveActorForFilters]);

  const [stakeSnapRows, setStakeSnapRows] = useState([]);
  const [stakeSnapLoading, setStakeSnapLoading] = useState(false);
  const [stakeEligRows, setStakeEligRows] = useState([]);
  const [stakeEligLoading, setStakeEligLoading] = useState(false);
  const [stakeSnapForm, setStakeSnapForm] = useState({
    pos: "1",
    stakeToken: "",
    tokenContract: "",
    minAmount: "",
    minTokenSymbolRaw: "",
    pointMultiplier: "1",
    stakingPlatform: "",
    stakeType: "",
    stakeName: "",
  });
  const [stakeSnapClrMax, setStakeSnapClrMax] = useState("500");

  const refetchStakeSnapForSelectedPool = useCallback(async () => {
    if (!selectedPool) {
      setStakeSnapRows([]);
      return;
    }
    const scope = derivePoolScope(selectedPool);
    if (!scope) {
      setStakeSnapRows([]);
      return;
    }
    setStakeSnapLoading(true);
    try {
      const rows = await fetchAllRows(rpc, { code: CONTRACT, scope, table: "nstaksnap", limit: 1000 });
      const arr = Array.isArray(rows) ? rows : [];
      arr.sort((a, b) => Number(a?.pos ?? 0) - Number(b?.pos ?? 0));
      setStakeSnapRows(arr);
    } catch (e) {
      setError(serializeError(e).message);
      setStakeSnapRows([]);
    } finally {
      setStakeSnapLoading(false);
    }
  }, [selectedPool, rpc]);

  const refetchStakeEligForSelectedPool = useCallback(async () => {
    if (!selectedPool) {
      setStakeEligRows([]);
      return;
    }
    const scope = derivePoolScope(selectedPool);
    if (!scope) {
      setStakeEligRows([]);
      return;
    }
    setStakeEligLoading(true);
    try {
      const rows = await fetchAllRows(rpc, { code: CONTRACT, scope, table: "nstakelig", limit: 1000 });
      const arr = Array.isArray(rows) ? rows : [];
      arr.sort((a, b) => String(a?.account || "").localeCompare(String(b?.account || "")));
      setStakeEligRows(arr);
    } catch (e) {
      setError(serializeError(e).message);
      setStakeEligRows([]);
    } finally {
      setStakeEligLoading(false);
    }
  }, [selectedPool, rpc]);

  // Survey Votes
  const [surveyVoteFilterAccount, setSurveyVoteFilterAccount] = useState("");
  const [surveyVoteFilterSurveyKey, setSurveyVoteFilterSurveyKey] = useState("");

  // Excluded claim assets (per project)
  const [excludedAssets, setExcludedAssets] = useState([]);
  const [excludedAssetsLoading, setExcludedAssetsLoading] = useState(false);
  const [exclAssetId, setExclAssetId] = useState("");
  const [exclReason, setExclReason] = useState("");


  // Claim calculator
  const [claimCalc, setClaimCalc] = useState(null);
  const [claimCalcLoading, setClaimCalcLoading] = useState(false);
  const [claimCalcError, setClaimCalcError] = useState("");

  // Claim preview modal (shown before saving pool/reqnfts)
  const [claimPreviewModalOpen, setClaimPreviewModalOpen] = useState(false);
  const [pendingSaveAction, setPendingSaveAction] = useState(null); // { type: 'pool' | 'reqnfts', action: function }
  const [claimPreviewMode, setClaimPreviewMode] = useState("save"); // 'save' | 'info'

  // Quick RAM / deposit actions
  const [ramFeeDepositId, setRamFeeDepositId] = useState("");
  const [ramSetClaims, setRamSetClaims] = useState("");
  const [refundDepositId, setRefundDepositId] = useState("");

  // Manual deposits (type 2)
  const [dep2TokenKey, setDep2TokenKey] = useState("");
  const [dep2Amount, setDep2Amount] = useState("");
  const [dep2UpdateId, setDep2UpdateId] = useState("");
  const [dep2UpdateAmount, setDep2UpdateAmount] = useState("");

  // Promotions
  const [promotionsLoading, setPromotionsLoading] = useState(false);
  const [selectedPromotionId, setSelectedPromotionId] = useState("");
  const selectedPromotion = useMemo(() => {
    const id = String(selectedPromotionId || "");
    return (promotions || []).find((p) => String(p.promoteId) === id) || null;
  }, [promotions, selectedPromotionId]);
  const [promotionForm, setPromotionForm] = useState({
    promoteId: "",
    promotionName: "",
    projectId: "",
    collectionName: "",
    rewardAmount: "",
    rewardTokenSymbolRaw: "",
    rewardTokenContract: "",
    startTimeSec: "0",
    endTimeSec: "0",
    maxClaimCount: "1",
    description: "",
    imageUrl: "",
    active: true,
  });
  const [promotionFilterProject, setPromotionFilterProject] = useState("");
  const [promotionFilterStatus, setPromotionFilterStatus] = useState("");
  const [promotionSearch, setPromotionSearch] = useState("");
  const [promotionPoolForm, setPromotionPoolForm] = useState({
    poolKey: undefined, // undefined = not shown, string = shown
  });

  // Surveys
  const [surveysLoading, setSurveysLoading] = useState(false);
  const [selectedSurveyKey, setSelectedSurveyKey] = useState("");
  const selectedSurvey = useMemo(() => {
    const key = String(selectedSurveyKey || "");
    return (surveys || []).find((s) => String(s.surveyKey) === key) || null;
  }, [surveys, selectedSurveyKey]);
  const [surveyForm, setSurveyForm] = useState({
    surveyKey: "",
    projectId: "",
    surveyText: "",
    option1: "",
    option2: "",
    option3: "",
    option4: "",
    option5: "",
    option6: "",
    numOptions: 2,
    allowMultiple: false,
    surveyType: 1,
    requiredAnswers: "",
    active: true,
    closed: false,
    startTimeSec: "0",
    endTimeSec: "0",
    surveyDescription: "",
    surveyImageUrl: "",
  });
  const [surveyFilterProject, setSurveyFilterProject] = useState("");
  const [surveyFilterType, setSurveyFilterType] = useState("");
  const [surveyFilterStatus, setSurveyFilterStatus] = useState("");
  const [surveySearch, setSurveySearch] = useState("");
  const [surveyPoolForm, setSurveyPoolForm] = useState({
    poolKey: "",
    collectionName: "",
    rewardAmount: "",
    rewardTokenSymbolRaw: "",
    rewardTokenContract: "",
    active: true,
    startTimeSec: "0",
    endTimeSec: "0",
    cooldownSec: "4294967295", // Survey pools always use max cooldown
    requiredImmutableText: "[]",
    poolDescription: "",
    poolImageUrl: "",
  });
  const [drawWinnersNum, setDrawWinnersNum] = useState("1");

  // Streaks
  const [streaks, setStreaks] = useState([]);
  const [streakStates, setStreakStates] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [streaksLoading, setStreaksLoading] = useState(false);
  const [selectedStreakId, setSelectedStreakId] = useState("");
  const selectedStreak = useMemo(() => {
    const id = String(selectedStreakId || "");
    return (streaks || []).find((s) => String(s.streakId) === id) || null;
  }, [streaks, selectedStreakId]);
  const preferNewStreakRef = useRef(false);
  const [streakForm, setStreakForm] = useState({
    streakId: "",
    streakName: "",
    projectId: "",
    targetStreakDays: "7",
    targetInARow: "0",
    active: true,
    collectionName: "",
    rewardAmount: "",
    rewardTokenSymbolRaw: "",
    rewardTokenContract: "",
    cooldownSec: "86400",
    poolDescription: "",
    poolImageUrl: "",
    requiredImmutableText: "[]",
  });
  const [streakFilterProject, setStreakFilterProject] = useState("");
  const [streakFilterStatus, setStreakFilterStatus] = useState("");
  const [streakSearch, setStreakSearch] = useState("");
  const [streakPoolForm, setStreakPoolForm] = useState({
    poolKey: undefined, // undefined = not shown, string = shown
  });
  const [streakStateFilterAccount, setStreakStateFilterAccount] = useState("");
  const [streakStateFilterStatus, setStreakStateFilterStatus] = useState("");

  const filteredClaims = useMemo(() => {
    let arr = Array.isArray(claims) ? claims.slice() : [];
    // sort desc by claimedAt
    arr.sort((a, b) => Number(b?.claimedAt || 0) - Number(a?.claimedAt || 0));
    const fa = String(claimFilterAccount || "").trim().toLowerCase();
    const fs = String(claimFilterAsset || "").trim().toLowerCase();
    if (fa) arr = arr.filter((r) => String(r?.account || "").toLowerCase().includes(fa));
    if (fs) arr = arr.filter((r) => String(r?.assetId || "").toLowerCase().includes(fs));
    return arr;
  }, [claims, claimFilterAccount, claimFilterAsset]);

  const filteredWallets = useMemo(() => {
    let arr = Array.isArray(wallets) ? wallets.slice() : [];
    const fa = String(walletFilterAccount || "").trim().toLowerCase();
    if (fa) arr = arr.filter((r) => String(r?.account || "").toLowerCase().includes(fa));
    // default sort by lastClaimSec desc
    arr.sort((a, b) => Number(b?.lastClaimSec || 0) - Number(a?.lastClaimSec || 0));
    return arr;
  }, [wallets, walletFilterAccount]);

  const filteredSurveyVotes = useMemo(() => {
    let arr = Array.isArray(surveyVotes) ? surveyVotes.slice() : [];
    const fa = String(surveyVoteFilterAccount || "").trim().toLowerCase();
    const fs = String(surveyVoteFilterSurveyKey || "").trim();
    if (fa) arr = arr.filter((r) => String(r?.account || "").toLowerCase().includes(fa));
    if (fs) arr = arr.filter((r) => String(r?.surveyKey || "") === fs);
    // default sort by votedAt desc (if available) or by account
    arr.sort((a, b) => {
      const aTime = Number(a?.votedAt || a?.createdAt || 0);
      const bTime = Number(b?.votedAt || b?.createdAt || 0);
      if (aTime !== bTime) return bTime - aTime;
      return String(a?.account || "").localeCompare(String(b?.account || ""));
    });
    return arr;
  }, [surveyVotes, surveyVoteFilterAccount, surveyVoteFilterSurveyKey]);

  // Create Pool from Survey: only when closed and (for Raffle) winners drawn
  const canCreatePoolFromSurvey = useMemo(() => {
    if (!selectedSurvey || selectedSurvey.poolKey) return false;
    if (!selectedSurvey.closed) return false;
    if (Number(selectedSurvey.surveyType) !== 3) return true; // Normal/Answer-based: closed is enough
    const votesForSurvey = (surveyVotes || []).filter((v) => String(v?.surveyKey || "") === String(selectedSurvey.surveyKey || ""));
    const winnersDrawn = votesForSurvey.some((v) => Number(v?.winnerRank || 0) > 0);
    return winnersDrawn;
  }, [selectedSurvey, surveyVotes]);

  const excludedAssetsView = useMemo(() => {
    return (excludedAssets || []).map((r) => ({
      assetId: String(r?.assetId ?? r?.asset_id ?? ""),
      updatedAt: toReadableTime(r?.updatedAt ?? r?.updated_at ?? 0),
      actor: String(r?.actor ?? ""),
      reason: String(r?.reason ?? ""),
    }));
  }, [excludedAssets]);
  const excludedAssetOptions = useMemo(() => {
    const arr = Array.isArray(excludedAssetsView) ? excludedAssetsView.slice() : [];
    arr.sort((a, b) => {
      try {
        const ai = BigInt(String(a?.assetId || "0"));
        const bi = BigInt(String(b?.assetId || "0"));
        if (ai < bi) return -1;
        if (ai > bi) return 1;
        return 0;
      } catch {
        return String(a?.assetId || "").localeCompare(String(b?.assetId || ""));
      }
    });
    return arr;
  }, [excludedAssetsView]);

  // Filtered promotions
  const filteredPromotions = useMemo(() => {
    let arr = Array.isArray(promotions) ? promotions.slice() : [];
    const fp = String(promotionFilterProject || "").trim();
    const fs = String(promotionFilterStatus || "").trim();
    const search = String(promotionSearch || "").trim().toLowerCase();
    if (fp) {
      const pid = Number(fp);
      arr = arr.filter((p) => Number(p?.projectId || 0) === pid);
    }
    if (fs === "active") arr = arr.filter((p) => !!p?.active && !p?.promofinished);
    else if (fs === "inactive") arr = arr.filter((p) => !p?.active);
    else if (fs === "finished") arr = arr.filter((p) => !!p?.promofinished);
    if (search) {
      arr = arr.filter((p) => String(p?.promotionName || "").toLowerCase().includes(search));
    }
    // Sort by promoteId desc (newest first)
    arr.sort((a, b) => Number(b?.promoteId || 0) - Number(a?.promoteId || 0));
    return arr;
  }, [promotions, promotionFilterProject, promotionFilterStatus, promotionSearch]);

  // Filtered surveys
  const filteredSurveys = useMemo(() => {
    let arr = Array.isArray(surveys) ? surveys.slice() : [];
    const fp = String(surveyFilterProject || "").trim();
    const ft = String(surveyFilterType || "").trim();
    const fs = String(surveyFilterStatus || "").trim();
    const search = String(surveySearch || "").trim().toLowerCase();
    if (fp) {
      const pid = Number(fp);
      arr = arr.filter((s) => Number(s?.projectId || 0) === pid);
    }
    if (ft === "1") arr = arr.filter((s) => Number(s?.surveyType || 1) === 1);
    else if (ft === "2") arr = arr.filter((s) => Number(s?.surveyType || 1) === 2);
    else if (ft === "3") arr = arr.filter((s) => Number(s?.surveyType || 1) === 3);
    if (fs === "active") arr = arr.filter((s) => !!s?.active && !s?.closed);
    else if (fs === "inactive") arr = arr.filter((s) => !s?.active);
    else if (fs === "closed") arr = arr.filter((s) => !!s?.closed);
    if (search) {
      arr = arr.filter((s) => 
        String(s?.surveyKey || "").toLowerCase().includes(search) ||
        String(s?.surveyText || "").toLowerCase().includes(search)
      );
    }
    // Sort by surveyHash desc (newest first)
    arr.sort((a, b) => Number(b?.surveyHash || 0) - Number(a?.surveyHash || 0));
    return arr;
  }, [surveys, surveyFilterProject, surveyFilterType, surveyFilterStatus, surveySearch]);

  // Promotions view for table
  const promotionsView = useMemo(() => {
    return filteredPromotions.map((p) => {
      const pid = String(p?.projectId || "");
      const proj = projectsById.get(pid);
      const status = p?.promofinished ? "Finished" : p?.active ? "Active" : "Inactive";
      const poolCreated = p?.poolCreated ? "Yes" : "No";
      const actualClaimCount = Number(p?.actualClaimCount || 0);
      const maxClaimCount = Number(p?.maxClaimCount || 0);
      return {
        "Promotion Name": String(p?.promotionName || ""),
        "Project": String(proj?.projectName || pid),
        "Status": status,
        "Pool created": poolCreated,
        "Claims": `${actualClaimCount} / ${maxClaimCount}`,
        "Start": p?.startTimeSec,
        "End": p?.endTimeSec,
        _promoteId: String(p?.promoteId || ""),
        _promotion: p,
      };
    });
  }, [filteredPromotions, projectsById]);

  // Surveys view for table
  const surveysView = useMemo(() => {
    return filteredSurveys.map((s) => {
      const pid = String(s?.projectId || "");
      const proj = projectsById.get(pid);
      const surveyType = Number(s?.surveyType || 1);
      let typeText = "Normal";
      if (surveyType === 2) typeText = "Answer-based";
      else if (surveyType === 3) typeText = "Raffle";
      const status = s?.closed ? "Closed" : s?.active ? "Active" : "Inactive";
      const poolCreated = s?.poolKey ? "Yes" : "No";
      const voteCount = Number(s?.voteCount || 0);
      return {
        "Survey Key": String(s?.surveyKey || ""),
        "Project": String(proj?.projectName || pid),
        "Survey Type": typeText,
        "Status": status,
        "Pool created": poolCreated,
        "Participants": voteCount,
        "Start": s?.startTimeSec,
        "End": s?.endTimeSec,
      };
    });
  }, [filteredSurveys, projectsById]);

  // Filtered streaks
  const filteredStreaks = useMemo(() => {
    let arr = Array.isArray(streaks) ? streaks.slice() : [];
    const fp = String(streakFilterProject || "").trim();
    const fs = String(streakFilterStatus || "").trim();
    const search = String(streakSearch || "").trim().toLowerCase();
    if (fp) {
      const pid = Number(fp);
      arr = arr.filter((s) => Number(s?.projectId || 0) === pid);
    }
    if (fs === "active") arr = arr.filter((s) => !!s?.active && !s?.poolCreated);
    else if (fs === "inactive") arr = arr.filter((s) => !s?.active);
    else if (fs === "poolCreated") arr = arr.filter((s) => !!s?.poolCreated);
    if (search) {
      arr = arr.filter((s) => 
        String(s?.streakId || "").toLowerCase().includes(search) ||
        String(s?.streakName || "").toLowerCase().includes(search)
      );
    }
    // Sort by streakHash desc (newest first)
    arr.sort((a, b) => Number(b?.streakHash || 0) - Number(a?.streakHash || 0));
    return arr;
  }, [streaks, streakFilterProject, streakFilterStatus, streakSearch]);

  // Streaks view for table
  const streaksView = useMemo(() => {
    return filteredStreaks.map((s) => {
      const pid = String(s?.projectId || "");
      const proj = projectsById.get(pid);
      const status = s?.poolCreated ? "Pool Created" : s?.active ? "Active" : "Inactive";
      const poolCreated = s?.poolCreated ? "Yes" : "No";
      const targetStreakDays = Number(s?.targetStreakDays || 0);
      const targetInARow = Number(s?.targetInARow || 0);
      return {
        "Streak ID": String(s?.streakId || ""),
        "Name": String(s?.streakName || ""),
        "Project": String(proj?.projectName || pid),
        "Target Days": targetStreakDays,
        "In-A-Row": targetInARow > 0 ? targetInARow : "—",
        "Status": status,
        "Pool created": poolCreated,
        _streakId: String(s?.streakId || ""),
        _streak: s,
      };
    });
  }, [filteredStreaks, projectsById]);

  // Streaks for project
  const streaksForProject = useMemo(() => {
    const pid = String(selectedProjectId || "");
    if (!pid) return [];
    return (streaks || []).filter((s) => String(s?.projectId || "") === pid);
  }, [streaks, selectedProjectId]);

  // Filtered streak states
  const filteredStreakStates = useMemo(() => {
    let arr = Array.isArray(streakStates) ? streakStates.slice() : [];
    const fa = String(streakStateFilterAccount || "").trim().toLowerCase();
    const fs = String(streakStateFilterStatus || "").trim();
    if (fa) arr = arr.filter((r) => String(r?.userId || "").toLowerCase().includes(fa));
    if (fs === "active") arr = arr.filter((r) => Number(r?.status || 0) === 1);
    else if (fs === "completed") arr = arr.filter((r) => Number(r?.status || 0) === 2);
    else if (fs === "claimed") arr = arr.filter((r) => Number(r?.status || 0) === 3);
    // default sort by updatedAt desc
    arr.sort((a, b) => Number(b?.updatedAt || 0) - Number(a?.updatedAt || 0));
    return arr;
  }, [streakStates, streakStateFilterAccount, streakStateFilterStatus]);

  // Streak states view for table
  const streakStatesView = useMemo(() => {
    return filteredStreakStates.map((s) => {
      const status = Number(s?.status || 0);
      let statusText = "Unknown";
      if (status === 1) statusText = "Active";
      else if (status === 2) statusText = "Completed";
      else if (status === 3) statusText = "Claimed";
      return {
        "User": String(s?.userId || ""),
        "Streak ID": String(s?.streakId || ""),
        "Current Streak": Number(s?.currentStreak || 0),
        "Status": statusText,
        "Last Checked": toReadableTime(s?.lastCheckedDay || 0),
        "Last Qualified": toReadableTime(s?.lastQualifiedDay || 0),
        "Updated At": toReadableTime(s?.updatedAt || 0),
      };
    });
  }, [filteredStreakStates]);

  // Read-only overview tables (header cards)
  const allowTokensView = useMemo(() => {
    return (allowTokens || []).map((r) => ({
      tokenSymbol: String(r?.tokenSymbol ?? r?.token_symbol ?? ""),
      tokenContract: String(r?.tokenContract ?? r?.token_contract ?? ""),
      active: !!r?.active,
    }));
  }, [allowTokens]);

  const ramBudgetsView = useMemo(() => {
    return (ramBudgets || []).map((r) => {
      const pid = String(r?.projectId ?? r?.project_id ?? "");
      const p = projectsById.get(pid);
      return {
        projectId: pid,
        projectName: String(p?.projectName || ""),...r,
      };
    });
  }, [ramBudgets, projectsById]);

  const ramFeesView = useMemo(() => {
    const selPid = String(selectedProjectId || "");
    if (!selPid) return [];
    return (ramFees || [])
      .filter((r) => String(r?.projectId ?? r?.project_id ?? "") === selPid)
      .map((r) => {
        const pid = String(r?.projectId ?? r?.project_id ?? "");
        const p = projectsById.get(pid);
        return {
          projectId: pid,
          projectName: String(p?.projectName || ""),
          ...r,
        };
      });
  }, [ramFees, projectsById, selectedProjectId]);

  const depositsView = useMemo(() => {
    return (deposits || [])
      .filter((r) => Number(r?.type ?? 0) === 1)
      .map((r) => ({
        depositId: r?.depositId ?? r?.deposit_id ?? r?.id,
        type: "RAMFee",
        actor: r?.actor,
        amount: r?.amount,
        claimed: r?.claimed,
        createdAt: r?.createdAt,
        updatedAt: r?.updatedAt,
      }));
  }, [deposits]);

  // Deposit selectors for quick actions
  const ramFeeDepositOptions = useMemo(() => {
    const arr = Array.isArray(deposits) ? deposits : [];
    return arr.filter((d) => Number(d?.type ?? 0) === 1).map((d) => {
        const id = d?.depositId ?? d?.deposit_id ?? d?.id;
        return {...d, _id: String(id ?? "") };
      }).filter((d) => String(d._id).trim() !== "");
  }, [deposits]);


  // Manual deposit (type 2) selectors
  const dep2DepositOptions = useMemo(() => {
    const arr = Array.isArray(deposits) ? deposits : [];
    return arr.filter((d) => Number(d?.type ?? 0) === 2).map((d) => {
        const id = d?.depositId ?? d?.deposit_id ?? d?.id;
        return {...d, _id: String(id ?? "") };
      }).filter((d) => String(d._id).trim() !== "");
  }, [deposits]);

  const activeAllowTokenOptions = useMemo(() => {
    const arr = Array.isArray(allowTokens) ? allowTokens : [];
    return arr.filter((t) => !!t?.active).map((t) => {
        const tokenContract = String(t?.tokenContract ?? t?.token_contract ?? "").trim();
        const tokenSymbol = String(t?.tokenSymbol ?? t?.token_symbol ?? "").trim();
        const key = `${tokenContract}|${tokenSymbol}`;
        return { key, tokenContract, tokenSymbol };
      }).filter((t) => t.tokenContract && t.tokenSymbol);
  }, [allowTokens]);
  // Pool reward token selectors (based on Allowed Tokens)
  const poolAllowTokenOptions = useMemo(() => {
    const arr = Array.isArray(allowTokens) ? allowTokens : [];
    return arr
      .map((t) => {
        const tokenContract = String(t?.tokenContract ?? t?.token_contract ?? "").trim();
        const tokenSymbol = String(t?.tokenSymbol ?? t?.token_symbol ?? "").trim();
        const active = !!t?.active;
        const key = `${tokenContract}|${tokenSymbol}`;
        return { key, tokenContract, tokenSymbol, active };
      })
      .filter((t) => t.tokenContract && t.tokenSymbol);
  }, [allowTokens]);

  const poolTokenSymbolOptions = useMemo(() => {
    const map = new Map();
    for (const t of poolAllowTokenOptions) {
      if (!map.has(t.tokenSymbol)) map.set(t.tokenSymbol, parseSymbolParts(t.tokenSymbol));
    }
    return Array.from(map.entries())
      .map(([tokenSymbolRaw, parts]) => ({ tokenSymbolRaw, precision: parts.precision, symbol: parts.symbol }))
      .sort((a, b) => a.symbol.localeCompare(b.symbol) || a.precision - b.precision);
  }, [poolAllowTokenOptions]);

  const poolTokenContractOptions = useMemo(() => {
    const raw = String(poolForm.rewardTokenSymbolRaw || "").trim();
    const base = poolAllowTokenOptions.filter((t) => String(t.tokenSymbol) === raw);
    const uniq = new Map();
    for (const t of base) {
      if (!uniq.has(t.tokenContract)) uniq.set(t.tokenContract, t);
    }
    return Array.from(uniq.values()).sort((a, b) => a.tokenContract.localeCompare(b.tokenContract));
  }, [poolAllowTokenOptions, poolForm.rewardTokenSymbolRaw]);

  // Token contract options for promotion form
  const promotionTokenContractOptions = useMemo(() => {
    const raw = String(promotionForm.rewardTokenSymbolRaw || "").trim();
    const base = poolAllowTokenOptions.filter((t) => String(t.tokenSymbol) === raw);
    const uniq = new Map();
    for (const t of base) {
      if (!uniq.has(t.tokenContract)) uniq.set(t.tokenContract, t);
    }
    return Array.from(uniq.values()).sort((a, b) => a.tokenContract.localeCompare(b.tokenContract));
  }, [poolAllowTokenOptions, promotionForm.rewardTokenSymbolRaw]);

  // Token contract options for survey pool form
  const surveyPoolTokenContractOptions = useMemo(() => {
    const raw = String(surveyPoolForm.rewardTokenSymbolRaw || "").trim();
    const base = poolAllowTokenOptions.filter((t) => String(t.tokenSymbol) === raw);
    const uniq = new Map();
    for (const t of base) {
      if (!uniq.has(t.tokenContract)) uniq.set(t.tokenContract, t);
    }
    return Array.from(uniq.values()).sort((a, b) => a.tokenContract.localeCompare(b.tokenContract));
  }, [poolAllowTokenOptions, surveyPoolForm.rewardTokenSymbolRaw]);

  // Token contract options for streak form (by streak symbol, not pool symbol)
  const streakTokenContractOptions = useMemo(() => {
    const raw = String(streakForm.rewardTokenSymbolRaw || "").trim();
    const base = poolAllowTokenOptions.filter((t) => String(t.tokenSymbol) === raw);
    const uniq = new Map();
    for (const t of base) {
      if (!uniq.has(t.tokenContract)) uniq.set(t.tokenContract, t);
    }
    return Array.from(uniq.values()).sort((a, b) => a.tokenContract.localeCompare(b.tokenContract));
  }, [poolAllowTokenOptions, streakForm.rewardTokenSymbolRaw]);

  // Keep token contract in sync with selected raw symbol
  useEffect(() => {
    const raw = String(poolForm.rewardTokenSymbolRaw || "").trim();
    if (!raw) return;
    if (!poolTokenContractOptions.length) return;
    const cur = String(poolForm.rewardTokenContract || "").trim();
    const ok = poolTokenContractOptions.some((t) => String(t.tokenContract) === cur);
    if (!ok) {
      setPoolForm((s) => ({ ...s, rewardTokenContract: String(poolTokenContractOptions[0].tokenContract || "") }));
    }
  }, [poolForm.rewardTokenSymbolRaw, poolTokenContractOptions, poolForm.rewardTokenContract]);

  const poolRewardAssetPreview = useMemo(() => {
    const raw = String(poolForm.rewardTokenSymbolRaw || "").trim();
    if (!raw) return "";
    return normalizeAssetInputForSymbol(String(poolForm.rewardAmount || "").trim(), raw);
  }, [poolForm.rewardAmount, poolForm.rewardTokenSymbolRaw]);

  const poolRewardHelpText = useMemo(() => {
    const raw = String(poolForm.rewardTokenSymbolRaw || "").trim();
    if (!raw) return "Enter the numeric amount (decimals will be validated against the selected token precision).";
    const symbolParts = parseSymbolParts(raw);
    const precision = symbolParts?.precision ?? null;
    if (precision === null) return "Enter the numeric amount (decimals will be validated against the selected token precision).";
    const example = precision > 0 ? "1." + "0".repeat(precision) : "1";
    return `Enter the numeric amount with exactly ${precision} decimal place${precision !== 1 ? "s" : ""} (e.g. ${example}).`;
  }, [poolForm.rewardTokenSymbolRaw]);

  const poolRewardPlaceholder = useMemo(() => {
    const raw = String(poolForm.rewardTokenSymbolRaw || "").trim();
    if (!raw) return "e.g. 1.0000";
    const placeholder = assetPlaceholderForSymbol(raw);
    // Extract just the numeric part (without symbol) for placeholder
    const match = placeholder.match(/^(\d+\.\d+)/);
    return match ? `e.g. ${match[1]}` : "e.g. 1.0000";
  }, [poolForm.rewardTokenSymbolRaw]);


  const selectedDep2Token = useMemo(() => {
    const k = String(dep2TokenKey || "");
    return activeAllowTokenOptions.find((t) => t.key === k) || null;
  }, [activeAllowTokenOptions, dep2TokenKey]);

  const selectedDep2Deposit = useMemo(() => {
    const id = String(dep2UpdateId || "");
    return dep2DepositOptions.find((d) => String(d._id) === id) || null;
  }, [dep2DepositOptions, dep2UpdateId]);


  const refundDepositOptions = useMemo(() => {
    const arr = Array.isArray(deposits) ? deposits : [];
    return arr.filter((d) => Number(d?.type ?? 0) === 1).map((d) => {
        const id = d?.depositId ?? d?.deposit_id ?? d?.id;
        return {...d, _id: String(id ?? "") };
      }).filter((d) => String(d._id).trim() !== "");
  }, [deposits]);

  const selectedRamFeeDeposit = useMemo(() => {
    const id = String(ramFeeDepositId || "");
    return ramFeeDepositOptions.find((d) => String(d._id) === id) || null;
  }, [ramFeeDepositOptions, ramFeeDepositId]);

  const selectedRefundDeposit = useMemo(() => {
    const id = String(refundDepositId || "");
    return refundDepositOptions.find((d) => String(d._id) === id) || null;
  }, [refundDepositOptions, refundDepositId]);

  const ramFeeRowForProject = useMemo(() => {
    const pid = String(selectedProjectId || "");
    if (!pid) return null;
    return (
      (ramFees || []).find((r) => String(r?.projectId ?? r?.project_id ?? "") === pid) || null
    );
  }, [ramFees, selectedProjectId]);

  const ramBudgetRowForProject = useMemo(() => {
    const pid = String(selectedProjectId || "");
    if (!pid) return null;
    return (ramBudgets || []).find((r) => String(r?.projectId ?? r?.project_id ?? "") === pid) || null;
  }, [ramBudgets, selectedProjectId]);

  const ramFeeMaxClaims = useMemo(() => {
    // Best-effort helper: remaining RAMFee deposit divided by feePerClaim
    if (!selectedRamFeeDeposit || !ramFeeRowForProject) return { maxClaims: null, note: "" };
    const depAmt = parseAssetString(selectedRamFeeDeposit?.amount);
    const depClaimed = parseAssetString(selectedRamFeeDeposit?.claimed);
    const fee = parseAssetString(ramFeeRowForProject?.feePerClaim ?? ramFeeRowForProject?.fee_per_claim);
    if (!depAmt || !depClaimed || !fee) return { maxClaims: null, note: "" };
    if (depAmt.symbol !== fee.symbol || depAmt.precision !== fee.precision) {
      return { maxClaims: null, note: "Token mismatch between RAMFee deposit and feePerClaim." };
    }
    const remaining = depAmt.amountInt - depClaimed.amountInt;
    if (remaining <= 0n) return { maxClaims: 0n, note: "" };
    if (fee.amountInt <= 0n) return { maxClaims: null, note: "Invalid feePerClaim." };
    return { maxClaims: remaining / fee.amountInt, note: "" };
  }, [selectedRamFeeDeposit, ramFeeRowForProject]);

  const ramFeeMaxSetBudget = useMemo(() => {
    // Computes the maximum budgetClaims you can set based on:
    // - remaining RAMFee tokens in the selected deposit
    // - feePerClaim configured for the selected project
    // - current budgetClaims (if a budget row already exists)
    if (ramFeeMaxClaims?.maxClaims === null || ramFeeMaxClaims?.maxClaims === undefined) {
      return { maxBudgetClaims: null, note: ramFeeMaxClaims?.note || "" };
    }
    const maxDelta = ramFeeMaxClaims.maxClaims;
    if (typeof maxDelta !== "bigint") return { maxBudgetClaims: null, note: ramFeeMaxClaims?.note || "" };

    const currentBudget = BigInt(ramBudgetRowForProject?.budgetClaims ?? ramBudgetRowForProject?.budget_claims ?? 0);
    const usedClaims = BigInt(ramBudgetRowForProject?.usedClaims ?? ramBudgetRowForProject?.used_claims ?? 0);

    let maxBudgetClaims = ramBudgetRowForProject ? currentBudget + maxDelta : maxDelta;
    if (maxBudgetClaims < usedClaims) maxBudgetClaims = usedClaims;

    return { maxBudgetClaims, maxDelta, currentBudget, usedClaims, note: ramFeeMaxClaims?.note || "" };
  }, [ramFeeMaxClaims, ramBudgetRowForProject]);


  // Restore session on load
  useEffect(() => {
    let cancelled = false;
    async function tryRestore() {
      try {
        if (session || auth) return;
        const rpcNormalized = normalizeRpcEndpoint(rpc);
        const endpoints = uniq([rpcNormalized,...FALLBACK_RPCS]);
        const { link: l, session: s } = await ProtonWebSDK({
          linkOptions: { endpoints, restoreSession: true },
          transportOptions: { requestAccount: CONTRACT, backButton: true },
          selectorOptions: {
            appName: APP_NAME,
            showSelector: false,
            enabledWalletTypes: ["webauth", "proton"],
            dialogRootNode: document.body,
          },
        });
        if (cancelled) return;
        if (s) {
          setLink(l);
          setSession(s);
          setAuth(s.auth);
        }
      } catch {
        // ignore
      }
    }
    const t = setTimeout(() => tryRestore(), 100);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectWallet = useCallback(async () => {
    setError("");
    setNotice("");
    setAccessGateMsg("");
    try {
      const rpcNormalized = normalizeRpcEndpoint(rpc);
      const endpoints = uniq([rpcNormalized, ...FALLBACK_RPCS]);
      
      // Edge-Browser-Erkennung für spezielle Behandlung
      const isEdge = typeof navigator !== "undefined" && /Edg/i.test(navigator.userAgent);
      
      const { link: l, session: s } = await ProtonWebSDK({
        linkOptions: { endpoints },
        transportOptions: { 
          requestAccount: CONTRACT, 
          backButton: true,
        },
        selectorOptions: {
          appName: APP_NAME,
          showSelector: true,
          enabledWalletTypes: ["webauth", "proton"],
          dialogRootNode: document.body,
        },
      });
      
      // Edge-spezifischer Workaround: Wenn keine Session zurückkommt, 
      // geben wir hilfreiche Hinweise
      if (!s && isEdge && l) {
        setNotice("Im Edge-Browser: Falls die Wallet-App nicht automatisch geöffnet wird, tippen Sie bitte manuell auf 'Open Wallet' im QR-Code-Dialog oder öffnen Sie die Proton Wallet-App direkt auf Ihrem Gerät.");
      } else if (s) {
        // Update wallet state manually since we're using custom connection
        // Note: In a full refactor, this would be moved to useWallet hook
        setLink(l);
        setSession(s);
        setAuth(s.auth);
      }
    } catch (e) {
      const err = serializeError(e);
      setError(err.message);
      // Zusätzliche Edge-spezifische Fehlerbehandlung
      const isEdge = typeof navigator !== "undefined" && /Edg/i.test(navigator.userAgent);
      if (isEdge && err.message && err.message.includes("session")) {
        setNotice("Hinweis: Im Edge-Browser müssen Sie möglicherweise die Wallet-App manuell öffnen, nachdem Sie den QR-Code gescannt haben.");
      }
    }
  }, [rpc]);

  const openTermsGate = useCallback(() => {
    setError("");
    setNotice("");
    setTermsChecked(false);
    setTermsGateOpen(true);
  }, []);

  const persistTermsAcceptance = useCallback(() => {
    try {
      localStorage.setItem(TERMS_STORAGE_KEY, "yes");
      localStorage.setItem("nr_terms_accept_time", new Date().toISOString());
    } catch {
      // ignore
    }
    setTermsAccepted(true);
  }, []);

  const acceptTermsAndContinue = useCallback(async () => {
    persistTermsAcceptance();
    setTermsGateOpen(false);
    // If user is not connected yet, continue with wallet connect.
    if (!auth) {
      await connectWallet();
    } else {
      setNotice("Terms accepted.");
    }
  }, [auth, persistTermsAcceptance, connectWallet]);

  const handleConnectClick = useCallback(() => {
    void logAuthEvent("connect_attempt", "");
    if (!termsAccepted) {
      openTermsGate();
      return;
    }
    void connectWallet();
  }, [termsAccepted, logAuthEvent, openTermsGate, connectWallet]);

  const disconnect = useCallback(async () => {
    setError("");
    setNotice("");
    try {
      if (link && session) await link.removeSession(session);
    } catch {
      // ignore
    }
    setLink(null);
    setSession(null);
    setAuth(null);
    setProjects([]);
    setPools([]);
    setReqNfts([]);
    setClaims([]);
    setWallets([]);
    setExcludedAssets([]);
    setSelectedProjectId("");
    setSelectedPoolHash("");
    setSelectedReqId("");
    setSelectedSurveyKey("");
    setExclAssetId("");
    setExclReason("");
    preferNewSurveyRef.current = false;
    setContractViewAsProjectId("");
    setContractViewAsAccount("");
  }, [link, session]);

  const transact = useCallback(async (actions) => {
    setError("");
    setNotice("");
    // Store tx json so users can copy it from Advanced section
    try {
      setLastSignedTxJson(JSON.stringify({ actions }, null, 2));
    } catch {
      // ignore
    }
    if (!session || !auth) {
      setError("Please connect your WebAuth wallet first.");
      setTxBanner({ ok: false, message: "Please connect your WebAuth wallet first." });
      return null;
    }
    const signerName = String(auth.actor || "").trim();
    const txActions = actions.map((a) => {
      if (!a || a.account !== TX_CONTRACT || !a.data || typeof a.data !== "object") return a;
      const data = { ...a.data };
      if (!data.authAccount && !data.signer && !data.actor) data.authAccount = signerName;
      return { ...a, data };
    });
    setBusy(true);
    try {
      const txResult = await session.transact({ actions: txActions }, { broadcast: true });
      setNotice("Transaction sent successfully.");
      setTxBanner({ ok: true, message: "Transaction successful.", txid: txResult?.transaction_id || txResult?.processed?.id });
      // Always refresh dropdown-backed data after any signed transaction
      try {
        await loadCoreState({ silent: true });
        await loadNauths({ silent: true });
      } catch {
        // ignore
      }
      // Also refresh project-scoped tables that are not part of loadCoreState
      try {
        if (selectedProjectId) await loadExcludedAssetsForProject(selectedProjectId, { silent: true });
      } catch {
        // ignore
      }
      return txResult;
    } catch (e) {
      const err = serializeError(e);
      setError(err.message);
      setTxBanner({ ok: false, message: err.message });
      return null;
    } finally {
      setBusy(false);
      postWalletUiCleanup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, auth, selectedProjectId]);

  async function loadCoreState(opts = {}) {
    const silent = !!opts.silent;
    if (!silent) {
      setError("");
      setNotice("");
    }
    if (!actor) return;
    setProjectsLoading(true);
    try {
      const allProjects = await fetchAllRows(rpc, { code: CONTRACT, scope: CONTRACT, table: "nprojects", limit: 1000 });
      setProjects(allProjects);

      // Access gate (except contract)
      if (!isContract) {
        const allowed = (allProjects || []).some((p) => (p.allowedAccounts || []).map((x) => String(x)).includes(actor));
        if (!allowed) {
          const acct = String(actor || "");
          setAccessGateMsg(
            `No project found for account "${acct}".\n` +
              `This account is not listed in any project's allowedAccounts.\n` +
              `If you run an NFT project on the XPR Network and want to onboard it, join our Telegram group: https://t.me/nftclaimhub`
          );
          await disconnect();
          return;
        }
      }

      const [tokRows, budgetRows, feeRows, depRows, poolRows, poolMutRowsRaw, poolScopeRowsRaw, reqRows, vaultRows, promoRows, surveyRows, streakRows] = await Promise.all([
        fetchAllRows(rpc, { code: CONTRACT, scope: CONTRACT, table: "nallowtoks", limit: 1000 }),
        fetchAllRows(rpc, { code: CONTRACT, scope: CONTRACT, table: "nrambudgets", limit: 1000 }),
        fetchAllRows(rpc, { code: CONTRACT, scope: CONTRACT, table: "nramfees", limit: 1000 }),
        fetchAllRows(rpc, { code: CONTRACT, scope: CONTRACT, table: "ndeposits", limit: 1000, reverse: true }),
        fetchAllRows(rpc, { code: CONTRACT, scope: CONTRACT, table: "npools", limit: 1000 }),
        fetchAllRows(rpc, { code: CONTRACT, scope: CONTRACT, table: "npoolmut", limit: 1000 }).catch(() => []),
        fetchAllRows(rpc, { code: CONTRACT, scope: CONTRACT, table: "npoolscope", limit: 1000 }).catch(() => []),
        fetchAllRows(rpc, { code: CONTRACT, scope: CONTRACT, table: "nreqnfts", limit: 1000 }),
        fetchAllRows(rpc, { code: CONTRACT, scope: CONTRACT, table: "nprojvaults", limit: 1000 }),
        fetchAllRows(rpc, { code: CONTRACT, scope: CONTRACT, table: "npromotions", limit: 1000 }),
        fetchAllRows(rpc, { code: CONTRACT, scope: CONTRACT, table: "nsurveys", limit: 1000 }),
        fetchAllRows(rpc, { code: CONTRACT, scope: CONTRACT, table: "nstreaks", limit: 1000 }),
      ]);

      setAllowTokens(tokRows);
      setProjVaults(vaultRows);


      // Actor filter: contract "god mode" sees all rows; contract "view as" and normal users see their actor rows
      const filterActor = (rows) => {
        const arr = Array.isArray(rows) ? rows : [];
        if (contractGodMode) return arr;
        const a = String(effectiveActorForFilters || "").trim();
        if (!a) return [];
        return arr.filter((r) => {
          if (r && Object.prototype.hasOwnProperty.call(r, "actor")) return String(r.actor) === a;
          return true;
        });
      };

      const ramBudgetsFiltered = filterActor(budgetRows);
      const depositsFiltered = filterActor(depRows);
      const poolsFiltered = filterActor(poolRows);
      const reqFiltered = filterActor(reqRows);
      const promotionsFiltered = filterActor(promoRows);
      const surveysFiltered = filterActor(surveyRows);
      const streaksFiltered = filterActor(streakRows);

      setRamBudgets(ramBudgetsFiltered);
      setRamFees(feeRows); // no actor field
      setDeposits(depositsFiltered);
      setPools(poolsFiltered);
      setPoolMutRows(Array.isArray(poolMutRowsRaw) ? poolMutRowsRaw : []);
      setPoolScopeRows(Array.isArray(poolScopeRowsRaw) ? poolScopeRowsRaw : []);
      setReqNfts(reqFiltered);
      setPromotions(promotionsFiltered);
      setSurveys(surveysFiltered);
      setStreaks(streaksFiltered);
      
      // Load survey votes for each survey (survey-scoped table)
      const allSurveyVotes = [];
      const surveysArray = Array.isArray(surveysFiltered) ? surveysFiltered : [];
      console.log("[loadCoreState] Loading survey votes for", surveysArray.length, "surveys");
      
      for (const survey of surveysArray) {
        const surveyHash = String(survey.surveyHash || "");
        if (!surveyHash) continue;
        
        try {
          const votes = await fetchAllRows(rpc, { 
            code: CONTRACT, 
            scope: surveyHash, 
            table: "nsurveyvotes", 
            limit: 1000 
          });
          if (Array.isArray(votes)) {
            const votesWithKey = votes.map((v) => ({ ...v, surveyKey: v.surveyKey ?? survey.surveyKey }));
            allSurveyVotes.push(...votesWithKey);
            console.log("[loadCoreState] Loaded", votes.length, "votes for survey", survey.surveyKey || surveyHash);
          }
        } catch (e) {
          console.warn("[loadCoreState] Failed to load votes for survey", survey.surveyKey || surveyHash, ":", e);
        }
      }
      
      console.log("[loadCoreState] Total surveyVotes loaded:", allSurveyVotes.length);
      setSurveyVotes(allSurveyVotes);

      // Load npoolcrefee: try CONTRACT scope first; if empty, try per-pool (scope = poolHash)
      let creatorFeeRows = [];
      try {
        creatorFeeRows = await fetchAllRows(rpc, { code: CONTRACT, scope: CONTRACT, table: "npoolcrefee", limit: 1000 });
        if (!Array.isArray(creatorFeeRows)) creatorFeeRows = [];
        if (creatorFeeRows.length === 0 && poolsFiltered.length > 0) {
          const byPool = [];
          for (const pool of poolsFiltered) {
            const scope = String(pool.poolHash ?? "").trim();
            if (!scope) continue;
            const rows = await fetchAllRows(rpc, { code: CONTRACT, scope, table: "npoolcrefee", limit: 100 });
            const arr = Array.isArray(rows) ? rows : [];
            for (const r of arr) {
              byPool.push({ ...r, poolKey: pool.poolKey, poolHash: scope });
            }
          }
          creatorFeeRows = byPool;
        }
        setPoolCreatorFees(creatorFeeRows);
      } catch (e) {
        console.warn("[loadCoreState] npoolcrefee load failed:", e);
        setPoolCreatorFees([]);
      }

      // Load streak states for each streak (streak-scoped table)
      const allStreakStates = [];
      const streaksArray = Array.isArray(streaksFiltered) ? streaksFiltered : [];
      console.log("[loadCoreState] Loading streak states for", streaksArray.length, "streaks");
      
      for (const streak of streaksArray) {
        const streakHash = String(streak.streakHash || "");
        if (!streakHash) continue;
        
        try {
          const states = await fetchAllRows(rpc, { 
            code: CONTRACT, 
            scope: streakHash, 
            table: "nstreaksts", 
            limit: 1000 
          });
          if (Array.isArray(states)) {
            // Add streakId to each state for easier reference
            const statesWithId = states.map((s) => ({ ...s, streakId: streak.streakId, streakHash }));
            allStreakStates.push(...statesWithId);
            console.log("[loadCoreState] Loaded", states.length, "states for streak", streak.streakId || streakHash);
          }
        } catch (e) {
          console.warn("[loadCoreState] Failed to load states for streak", streak.streakId || streakHash, ":", e);
        }
      }
      
      console.log("[loadCoreState] Total streakStates loaded:", allStreakStates.length);
      setStreakStates(allStreakStates);

      return {
        projects: allProjects,
        allowTokens: tokRows,
        ramBudgets: ramBudgetsFiltered,
        ramFees: feeRows,
        deposits: depositsFiltered,
        pools: poolsFiltered,
        reqNfts: reqFiltered,
      };
    } catch (e) {
      setError(serializeError(e).message);
      return null;
    } finally {
      setProjectsLoading(false);
    }
  }


// Excluded claim assets table (nexclassets) is scoped by projectId.
async function loadExcludedAssetsForProject(projectId, opts = {}) {
  const silent = !!opts.silent;
  const pid = String(projectId || "").trim();
  if (!pid) {
    setExcludedAssets([]);
    return [];
  }
  setExcludedAssetsLoading(true);
  try {
    const rows = await fetchAllRows(rpc, { code: CONTRACT, scope: pid, table: "nexclassets", limit: 1000 });
    const arr = Array.isArray(rows) ? rows : [];
    setExcludedAssets(arr);
    return arr;
  } catch (e) {
    if (!silent) setError(serializeError(e).message);
    setExcludedAssets([]);
    return [];
  } finally {
    setExcludedAssetsLoading(false);
  }
}

// Load nauths (action permissions) for the current account.
// Table nauths is scoped by account (actor) in the contract.
async function loadNauths(opts = {}) {
  const silent = !!opts.silent;
  const scopeAccount = contractViewAsActive
    ? String(contractViewAsAccount || "").trim()
    : String(actor || "").trim();
  if (!scopeAccount) {
    setNauths([]);
    return [];
  }
  setNauthsLoading(true);
  try {
    const scope = scopeAccount.toLowerCase();
    const rows = await fetchAllRows(rpc, { code: CONTRACT, scope, table: "nauths", limit: 1000 });
    const arr = Array.isArray(rows) ? rows : [];
    setNauths(arr);
    return arr;
  } catch (e) {
    if (!silent) setError(serializeError(e).message);
    setNauths([]);
    return [];
  } finally {
    setNauthsLoading(false);
  }
}

  // Load core state after login (and when contract "view as" changes filtering)
  useEffect(() => {
    if (!actor) return;
    loadCoreState();
    loadNauths({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actor, contractViewAsActive, contractViewAsProjectId, contractViewAsAccount]);

  // Refetch nauths when Pool card is opened (so newly granted actions like delpool appear)
  useEffect(() => {
    if (!actor || contractGodMode) return;
    if (!openCards.pool) return;
    loadNauths({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actor, contractGodMode, openCards.pool]);

  // Default select first allowed project
  useEffect(() => {
    if (!actor) return;
    if (selectedProjectId) return;
    if ((allowedProjects || []).length > 0) setSelectedProjectId(String(allowedProjects[0].projectId));
  }, [actor, allowedProjects, selectedProjectId]);

  // Load excluded assets (nexclassets) for the selected project
  useEffect(() => {
    if (!actor || !selectedProjectId) {
      setExcludedAssets([]);
      return;
    }
    setExclAssetId("");
    setExclReason("");
    loadExcludedAssetsForProject(selectedProjectId, { silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actor, selectedProjectId]);

  // Default/select RAMFee deposit (type=1). Prefer a deposit that matches the selected project's RAM fee token.
  useEffect(() => {
    if (!actor) return;
    const opts = ramFeeDepositOptions || [];
    if (opts.length === 0) {
      if (ramFeeDepositId) setRamFeeDepositId("");
      return;
    }

    const feeContract = String(ramFeeRowForProject?.tokenContract ?? ramFeeRowForProject?.token_contract ?? "").trim();
    const feeSymbol = symbolCodeFrom(ramFeeRowForProject?.tokenSymbol ?? ramFeeRowForProject?.token_symbol ?? "");

    const actorOk = (d) => (contractGodMode ? true : String(d?.actor || "") === String(effectiveActorForFilters));
    const feeOk = (d) => {
      if (!feeContract || !feeSymbol) return true;
      const dc = String(d?.tokenContract ?? d?.token_contract ?? "").trim();
      const ds = symbolCodeFrom(d?.tokenSymbol ?? d?.token_symbol ?? "");
      return dc === feeContract && ds === feeSymbol;
    };

    const current = opts.find((d) => String(d._id) === String(ramFeeDepositId)) || null;
    if (current && actorOk(current) && feeOk(current)) return;

    // Prefer: actor+fee match, then actor match, then any.
    const preferred = opts.find((d) => actorOk(d) && feeOk(d)) || opts.find((d) => actorOk(d)) || opts[0];
    if (preferred?._id) setRamFeeDepositId(String(preferred._id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actor, contractGodMode, effectiveActorForFilters, ramFeeDepositOptions, ramFeeRowForProject]);

  // Default select first available deposit for refund
  useEffect(() => {
    if (!actor) return;
    const opts = refundDepositOptions || [];
    if (opts.length === 0) {
      if (refundDepositId) setRefundDepositId("");
      return;
    }
    const currentOk = refundDepositId && opts.some((d) => String(d._id) === String(refundDepositId));
    if (currentOk) return;
    // For non-contract accounts deposits are already actor-filtered. For contract, keep current selection if possible.
    const preferred =
      opts.find((d) => String(d?.actor || "") === String(effectiveActorForFilters)) || opts[0];
    if (preferred?._id) setRefundDepositId(String(preferred._id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actor, effectiveActorForFilters, refundDepositOptions]);


  // Default select first active allowed token for manual deposit creation
  useEffect(() => {
    if (!actor) return;
    const opts = activeAllowTokenOptions || [];
    if (opts.length === 0) {
      if (dep2TokenKey) setDep2TokenKey("");
      return;
    }
    const ok = dep2TokenKey && opts.some((t) => String(t.key) === String(dep2TokenKey));
    if (ok) return;
    setDep2TokenKey(String(opts[0].key));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actor, activeAllowTokenOptions]);

  // Default select first manual deposit (type=2) for update
  useEffect(() => {
    if (!actor) return;
    const opts = dep2DepositOptions || [];
    if (opts.length === 0) {
      if (dep2UpdateId) setDep2UpdateId("");
      return;
    }
    const ok = dep2UpdateId && opts.some((d) => String(d._id) === String(dep2UpdateId));
    if (ok) return;
    const preferred = contractGodMode
      ? opts[0]
      : opts.find((d) => String(d?.actor || "") === String(effectiveActorForFilters)) || opts[0];
    if (preferred?._id) setDep2UpdateId(String(preferred._id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actor, contractGodMode, effectiveActorForFilters, dep2DepositOptions]);

  // Prefill update amount when selecting a manual deposit
  useEffect(() => {
    if (!selectedDep2Deposit) return;
    setDep2UpdateAmount(String(selectedDep2Deposit?.amount || ""));
  }, [selectedDep2Deposit]);


  // Prefill project form when selection changes
  useEffect(() => {
    if (!selectedProject) return;
    const allowed = Array.isArray(selectedProject.allowedAccounts) ? selectedProject.allowedAccounts : [];
    setProjectForm({
      projectId: String(selectedProject.projectId ?? ""),
      projectName: String(selectedProject.projectName ?? ""),
      homepageUrl: String(selectedProject.homepageUrl ?? ""),
      projectImageUrl: String(selectedProject.projectImageUrl ?? ""),
      projectDescription: String(selectedProject.projectDescription ?? ""),
      allowedAccountsText: JSON.stringify(allowed.map((x) => String(x)), null, 2),
    });
  }, [selectedProject]);

  // Helper function to get pool type label
  const getPoolTypeLabel = useCallback((poolType) => {
    const type = Number(poolType || 1);
    if (type === 2) return "Promotion";
    if (type === 3) return "Survey";
    if (type === 4) return "Streak";
    if (type === 5) return "Repeatable";
    if (type === 6) return "Stake Claim";
    return "Reward";
  }, []);

  // Pools for selected project
  const poolsForProject = useMemo(() => {
    if (!selectedProject) return [];
    const list = (pools || []).filter((p) => String(p.projectId) === String(selectedProject.projectId));
    // Sort pools alphabetically by poolKey for the dropdown (natural numeric ordering).
    list.sort((a, b) => alphaCompare(a?.poolKey ?? "", b?.poolKey ?? ""));
    return list;
  }, [pools, selectedProject]);

  // Surveys for selected project
  const surveysForProject = useMemo(() => {
    if (!selectedProject) return [];
    const list = (surveys || []).filter((s) => String(s.projectId) === String(selectedProject.projectId));
    // Sort surveys alphabetically by surveyKey for the dropdown (natural numeric ordering).
    list.sort((a, b) => alphaCompare(a?.surveyKey ?? "", b?.surveyKey ?? ""));
    return list;
  }, [surveys, selectedProject]);

  const promotionsForProject = useMemo(() => {
    if (!selectedProject) return [];
    const list = (promotions || []).filter((p) => String(p.projectId) === String(selectedProject.projectId));
    // Sort promotions alphabetically by promotionName for the dropdown (natural numeric ordering).
    list.sort((a, b) => alphaCompare(a?.promotionName ?? "", b?.promotionName ?? ""));
    return list;
  }, [promotions, selectedProject]);

  // Clear "new pool" preference when switching projects.
  useEffect(() => {
    preferNewPoolRef.current = false;
    setPoolCheck(null);
  }, [selectedProjectId]);

  // Clear check panels when switching selections.
  useEffect(() => {
    setPoolCheck(null);
  }, [selectedPoolHash]);

  // Clear survey check when form changes
  useEffect(() => {
    setSurveyCheck(null);
  }, [surveyForm.surveyKey, surveyForm.surveyText, surveyForm.numOptions, surveyForm.surveyType, surveyForm.requiredAnswers, surveyForm.projectId]);

  // Clear promotion check and pool form when promotion selection changes
  useEffect(() => {
    setPromotionCheck(null);
    setPromotionPoolForm({ poolKey: undefined });
  }, [selectedPromotionId]);

  useEffect(() => {
    setReqCheck(null);
  }, [selectedPoolHash, selectedReqId]);

  // Select first pool when project changes
  useEffect(() => {
    if (!selectedProject) return;

    // If the user explicitly selected "(new pool)", keep it.
    if (preferNewPoolRef.current && !String(selectedPoolHash || "").trim()) return;
    // Keep the current selection if it still exists for this project
    const cur = String(selectedPoolHash || "");
    const stillExists = cur && (poolsForProject || []).some((p) => String(p.poolHash) === cur);
    if (stillExists) return;

    // If we just created/updated a pool, jump to that exact poolKey (even if labels/names repeat)
    const pendingKey = String(pendingSelectPoolKeyRef.current || "").trim();
    if (pendingKey) {
      const hit = (poolsForProject || []).find((p) => String(p.poolKey) === pendingKey);
      pendingSelectPoolKeyRef.current = "";
      if (hit?.poolHash !== undefined && hit?.poolHash !== null) {
        setSelectedPoolHash(String(hit.poolHash));
        return;
      }
    }

    // Fall back to first pool (or none)
    if (poolsForProject.length > 0) setSelectedPoolHash(String(poolsForProject[0].poolHash));
    else setSelectedPoolHash("");
  }, [selectedProjectId, poolsForProject, selectedPoolHash]);

  // Select first survey when project changes
  useEffect(() => {
    if (!selectedProject) return;
    // If the user explicitly selected "(new survey)", keep it.
    if (preferNewSurveyRef.current && !String(selectedSurveyKey || "").trim()) return;
    // Keep the current selection if it still exists for this project
    const cur = String(selectedSurveyKey || "");
    const stillExists = cur && (surveysForProject || []).some((s) => String(s.surveyKey) === cur);
    if (stillExists) return;
    // Fall back to first survey (or none)
    if (surveysForProject.length > 0) setSelectedSurveyKey(String(surveysForProject[0].surveyKey));
    else setSelectedSurveyKey("");
  }, [selectedProjectId, surveysForProject, selectedSurveyKey]);

  // Prefill pool form
  useEffect(() => {
    if (!selectedPool) return;

    const rewardStr = String(pick(selectedPool, "reward", "") || "").trim();
    const parsedReward = parseAssetString(rewardStr);
    const rewardAmount = parsedReward
      ? String((formatAssetInt(parsedReward.amountInt, parsedReward.precision, parsedReward.symbol).split(" ")[0]) || "")
      : "";
    const rewardTokenSymbolRaw = parsedReward ? `${parsedReward.precision},${parsedReward.symbol}` : "";

    setPoolForm({
      poolKey: String(pick(selectedPool, "poolKey", "")),
      projectId: String(pick(selectedPool, "projectId", "")),
      collectionName: String(pick(selectedPool, "collectionName", "")),
      rewardAmount,
      rewardTokenSymbolRaw,
      rewardTokenContract: String(pick(selectedPool, "rewardTokenContract", "")),
      rewardWallet: String(pick(selectedPool, "rewardWallet", CONTRACT)),
      active: !!pick(selectedPool, "active", true),
      startTimeSec: String(pick(selectedPool, "startTimeSec", "0")),
      endTimeSec: String(pick(selectedPool, "endTimeSec", "0")),
      cooldownSec: String(pick(selectedPool, "cooldownSec", "0")),
      requiredImmutableText: JSON.stringify(pick(selectedPool, "requiredImmutable", []), null, 2),
      requiredMutableText: JSON.stringify(requiredMutableForSelectedPool, null, 2),
      poolDescription: String(pick(selectedPool, "poolDescription", "")),
      poolImageUrl: String(pick(selectedPool, "poolImageUrl", "")),
      poolType: Number(pick(selectedPool, "poolType", 1)),
      claimScope: claimScopeForSelectedPool,
      creatorFeeEnabled: !!pick(selectedPool, "creatorFeeEnabled", false),
    });
  }, [selectedPool, requiredMutableForSelectedPool, claimScopeForSelectedPool]);

  // Defaults for creating a new pool (when no pool is selected)
  useEffect(() => {
    if (!selectedProject) return;
    if (selectedPool) return;

    // Pool Copy: use selected pool as template with empty pool key
    if (justCopiedPoolRef.current) {
      const p = justCopiedPoolRef.current;
      justCopiedPoolRef.current = null;
      const rewardStr = String(pick(p, "reward", "") || "").trim();
      const parsedReward = parseAssetString(rewardStr);
      const rewardAmount = parsedReward
        ? String((formatAssetInt(parsedReward.amountInt, parsedReward.precision, parsedReward.symbol).split(" ")[0]) || "")
        : "";
      const rewardTokenSymbolRaw = parsedReward ? `${parsedReward.precision},${parsedReward.symbol}` : "";
      setPoolForm({
        poolKey: "",
        projectId: String(pick(p, "projectId", selectedProject?.projectId ?? "")),
        collectionName: String(pick(p, "collectionName", "")),
        rewardAmount,
        rewardTokenSymbolRaw,
        rewardTokenContract: String(pick(p, "rewardTokenContract", "")),
        rewardWallet: String(pick(p, "rewardWallet", CONTRACT)),
        active: !!pick(p, "active", true),
        startTimeSec: String(pick(p, "startTimeSec", "0")),
        endTimeSec: String(pick(p, "endTimeSec", "0")),
        cooldownSec: String(pick(p, "cooldownSec", "0")),
        requiredImmutableText: JSON.stringify(pick(p, "requiredImmutable", []), null, 2),
        requiredMutableText: (() => {
          const h = String(p?.poolHash ?? "").trim();
          const row = (poolMutRows || []).find((r) => String(r.poolHash ?? r.poolhash ?? "") === h);
          const arr = row?.requiredMutable ?? row?.required_mutable;
          return JSON.stringify(Array.isArray(arr) ? arr : [], null, 2);
        })(),
        poolDescription: String(pick(p, "poolDescription", "")),
        poolImageUrl: String(pick(p, "poolImageUrl", "")),
        poolType: Number(pick(p, "poolType", 1)),
        claimScope: (() => {
          const h = String(p?.poolHash ?? "").trim();
          const row = (poolScopeRows || []).find((r) => String(r.poolHash ?? r.poolhash ?? "") === h);
          const raw = row?.claimScope ?? row?.claim_scope;
          return Number(raw) === CLAIM_SCOPE_ACCOUNT ? CLAIM_SCOPE_ACCOUNT : CLAIM_SCOPE_NFT;
        })(),
        creatorFeeEnabled: !!pick(p, "creatorFeeEnabled", false),
      });
      if (poolCreatorFees && poolCreatorFees.length > 0) {
        const poolKey = String(p?.poolKey || "").trim();
        const poolHash = String(p?.poolHash ?? "").trim();
        const feeRow = poolCreatorFees.find(
          (r) =>
            String(r.poolKey ?? r.pool_key ?? "").trim() === poolKey ||
            String(r.poolHash ?? r.poolhash ?? "").trim() === poolHash
        );
        if (feeRow) {
          const feePercent = feeRow.feePercent ?? feeRow.fee_percent ?? feeRow.feePercentBps ?? 0;
          const receiver = String(feeRow.receiver ?? feeRow.receiverAccount ?? "").trim();
          setCreatorFeeForm({ feePercent: String(feePercent), receiver });
        }
      }
      return;
    }

    setPoolForm({
      poolKey: "",
      projectId: String(selectedProject.projectId ?? ""),
      collectionName: "",
      rewardAmount: "",
      rewardTokenSymbolRaw: "",
      rewardTokenContract: "",
      rewardWallet: CONTRACT,
      active: true,
      startTimeSec: "0",
      endTimeSec: "0",
      cooldownSec: "0",
      requiredImmutableText: "[]",
      requiredMutableText: "[]",
      poolDescription: "",
      poolImageUrl: "",
      poolType: 1,
      claimScope: CLAIM_SCOPE_NFT,
      creatorFeeEnabled: false,
    });
  }, [selectedProjectId, selectedPoolHash, selectedPool, selectedProject, poolMutRows, poolScopeRows]);

  // Clear pool reset result when pool selection changes
  useEffect(() => {
    setResetPoolResult(null);
  }, [resetPoolForm.poolKey]);

  // Prefill creator fee form from npoolcrefee when a pool with creator fee is selected
  useEffect(() => {
    if (!selectedPool || !poolCreatorFees.length) return;
    const poolKey = String(selectedPool.poolKey || "").trim();
    const poolHash = String(selectedPool.poolHash ?? "").trim();
    const row = poolCreatorFees.find(
      (r) =>
        String(r.poolKey ?? r.pool_key ?? "").trim() === poolKey ||
        String(r.poolHash ?? r.poolhash ?? "").trim() === poolHash
    );
    if (row) {
      const feePercent = row.feePercent ?? row.fee_percent ?? row.feePercentBps ?? 0;
      const receiver = String(row.receiver ?? row.receiverAccount ?? "").trim();
      setCreatorFeeForm({ feePercent: String(feePercent), receiver });
    }
  }, [selectedPool, poolCreatorFees]);

  // Prefill promotion form
  useEffect(() => {
    if (!selectedPromotion) return;
    const rewardStr = String(selectedPromotion.reward || "").trim();
    const parsedReward = parseAssetString(rewardStr);
    const rewardAmount = parsedReward
      ? String((formatAssetInt(parsedReward.amountInt, parsedReward.precision, parsedReward.symbol).split(" ")[0]) || "")
      : "";
    const rewardTokenSymbolRaw = parsedReward ? `${parsedReward.precision},${parsedReward.symbol}` : "";
    setPromotionForm({
      promoteId: String(selectedPromotion.promoteId || ""),
      promotionName: String(selectedPromotion.promotionName || ""),
      projectId: String(selectedPromotion.projectId || ""),
      collectionName: String(selectedPromotion.collectionName || ""),
      rewardAmount,
      rewardTokenSymbolRaw,
      rewardTokenContract: String(selectedPromotion.rewardTokenContract || ""),
      startTimeSec: String(selectedPromotion.startTimeSec || "0"),
      endTimeSec: String(selectedPromotion.endTimeSec || "0"),
      maxClaimCount: String(selectedPromotion.maxClaimCount || "1"),
      description: String(selectedPromotion.description || ""),
      imageUrl: String(selectedPromotion.imageUrl || ""),
      active: !!selectedPromotion.active,
    });
  }, [selectedPromotion]);

  // Defaults for creating a new promotion
  useEffect(() => {
    if (!selectedProject) return;
    if (selectedPromotion) return;
    setPromotionForm({
      promoteId: "",
      promotionName: "",
      projectId: String(selectedProject.projectId ?? ""),
      collectionName: "",
      rewardAmount: "",
      rewardTokenSymbolRaw: "",
      rewardTokenContract: "",
      startTimeSec: "0",
      endTimeSec: "0",
      maxClaimCount: "1",
      description: "",
      imageUrl: "",
      active: true,
    });
  }, [selectedProjectId, selectedPromotionId, selectedPromotion, selectedProject]);

  // Prefill survey form
  useEffect(() => {
    if (!selectedSurvey) return;
    setSurveyForm({
      surveyKey: String(selectedSurvey.surveyKey || ""),
      projectId: String(selectedSurvey.projectId || ""),
      surveyText: String(selectedSurvey.surveyText || ""),
      option1: String(selectedSurvey.option1 || ""),
      option2: String(selectedSurvey.option2 || ""),
      option3: String(selectedSurvey.option3 || ""),
      option4: String(selectedSurvey.option4 || ""),
      option5: String(selectedSurvey.option5 || ""),
      option6: String(selectedSurvey.option6 || ""),
      numOptions: Number(selectedSurvey.numOptions || 2),
      allowMultiple: !!selectedSurvey.allowMultiple,
      surveyType: Number(selectedSurvey.surveyType || 1),
      requiredAnswers: String(selectedSurvey.requiredAnswers || ""),
      active: !!selectedSurvey.active,
      closed: !!selectedSurvey.closed,
      startTimeSec: String(selectedSurvey.startTimeSec || "0"),
      endTimeSec: String(selectedSurvey.endTimeSec || "0"),
      surveyDescription: String(selectedSurvey.surveyDescription || ""),
      surveyImageUrl: String(selectedSurvey.surveyImageUrl || ""),
    });
  }, [selectedSurvey]);

  // Defaults for creating a new survey
  useEffect(() => {
    if (!selectedProject) return;
    if (selectedSurvey) return;
    setSurveyForm({
      surveyKey: "",
      projectId: String(selectedProject.projectId ?? ""),
      surveyText: "",
      option1: "",
      option2: "",
      option3: "",
      option4: "",
      option5: "",
      option6: "",
      numOptions: 2,
      allowMultiple: false,
      surveyType: 1,
      requiredAnswers: "",
      active: true,
      closed: false,
      startTimeSec: "0",
      endTimeSec: "0",
      surveyDescription: "",
      surveyImageUrl: "",
    });
  }, [selectedProjectId, selectedSurveyKey, selectedSurvey, selectedProject]);

  // Req rules for selected pool
  const reqForPool = useMemo(() => {
    const pk = String(selectedPool?.poolKey || "");
    if (!pk) return [];
    const list = (reqNfts || []).filter((r) => String(r.poolKey) === pk);
    // Sort dependent-NFT rules alphabetically by key for the dropdown (natural numeric ordering).
    list.sort((a, b) => alphaCompare(a?.reqKey ?? "", b?.reqKey ?? ""));
    return list;
  }, [reqNfts, selectedPool]);

  // Reset "(new rule)" mode when switching pools.
  useEffect(() => {
    preferNewReqRef.current = false;
  }, [selectedPoolHash]);

  useEffect(() => {
    if (!selectedPool) return;
    // Keep selection if it still exists; otherwise fall back to the first rule.
    if (reqForPool.length === 0) {
      if (selectedReqId) setSelectedReqId("");
      return;
    }

    // If we just created/updated a rule, jump to that exact (poolKey, reqKey) deterministically.
    const pending = pendingSelectReqRef.current || { poolKey: "", reqKey: "" };
    if (pending?.poolKey && pending?.reqKey && String(pending.poolKey) === String(selectedPool.poolKey)) {
      const hit = (reqForPool || []).find((r) => String(r.reqKey) === String(pending.reqKey));
      if (hit && hit.reqId !== undefined && hit.reqId !== null) {
        pendingSelectReqRef.current = { poolKey: "", reqKey: "" };
        preferNewReqRef.current = false;
        setSelectedReqId(String(hit.reqId));
        return;
      }
    }

    const cur = String(selectedReqId || "");
    if (cur === "" && preferNewReqRef.current) return;
    const exists = (reqForPool || []).some((r) => String(r.reqId) === cur);
    if (exists) return;
    setSelectedReqId(String(reqForPool[0].reqId));
  }, [selectedPoolHash, reqForPool, selectedReqId]);

  // Reset claim calculator when project/pool changes
  useEffect(() => {
    setClaimCalc(null);
    setClaimCalcError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId, selectedPoolHash]);

  // Prefill req form
  useEffect(() => {
    if (!selectedPool) return;
    if (!selectedReqRow) {
      setReqForm({
        poolKey: String(selectedPool.poolKey || ""),
        reqKey: "default",
        collectionName: String(selectedPool.collectionName || ""),
        requiredImmutableText: "[]",
        nftcount: "1",
        active: true,
      });
      return;
    }
    setReqForm({
      poolKey: String(selectedReqRow.poolKey || selectedPool.poolKey || ""),
      reqKey: String(selectedReqRow.reqKey || "default"),
      collectionName: String(selectedReqRow.collectionName || ""),
      requiredImmutableText: JSON.stringify(selectedReqRow.requiredImmutable || [], null, 2),
      nftcount: String(selectedReqRow.nftcount ?? "1"),
      active: !!selectedReqRow.active,
    });
  }, [selectedPool, selectedReqRow]);

  // Load claims + wallets when pool changes (Full Console and whenever pool / actor filter changes)
  useEffect(() => {
    if (!selectedPool) {
      setClaims([]);
      setWallets([]);
      setExcludedAssets([]);
      setStakeSnapRows([]);
      return;
    }
    const scope = derivePoolScope(selectedPool);
    if (!scope) {
      setClaims([]);
      setWallets([]);
      setExcludedAssets([]);
      setStakeSnapRows([]);
      return;
    }
    void refetchClaimsWalletsForSelectedPool();
    void refetchStakeSnapForSelectedPool();
    void refetchStakeEligForSelectedPool();
  }, [selectedPoolHash, refetchClaimsWalletsForSelectedPool, refetchStakeSnapForSelectedPool, refetchStakeEligForSelectedPool]);

  // Pool Assistent step 7: refresh claims/wallets when entering Verify & test (or switching to Pool Assistent on step 7)
  useEffect(() => {
    const prevStep = prevWizardStepRef.current;
    const prevMode = prevAdminUiModeRef.current;
    prevWizardStepRef.current = wizardStep;
    prevAdminUiModeRef.current = adminUiMode;
    if (!loggedIn || !termsAccepted) return;
    if (adminUiMode !== "wizard" || wizardStep !== 7) return;
    if (!selectedPool) return;
    const enteredStep7 = prevStep !== 7 && wizardStep === 7;
    const switchedToWizardOnStep7 = prevMode !== "wizard" && adminUiMode === "wizard" && wizardStep === 7;
    if (!enteredStep7 && !switchedToWizardOnStep7) return;
    void refetchClaimsWalletsForSelectedPool();
    void refetchStakeSnapForSelectedPool();
    void refetchStakeEligForSelectedPool();
  }, [loggedIn, termsAccepted, adminUiMode, wizardStep, selectedPool, refetchClaimsWalletsForSelectedPool, refetchStakeSnapForSelectedPool, refetchStakeEligForSelectedPool]);

  async function saveProject() {
    if (!selectedProject) return;
    const pid = Number(projectForm.projectId);
    if (!pid) {
      setError("Missing projectId");
      return;
    }

    let newAllowed = [];
    try {
      newAllowed = asArrayOfNames(projectForm.allowedAccountsText);
    } catch {
      newAllowed = [];
    }
    const oldAllowed = (selectedProject.allowedAccounts || []).map((x) => String(x));
    const toAdd = newAllowed.filter((x) => !oldAllowed.includes(x));
    const toDel = oldAllowed.filter((x) => !newAllowed.includes(x));

    const actions = [];
    actions.push({
      account: TX_CONTRACT,
      name: "updproject",
      authorization: [auth],
      data: {
        projectId: pid,
        projectName: String(projectForm.projectName || ""),
        homepageUrl: String(projectForm.homepageUrl || ""),
        projectImageUrl: String(projectForm.projectImageUrl || ""),
        projectDescription: String(projectForm.projectDescription || ""),
      },
    });
    for (const a of toAdd) {
      actions.push({ account: TX_CONTRACT, name: "addprojacc", authorization: [auth], data: { projectId: pid, account: a } });
    }
    for (const a of toDel) {
      actions.push({ account: TX_CONTRACT, name: "delprojacc", authorization: [auth], data: { projectId: pid, account: a } });
    }

    const ok = await transact(actions);
    if (ok) await loadCoreState({ silent: true });
  }

  function isEosioName(v) {
    const s = String(v || "").trim();
    return /^[a-z1-5.]{1,12}$/.test(s);
  }

  function normalizeAtomicCollectionName(input) {
    const raw = String(input || "").trim();
    if (!raw) return { atomicName: "", normalizedFrom: "", warning: "" };
    if (isEosioName(raw)) return { atomicName: raw, normalizedFrom: "", warning: "" };
    const lower = raw.toLowerCase();
    if (isEosioName(lower)) {
      return {
        atomicName: lower,
        normalizedFrom: raw,
        warning: `Collection "${raw}" was normalized to "${lower}" for AtomicAssets lookup.`,
      };
    }
    return {
      atomicName: "",
      normalizedFrom: "",
      warning: `Collection "${raw}" does not match AtomicAssets naming rules (lowercase a-z, 1-5, dot, max 12 chars). AtomicAssets verification and claim calculator may fail.`,
    };
  }

function validatePoolForm() {
    const errors = [];
    const warnings = [];
    const isNew = !selectedPool;
    const poolKey = String(poolForm.poolKey || "").trim();
    const collectionName = String(poolForm.collectionName || "").trim();
    const rewardAmount = String(poolForm.rewardAmount || "").trim();
    const rewardTokenSymbolRaw = String(poolForm.rewardTokenSymbolRaw || "").trim();
    const rewardTokenContract = String(poolForm.rewardTokenContract || "").trim();
    const symbolParts = parseSymbolParts(rewardTokenSymbolRaw);
    const startTimeSecRaw = String(poolForm.startTimeSec || "0").trim();
    const endTimeSecRaw = String(poolForm.endTimeSec || "0").trim();
    const cooldownRaw = String(poolForm.cooldownSec || "0").trim();

    if (!poolKey) errors.push("Pool Key is required.");
    else {
      // Check poolKey against allowed pattern: a-zA-Z0-9_, space, hyphen, apostrophe (' or typographic ' '), parentheses ( ) or （）, '$', '⚛'
      // Ignore invisible emoji variation selectors (e.g. U+FE0F) during validation.
      const sanitized = poolKey.replace(/\uFE0F/g, "");
      const poolKeyPattern = /^[a-zA-Z0-9_\s\-'\u2018\u2019()（）$⚛]+$/u;
      if (!poolKeyPattern.test(sanitized)) {
        // Find invalid characters (iterate by Unicode code points so surrogate pairs stay one char)
        const invalidChars = new Set();
        const allowedCharPattern = /^[a-zA-Z0-9_\s\-'\u2018\u2019()（）$⚛]$/u;
        for (const char of [...sanitized]) {
          if (!allowedCharPattern.test(char)) {
            invalidChars.add(char);
          }
        }
        const invalidCharsList = Array.from(invalidChars).map((c) => {
          if (c === ' ') return "' ' (space)";
          if (c === '\t') return "'\\t' (tab)";
          if (c === '\n') return "'\\n' (newline)";
          if (c === '\r') return "'\\r' (carriage return)";
          if (!c || c.length === 0) return "(empty)";
          const code = c.codePointAt(0);
          const codeHex = (code != null ? code : c.charCodeAt(0)).toString(16).toUpperCase().padStart(4, '0');
          if (code != null && code < 32) return `U+${codeHex}`;
          if (/[\u200B-\u200D\u2060\uFEFF]/.test(c)) return `U+${codeHex} (invisible)`;
          if (c.length === 2 && /[\uD800-\uDBFF][\uDC00-\uDFFF]/.test(c)) return `U+${(c.codePointAt(0) ?? 0).toString(16).toUpperCase()} (Unicode)`;
          return `'${c}'`;
        }).join(', ');
        errors.push(`Pool Key contains invalid characters: ${invalidCharsList}. Only letters (a-z, A-Z), numbers (0-9), underscores (_), spaces, hyphens (-), apostrophe (' or '), parentheses ( ) or （）, '$', and '⚛' are allowed.`);
      }
    }
    if (isNew && poolKey) {
      const dup = (poolsForProject || []).some((p) => String(p.poolKey) === poolKey);
      if (dup) errors.push("A pool with this Pool Key already exists in the selected project.");
    }
    if (!collectionName) errors.push("Collection is required.");
    else {
      const norm = normalizeAtomicCollectionName(collectionName);
      if (norm.warning) warnings.push(norm.warning);
    }

    if (!rewardTokenSymbolRaw) errors.push("Token symbol is required.");
    else if (!symbolParts.symbol) errors.push('Token symbol must be in the form "precision,SYMBOL" (e.g. 4,XPR).');

    if (!rewardTokenContract) errors.push("Token contract is required.");
    else if (!isEosioName(rewardTokenContract)) errors.push("Token contract must be a valid account-style name.");

    const poolType = Number(poolForm.poolType || 1);
    const cooldownSecVal = parseInt(cooldownRaw, 10);
    if (poolType === 5 && (isNaN(cooldownSecVal) || cooldownSecVal <= 0)) {
      errors.push("For Repeatable pools (type 5), Cooldown must be greater than 0.");
    }

    // Ensure token exists in Allowed Tokens and the precision matches exactly.
    if (rewardTokenSymbolRaw && rewardTokenContract) {
      const tokRows = Array.isArray(allowTokens) ? allowTokens : [];
      const hit = tokRows.find((t) => {
        const tc = String(t?.tokenContract ?? t?.token_contract ?? "").trim();
        const ts = String(t?.tokenSymbol ?? t?.token_symbol ?? "").trim();
        return tc === rewardTokenContract && ts === rewardTokenSymbolRaw;
      });
      if (!hit) errors.push("Selected token is not present in Allowed Tokens.");
      else if (!hit.active) warnings.push("Selected token is currently inactive in Allowed Tokens.");
    }

    if (!rewardAmount) errors.push("Reward amount is required.");
    else {
      if (!/^-?\d+(\.\d+)?$/.test(rewardAmount)) {
        errors.push("Reward amount must be a number.");
      } else {
                const hasDot = rewardAmount.includes(".");
        const dec = hasDot ? String(rewardAmount.split(".")[1] || "").length : 0;
        const reqDec = Number(symbolParts.precision || 0);

        // Require EXACT decimal places to match the selected token precision.
        // Example: precision 4 => "1.0000" (not "1" or "1.0").
        if (reqDec === 0) {
          if (hasDot) errors.push("Reward amount must not include decimals for this token (precision 0).");
        } else {
          if (!hasDot) errors.push(`Reward amount must include exactly ${reqDec} decimal places for ${symbolParts.symbol}.`);
          else if (dec !== reqDec) errors.push(`Reward amount must have exactly ${reqDec} decimal places for ${symbolParts.symbol}.`);
        }
}
      const rewardAsset = rewardTokenSymbolRaw ? normalizeAssetInputForSymbol(rewardAmount, rewardTokenSymbolRaw) : "";
      const parsedReward = parseAssetString(rewardAsset);
      if (!parsedReward) errors.push("Reward must be a valid asset (amount + symbol).");
      else if (parsedReward.amountInt <= 0n) errors.push("Reward amount must be greater than 0.");
    }

const startTimeSec = Number(startTimeSecRaw || 0);
    const endTimeSec = Number(endTimeSecRaw || 0);
    const cooldownSec = Number(cooldownRaw || 0);
    if (!Number.isFinite(startTimeSec) || !Number.isInteger(startTimeSec) || startTimeSec < 0) errors.push("Start must be an integer number of seconds (0 or greater).");
    if (!Number.isFinite(endTimeSec) || !Number.isInteger(endTimeSec) || endTimeSec < 0) errors.push("End must be an integer number of seconds (0 or greater).");
    if (startTimeSec > 0 && endTimeSec > 0 && endTimeSec < startTimeSec) errors.push("End must be greater than or equal to Start (or set End to 0).");
    if (!Number.isFinite(cooldownSec) || !Number.isInteger(cooldownSec) || cooldownSec < 0) errors.push("Cooldown must be an integer number of seconds (0 or greater).");

    const rimRaw = String(poolForm.requiredImmutableText || "").trim();
    if (rimRaw) {
      try {
        const parsed = JSON.parse(rimRaw);
        if (!Array.isArray(parsed)) errors.push("Required Immutable must be a JSON array (e.g. []).");
      } catch {
        if (rimRaw !== "[]") warnings.push("Required Immutable is not valid JSON; it will be parsed as a space/comma-separated list.");
      }
    }


    // Required Immutable pairs: if a row exists, both attribute name AND value must be filled.
    // New pools may have zero rows (valid).
    const rimPairs = Array.isArray(poolReqPairs)
      ? poolReqPairs
      : parseRequiredImmutablePairs(String(poolForm.requiredImmutableText || "").trim(), 5);

    for (let i = 0; i < rimPairs.length; i++) {
      const k = String(rimPairs[i]?.key ?? "").trim();
      const v = String(rimPairs[i]?.value ?? "").trim();
      if (!k && !v) {
        errors.push(`Required Immutable row ${i + 1}: both fields are empty. Either fill them or delete the row.`);
      } else if (!k) {
        errors.push(`Required Immutable row ${i + 1}: Attribute name is required.`);
      } else if (!v) {
        errors.push(`Required Immutable row ${i + 1}: Value is required.`);
      }
    }

    if (poolSupportsRequiredMutable(poolForm.poolType, poolForm.collectionName)) {
      const mutPairs = Array.isArray(poolMutPairs)
        ? poolMutPairs
        : parseRequiredMutablePairs(String(poolForm.requiredMutableText || "").trim(), 5);
      const mutVal = validateRequiredMutablePairs(mutPairs);
      errors.push(...(mutVal.errors || []));
      warnings.push(...(mutVal.warnings || []));
      const mutEntries = requiredMutableEntriesFromPairs(mutPairs);
      if (mutEntries.length > 0 && !actionPermissions.setpoolmut) {
        errors.push("You need setpoolmut permission to save Required Mutable rules.");
      }
    }

    if (poolSupportsClaimScope(poolForm.poolType)) {
      const cs = Number(poolForm.claimScope ?? CLAIM_SCOPE_NFT);
      if (cs !== CLAIM_SCOPE_NFT && cs !== CLAIM_SCOPE_ACCOUNT) {
        errors.push("Claim scope must be per NFT (0) or per account (1).");
      }
      // New pool + Per NFT (default): no setpoolscope tx — do not block save/check.
      const isNewPoolForm = !selectedPool;
      const defaultNftScope = cs === CLAIM_SCOPE_NFT;
      const needsScopeTx =
        !(isNewPoolForm && defaultNftScope) &&
        poolClaimScopeNeedsSetpoolscope(
          poolForm.claimScope,
          selectedPool,
          claimScopeForSelectedPool,
        );
      if (needsScopeTx && !actionPermissions.setpoolscope) {
        errors.push("You need setpoolscope permission to save claim scope (Per account or changing scope).");
      }
      if (cs === CLAIM_SCOPE_ACCOUNT && Number(poolForm.poolType) === 1) {
        warnings.push(
          "Account scope: one claim per wallet per pool (any qualifying NFT). Wallet cooldown (nwallets) is not used for account scope.",
        );
      }
      if (cs === CLAIM_SCOPE_ACCOUNT && Number(poolForm.poolType) === 5) {
        warnings.push(
          "Account scope: one claim per wallet per cooldown period; user picks any qualifying NFT as gate.",
        );
      }
    }

    const img = String(poolForm.poolImageUrl || "").trim();
    if (img && !/^https?:\/\//i.test(img)) warnings.push("Image URL should start with http:// or https:// (or leave it empty).");

    return { ok: errors.length === 0, errors, warnings };
  }

  function validateReqForm() {
    const errors = [];
    const warnings = [];
    const isNew = !String(selectedReqId || "");

    const reqKey = String(reqForm.reqKey || "").trim();
    const collectionName = String(reqForm.collectionName || "").trim();
    const nftcountRaw = String(reqForm.nftcount || "1").trim();

    if (!reqKey) errors.push("Req Key is required.");
    if (isNew && reqKey) {
      const dup = (reqForPool || []).some((r) => String(r.reqKey) === reqKey);
      if (dup) errors.push("A rule with this Req Key already exists for the selected pool.");
    }

    if (!collectionName) errors.push("Collection is required.");
    else {
      const norm = normalizeAtomicCollectionName(collectionName);
      if (norm.warning) warnings.push(norm.warning);
    }

    const nftcount = Number(nftcountRaw || 0);
    if (!Number.isFinite(nftcount) || !Number.isInteger(nftcount) || nftcount < 1) errors.push("NFT count must be an integer number (1 or greater).");

    const rimRaw = String(reqForm.requiredImmutableText || "").trim();
    if (rimRaw) {
      try {
        const parsed = JSON.parse(rimRaw);
        if (!Array.isArray(parsed)) errors.push("Required Immutable must be a JSON array (e.g. []).");
      } catch {
        if (rimRaw !== "[]") warnings.push("Required Immutable is not valid JSON; it will be parsed as a space/comma-separated list.");
      }
    }

    return { ok: errors.length === 0, errors, warnings };
  }

  async function checkPool() {
    const base = validatePoolForm();
    const errors = [...(base.errors || [])];
    const warnings = [...(base.warnings || [])];

    const c = String(poolForm.collectionName || "").trim();
    if (c) {
      // Skip AtomicAssets verification for "account" (per-wallet claims)
      if (c.toLowerCase() !== "account") {
        const norm = normalizeAtomicCollectionName(c);
        if (norm.warning) warnings.push(norm.warning);
        if (norm.atomicName) {
          const ex = await atomicCollectionExists(norm.atomicName);
          if (ex.ok && !ex.exists) errors.push(`Collection "${c}" was not found (AtomicAssets).`);
          else if (!ex.ok) warnings.push(`Could not verify collection "${c}" (AtomicAssets API unreachable).`);
        }
      }
    }

    const r = { ok: errors.length === 0, errors, warnings };
    setPoolCheck(r);
    if (!r.ok) setError(r.errors[0] || "Invalid pool data");
    return r.ok;
  }

  async function checkReq() {
    const base = validateReqForm();
    const errors = [...(base.errors || [])];
    const warnings = [...(base.warnings || [])];

    const c = String(reqForm.collectionName || "").trim();
    if (c) {
      // Skip AtomicAssets verification for "account" (per-wallet claims)
      if (c.toLowerCase() !== "account") {
        const norm = normalizeAtomicCollectionName(c);
        if (norm.warning) warnings.push(norm.warning);
        if (norm.atomicName) {
          const ex = await atomicCollectionExists(norm.atomicName);
          if (ex.ok && !ex.exists) errors.push(`Collection "${c}" was not found (AtomicAssets).`);
          else if (!ex.ok) warnings.push(`Could not verify collection "${c}" (AtomicAssets API unreachable).`);
        }
      }
    }

    const r = { ok: errors.length === 0, errors, warnings };
    setReqCheck(r);
    if (!r.ok) setError(r.errors[0] || "Invalid dependent NFT rule data");
    return r.ok;
  }


  // Internal function to actually save the pool. Returns true if tx sent successfully.
  async function doSavePool() {
    if (!selectedProject) {
      setError("Please select a project first.");
      return false;
    }
    const requiredImmutable = asArrayOfNames(poolForm.requiredImmutableText);
    
    // Validate cooldownSec - ensure it's a valid number
    const cooldownSecRaw = String(poolForm.cooldownSec || "0").trim();
    const cooldownSecNum = Number(cooldownSecRaw);
    if (!Number.isFinite(cooldownSecNum) || !Number.isInteger(cooldownSecNum) || cooldownSecNum < 0) {
      setError(`Invalid cooldown value: ${cooldownSecRaw}. Must be a non-negative integer.`);
      return false;
    }
    const poolTypeNum = Number(poolForm.poolType || 1);
    if (poolTypeNum === 5 && cooldownSecNum <= 0) {
      setError("For Repeatable pools (type 5), Cooldown must be greater than 0.");
      return false;
    }

    const data = {
      poolKey: String(poolForm.poolKey || "").trim(),
      projectId: Number(selectedProject.projectId || poolForm.projectId || 0),
      collectionName: String(poolForm.collectionName || "").trim(),
      reward: normalizeAssetInputForSymbol(String(poolForm.rewardAmount || "").trim(), String(poolForm.rewardTokenSymbolRaw || "").trim()),
      rewardTokenContract: String(poolForm.rewardTokenContract || "").trim(),
      rewardWallet: CONTRACT,
      active: !!poolForm.active,
      startTimeSec: Number(poolForm.startTimeSec || 0),
      endTimeSec: Number(poolForm.endTimeSec || 0),
      cooldownSec: cooldownSecNum,
      poolType: Number(poolForm.poolType || 1),
      requiredImmutable,
      poolDescription: String(poolForm.poolDescription || ""),
      poolImageUrl: String(poolForm.poolImageUrl || ""),
      creatorFeeEnabled: !!poolForm.creatorFeeEnabled,
    };
    if (!data.poolKey) {
      setError("Missing poolKey");
      return false;
    }
    if (!data.collectionName) {
      setError("Missing collectionName");
      return false;
    }
    if (!data.reward) {
      setError("Missing reward");
      return false;
    }
    if (!data.rewardTokenContract) {
      setError("Missing rewardTokenContract");
      return false;
    }
    if (data.creatorFeeEnabled) {
      const feePercent = Number(creatorFeeForm.feePercent);
      const receiver = String(creatorFeeForm.receiver || "").trim();
      if (!Number.isInteger(feePercent) || feePercent < 0 || feePercent > 10000) {
        setError("Creator Fee % must be an integer between 0 and 10000 (basis points).");
        return false;
      }
      if (!receiver) {
        setError("Creator Fee Receiver is required when Creator Fee Enabled is true.");
        return false;
      }
    }
    // Ensure the UI jumps to the exact pool that was just written, even if option labels are identical.
    pendingSelectPoolKeyRef.current = String(data.poolKey);
    preferNewPoolRef.current = false;
    const actions = [{ account: TX_CONTRACT, name: "setpool", authorization: [auth], data }];
    const supportsMut = poolSupportsRequiredMutable(data.poolType, data.collectionName);
    if (supportsMut) {
      const mutPairs = Array.isArray(poolMutPairs)
        ? poolMutPairs
        : parseRequiredMutablePairs(String(poolForm.requiredMutableText || "").trim(), 5);
      const requiredMutable = requiredMutableEntriesFromPairs(mutPairs);
      const onChainMutable = selectedPool ? requiredMutableForSelectedPool : [];
      const mutableChanged = !requiredMutableArraysEqual(requiredMutable, onChainMutable);
      if (mutableChanged) {
        if (!actionPermissions.setpoolmut) {
          setError("Missing setpoolmut permission for Required Mutable rules.");
          return false;
        }
        actions.push({
          account: TX_CONTRACT,
          name: "setpoolmut",
          authorization: [auth],
          data: { poolKey: data.poolKey, requiredMutable },
        });
      }
    }
    if (poolSupportsClaimScope(data.poolType)) {
      const cs = Number(poolForm.claimScope ?? CLAIM_SCOPE_NFT);
      const isNewPoolForm = !selectedPool;
      const needsScopeTx =
        !(isNewPoolForm && cs === CLAIM_SCOPE_NFT) &&
        poolClaimScopeNeedsSetpoolscope(
          poolForm.claimScope,
          selectedPool,
          claimScopeForSelectedPool,
        );
      if (needsScopeTx) {
        if (!actionPermissions.setpoolscope) {
          setError("Missing setpoolscope permission for claim scope (Per account or changing scope).");
          return false;
        }
        const claimScope = Number(poolForm.claimScope ?? CLAIM_SCOPE_NFT);
        const scopeVal =
          claimScope === CLAIM_SCOPE_ACCOUNT ? CLAIM_SCOPE_ACCOUNT : CLAIM_SCOPE_NFT;
        actions.push({
          account: TX_CONTRACT,
          name: "setpoolscope",
          authorization: [auth],
          data: { poolKey: data.poolKey, claimScope: scopeVal },
        });
      }
    }
    if (data.creatorFeeEnabled) {
      const feePercent = Number(creatorFeeForm.feePercent);
      const receiver = String(creatorFeeForm.receiver || "").trim();
      actions.push({
        account: TX_CONTRACT,
        name: "setcrefee",
        authorization: [auth],
        data: {
          poolKey: data.poolKey,
          projectId: data.projectId,
          feePercent,
          receiver,
        },
      });
    }
    const ok = await transact(actions);
    if (ok) {
      // After the transaction, reload state and reliably jump to the newly created/updated pool.
      // Some RPC nodes may take a short moment to reflect new rows, so we retry a couple of times.
      for (let attempt = 0; attempt < 3; attempt++) {
        const res = await loadCoreState({ silent: true });
        const freshPools = (res && res.pools) || [];
        const found = freshPools.find((p) => String(p.poolKey) === String(data.poolKey));
        if (found && found.poolHash) {
          pendingSelectPoolKeyRef.current = "";
          setSelectedPoolHash(String(found.poolHash));
          // Reset claim calculator so it uses fresh data after pool update
          setClaimCalc(null);
          setClaimCalcError("");
          // Claims/Wallets will be reloaded automatically by useEffect when selectedPoolHash changes
          break;
        }
        // small delay before retry
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 700));
      }
      return true;
    }
    // Transaction failed - error should already be set by transact()
    if (!error) {
      setError("Failed to save pool. Please check the transaction details and try again.");
    }
    return false;
  }

  async function savePool() {
    if (!selectedProject) {
      setError("Please select a project first.");
      return;
    }
    if (!(await checkPool())) {
      // Error already set by checkPool()
      return;
    }
    
    // If pool is inactive, save directly without showing claim preview modal
    if (!poolForm.active) {
      await doSavePool();
      return;
    }
    
    // Calculate claim preview and show modal
    setClaimPreviewMode("save");
    await calculateClaimPreviewWithFormData(poolForm, null);
    setPendingSaveAction({ type: 'pool', action: doSavePool });
    setClaimPreviewModalOpen(true);
  }

  async function setCreFeeAction() {
    const poolKey = String(creFeeFormAction.poolKey || "").trim();
    const projectId = Number(creFeeFormAction.projectId || 0);
    const feePercent = Number(creFeeFormAction.feePercent);
    const receiver = String(creFeeFormAction.receiver || "").trim();
    if (!poolKey) {
      setError("Pool Key is required for Set Creator Fee.");
      return;
    }
    if (!projectId) {
      setError("Project ID is required for Set Creator Fee.");
      return;
    }
    if (!Number.isInteger(feePercent) || feePercent < 0 || feePercent > 10000) {
      setError("Creator Fee % must be an integer between 0 and 10000 (basis points).");
      return;
    }
    if (!receiver) {
      setError("Receiver account is required for Set Creator Fee.");
      return;
    }
    const ok = await transact([
      {
        account: TX_CONTRACT,
        name: "setcrefee",
        authorization: [auth],
        data: { poolKey, projectId, feePercent, receiver },
      },
    ]);
    if (ok) {
      await loadCoreState({ silent: true });
      setNotice("Creator fee set successfully.");
    }
  }

  async function doResetPool() {
    setResetPoolConfirmOpen(false);
    const poolKey = String(resetPoolForm.poolKey || "").trim();
    if (!poolKey) {
      setError("Select a pool for Pool Reset.");
      return;
    }
    const poolRow = (poolsForProject || []).find((p) => String(p?.poolKey || "").trim() === poolKey);
    if (!poolRow) {
      setError("Selected pool was not found in the current project.");
      return;
    }
    const safePoolKey = toAlnumPoolKey(poolKey);
    const dateTag = new Date().toISOString().slice(0, 10);
    const claimsFilename = `${safePoolKey}_${dateTag}_nclaims.csv`;
    const walletsFilename = `${safePoolKey}_${dateTag}_nwallets.csv`;

    // Reset is only allowed when both exports (data fetch + download trigger) succeed.
    try {
      const scope = derivePoolScope(poolRow);
      const [claimsRows, walletsRows] = await Promise.all([
        fetchAllRows(rpc, { code: CONTRACT, scope, table: "nclaims", limit: 1000 }),
        fetchAllRows(rpc, { code: CONTRACT, scope, table: "nwallets", limit: 1000 }),
      ]);
      const claimsAll = Array.isArray(claimsRows) ? claimsRows.slice() : [];
      const walletsAll = Array.isArray(walletsRows) ? walletsRows.slice() : [];
      claimsAll.sort((a, b) => Number(b?.claimedAt || 0) - Number(a?.claimedAt || 0));
      walletsAll.sort((a, b) => Number(b?.lastClaimSec || 0) - Number(a?.lastClaimSec || 0));

      // Trigger both downloads back-to-back after data is ready.
      downloadCsv(claimsFilename, claimsAll);
      downloadCsv(walletsFilename, walletsAll);
    } catch (e) {
      setError(`Pool Reset aborted: CSV export failed. ${serializeError(e).message}`);
      return;
    }

    const maxRows = Math.max(1, Math.min(10000, Number(resetPoolForm.maxRows) || 500));
    const data = { poolKey, maxRows };
    const txResult = await transact([{ account: TX_CONTRACT, name: "clrclaims", authorization: [auth], data }]);
    if (txResult) {
      let removed = null;
      try {
        const traces = txResult?.processed?.action_traces || [];
        for (const t of traces) {
          const con = t?.console || "";
          const m = String(con).match(/Deleted (\d+) claims for pool/);
          if (m) {
            removed = parseInt(m[1], 10);
            break;
          }
        }
      } catch {
        // ignore
      }
      setResetPoolResult(removed !== null ? { removed } : { message: "Transaction successful. If more claims exist, call again." });
      try {
        await loadCoreState({ silent: true });
        if (selectedProjectId) await loadExcludedAssetsForProject(selectedProjectId, { silent: true });
      } catch {
        // ignore
      }
    }
  }

  async function doDeletePool() {
    if (!selectedPool || !auth) return;
    setDeletePoolConfirmOpen(false);
    const poolKey = String(selectedPool.poolKey || "").trim();
    const poolId = String(selectedPool.poolHash ?? "").trim();
    if (!poolId) {
      setError("Pool has no poolId/poolHash. Cannot delete.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      // Export claims and wallets to CSV before deleting
      await exportClaimsCsv();
      await exportWalletsCsv();
      const data = { poolId };
      const ok = await transact([{ account: TX_CONTRACT, name: "delpool", authorization: [auth], data }]);
      if (ok) {
        setNotice(`Pool "${poolKey}" has been deleted.`);
        setSelectedPoolHash("");
        await loadCoreState({ silent: true });
      }
    } catch (e) {
      setError(serializeError(e).message);
    } finally {
      setBusy(false);
    }
  }

  // Internal function to actually save the dependent NFT rule
  async function doSaveDependentNfts() {
    if (!selectedPool) return false;
    const data = {
      poolKey: String(reqForm.poolKey || selectedPool.poolKey || "").trim(),
      reqKey: String(reqForm.reqKey || "default").trim(),
      collectionName: String(reqForm.collectionName || "").trim(),
      requiredImmutable: asArrayOfNames(reqForm.requiredImmutableText),
      nftcount: Number(reqForm.nftcount || 1),
      active: !!reqForm.active,
    };
    if (!data.poolKey) {
      setError("Missing poolKey");
      return false;
    }
    if (!data.reqKey) {
      setError("Missing reqKey");
      return false;
    }
    // Ensure the UI jumps to the exact rule that was just written.
    pendingSelectReqRef.current = { poolKey: String(data.poolKey), reqKey: String(data.reqKey) };
    const ok = await transact([{ account: TX_CONTRACT, name: "setreqnftx", authorization: [auth], data }]);
    if (ok) {
      // Reload and reliably jump to the newly created/updated rule. Retry a couple of times for eventual consistency.
      for (let attempt = 0; attempt < 3; attempt++) {
        const res = await loadCoreState({ silent: true });
        const freshReqs = (res && res.reqNfts) || [];
        const found = freshReqs.find((r) => String(r.poolKey) === String(data.poolKey) && String(r.reqKey) === String(data.reqKey));
        if (found && found.reqId !== undefined && found.reqId !== null) {
          pendingSelectReqRef.current = { poolKey: "", reqKey: "" };
          preferNewReqRef.current = false;
          setSelectedReqId(String(found.reqId));
          break;
        }
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 700));
      }
      return true;
    }
    return false;
  }

  function fillStakeSnapFormFromRow(row) {
    const minQ =
      typeof row?.minQuantity === "string"
        ? row.minQuantity
        : row?.minQuantity != null
          ? String(row.minQuantity)
          : "";
    const parsed = parseAssetString(minQ);
    const minAmount = parsed
      ? String((formatAssetInt(parsed.amountInt, parsed.precision, parsed.symbol).split(" ")[0] || "").trim())
      : "";
    const minTokenSymbolRaw = parsed ? `${parsed.precision},${parsed.symbol}` : "";
    const pm = row?.pointMultiplier;
    const pointMultiplier =
      pm !== undefined && pm !== null && String(pm).trim() !== ""
        ? String(pm)
        : "1";
    setStakeSnapForm({
      pos: String(row?.pos ?? "1"),
      stakeToken: String(row?.stakeToken ?? "").trim(),
      tokenContract: String(row?.tokenContract ?? "").trim(),
      minAmount,
      minTokenSymbolRaw,
      pointMultiplier,
      stakingPlatform: String(row?.stakingPlatform ?? "").trim(),
      stakeType: String(row?.stakeType ?? "").trim(),
      stakeName: String(row?.stakeName ?? "").trim(),
    });
  }

  async function saveStakeSnapRow() {
    if (!session || !auth) {
      setError("Please connect your WebAuth wallet first.");
      return;
    }
    if (!selectedPool) return;
    if (!actionPermissions.setstaksnap) {
      setError("No nauths permission for setstaksnap.");
      return;
    }
    const poolKey = String(selectedPool.poolKey || "").trim();
    const pos = parseInt(String(stakeSnapForm.pos || "0"), 10);
    if (!Number.isFinite(pos) || pos < 1 || pos > 65535) {
      setError("Position (pos) must be between 1 and 65535.");
      return;
    }
    const stakeToken = String(stakeSnapForm.stakeToken || "").trim();
    const tokenContract = String(stakeSnapForm.tokenContract || "").trim();
    const stakingPlatform = String(stakeSnapForm.stakingPlatform || "").trim();
    const stakeType = String(stakeSnapForm.stakeType || "").trim();
    const stakeName = String(stakeSnapForm.stakeName || "").trim();
    if (!tokenContract || !isEosioName(tokenContract)) {
      setError("Token contract must be a valid EOSIO account name.");
      return;
    }
    const minSym = String(stakeSnapForm.minTokenSymbolRaw || "").trim();
    const minAmt = String(stakeSnapForm.minAmount || "").trim();
    if (!minSym) {
      setError("Min quantity symbol is required (e.g. 4,XPR).");
      return;
    }
    const minQuantity = normalizeAssetInputForSymbol(minAmt, minSym);
    const parsedMin = parseAssetString(minQuantity);
    if (!parsedMin || parsedMin.amountInt <= 0n) {
      setError("Min quantity must be a valid asset with amount greater than 0.");
      return;
    }
    const pointMultiplier = parseInt(String(stakeSnapForm.pointMultiplier || "1"), 10);
    if (!Number.isInteger(pointMultiplier) || pointMultiplier < 1 || pointMultiplier > 4294967295) {
      setError("pointMultiplier must be an integer between 1 and 4294967295.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const ok = await transact([
        {
          account: TX_CONTRACT,
          name: "setstaksnap",
          authorization: [auth],
          data: {
            poolKey,
            pos,
            stakeToken,
            tokenContract,
            minQuantity,
            pointMultiplier,
            stakingPlatform,
            stakeType,
            stakeName,
          },
        },
      ]);
      if (ok) await refetchStakeSnapForSelectedPool();
    } finally {
      setBusy(false);
    }
  }

  async function deleteStakeEligRow(accountRaw) {
    if (!session || !auth) {
      setError("Please connect your WebAuth wallet first.");
      return;
    }
    if (!selectedPool) return;
    if (Number(selectedPool.poolType || 0) !== 6) {
      setError("delstakelig applies only to Stake Claim pools (type 6).");
      return;
    }
    if (!actionPermissions.delstakelig) {
      setError("No nauths permission for delstakelig.");
      return;
    }
    const poolKey = String(selectedPool.poolKey || "").trim();
    const account = String(accountRaw ?? "").trim();
    if (!account || !isEosioName(account)) {
      setError("Invalid account for delstakelig.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const ok = await transact([
        { account: TX_CONTRACT, name: "delstakelig", authorization: [auth], data: { poolKey, account } },
      ]);
      if (ok) await refetchStakeEligForSelectedPool();
    } finally {
      setBusy(false);
    }
  }

  async function deleteStakeSnapRow(posRaw) {
    if (!session || !auth) {
      setError("Please connect your WebAuth wallet first.");
      return;
    }
    if (!selectedPool) return;
    if (!actionPermissions.delstaksnap) {
      setError("No nauths permission for delstaksnap.");
      return;
    }
    const poolKey = String(selectedPool.poolKey || "").trim();
    const pos = parseInt(String(posRaw ?? "0"), 10);
    if (!Number.isFinite(pos) || pos < 1) {
      setError("Invalid pos.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const ok = await transact([
        { account: TX_CONTRACT, name: "delstaksnap", authorization: [auth], data: { poolKey, pos } },
      ]);
      if (ok) await refetchStakeSnapForSelectedPool();
    } finally {
      setBusy(false);
    }
  }

  async function clearStakeSnapTable() {
    if (!session || !auth) {
      setError("Please connect your WebAuth wallet first.");
      return;
    }
    if (!selectedPool) return;
    if (!actionPermissions.clrstaksnap) {
      setError("No nauths permission for clrstaksnap.");
      return;
    }
    const poolKey = String(selectedPool.poolKey || "").trim();
    const maxRows = Math.max(1, parseInt(String(stakeSnapClrMax || "500"), 10) || 500);
    setBusy(true);
    setError("");
    try {
      const ok = await transact([
        { account: TX_CONTRACT, name: "clrstaksnap", authorization: [auth], data: { poolKey, maxRows } },
      ]);
      if (ok) await refetchStakeSnapForSelectedPool();
    } finally {
      setBusy(false);
    }
  }

  async function saveDependentNfts() {
    if (!selectedPool) return;
    if (!(await checkReq())) return;
    
    // Build poolForm data from selectedPool for calculation
    const rewardStr = String(selectedPool.reward || "").trim();
    const parsedReward = parseAssetString(rewardStr);
    const rewardAmount = parsedReward
      ? String((formatAssetInt(parsedReward.amountInt, parsedReward.precision, parsedReward.symbol).split(" ")[0]) || "")
      : "";
    const rewardTokenSymbolRaw = parsedReward ? `${parsedReward.precision},${parsedReward.symbol}` : "";
    
    const poolFormData = {
      poolKey: String(selectedPool.poolKey || ""),
      collectionName: String(selectedPool.collectionName || ""),
      rewardAmount,
      rewardTokenSymbolRaw,
      requiredImmutableText: JSON.stringify(selectedPool.requiredImmutable || [], null, 2),
      requiredMutableText: JSON.stringify(requiredMutableForSelectedPool, null, 2),
      active: selectedPool.active,
      cooldownSec: String(selectedPool.cooldownSec || "0"),
      poolType: Number(selectedPool.poolType || 1),
    };
    
    // Calculate claim preview with pool data and reqForm data, then show modal
    setClaimPreviewMode("save");
    await calculateClaimPreviewWithFormData(poolFormData, reqForm);
    setPendingSaveAction({ type: 'reqnfts', action: doSaveDependentNfts });
    setClaimPreviewModalOpen(true);
  }

  // === Claim preview for Check buttons (info-only, OK modal) ===

  async function previewPoolClaimFromCheck() {
    const ok = await checkPool();
    if (!ok) return;
    setClaimPreviewMode("info");
    setPendingSaveAction(null);
    await calculateClaimPreviewWithFormData(poolForm, null);
    setClaimPreviewModalOpen(true);
  }

  async function previewReqClaimFromCheck() {
    if (!selectedPool) return;
    const ok = await checkReq();
    if (!ok) return;

    // Build pool data from current selectedPool (same logic as in saveDependentNfts)
    const rewardStr = String(selectedPool.reward || "").trim();
    const parsedReward = parseAssetString(rewardStr);
    const rewardAmount = parsedReward
      ? String((formatAssetInt(parsedReward.amountInt, parsedReward.precision, parsedReward.symbol).split(" ")[0]) || "")
      : "";
    const rewardTokenSymbolRaw = parsedReward ? `${parsedReward.precision},${parsedReward.symbol}` : "";

    const poolFormData = {
      poolKey: String(selectedPool.poolKey || ""),
      collectionName: String(selectedPool.collectionName || ""),
      rewardAmount,
      rewardTokenSymbolRaw,
      requiredImmutableText: JSON.stringify(selectedPool.requiredImmutable || [], null, 2),
      requiredMutableText: JSON.stringify(requiredMutableForSelectedPool, null, 2),
      active: selectedPool.active,
      cooldownSec: String(selectedPool.cooldownSec || "0"),
      poolType: Number(selectedPool.poolType || 1),
    };

    setClaimPreviewMode("info");
    setPendingSaveAction(null);
    await calculateClaimPreviewWithFormData(poolFormData, reqForm);
    setClaimPreviewModalOpen(true);
  }

  

  async function deleteDependentNftRule() {
    if (!selectedPool) return;
    const rid = String(selectedReqId || "").trim();
    if (!rid) {
      setError("Select a dependent NFT rule to delete.");
      return;
    }

    const row = (reqForPool || []).find((r) => String(r?.reqId ?? r?.reqid ?? r?.id ?? "") === rid);
    const poolKey = String((row && row.poolKey) || selectedPool.poolKey || "").trim();
    const reqKey = String((row && row.reqKey) || "").trim();

    if (!poolKey || !reqKey) {
      setError("Missing poolKey/reqKey for selected rule.");
      return;
    }

    const sure = window.confirm(
      `This will permanently delete the dependent NFT rule "${reqKey}" for pool "${poolKey}".

Do you want to continue?`,
    );
    if (!sure) return;

    const ok = await transact([{ account: TX_CONTRACT, name: "delreqnftx", authorization: [auth], data: { poolKey, reqKey } }]);
    if (ok) {
      // After deletion, show the (new rule) state and refresh dropdown data.
      pendingSelectReqRef.current = { poolKey: "", reqKey: "" };
      preferNewReqRef.current = true;
      setSelectedReqId("");
    setExclAssetId("");
    setExclReason("");
      await loadCoreState({ silent: true });
    }
  }

  
// Excluded claim assets (nexclassets) management
function normalizeU64String(v) {
  const s = String(v || "").trim();
  if (!s) return "";
  if (!/^[0-9]+$/.test(s)) return "";
  // prevent leading "+" or negatives; allow leading zeros but normalize
  return String(BigInt(s));
}

async function addExcludedAsset() {
  if (!selectedProjectId) {
    setError("Select a project first.");
    return;
  }
  const projectId = String(selectedProjectId);
  const assetId = normalizeU64String(exclAssetId);
  if (!assetId) {
    setError("Asset ID must be a positive integer.");
    return;
  }
  const reason = String(exclReason || "");
  await transact([
    { account: TX_CONTRACT, name: "setexcl", authorization: [auth], data: { projectId, assetId, reason } },
  ]);
  setExclAssetId(assetId);
  setExclReason(reason);
  await loadExcludedAssetsForProject(projectId, { silent: true });
  setExclAssetId("");
  setExclReason("");
}

async function deleteExcludedAsset(assetIdValue) {
  if (!selectedProjectId) {
    setError("Select a project first.");
    return;
  }
  const projectId = String(selectedProjectId);
  const assetId = normalizeU64String(assetIdValue);
  if (!assetId) {
    setError("Asset ID must be a positive integer.");
    return;
  }
  const sure = window.confirm(`Remove excluded asset ${assetId} from this project?`);
  if (!sure) return;
  await transact([
    { account: TX_CONTRACT, name: "delexcl", authorization: [auth], data: { projectId, assetId } },
  ]);
  await loadExcludedAssetsForProject(projectId, { silent: true });
}

async function clearExcludedAssets() {
  if (!selectedProjectId) {
    setError("Select a project first.");
    return;
  }
  const projectId = String(selectedProjectId);
  const sure = window.confirm("This will remove ALL excluded assets for the selected project. Do you want to continue?");
  if (!sure) return;
  await transact([
    { account: TX_CONTRACT, name: "clrexcl", authorization: [auth], data: { projectId, maxRows: 5000 } },
  ]);
  await loadExcludedAssetsForProject(projectId, { silent: true });
  setExclAssetId("");
  setExclReason("");
}

// === Promotion Management Functions ===

function validatePromotionForm() {
  const errors = [];
  const warnings = [];
  const isNew = !selectedPromotion;
  const promotionName = String(promotionForm.promotionName || "").trim();
  
  if (!promotionName) {
    errors.push("Promotion Name is required.");
  } else if (promotionName.length > 70) {
    errors.push("Promotion Name must not be longer than 70 characters.");
  }
  if (isNew && promotionName) {
    const dup = (promotionsForProject || []).some((p) => String(p.promotionName) === promotionName);
    if (dup) errors.push("A promotion with this Promotion Name already exists in the selected project.");
  }
  
  const projectId = Number(selectedProject?.projectId || promotionForm.projectId || 0);
  if (!projectId) {
    errors.push("Project ID is required.");
  }
  
  const collectionName = String(promotionForm.collectionName || "").trim();
  if (!collectionName) {
    errors.push("Collection Name is required.");
  }
  
  const rewardTokenSymbolRaw = String(promotionForm.rewardTokenSymbolRaw || "").trim();
  const symbolParts = parseSymbolParts(rewardTokenSymbolRaw);
  
  if (!rewardTokenSymbolRaw) {
    errors.push("Token symbol is required.");
  } else if (!symbolParts.symbol) {
    errors.push('Token symbol must be in the form "precision,SYMBOL" (e.g. 4,XPR).');
  }
  
  const rewardTokenContract = String(promotionForm.rewardTokenContract || "").trim();
  if (!rewardTokenContract) {
    errors.push("Reward Token Contract is required.");
  }
  
  // Validate reward amount with strict decimal places check (like Pool)
  const rewardAmount = String(promotionForm.rewardAmount || "").trim();
  if (!rewardAmount) {
    errors.push("Reward amount is required.");
  } else {
    if (!/^-?\d+(\.\d+)?$/.test(rewardAmount)) {
      errors.push("Reward amount must be a number.");
    } else {
      const hasDot = rewardAmount.includes(".");
      const dec = hasDot ? String(rewardAmount.split(".")[1] || "").length : 0;
      const reqDec = Number(symbolParts.precision || 0);

      // Require EXACT decimal places to match the selected token precision.
      if (reqDec === 0) {
        if (hasDot) errors.push("Reward amount must not include decimals for this token (precision 0).");
      } else {
        if (!hasDot) errors.push(`Reward amount must include exactly ${reqDec} decimal places for ${symbolParts.symbol}.`);
        else if (dec !== reqDec) errors.push(`Reward amount must have exactly ${reqDec} decimal places for ${symbolParts.symbol}.`);
      }
    }
    
    // Validate that the normalized asset is valid and > 0
    const reward = rewardTokenSymbolRaw ? normalizeAssetInputForSymbol(rewardAmount, rewardTokenSymbolRaw) : "";
    const parsedReward = parseAssetString(reward);
    if (!parsedReward) {
      errors.push("Reward must be a valid asset (amount + symbol).");
    } else if (parsedReward.amountInt <= 0n) {
      errors.push("Reward amount must be greater than 0.");
    }
  }
  
  const maxClaimCount = Number(promotionForm.maxClaimCount || "1");
  if (!Number.isFinite(maxClaimCount) || !Number.isInteger(maxClaimCount) || maxClaimCount < 1) {
    errors.push("Max Claim Count must be an integer >= 1.");
  }
  
  const startTimeSec = Number(promotionForm.startTimeSec || 0);
  const endTimeSec = Number(promotionForm.endTimeSec || 0);
  if (startTimeSec > 0 && endTimeSec > 0 && endTimeSec < startTimeSec) {
    errors.push("End time must be >= start time.");
  }
  
  const description = String(promotionForm.description || "").trim();
  if (description.length > 500) {
    errors.push("Description must not be longer than 500 characters.");
  }
  
  const imageUrl = String(promotionForm.imageUrl || "").trim();
  if (imageUrl.length > 256) {
    errors.push("Image URL must not be longer than 256 characters.");
  }
  
  return { errors, warnings };
}

async function checkPromotion() {
  const base = validatePromotionForm();
  const errors = [...(base.errors || [])];
  const warnings = [...(base.warnings || [])];

  const r = { ok: errors.length === 0, errors, warnings };
  setPromotionCheck(r);
  if (!r.ok) setError(r.errors[0] || "Invalid promotion data");
  return r.ok;
}

async function previewPromotionClaimFromCheck() {
  const ok = await checkPromotion();
  if (!ok) return;
  setNotice("Promotion data is valid.");
}

async function savePromotion() {
  if (!selectedProject) {
    setError("Please select a project first.");
    return;
  }
  const promotionName = String(promotionForm.promotionName || "").trim();
  if (!promotionName) {
    setError("Promotion Name is required.");
    return;
  }
  if (promotionName.length > 70) {
    setError("Promotion Name must not be longer than 70 characters.");
    return;
  }
  const projectId = Number(selectedProject.projectId || promotionForm.projectId || 0);
  if (!projectId) {
    setError("Project ID is required.");
    return;
  }
  const collectionName = String(promotionForm.collectionName || "").trim();
  if (!collectionName) {
    setError("Collection Name is required.");
    return;
  }
  
  const rewardTokenSymbolRaw = String(promotionForm.rewardTokenSymbolRaw || "").trim();
  const symbolParts = parseSymbolParts(rewardTokenSymbolRaw);
  if (!rewardTokenSymbolRaw) {
    setError("Token symbol is required.");
    return;
  } else if (!symbolParts.symbol) {
    setError('Token symbol must be in the form "precision,SYMBOL" (e.g. 4,XPR).');
    return;
  }
  
  const rewardTokenContract = String(promotionForm.rewardTokenContract || "").trim();
  if (!rewardTokenContract) {
    setError("Reward Token Contract is required.");
    return;
  }
  
  // Validate reward amount with strict decimal places check (like Pool)
  const rewardAmount = String(promotionForm.rewardAmount || "").trim();
  if (!rewardAmount) {
    setError("Reward amount is required.");
    return;
  }
  
  if (!/^-?\d+(\.\d+)?$/.test(rewardAmount)) {
    setError("Reward amount must be a number.");
    return;
  }
  
  const hasDot = rewardAmount.includes(".");
  const dec = hasDot ? String(rewardAmount.split(".")[1] || "").length : 0;
  const reqDec = Number(symbolParts.precision || 0);

  // Require EXACT decimal places to match the selected token precision.
  if (reqDec === 0) {
    if (hasDot) {
      setError("Reward amount must not include decimals for this token (precision 0).");
      return;
    }
  } else {
    if (!hasDot) {
      setError(`Reward amount must include exactly ${reqDec} decimal places for ${symbolParts.symbol}.`);
      return;
    } else if (dec !== reqDec) {
      setError(`Reward amount must have exactly ${reqDec} decimal places for ${symbolParts.symbol}.`);
      return;
    }
  }
  
  const reward = normalizeAssetInputForSymbol(rewardAmount, rewardTokenSymbolRaw);
  const parsedReward = parseAssetString(reward);
  if (!parsedReward) {
    setError("Reward must be a valid asset (amount + symbol).");
    return;
  } else if (parsedReward.amountInt <= 0n) {
    setError("Reward amount must be greater than 0.");
    return;
  }
  const maxClaimCount = Number(promotionForm.maxClaimCount || "1");
  if (!Number.isFinite(maxClaimCount) || !Number.isInteger(maxClaimCount) || maxClaimCount < 1) {
    setError("Max Claim Count must be an integer >= 1.");
    return;
  }
  const startTimeSec = Number(promotionForm.startTimeSec || 0);
  const endTimeSec = Number(promotionForm.endTimeSec || 0);
  if (startTimeSec > 0 && endTimeSec > 0 && endTimeSec < startTimeSec) {
    setError("End time must be >= start time.");
    return;
  }
  const description = String(promotionForm.description || "").trim();
  if (description.length > 500) {
    setError("Description must not be longer than 500 characters.");
    return;
  }
  const imageUrl = String(promotionForm.imageUrl || "").trim();
  if (imageUrl.length > 256) {
    setError("Image URL must not be longer than 256 characters.");
    return;
  }
  const promoteId = promotionForm.promoteId ? Number(promotionForm.promoteId) : 0;
  const data = {
    promoteId,
    promotionName,
    projectId,
    collectionName,
    reward,
    rewardTokenContract,
    startTimeSec,
    endTimeSec,
    maxClaimCount: String(maxClaimCount), // Send as string for EOSIO uint64
    description,
    imageUrl,
    active: !!promotionForm.active,
  };
  const ok = await transact([{ account: TX_CONTRACT, name: "setpromo", authorization: [auth], data }]);
  if (ok) {
    const res = await loadCoreState({ silent: true });
    // Reset claim calculator if a pool exists for this promotion and is currently selected
    const freshPromotions = (res && res.promotions) || [];
    const savedPromotion = freshPromotions.find((p) => String(p.promotionName) === promotionName);
    if (savedPromotion && savedPromotion.poolKey && selectedPool && String(selectedPool.poolKey) === String(savedPromotion.poolKey)) {
      setClaimCalc(null);
      setClaimCalcError("");
      // Reload claims/wallets for the pool if it's currently selected
      const scope = derivePoolScope(selectedPool);
      if (scope) {
        setClaimsLoading(true);
        setWalletsLoading(true);
        try {
          const [cRows, wRows] = await Promise.all([
            fetchAllRows(rpc, { code: CONTRACT, scope, table: "nclaims", limit: 1000 }),
            fetchAllRows(rpc, { code: CONTRACT, scope, table: "nwallets", limit: 1000, index_position: 2, key_type: "i64" }),
          ]);
          const filterActor = (rows) => {
            const arr = Array.isArray(rows) ? rows : [];
            if (contractGodMode) return arr;
            const a = String(effectiveActorForFilters || "").trim();
            if (!a) return [];
            return arr.filter((r) => {
              if (r && Object.prototype.hasOwnProperty.call(r, "actor")) return String(r.actor) === a;
              return true;
            });
          };
          setClaims(filterActor(cRows));
          setWallets(filterActor(wRows));
        } catch (e) {
          // ignore errors during refresh
        } finally {
          setClaimsLoading(false);
          setWalletsLoading(false);
        }
      }
    }
    setSelectedPromotionId("");
    setPromotionForm({
      promoteId: "",
      promotionName: "",
      projectId: "",
      collectionName: "",
      rewardAmount: "",
      rewardTokenSymbolRaw: "",
      rewardTokenContract: "",
      startTimeSec: "0",
      endTimeSec: "0",
      maxClaimCount: "1",
      description: "",
      imageUrl: "",
      active: true,
    });
  }
}

async function deletePromotion() {
  const promo = selectedPromotion;
  if (!promo) {
    setError("Select a promotion to delete.");
    return;
  }
  const promoteId = Number(promo.promoteId || 0);
  if (!promoteId) {
    setError("Invalid promotion ID.");
    return;
  }
  const poolCreated = !!promo.poolCreated;
  const msg = poolCreated
    ? `This will permanently delete the promotion "${promo.promotionName}".\n\nNote: The pool has already been created and will NOT be deleted.\n\nDo you want to continue?`
    : `This will permanently delete the promotion "${promo.promotionName}".\n\nDo you want to continue?`;
  const sure = window.confirm(msg);
  if (!sure) return;
  const ok = await transact([{ account: TX_CONTRACT, name: "delpromo", authorization: [auth], data: { promoteId } }]);
  if (ok) {
    await loadCoreState({ silent: true });
    setSelectedPromotionId("");
  }
}

async function createPromotionPool() {
  const promo = selectedPromotion;
  if (!promo) {
    setError("Select a promotion first.");
    return;
  }
  if (promo.poolCreated) {
    setError("Pool has already been created for this promotion.");
    return;
  }
  if (!promo.active) {
    setError("Promotion must be active to create a pool.");
    return;
  }
  const maxClaimCount = Number(promo.maxClaimCount || 0);
  if (maxClaimCount < 1) {
    setError("maxClaimCount must be > 0.");
    return;
  }
  const promotionId = String(promo.promoteId || "");
  if (!promotionId || promotionId === "0") {
    setError("Invalid promotion ID.");
    return;
  }
  const ok = await transact([{ account: TX_CONTRACT, name: "creatpromo", authorization: [auth], data: { promotionId } }]);
  if (ok) {
    const res = await loadCoreState({ silent: true });
    // After creating a pool from promotion, reload the pool data and reset claim calculator
    // The pool should be automatically selected if it matches the promotion name
    const freshPromotions = (res && res.promotions) || [];
    const freshPools = (res && res.pools) || [];
    const freshPromotion = freshPromotions.find((p) => String(p.promoteId) === promotionId);
    if (freshPromotion && freshPromotion.poolKey) {
      // Find and select the newly created pool
      const createdPool = freshPools.find((p) => String(p.poolKey) === String(freshPromotion.poolKey));
      if (createdPool && createdPool.poolHash) {
        setSelectedPoolHash(String(createdPool.poolHash));
        // Reset claim calculator so it uses fresh data
        setClaimCalc(null);
        setClaimCalcError("");
        // Claims/Wallets will be reloaded automatically by useEffect when selectedPoolHash changes
      }
    }
    setPromotionPoolForm({ poolKey: undefined });
    setNotice("Pool created successfully from promotion.");
  }
}

async function updatePromotion() {
  if (!selectedPromotion) {
    setError("Select a promotion to update.");
    return;
  }
  const promoteId = String(selectedPromotion.promoteId || "");
  if (!promoteId || promoteId === "0") {
    setError("Invalid promotion ID.");
    return;
  }
  const promotionName = String(promotionForm.promotionName || "").trim();
  if (!promotionName) {
    setError("Promotion Name is required.");
    return;
  }
  if (promotionName.length > 70) {
    setError("Promotion Name must not be longer than 70 characters.");
    return;
  }
  const collectionName = String(promotionForm.collectionName || "").trim();
  if (!collectionName) {
    setError("Collection Name is required.");
    return;
  }
  
  const rewardTokenSymbolRaw = String(promotionForm.rewardTokenSymbolRaw || "").trim();
  const symbolParts = parseSymbolParts(rewardTokenSymbolRaw);
  if (!rewardTokenSymbolRaw) {
    setError("Token symbol is required.");
    return;
  } else if (!symbolParts.symbol) {
    setError('Token symbol must be in the form "precision,SYMBOL" (e.g. 4,XPR).');
    return;
  }
  
  const rewardTokenContract = String(promotionForm.rewardTokenContract || "").trim();
  if (!rewardTokenContract) {
    setError("Reward Token Contract is required.");
    return;
  }
  
  // Validate reward amount with strict decimal places check (like Pool)
  const rewardAmount = String(promotionForm.rewardAmount || "").trim();
  if (!rewardAmount) {
    setError("Reward amount is required.");
    return;
  }
  
  if (!/^-?\d+(\.\d+)?$/.test(rewardAmount)) {
    setError("Reward amount must be a number.");
    return;
  }
  
  const hasDot = rewardAmount.includes(".");
  const dec = hasDot ? String(rewardAmount.split(".")[1] || "").length : 0;
  const reqDec = Number(symbolParts.precision || 0);

  // Require EXACT decimal places to match the selected token precision.
  if (reqDec === 0) {
    if (hasDot) {
      setError("Reward amount must not include decimals for this token (precision 0).");
      return;
    }
  } else {
    if (!hasDot) {
      setError(`Reward amount must include exactly ${reqDec} decimal places for ${symbolParts.symbol}.`);
      return;
    } else if (dec !== reqDec) {
      setError(`Reward amount must have exactly ${reqDec} decimal places for ${symbolParts.symbol}.`);
      return;
    }
  }
  
  const reward = normalizeAssetInputForSymbol(rewardAmount, rewardTokenSymbolRaw);
  const parsedReward = parseAssetString(reward);
  if (!parsedReward) {
    setError("Reward must be a valid asset (amount + symbol).");
    return;
  } else if (parsedReward.amountInt <= 0n) {
    setError("Reward amount must be greater than 0.");
    return;
  }
  
  const maxClaimCount = Number(promotionForm.maxClaimCount || "1");
  if (!Number.isFinite(maxClaimCount) || !Number.isInteger(maxClaimCount) || maxClaimCount < 1) {
    setError("Max Claim Count must be an integer >= 1.");
    return;
  }
  // If promotion is finished, don't allow reducing maxClaimCount below actualClaimCount
  if (selectedPromotion.promofinished) {
    const actualClaimCount = Number(selectedPromotion.actualClaimCount || 0);
    if (maxClaimCount < actualClaimCount) {
      setError(`Cannot reduce maxClaimCount below actualClaimCount (${actualClaimCount}) for finished promotion.`);
      return;
    }
  }
  const startTimeSec = Number(promotionForm.startTimeSec || 0);
  const endTimeSec = Number(promotionForm.endTimeSec || 0);
  if (startTimeSec > 0 && endTimeSec > 0 && endTimeSec < startTimeSec) {
    setError("End time must be >= start time.");
    return;
  }
  const description = String(promotionForm.description || "").trim();
  if (description.length > 500) {
    setError("Description must not be longer than 500 characters.");
    return;
  }
  const imageUrl = String(promotionForm.imageUrl || "").trim();
  if (imageUrl.length > 256) {
    setError("Image URL must not be longer than 256 characters.");
    return;
  }
  const data = {
    promoteId,
    promotionName,
    collectionName,
    reward,
    rewardTokenContract,
    startTimeSec,
    endTimeSec,
    maxClaimCount: String(maxClaimCount),
    description,
    imageUrl,
    active: !!promotionForm.active,
  };
  const ok = await transact([{ account: TX_CONTRACT, name: "updpromo", authorization: [auth], data }]);
  if (ok) {
    const res = await loadCoreState({ silent: true });
    // Reset claim calculator if a pool exists for this promotion and is currently selected
    const freshPromotions = (res && res.promotions) || [];
    const updatedPromotion = freshPromotions.find((p) => String(p.promoteId) === promoteId);
    if (updatedPromotion && updatedPromotion.poolKey && selectedPool && String(selectedPool.poolKey) === String(updatedPromotion.poolKey)) {
      setClaimCalc(null);
      setClaimCalcError("");
      // Reload claims/wallets for the pool if it's currently selected
      const scope = derivePoolScope(selectedPool);
      if (scope) {
        setClaimsLoading(true);
        setWalletsLoading(true);
        try {
          const [cRows, wRows] = await Promise.all([
            fetchAllRows(rpc, { code: CONTRACT, scope, table: "nclaims", limit: 1000 }),
            fetchAllRows(rpc, { code: CONTRACT, scope, table: "nwallets", limit: 1000, index_position: 2, key_type: "i64" }),
          ]);
          const filterActor = (rows) => {
            const arr = Array.isArray(rows) ? rows : [];
            if (contractGodMode) return arr;
            const a = String(effectiveActorForFilters || "").trim();
            if (!a) return [];
            return arr.filter((r) => {
              if (r && Object.prototype.hasOwnProperty.call(r, "actor")) return String(r.actor) === a;
              return true;
            });
          };
          setClaims(filterActor(cRows));
          setWallets(filterActor(wRows));
        } catch (e) {
          // ignore errors during refresh
        } finally {
          setClaimsLoading(false);
          setWalletsLoading(false);
        }
      }
    }
    setNotice("Promotion updated successfully.");
  }
}

// === Survey Management Functions ===

function validateSurveyForm() {
  const errors = [];
  const warnings = [];
  const isNew = !selectedSurvey;
  const surveyKey = String(surveyForm.surveyKey || "").trim();
  const surveyText = String(surveyForm.surveyText || "").trim();
  const numOptions = Number(surveyForm.numOptions || 2);
  const surveyType = Number(surveyForm.surveyType || 1);

  if (!surveyKey) errors.push("Survey Key is required.");
  else if (surveyKey.length > 70) errors.push("Survey Key must not be longer than 70 characters.");
  if (isNew && surveyKey) {
    const dup = (surveysForProject || []).some((s) => String(s.surveyKey) === surveyKey);
    if (dup) errors.push("A survey with this Survey Key already exists in the selected project.");
  }

  if (!surveyText) errors.push("Survey Text is required.");
  else if (surveyText.length > 500) errors.push("Survey Text must not be longer than 500 characters.");

  if (numOptions < 1 || numOptions > 6) {
    errors.push("Number of options must be between 1 and 6.");
  } else {
    for (let i = 1; i <= numOptions; i++) {
      const opt = String(surveyForm[`option${i}`] || "").trim();
      if (!opt) {
        errors.push(`Option ${i} is required.`);
      } else if (opt.length > 200) {
        errors.push(`Option ${i} must not be longer than 200 characters.`);
      }
    }
  }

  if (surveyType < 1 || surveyType > 3) {
    errors.push("Survey Type must be 1, 2, or 3.");
  }

  if (surveyType === 2) {
    const requiredAnswers = String(surveyForm.requiredAnswers || "").trim();
    if (requiredAnswers.length > 20) {
      errors.push("Required Answers must not be longer than 20 characters.");
    } else if (requiredAnswers) {
      const parts = requiredAnswers.split(",");
      const seen = new Set();
      for (const part of parts) {
        const val = Number(part.trim());
        if (!Number.isFinite(val) || !Number.isInteger(val) || val < 1 || val > numOptions) {
          errors.push(`Required Answers must be comma-separated numbers between 1 and ${numOptions}.`);
          break;
        }
        if (seen.has(val)) {
          errors.push("Required Answers must not contain duplicates.");
          break;
        }
        seen.add(val);
      }
    }
  } else if (surveyForm.requiredAnswers) {
    errors.push("Required Answers can only be set for Survey Type = Answer-based (2).");
  }

  const surveyDescription = String(surveyForm.surveyDescription || "").trim();
  if (surveyDescription.length > 500) {
    errors.push("Survey Description must not be longer than 500 characters.");
  }

  const surveyImageUrl = String(surveyForm.surveyImageUrl || "").trim();
  if (surveyImageUrl.length > 256) {
    errors.push("Survey Image URL must not be longer than 256 characters.");
  }

  const startTimeSec = Number(surveyForm.startTimeSec || 0);
  const endTimeSec = Number(surveyForm.endTimeSec || 0);
  if (startTimeSec > 0 && endTimeSec > 0 && endTimeSec < startTimeSec) {
    errors.push("End time must be >= start time.");
  }

  return { errors, warnings };
}

async function checkSurvey() {
  const base = validateSurveyForm();
  const errors = [...(base.errors || [])];
  const warnings = [...(base.warnings || [])];

  const r = { ok: errors.length === 0, errors, warnings };
  setSurveyCheck(r);
  if (!r.ok) setError(r.errors[0] || "Invalid survey data");
  return r.ok;
}

async function previewSurveyClaimFromCheck() {
  const ok = await checkSurvey();
  if (!ok) return;
  setNotice("Survey data is valid.");
}

async function previewSurveyPoolClaimFromCheck() {
  if (!selectedSurvey) {
    setError("Select a survey first.");
    return;
  }
  
  // Validate survey pool form fields
  const poolKey = String(surveyPoolForm.poolKey || "").trim();
  if (!poolKey) {
    setError("Pool Key is required.");
    return;
  }
  const collectionName = String(surveyPoolForm.collectionName || "").trim();
  if (!collectionName) {
    setError("Collection Name is required.");
    return;
  }
  
  const rewardTokenSymbolRaw = String(surveyPoolForm.rewardTokenSymbolRaw || "").trim();
  const symbolParts = parseSymbolParts(rewardTokenSymbolRaw);
  if (!rewardTokenSymbolRaw) {
    setError("Token symbol is required.");
    return;
  } else if (!symbolParts.symbol) {
    setError('Token symbol must be in the form "precision,SYMBOL" (e.g. 4,XPR).');
    return;
  }
  
  const rewardTokenContract = String(surveyPoolForm.rewardTokenContract || "").trim();
  if (!rewardTokenContract) {
    setError("Token contract is required.");
    return;
  }
  
  // Validate reward amount with strict decimal places check (like Pool)
  const rewardAmount = String(surveyPoolForm.rewardAmount || "").trim();
  if (!rewardAmount) {
    setError("Reward Amount is required.");
    return;
  }
  
  if (!/^-?\d+(\.\d+)?$/.test(rewardAmount)) {
    setError("Reward amount must be a number.");
    return;
  }
  
  const hasDot = rewardAmount.includes(".");
  const dec = hasDot ? String(rewardAmount.split(".")[1] || "").length : 0;
  const reqDec = Number(symbolParts.precision || 0);

  // Require EXACT decimal places to match the selected token precision.
  if (reqDec === 0) {
    if (hasDot) {
      setError("Reward amount must not include decimals for this token (precision 0).");
      return;
    }
  } else {
    if (!hasDot) {
      setError(`Reward amount must include exactly ${reqDec} decimal places for ${symbolParts.symbol}.`);
      return;
    } else if (dec !== reqDec) {
      setError(`Reward amount must have exactly ${reqDec} decimal places for ${symbolParts.symbol}.`);
      return;
    }
  }
  
  // Validate that the normalized asset is valid and > 0
  const reward = normalizeAssetInputForSymbol(rewardAmount, rewardTokenSymbolRaw);
  const parsedReward = parseAssetString(reward);
  if (!parsedReward) {
    setError("Reward must be a valid asset (amount + symbol).");
    return;
  } else if (parsedReward.amountInt <= 0n) {
    setError("Reward amount must be greater than 0.");
    return;
  }

  // Build pool data from surveyPoolForm
  const poolFormData = {
    poolKey,
    collectionName,
    rewardAmount,
    rewardTokenSymbolRaw,
    rewardTokenContract,
    requiredImmutableText: String(surveyPoolForm.requiredImmutableText || "[]"),
    active: surveyPoolForm.active,
    cooldownSec: "4294967295", // Always max for survey pools
    poolType: 3, // Survey pool
    startTimeSec: String(surveyPoolForm.startTimeSec || "0"),
    endTimeSec: String(surveyPoolForm.endTimeSec || "0"),
  };

  setClaimPreviewMode("info");
  setPendingSaveAction(null);
  await calculateClaimPreviewWithFormData(poolFormData, null);
  setClaimPreviewModalOpen(true);
}

async function saveSurvey() {
  if (!selectedProject) {
    setError("Please select a project first.");
    return;
  }
  const surveyKey = String(surveyForm.surveyKey || "").trim();
  if (!surveyKey) {
    setError("Survey Key is required.");
    return;
  }
  if (surveyKey.length > 70) {
    setError("Survey Key must not be longer than 70 characters.");
    return;
  }
  const projectId = Number(selectedProject.projectId || surveyForm.projectId || 0);
  if (!projectId) {
    setError("Project ID is required.");
    return;
  }
  const surveyText = String(surveyForm.surveyText || "").trim();
  if (!surveyText) {
    setError("Survey Text is required.");
    return;
  }
  if (surveyText.length > 500) {
    setError("Survey Text must not be longer than 500 characters.");
    return;
  }
  const numOptions = Number(surveyForm.numOptions || 2);
  if (numOptions < 1 || numOptions > 6) {
    setError("Number of options must be between 1 and 6.");
    return;
  }
  // Validate options based on numOptions
  for (let i = 1; i <= numOptions; i++) {
    const opt = String(surveyForm[`option${i}`] || "").trim();
    if (!opt) {
      setError(`Option ${i} is required.`);
      return;
    }
    if (opt.length > 200) {
      setError(`Option ${i} must not be longer than 200 characters.`);
      return;
    }
  }
  const surveyType = Number(surveyForm.surveyType || 1);
  if (surveyType < 1 || surveyType > 3) {
    setError("Survey Type must be 1, 2, or 3.");
    return;
  }
  // Validate requiredAnswers for Answer-based surveys
  if (surveyType === 2) {
    const requiredAnswers = String(surveyForm.requiredAnswers || "").trim();
    if (requiredAnswers.length > 20) {
      setError("Required Answers must not be longer than 20 characters.");
      return;
    }
    if (requiredAnswers) {
      // Validate format: comma-separated numbers 1-6
      const parts = requiredAnswers.split(",");
      const seen = new Set();
      for (const part of parts) {
        const val = Number(part.trim());
        if (!Number.isFinite(val) || !Number.isInteger(val) || val < 1 || val > numOptions) {
          setError(`Required Answers must be comma-separated numbers between 1 and ${numOptions}.`);
          return;
        }
        if (seen.has(val)) {
          setError("Required Answers must not contain duplicates.");
          return;
        }
        seen.add(val);
      }
    }
  } else if (surveyForm.requiredAnswers) {
    setError("Required Answers can only be set for Survey Type = Answer-based (2).");
    return;
  }
  const surveyDescription = String(surveyForm.surveyDescription || "").trim();
  if (surveyDescription.length > 500) {
    setError("Survey Description must not be longer than 500 characters.");
    return;
  }
  const surveyImageUrl = String(surveyForm.surveyImageUrl || "").trim();
  if (surveyImageUrl.length > 256) {
    setError("Survey Image URL must not be longer than 256 characters.");
    return;
  }
  const startTimeSec = Number(surveyForm.startTimeSec || 0);
  const endTimeSec = Number(surveyForm.endTimeSec || 0);
  if (startTimeSec > 0 && endTimeSec > 0 && endTimeSec < startTimeSec) {
    setError("End time must be >= start time.");
    return;
  }
  const data = {
    surveyKey,
    projectId,
    surveyText,
    option1: String(surveyForm.option1 || "").trim(),
    option2: String(surveyForm.option2 || "").trim(),
    option3: String(surveyForm.option3 || "").trim(),
    option4: String(surveyForm.option4 || "").trim(),
    option5: String(surveyForm.option5 || "").trim(),
    option6: String(surveyForm.option6 || "").trim(),
    numOptions,
    allowMultiple: !!surveyForm.allowMultiple,
    surveyType,
    requiredAnswers: String(surveyForm.requiredAnswers || "").trim(),
    active: !!surveyForm.active,
    closed: !!surveyForm.closed,
    startTimeSec,
    endTimeSec,
    surveyDescription,
    surveyImageUrl,
  };
  const surveyActive = !!surveyForm.active;
  const ok = await transact([{ account: TX_CONTRACT, name: "setsurvey", authorization: [auth], data }]);
  if (ok) {
    // If survey is set to inactive and has a pool, also disable the pool
    if (!surveyActive && selectedSurvey && selectedSurvey.poolKey) {
      const poolKey = String(selectedSurvey.poolKey || "").trim();
      if (poolKey) {
        const pool = (pools || []).find((p) => String(p.poolKey || "") === poolKey);
        if (pool && pool.active) {
          // Disable pool using disablepool action
          await transact([{ account: TX_CONTRACT, name: "disablepool", authorization: [auth], data: { poolKey } }]);
        }
      }
    }
    
    await loadCoreState({ silent: true });
    setSelectedSurveyKey("");
    setSurveyForm({
      surveyKey: "",
      projectId: "",
      surveyText: "",
      option1: "",
      option2: "",
      option3: "",
      option4: "",
      option5: "",
      option6: "",
      numOptions: 2,
      allowMultiple: false,
      surveyType: 1,
      requiredAnswers: "",
      active: true,
      closed: false,
      startTimeSec: "0",
      endTimeSec: "0",
      surveyDescription: "",
      surveyImageUrl: "",
    });
  }
}

async function deleteSurvey() {
  const survey = selectedSurvey;
  if (!survey) {
    setError("Select a survey to delete.");
    return;
  }
  const surveyKey = String(survey.surveyKey || "").trim();
  if (!surveyKey) {
    setError("Invalid survey key.");
    return;
  }
  const poolKey = String(survey.poolKey || "").trim();
  const voteCount = Number(survey.voteCount || 0);
  let msg = `This will permanently delete the survey "${surveyKey}".\n\n`;
  if (poolKey) {
    msg += "Note: The pool has already been created and will NOT be deleted.\n\n";
  }
  if (voteCount > 0) {
    msg += `Note: ${voteCount} vote(s) will also be deleted.\n\n`;
  }
  msg += "Do you want to continue?";
  const sure = window.confirm(msg);
  if (!sure) return;
  const ok = await transact([{ account: TX_CONTRACT, name: "delsurvey", authorization: [auth], data: { surveyKey } }]);
  if (ok) {
    await loadCoreState({ silent: true });
    setSelectedSurveyKey("");
  }
}

async function createSurveyPool() {
  const survey = selectedSurvey;
  if (!survey) {
    setError("Select a survey first.");
    return;
  }
  if (survey.poolKey) {
    setError("Pool has already been created for this survey.");
    return;
  }
  const poolKey = String(surveyPoolForm.poolKey || "").trim();
  if (!poolKey) {
    setError("Pool Key is required.");
    return;
  }
  if (poolKey.length > 70) {
    setError("Pool Key must not be longer than 70 characters.");
    return;
  }
  const projectId = Number(survey.projectId || 0);
  if (!projectId) {
    setError("Invalid project ID.");
    return;
  }
  const collectionName = String(surveyPoolForm.collectionName || "").trim();
  if (!collectionName) {
    setError("Collection Name is required.");
    return;
  }
  
  const rewardTokenSymbolRaw = String(surveyPoolForm.rewardTokenSymbolRaw || "").trim();
  const symbolParts = parseSymbolParts(rewardTokenSymbolRaw);
  if (!rewardTokenSymbolRaw) {
    setError("Token symbol is required.");
    return;
  } else if (!symbolParts.symbol) {
    setError('Token symbol must be in the form "precision,SYMBOL" (e.g. 4,XPR).');
    return;
  }
  
  const rewardTokenContract = String(surveyPoolForm.rewardTokenContract || "").trim();
  if (!rewardTokenContract) {
    setError("Reward Token Contract is required.");
    return;
  }
  
  // Validate reward amount with strict decimal places check (like Pool)
  const rewardAmount = String(surveyPoolForm.rewardAmount || "").trim();
  if (!rewardAmount) {
    setError("Reward amount is required.");
    return;
  }
  
  if (!/^-?\d+(\.\d+)?$/.test(rewardAmount)) {
    setError("Reward amount must be a number.");
    return;
  }
  
  const hasDot = rewardAmount.includes(".");
  const dec = hasDot ? String(rewardAmount.split(".")[1] || "").length : 0;
  const reqDec = Number(symbolParts.precision || 0);

  // Require EXACT decimal places to match the selected token precision.
  if (reqDec === 0) {
    if (hasDot) {
      setError("Reward amount must not include decimals for this token (precision 0).");
      return;
    }
  } else {
    if (!hasDot) {
      setError(`Reward amount must include exactly ${reqDec} decimal places for ${symbolParts.symbol}.`);
      return;
    } else if (dec !== reqDec) {
      setError(`Reward amount must have exactly ${reqDec} decimal places for ${symbolParts.symbol}.`);
      return;
    }
  }
  
  const reward = normalizeAssetInputForSymbol(rewardAmount, rewardTokenSymbolRaw);
  const parsedReward = parseAssetString(reward);
  if (!parsedReward) {
    setError("Reward must be a valid asset (amount + symbol).");
    return;
  } else if (parsedReward.amountInt <= 0n) {
    setError("Reward amount must be greater than 0.");
    return;
  }
  const startTimeSec = Number(surveyPoolForm.startTimeSec || 0);
  const endTimeSec = Number(surveyPoolForm.endTimeSec || 0);
  if (startTimeSec > 0 && endTimeSec > 0 && endTimeSec < startTimeSec) {
    setError("End time must be >= start time.");
    return;
  }
  // Survey pools always have max cooldown (4294967295) to ensure one claim per wallet
  const cooldownSec = 4294967295;
  const requiredImmutable = asArrayOfNames(surveyPoolForm.requiredImmutableText);
  const data = {
    surveyKey: survey.surveyKey,
    poolKey,
    projectId,
    collectionName,
    reward,
    rewardTokenContract,
    active: !!surveyPoolForm.active,
    startTimeSec,
    endTimeSec,
    cooldownSec,
    requiredImmutable,
    poolDescription: String(surveyPoolForm.poolDescription || "").trim(),
    poolImageUrl: String(surveyPoolForm.poolImageUrl || "").trim(),
  };
  const ok = await transact([{ account: TX_CONTRACT, name: "creatsurvpl", authorization: [auth], data }]);
  if (ok) {
    const res = await loadCoreState({ silent: true });
    // After creating a pool from survey, reload the pool data and reset claim calculator
    const freshSurveys = (res && res.surveys) || [];
    const freshPools = (res && res.pools) || [];
    const freshSurvey = freshSurveys.find((s) => String(s.surveyKey) === survey.surveyKey);
    if (freshSurvey && freshSurvey.poolKey) {
      // Find and select the newly created pool
      const createdPool = freshPools.find((p) => String(p.poolKey) === String(freshSurvey.poolKey));
      if (createdPool && createdPool.poolHash) {
        setSelectedPoolHash(String(createdPool.poolHash));
        // Reset claim calculator so it uses fresh data
        setClaimCalc(null);
        setClaimCalcError("");
        // Claims/Wallets will be reloaded automatically by useEffect when selectedPoolHash changes
      }
    }
    setNotice("Pool created successfully from survey.");
    setSurveyPoolForm({
      poolKey: "",
      collectionName: "",
      rewardAmount: "",
      rewardTokenSymbolRaw: "",
      rewardTokenContract: "",
      active: true,
      startTimeSec: "0",
      endTimeSec: "0",
      cooldownSec: "4294967295", // Survey pools always use max cooldown
      requiredImmutableText: "[]",
      poolDescription: "",
      poolImageUrl: "",
    });
  }
}

async function drawSurveyWinners() {
  setError("");
  const survey = selectedSurvey;
  if (!survey) {
    setError("Select a survey first.");
    return;
  }
  if (Number(survey.surveyType) !== 3) {
    setError("Only Raffle surveys (Type 3) can draw winners.");
    return;
  }
  const surveyKey = String(survey.surveyKey || "").trim();
  if (!surveyKey) {
    setError("Invalid survey key.");
    return;
  }
  const now = Math.floor(Date.now() / 1000);
  const closed = !!survey.closed;
  const endTimeSec = Number(survey.endTimeSec || 0);
  if (!closed && (endTimeSec === 0 || now < endTimeSec)) {
    setError("Survey must be closed or endTimeSec must be reached to draw winners.");
    return;
  }
  const numWinners = Number(drawWinnersNum || "1");
  if (!Number.isFinite(numWinners) || !Number.isInteger(numWinners) || numWinners < 1 || numWinners > 6) {
    setError("Number of winners must be between 1 and 6.");
    return;
  }
  const voteCount = Number(survey.voteCount || 0);
  if (voteCount === 0) {
    setError("No votes found for this survey.");
    return;
  }
  const msg = `This will draw ${numWinners} winner(s) from ${voteCount} participant(s) for the survey "${surveyKey}".\n\nThis action cannot be undone.\n\nDo you want to continue?`;
  const sure = window.confirm(msg);
  if (!sure) return;
  setBusy(true);
  try {
    const ok = await transact([{ account: TX_CONTRACT, name: "drawwinners", authorization: [auth], data: { surveyKey, numWinners } }]);
    if (ok) {
      await loadCoreState({ silent: true });
      setNotice(`${numWinners} winner(s) drawn successfully.`);
    }
  } catch (e) {
    setError(serializeError(e));
  } finally {
    setBusy(false);
    postWalletUiCleanup();
  }
}

// === Streak Management Functions ===

function validateStreakForm() {
  const errors = [];
  const warnings = [];
  const isNew = !selectedStreak;
  const streakId = String(streakForm.streakId || "").trim();
  const streakName = String(streakForm.streakName || "").trim();
  const projectId = Number(selectedProject?.projectId || streakForm.projectId || 0);
  const targetStreakDays = Number(streakForm.targetStreakDays || 0);
  const targetInARow = Number(streakForm.targetInARow || 0);

  if (!streakId) errors.push("Streak ID is required.");
  else if (streakId.length > 70) errors.push("Streak ID must not be longer than 70 characters.");
  if (isNew && streakId) {
    const dup = (streaksForProject || []).some((s) => String(s.streakId) === streakId);
    if (dup) errors.push("A streak with this Streak ID already exists in the selected project.");
  }

  if (!streakName) errors.push("Streak Name is required.");
  else if (streakName.length > 70) errors.push("Streak Name must not be longer than 70 characters.");

  if (!projectId) errors.push("Project ID is required.");

  if (!Number.isFinite(targetStreakDays) || !Number.isInteger(targetStreakDays) || targetStreakDays < 1) {
    errors.push("Target Streak Days must be an integer >= 1.");
  }

  if (!Number.isFinite(targetInARow) || !Number.isInteger(targetInARow) || targetInARow < 0) {
    errors.push("Target In-A-Row must be an integer >= 0.");
  }

  const collectionName = String(streakForm.collectionName || "").trim();
  if (!collectionName) errors.push("Collection Name is required.");

  const rewardTokenSymbolRaw = String(streakForm.rewardTokenSymbolRaw || "").trim();
  const symbolParts = parseSymbolParts(rewardTokenSymbolRaw);
  if (!rewardTokenSymbolRaw) errors.push("Token symbol is required.");
  else if (!symbolParts.symbol) errors.push('Token symbol must be in the form "precision,SYMBOL" (e.g. 4,XPR).');

  const rewardTokenContract = String(streakForm.rewardTokenContract || "").trim();
  if (!rewardTokenContract) errors.push("Reward Token Contract is required.");

  // Validate reward amount with strict decimal places check
  const rewardAmount = String(streakForm.rewardAmount || "").trim();
  if (!rewardAmount) {
    errors.push("Reward amount is required.");
  } else {
    if (!/^-?\d+(\.\d+)?$/.test(rewardAmount)) {
      errors.push("Reward amount must be a number.");
    } else {
      const hasDot = rewardAmount.includes(".");
      const dec = hasDot ? String(rewardAmount.split(".")[1] || "").length : 0;
      const reqDec = Number(symbolParts.precision || 0);

      if (reqDec === 0) {
        if (hasDot) errors.push("Reward amount must not include decimals for this token (precision 0).");
      } else {
        if (!hasDot) errors.push(`Reward amount must include exactly ${reqDec} decimal places for ${symbolParts.symbol}.`);
        else if (dec !== reqDec) errors.push(`Reward amount must have exactly ${reqDec} decimal places for ${symbolParts.symbol}.`);
      }
    }

    const reward = rewardTokenSymbolRaw ? normalizeAssetInputForSymbol(rewardAmount, rewardTokenSymbolRaw) : "";
    const parsedReward = parseAssetString(reward);
    if (!parsedReward) {
      errors.push("Reward must be a valid asset (amount + symbol).");
    } else if (parsedReward.amountInt <= 0n) {
      errors.push("Reward amount must be greater than 0.");
    }
  }

  const cooldownSec = Number(streakForm.cooldownSec || 0);
  if (!Number.isFinite(cooldownSec) || cooldownSec < 0) {
    errors.push("Cooldown must be a number >= 0.");
  }

  const poolDescription = String(streakForm.poolDescription || "").trim();
  if (poolDescription.length > 500) errors.push("Pool Description must not be longer than 500 characters.");

  const poolImageUrl = String(streakForm.poolImageUrl || "").trim();
  if (poolImageUrl.length > 256) errors.push("Pool Image URL must not be longer than 256 characters.");

  // requiredImmutableText
  const rimRaw = String(streakForm.requiredImmutableText || "[]").trim();
  if (rimRaw) {
    try {
      const parsed = JSON.parse(rimRaw);
      if (!Array.isArray(parsed)) errors.push("Required Immutable must be a JSON array (e.g. []).");
    } catch {
      if (rimRaw !== "[]") warnings.push("Required Immutable is not valid JSON; it will be parsed as a space/comma-separated list.");
    }
  }

  return { errors, warnings };
}

async function checkStreak() {
  const base = validateStreakForm();
  const errors = [...(base.errors || [])];
  const warnings = [...(base.warnings || [])];

  const c = String(streakForm.collectionName || "").trim();
  if (c) {
    // Skip AtomicAssets verification for "account" (per-wallet claims)
    if (c.toLowerCase() !== "account") {
      const norm = normalizeAtomicCollectionName(c);
      if (norm.warning) warnings.push(norm.warning);
      if (norm.atomicName) {
        const ex = await atomicCollectionExists(norm.atomicName);
        if (ex.ok && !ex.exists) errors.push(`Collection "${c}" was not found (AtomicAssets).`);
        else if (!ex.ok) warnings.push(`Could not verify collection "${c}" (AtomicAssets API unreachable).`);
      }
    }
  }

  const r = { ok: errors.length === 0, errors, warnings };
  setStreakCheck(r);
  if (!r.ok) setError(r.errors[0] || "Invalid streak data");
  return r.ok;
}

async function previewStreakFromCheck() {
  const ok = await checkStreak();
  if (!ok) return;
  setNotice("Streak data is valid.");
}

async function saveStreak() {
  if (!selectedProject) {
    setError("Please select a project first.");
    return;
  }
  const streakId = String(streakForm.streakId || "").trim();
  if (!streakId) {
    setError("Streak ID is required.");
    return;
  }
  if (streakId.length > 70) {
    setError("Streak ID must not be longer than 70 characters.");
    return;
  }
  const streakName = String(streakForm.streakName || "").trim();
  if (!streakName) {
    setError("Streak Name is required.");
    return;
  }
  if (streakName.length > 70) {
    setError("Streak Name must not be longer than 70 characters.");
    return;
  }
  const collectionName = String(streakForm.collectionName || "").trim();
  if (!collectionName) {
    setError("Collection Name is required.");
    return;
  }

  const rewardTokenSymbolRaw = String(streakForm.rewardTokenSymbolRaw || "").trim();
  const symbolParts = parseSymbolParts(rewardTokenSymbolRaw);
  if (!rewardTokenSymbolRaw) {
    setError("Token symbol is required.");
    return;
  } else if (!symbolParts.symbol) {
    setError('Token symbol must be in the form "precision,SYMBOL" (e.g. 4,XPR).');
    return;
  }

  const rewardTokenContract = String(streakForm.rewardTokenContract || "").trim();
  if (!rewardTokenContract) {
    setError("Reward Token Contract is required.");
    return;
  }

  const rewardAmount = String(streakForm.rewardAmount || "").trim();
  if (!rewardAmount) {
    setError("Reward amount is required.");
    return;
  }

  if (!/^-?\d+(\.\d+)?$/.test(rewardAmount)) {
    setError("Reward amount must be a number.");
    return;
  }

  const hasDot = rewardAmount.includes(".");
  const dec = hasDot ? String(rewardAmount.split(".")[1] || "").length : 0;
  const reqDec = Number(symbolParts.precision || 0);

  if (reqDec === 0) {
    if (hasDot) {
      setError("Reward amount must not include decimals for this token (precision 0).");
      return;
    }
  } else {
    if (!hasDot) {
      setError(`Reward amount must include exactly ${reqDec} decimal places for ${symbolParts.symbol}.`);
      return;
    } else if (dec !== reqDec) {
      setError(`Reward amount must have exactly ${reqDec} decimal places for ${symbolParts.symbol}.`);
      return;
    }
  }

  const reward = normalizeAssetInputForSymbol(rewardAmount, rewardTokenSymbolRaw);
  const parsedReward = parseAssetString(reward);
  if (!parsedReward) {
    setError("Reward must be a valid asset (amount + symbol).");
    return;
  } else if (parsedReward.amountInt <= 0n) {
    setError("Reward amount must be greater than 0.");
    return;
  }

  const projectId = Number(selectedProject.projectId);
  const targetStreakDays = Number(streakForm.targetStreakDays || 7);
  const targetInARow = Number(streakForm.targetInARow || 0);
  const active = !!streakForm.active;
  const cooldownSec = Number(streakForm.cooldownSec || 86400);
  const poolDescription = String(streakForm.poolDescription || "").trim();
  const poolImageUrl = String(streakForm.poolImageUrl || "").trim();
  const requiredImmutable = asArrayOfNames(streakForm.requiredImmutableText);

  const data = {
    streakId,
    streakName,
    projectId,
    targetStreakDays,
    targetInARow,
    active,
    collectionName,
    reward,
    rewardTokenContract,
    cooldownSec,
    poolDescription,
    poolImageUrl,
    requiredImmutable,
  };
  const ok = await transact([{ account: TX_CONTRACT, name: "setstreak", authorization: [auth], data }]);
  if (ok) {
    await loadCoreState({ silent: true });
    setNotice("Streak saved successfully.");
  }
}

async function updateStreak() {
  if (!selectedStreak) {
    setError("Select a streak to update.");
    return;
  }
  const streakId = String(selectedStreak.streakId || "").trim();
  if (!streakId) {
    setError("Invalid streak ID.");
    return;
  }
  if (selectedStreak.poolCreated) {
    setError("Cannot modify streak after pool was created.");
    return;
  }
  const streakName = String(streakForm.streakName || "").trim();
  if (!streakName) {
    setError("Streak Name is required.");
    return;
  }
  if (streakName.length > 70) {
    setError("Streak Name must not be longer than 70 characters.");
    return;
  }
  const collectionName = String(streakForm.collectionName || "").trim();
  if (!collectionName) {
    setError("Collection Name is required.");
    return;
  }

  const rewardTokenSymbolRaw = String(streakForm.rewardTokenSymbolRaw || "").trim();
  const symbolParts = parseSymbolParts(rewardTokenSymbolRaw);
  if (!rewardTokenSymbolRaw) {
    setError("Token symbol is required.");
    return;
  } else if (!symbolParts.symbol) {
    setError('Token symbol must be in the form "precision,SYMBOL" (e.g. 4,XPR).');
    return;
  }

  const rewardTokenContract = String(streakForm.rewardTokenContract || "").trim();
  if (!rewardTokenContract) {
    setError("Reward Token Contract is required.");
    return;
  }

  const rewardAmount = String(streakForm.rewardAmount || "").trim();
  if (!rewardAmount) {
    setError("Reward amount is required.");
    return;
  }

  if (!/^-?\d+(\.\d+)?$/.test(rewardAmount)) {
    setError("Reward amount must be a number.");
    return;
  }

  const hasDot = rewardAmount.includes(".");
  const dec = hasDot ? String(rewardAmount.split(".")[1] || "").length : 0;
  const reqDec = Number(symbolParts.precision || 0);

  if (reqDec === 0) {
    if (hasDot) {
      setError("Reward amount must not include decimals for this token (precision 0).");
      return;
    }
  } else {
    if (!hasDot) {
      setError(`Reward amount must include exactly ${reqDec} decimal places for ${symbolParts.symbol}.`);
      return;
    } else if (dec !== reqDec) {
      setError(`Reward amount must have exactly ${reqDec} decimal places for ${symbolParts.symbol}.`);
      return;
    }
  }

  const reward = normalizeAssetInputForSymbol(rewardAmount, rewardTokenSymbolRaw);
  const parsedReward = parseAssetString(reward);
  if (!parsedReward) {
    setError("Reward must be a valid asset (amount + symbol).");
    return;
  } else if (parsedReward.amountInt <= 0n) {
    setError("Reward amount must be greater than 0.");
    return;
  }

  const projectId = Number(selectedStreak.projectId || selectedProject?.projectId);
  const targetStreakDays = Number(streakForm.targetStreakDays || 7);
  const targetInARow = Number(streakForm.targetInARow || 0);
  const active = !!streakForm.active;
  const cooldownSec = Number(streakForm.cooldownSec || 86400);
  const poolDescription = String(streakForm.poolDescription || "").trim();
  const poolImageUrl = String(streakForm.poolImageUrl || "").trim();
  const requiredImmutable = asArrayOfNames(streakForm.requiredImmutableText);

  const data = {
    streakId,
    streakName,
    projectId,
    targetStreakDays,
    targetInARow,
    active,
    collectionName,
    reward,
    rewardTokenContract,
    cooldownSec,
    poolDescription,
    poolImageUrl,
    requiredImmutable,
  };
  const ok = await transact([{ account: TX_CONTRACT, name: "setstreak", authorization: [auth], data }]);
  if (ok) {
    await loadCoreState({ silent: true });
    setClaimCalc(null);
    setClaimCalcError("");
    setNotice("Streak updated successfully.");
  }
}

async function deleteStreak() {
  if (!selectedStreak) {
    setError("Select a streak to delete.");
    return;
  }
  const streakId = String(selectedStreak.streakId || "").trim();
  if (!streakId) {
    setError("Invalid streak ID.");
    return;
  }
  if (selectedStreak.poolCreated) {
    setError("Cannot delete streak after pool was created.");
    return;
  }
  const sure = window.confirm(`Are you sure you want to delete streak "${streakId}"?`);
  if (!sure) return;
  const ok = await transact([{ account: TX_CONTRACT, name: "delstreak", authorization: [auth], data: { streakId } }]);
  if (ok) {
    await loadCoreState({ silent: true });
    setSelectedStreakId("");
    setNotice("Streak deleted successfully.");
  }
}

async function setStreakActive(active) {
  if (!selectedStreak) {
    setError("Select a streak first.");
    return;
  }
  const streakId = String(selectedStreak.streakId || "").trim();
  if (!streakId) {
    setError("Invalid streak ID.");
    return;
  }
  const ok = await transact([{ account: TX_CONTRACT, name: "setstreakact", authorization: [auth], data: { streakId, active } }]);
  if (ok) {
    await loadCoreState({ silent: true });
    setNotice(`Streak ${active ? "activated" : "deactivated"} successfully.`);
  }
}

async function createStreakPool() {
  const streak = selectedStreak;
  if (!streak) {
    setError("Select a streak first.");
    return;
  }
  if (streak.poolCreated) {
    setError("Pool has already been created for this streak.");
    return;
  }
  if (!streak.active) {
    setError("Streak must be active to create a pool.");
    return;
  }
  const streakId = String(streak.streakId || "").trim();
  if (!streakId) {
    setError("Invalid streak ID.");
    return;
  }
  const poolKey = String(streakPoolForm.poolKey || "").trim() || streakId;
  if (!poolKey) {
    setError("Pool Key is required.");
    return;
  }
  if (poolKey.length > 70) {
    setError("Pool Key must not be longer than 70 characters.");
    return;
  }

  const ok = await transact([{ account: TX_CONTRACT, name: "creatstrpl", authorization: [auth], data: { streakId, poolKey } }]);
  if (ok) {
    const res = await loadCoreState({ silent: true });
    // After creating a pool from streak, reload the pool data and reset claim calculator
    const freshStreaks = (res && res.streaks) || streaks || [];
    const freshPools = (res && res.pools) || pools || [];
    const freshStreak = freshStreaks.find((s) => String(s.streakId) === streakId);
    if (freshStreak && freshStreak.poolKey) {
      // Find and select the newly created pool
      const createdPool = freshPools.find((p) => String(p.poolKey) === String(freshStreak.poolKey));
      if (createdPool && createdPool.poolHash) {
        setSelectedPoolHash(String(createdPool.poolHash));
        // Reset claim calculator so it uses fresh data
        setClaimCalc(null);
        setClaimCalcError("");
        // Claims/Wallets will be reloaded automatically by useEffect when selectedPoolHash changes
      }
    }
    setStreakPoolForm({ poolKey: undefined });
    setNotice("Pool created successfully from streak.");
  }
}

// Streak check state
const [streakCheck, setStreakCheck] = useState(null);

// Effect to prefill streak form when streak is selected
useEffect(() => {
  if (preferNewStreakRef.current) return;
  if (selectedStreak) {
    const parseRewardSymbol = () => {
      const rewardStr = String(selectedStreak.reward || "");
      const parts = rewardStr.split(" ");
      if (parts.length === 2) {
        const amt = parts[0];
        const sym = parts[1];
        const dec = amt.includes(".") ? amt.split(".")[1].length : 0;
        return `${dec},${sym}`;
      }
      return "";
    };
    const rewardSymbol = parseRewardSymbol();
    const amt = String(selectedStreak.reward || "").split(" ")[0] || "";
    const requiredImmArr = selectedStreak.requiredImmutable;
    const requiredImmutableText = Array.isArray(requiredImmArr) && requiredImmArr.length > 0 ? JSON.stringify(requiredImmArr) : "[]";
    setStreakForm({
      streakId: String(selectedStreak.streakId || ""),
      streakName: String(selectedStreak.streakName || ""),
      projectId: String(selectedStreak.projectId || ""),
      targetStreakDays: String(selectedStreak.targetStreakDays || 7),
      targetInARow: String(selectedStreak.targetInARow || 0),
      active: !!selectedStreak.active,
      collectionName: String(selectedStreak.collectionName || ""),
      rewardAmount: amt,
      rewardTokenSymbolRaw: rewardSymbol,
      rewardTokenContract: String(selectedStreak.rewardTokenContract || ""),
      cooldownSec: String(selectedStreak.cooldownSec || 86400),
      poolDescription: String(selectedStreak.poolDescription || ""),
      poolImageUrl: String(selectedStreak.poolImageUrl || ""),
      requiredImmutableText,
    });
    setStreakCheck(null);
  } else {
    // New streak: prefill with defaults
    setStreakForm({
      streakId: "",
      streakName: "",
      projectId: String(selectedProject?.projectId || ""),
      targetStreakDays: "7",
      targetInARow: "0",
      active: true,
      collectionName: "",
      rewardAmount: "",
      rewardTokenSymbolRaw: "",
      rewardTokenContract: "",
      cooldownSec: "86400",
      poolDescription: "",
      poolImageUrl: "",
      requiredImmutableText: "[]",
    });
    setStreakCheck(null);
  }
}, [selectedStreak, selectedProject]);

async function setRamBudget() {
    if (!selectedProject) {
      setError("Select a project first.");
      return;
    }
    if (!selectedRamFeeDeposit) {
      setError("No RAMFee transfer entry found for this account/project. Send token to the contract with memo ramfee (any ASCII case), then refresh.");
      return;
    }
    const pid = Number(selectedProject.projectId);
    const budgetClaims = Number(String(ramSetClaims || "").trim());
    if (!pid) {
      setError("Missing projectId");
      return;
    }
    if (!budgetClaims || budgetClaims < 0) {
      setError("budgetClaims must be 0 or greater");
      return;
    }
    await transact([
      {
        account: TX_CONTRACT,
        name: "setrambudget",
        authorization: [auth],
        data: { projectId: pid, budgetClaims },
      },
    ]);
  }

  async function refundDeposit() {
    const did = String(refundDepositId || "").trim();
    if (!did) {
      setError("Select a deposit first.");
      return;
    }
    const depositId = Number(did);
    if (!depositId) {
      setError("Invalid depositId");
      return;
    }
    if (!actor) {
      setError("Connect wallet first.");
      return;
    }
    const depActor = String(selectedRefundDeposit?.actor || "").trim();
    if (depActor && depActor !== actor) {
      setError(`This deposit belongs to ${depActor}, not ${actor}.`);
      return;
    }
    await transact([
      {
        account: TX_CONTRACT,
        name: "refund",
        authorization: [auth],
        data: { depositId, account: actor },
      },
    ]);
  }

  function toAlnumPoolKey(raw) {
    const s = String(raw || "");
    const cleaned = s.replace(/[^a-z0-9]/gi, "");
    return cleaned || "pool";
  }

  async function exportClaimsCsv(poolOverride = null, filenameOverride = "") {
    const pool = poolOverride || selectedPool;
    if (!pool) return false;
    try {
      const scope = derivePoolScope(pool);
      const all = await fetchAllRows(rpc, { code: CONTRACT, scope, table: "nclaims", limit: 1000 });
      // Fallback to currently loaded rows if RPC export fetch returns nothing.
      const rows = (Array.isArray(all) && all.length > 0) ? all : (Array.isArray(filteredClaims) ? [...filteredClaims] : []);
      rows.sort((a, b) => Number(b?.claimedAt || 0) - Number(a?.claimedAt || 0));
      const filename = String(filenameOverride || `claims_${pool.poolKey || "pool"}.csv`);
      downloadCsv(filename, rows);
      return true;
    } catch (e) {
      setError(serializeError(e).message);
      return false;
    }
  }

  async function exportWalletsCsv(poolOverride = null, filenameOverride = "") {
    const pool = poolOverride || selectedPool;
    if (!pool) return false;
    try {
      const scope = derivePoolScope(pool);
      const all = await fetchAllRows(rpc, { code: CONTRACT, scope, table: "nwallets", limit: 1000, index_position: 2, key_type: "i64" });
      // Match on-screen query shape + fallback to loaded rows if RPC returns nothing.
      const rows = (Array.isArray(all) && all.length > 0) ? all : (Array.isArray(filteredWallets) ? [...filteredWallets] : []);
      rows.sort((a, b) => Number(b?.lastClaimSec || 0) - Number(a?.lastClaimSec || 0));
      const filename = String(filenameOverride || `wallets_${pool.poolKey || "pool"}.csv`);
      downloadCsv(filename, rows);
      return true;
    } catch (e) {
      setError(serializeError(e).message);
      return false;
    }
  }

  async function exportSurveyVotesCsv() {
    try {
      // Load all survey votes from all surveys
      const allSurveyVotes = [];
      const surveysArray = Array.isArray(surveys) ? surveys : [];
      
      for (const survey of surveysArray) {
        const surveyHash = String(survey.surveyHash || "");
        if (!surveyHash) continue;
        
        try {
          const votes = await fetchAllRows(rpc, { 
            code: CONTRACT, 
            scope: surveyHash, 
            table: "nsurveyvotes", 
            limit: 1000 
          });
          if (Array.isArray(votes)) {
            allSurveyVotes.push(...votes);
          }
        } catch (e) {
          console.warn("[exportSurveyVotesCsv] Failed to load votes for survey", survey.surveyKey || surveyHash, ":", e);
        }
      }
      
      // Sort by votedAt desc (if available) or by account
      allSurveyVotes.sort((a, b) => {
        const aTime = Number(a?.votedAt || a?.createdAt || 0);
        const bTime = Number(b?.votedAt || b?.createdAt || 0);
        if (aTime !== bTime) return bTime - aTime;
        return String(a?.account || "").localeCompare(String(b?.account || ""));
      });
      
      downloadCsv(`survey_votes_${new Date().toISOString().split('T')[0]}.csv`, allSurveyVotes);
    } catch (e) {
      setError(serializeError(e).message);
    }
  }

  function monthNameEnglish(monthNum) {
    const m = Number(monthNum || 0);
    const names = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return names[Math.max(0, Math.min(11, m - 1))] || "";
  }

  function toIsoUtc(d) {
    try {
      return new Date(d).toISOString();
    } catch {
      return "";
    }
  }

  function ymdUtcFromTimestamp(ts) {
    // Hyperion typically returns ISO timestamps. Accept seconds too.
    if (!ts) return "";
    let d;
    if (typeof ts === "number") d = new Date(ts * 1000);
    else {
      const s = String(ts);
      if (/^\d+$/.test(s)) d = new Date(Number(s) * 1000);
      else d = new Date(s);
    }
    if (Number.isNaN(d.getTime())) return String(ts);
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, "0");
    const day = String(d.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }

  function ymdBerlinFromTimestamp(ts) {
    // Convert an ISO timestamp (or seconds) to YYYY-MM-DD in Europe/Berlin.
    if (!ts) return "";
    let d;
    if (typeof ts === "number") d = new Date(ts * 1000);
    else {
      const s = String(ts);
      if (/^\d+$/.test(s)) d = new Date(Number(s) * 1000);
      else d = new Date(s);
    }
    if (Number.isNaN(d.getTime())) return String(ts);
    try {
      // en-CA yields ISO-like YYYY-MM-DD
      return new Intl.DateTimeFormat("en-CA", {
        timeZone: "Europe/Berlin",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(d);
    } catch {
      // Fallback to local date if timeZone is unavailable
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    }
  }


  async function fetchHistoryActionsForPeriod({ account, afterIso, beforeIso }) {
    const base = String(rpc || "").trim().replace(/\/+$/, "");
    if (!base) throw new Error("Missing history API endpoint.");
    const limit = 100;
    let skip = 0;
    const out = [];
    // Hard cap to prevent runaway calls in very active accounts.
    const MAX = 5000;

    while (skip < MAX) {
      const url =
        `${base}/v2/history/get_actions` +
        `?account=${encodeURIComponent(account)}` +
        `&skip=${skip}&limit=${limit}&sort=asc` +
        `&after=${encodeURIComponent(afterIso)}` +
        `&before=${encodeURIComponent(beforeIso)}`;
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error(`History API error (${res.status}): ${url}`);
      }
      const data = await res.json();
      const actions = Array.isArray(data?.actions) ? data.actions : [];
      out.push(...actions);
      if (actions.length < limit) break;
      skip += limit;
    }
    return out;
  }

  function normalizeHistoryAction(a) {
    const act = a?.act || a?.action_trace?.act || a?.trace?.act || null;
    const ts = a?.timestamp || a?.["@timestamp"] || a?.block_time || a?.time || "";
    const trx = a?.trx_id || a?.trxId || a?.transaction_id || a?.trx || "";
    return { act, timestamp: ts, trx_id: trx };
  }

  function shortTx(txid) {
    const s = String(txid || "");
    if (s.length <= 14) return s;
    return `${s.slice(0, 10)}...${s.slice(-4)}`;
  }

  function sumAssetsBySymbol(items) {
    const map = new Map();
    for (const it of items || []) {
      const a = parseAssetString(it?.quantity);
      if (!a) continue;
      const key = `${a.symbol}|${a.precision}`;
      const prev = map.get(key) || 0n;
      map.set(key, prev + a.amountInt);
    }
    return map;
  }

  function formatSumMap(map) {
    const parts = [];
    for (const [k, amtInt] of map.entries()) {
      const [sym, precStr] = String(k).split("|");
      const prec = Number(precStr || 0);
      parts.push(formatAssetInt(amtInt, prec, sym));
    }
    return parts;
  }


  function parseSimpleCsv(text) {
    const rows = [];
    const lines = String(text || "").replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
    if (lines.length < 2) return rows;
    // header in lines[0]
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      const parts = line.split(",");
      if (parts.length < 6) continue;
      rows.push({
        date: String(parts[0] || "").trim(),
        tokensymbol: String(parts[1] || "").trim(),
        tokenPrecision: Number(parts[2] || 0),
        tokencontract: String(parts[3] || "").trim(),
        USDvalue: Number(parts[4] || 0),
        EURvalue: Number(parts[5] || 0),
      });
    }
    return rows;
  }

  function bigIntToFloat(amountInt, precision) {
    try {
      const neg = amountInt < 0n;
      const abs = neg ? -amountInt : amountInt;
      const scale = BigInt("1" + "0".repeat(precision));
      const whole = abs / scale;
      const frac = abs % scale;
      const fracStr = frac.toString().padStart(precision, "0");
      const s = `${neg ? "-" : ""}${whole.toString()}.${fracStr}`;
      return Number.parseFloat(s);
    } catch {
      return 0;
    }
  }

  function formatFiat(x) {
    const n = Number(x);
    if (!Number.isFinite(n)) return "-";
    const a = Math.abs(n);
    if (a >= 100) return n.toFixed(2);
    if (a >= 1) return n.toFixed(4);
    return n.toFixed(8);
  }

  async function fetchCyphrPriceBookForMonth(year, month) {
  const y = Number(year);
  const m = Number(month);
  const debug = {
    month: "",
    candidates: [],
    selectedUrl: "",
    loaded: false,
    rowCount: 0,
    parsedCyphrRows: 0,
    avgUsd: null,
    avgEur: null,
    asOf: "",
    dateRange: "",
    error: "",
  };

  if (!y || !m) {
    debug.error = "Invalid year or month.";
    return { book: null, debug };
  }

  const mm = String(m).padStart(2, "0");
  debug.month = `${y}-${mm}`;

  // Price CSV files are served by the web server from /cyphrprice/ (e.g. /var/www/html/cyphrprice on Linux).
  const candidates = [
    `/cyphrprice/cyphr_prices_${y}-${mm}.csv`,
    `/cyphrprice/${y}-${mm}.csv`,
    `/cyphrprice/cyphr_prices_${y}-${mm}.CSV`,
    `/cyphrprice/${y}-${mm}.CSV`,
  ];

  for (const url of candidates) {
    const entry = { url, ok: false, status: 0, bytes: 0, cyphrRows: 0, note: "" };
    debug.candidates.push(entry);
    try {
      const res = await fetch(url, { cache: "no-store" });
      entry.status = res.status;
      if (!res.ok) {
        entry.note = `HTTP ${res.status}`;
        continue;
      }
      const text = await res.text();
      entry.bytes = text.length;

      const rowsAll = parseSimpleCsv(text);
      const rows = rowsAll.filter((r) => String(r.tokensymbol || "").toUpperCase() === "CYPHR");
      entry.cyphrRows = rows.length;

      if (!rows.length) {
        entry.note = "No CYPHR rows found.";
        continue;
      }

      const byDay = new Map();
      let sumUsd = 0;
      let sumEur = 0;
      let n = 0;
      let lastDate = "";
      let minDay = "";
      let maxDay = "";

      for (const r of rows) {
        const usd = Number(r.USDvalue);
        const eur = Number(r.EURvalue);
        if (!Number.isFinite(usd) || !Number.isFinite(eur)) continue;
        const dayKey = ymdBerlinFromTimestamp(String(r.date || "").trim());
        if (!dayKey) continue;

        byDay.set(dayKey, { usd, eur });
        sumUsd += usd;
        sumEur += eur;
        n += 1;

        lastDate = String(r.date || "") || lastDate;
        if (!minDay || dayKey < minDay) minDay = dayKey;
        if (!maxDay || dayKey > maxDay) maxDay = dayKey;
      }

      if (n === 0) {
        entry.note = "Rows exist but no valid numeric USD/EUR values.";
        continue;
      }

      const avgUsd = sumUsd / n;
      const avgEur = sumEur / n;

      const book = {
        sourceUrl: url,
        asOf: lastDate,
        byDay,
        avgUsd,
        avgEur,
        count: n,
      };

      entry.ok = true;
      debug.selectedUrl = url;
      debug.loaded = true;
      debug.rowCount = rowsAll.length;
      debug.parsedCyphrRows = rows.length;
      debug.avgUsd = avgUsd;
      debug.avgEur = avgEur;
      debug.asOf = lastDate;
      debug.dateRange = minDay && maxDay ? `${minDay}..${maxDay}` : "";

      return { book, debug };
    } catch (e) {
      entry.note = String(e && e.message ? e.message : e);
      // try next
    }
  }

  debug.error = "No matching price CSV file could be loaded.";
  return { book: null, debug };
}

function getCyphrRateForYmd(priceBook, ymd) {
    if (!priceBook) return null;
    const key = String(ymd || "").trim();
    const hit = priceBook.byDay?.get(key);
    if (hit && Number.isFinite(hit.usd) && Number.isFinite(hit.eur)) {
      return { usd: hit.usd, eur: hit.eur, usedMonthlyAvg: false };
    }
    // Fallback: monthly average
    if (Number.isFinite(priceBook.avgUsd) && Number.isFinite(priceBook.avgEur)) {
      return { usd: priceBook.avgUsd, eur: priceBook.avgEur, usedMonthlyAvg: true };
    }
    return null;
  }

  function formatSumMapWithFiatTotals(map, fiatTotalsByKey) {
    const parts = [];
    for (const [k, amtInt] of map.entries()) {
      const [sym, precStr] = String(k).split("|");
      const prec = Number(precStr || 0);
      const base = formatAssetInt(amtInt, prec, sym);
      const ft = fiatTotalsByKey ? fiatTotalsByKey.get(k) : null;
      if (ft && Number.isFinite(ft.usd) && Number.isFinite(ft.eur)) {
        parts.push(`${base} (${formatFiat(ft.usd)} USD / ${formatFiat(ft.eur)} EUR)`);
      } else {
        parts.push(base);
      }
    }
    return parts;
  }

  function computeCyphrFiatTotalsByAssetKey(items, priceBook) {
    const out = new Map();
    let usedAvgCount = 0;
    if (!priceBook) return { map: out, usedAvgCount };
    for (const it of items || []) {
      const a = parseAssetString(it.quantity);
      if (!a) continue;
      if (String(a.symbol || "").toUpperCase() !== "CYPHR") continue;
      const key = `${a.symbol}|${a.precision}`;
      const amt = bigIntToFloat(a.amountInt, a.precision);
      const rate = getCyphrRateForYmd(priceBook, it.date);
      if (!rate) continue;
      if (rate.usedMonthlyAvg) usedAvgCount += 1;
      const usd = amt * rate.usd;
      const eur = amt * rate.eur;
      const prev = out.get(key) || { usd: 0, eur: 0 };
      out.set(key, { usd: prev.usd + usd, eur: prev.eur + eur });
    }
    return { map: out, usedAvgCount };
  }
  
function computeCyphrFiatTotals(items, priceBook) {
  const out = {
    usd: 0,
    eur: 0,
    itemCount: 0,
    cyphrItemCount: 0,
    dailyHitCount: 0,
    monthlyAvgCount: 0,
    missingRateCount: 0,
    perDate: new Map(), // ymd -> { mode: "daily"|"avg"|"missing", usdRate, eurRate }
  };
  if (!items || !items.length) return out;

  for (const it of items) {
    out.itemCount += 1;
    const a = parseAssetString(it.quantity);
    if (!a) {
      out.missingRateCount += 1;
      continue;
    }
    if (String(a.symbol || "").toUpperCase() !== "CYPHR") continue;
    out.cyphrItemCount += 1;

    const amt = bigIntToFloat(a.amountInt, a.precision);
    const rate = getCyphrRateForYmd(priceBook, it.date);
    if (!rate) {
      out.missingRateCount += 1;
      out.perDate.set(String(it.date), { mode: "missing", usdRate: null, eurRate: null });
      continue;
    }
    if (rate.usedMonthlyAvg) {
      out.monthlyAvgCount += 1;
      out.perDate.set(String(it.date), { mode: "avg", usdRate: rate.usd, eurRate: rate.eur });
    } else {
      out.dailyHitCount += 1;
      out.perDate.set(String(it.date), { mode: "daily", usdRate: rate.usd, eurRate: rate.eur });
    }
    out.usd += amt * rate.usd;
    out.eur += amt * rate.eur;
  }
  return out;
}

function buildBillingDebugText({ priceDebug, priceBook, depositsIn, creditsOut }) {
  const lines = [];
  const month = priceDebug && priceDebug.month ? priceDebug.month : "unknown";
  lines.push(`CYPHR price lookup month: ${month}`);
  if (priceBook && priceBook.sourceUrl) {
    lines.push(`Price file FOUND: ${priceBook.sourceUrl}`);
    lines.push(`Parsed CYPHR rows: ${String(priceDebug?.parsedCyphrRows || "")} (total CSV rows: ${String(priceDebug?.rowCount || "")})`);
    lines.push(`Date range in CSV: ${String(priceDebug?.dateRange || "")}`);
    lines.push(`Monthly average: ${formatFiat(priceBook.avgUsd)} USD / ${formatFiat(priceBook.avgEur)} EUR`);
    lines.push(`As-of (last row date): ${String(priceBook.asOf || "")}`);
  } else {
    lines.push("Price file NOT FOUND.");
    if (priceDebug && Array.isArray(priceDebug.candidates) && priceDebug.candidates.length) {
      lines.push("Tried:");
      for (const c of priceDebug.candidates) {
        lines.push(`  - ${c.url} => ${c.ok ? "OK" : "MISS"} (${c.note || ("HTTP " + c.status)}) bytes=${c.bytes} cyphrRows=${c.cyphrRows}`);
      }
    }
    if (priceDebug?.error) lines.push(`Error: ${String(priceDebug.error)}`);
  }

  const allItems = [...(depositsIn || []), ...(creditsOut || [])];
  const uniqueDates = Array.from(new Set(allItems.map((x) => String(x.date || "").trim()).filter(Boolean))).sort();
  lines.push("");
  lines.push(`Transaction dates requested (Berlin YYYY-MM-DD): ${uniqueDates.length}`);
  if (!uniqueDates.length) {
    lines.push("  (none)");
  } else {
    for (const d of uniqueDates) {
      const daily = priceBook && priceBook.byDay ? priceBook.byDay.get(d) : null;
      if (daily && Number.isFinite(daily.usd) && Number.isFinite(daily.eur)) {
        lines.push(`  ${d}: DAILY rate found (${formatFiat(daily.usd)} USD / ${formatFiat(daily.eur)} EUR)`);
      } else if (priceBook && Number.isFinite(priceBook.avgUsd) && Number.isFinite(priceBook.avgEur)) {
        lines.push(`  ${d}: no daily rate -> using MONTHLY AVG (${formatFiat(priceBook.avgUsd)} USD / ${formatFiat(priceBook.avgEur)} EUR)`);
      } else {
        lines.push(`  ${d}: NO RATE (no daily and no monthly average)`);
      }
    }
  }

  lines.push("");
  const invTotalsFiat = computeCyphrFiatTotals(depositsIn || [], priceBook);
  const credTotalsFiat = computeCyphrFiatTotals(creditsOut || [], priceBook);
  lines.push(`Invoice items: ${invTotalsFiat.itemCount} (CYPHR: ${invTotalsFiat.cyphrItemCount})`);
  lines.push(`  Daily hits: ${invTotalsFiat.dailyHitCount}, Monthly avg used: ${invTotalsFiat.monthlyAvgCount}, Missing: ${invTotalsFiat.missingRateCount}`);
  lines.push(`  Total USD: ${formatFiat(invTotalsFiat.usd)} | Total EUR: ${formatFiat(invTotalsFiat.eur)}`);
  lines.push(`Credit items: ${credTotalsFiat.itemCount} (CYPHR: ${credTotalsFiat.cyphrItemCount})`);
  lines.push(`  Daily hits: ${credTotalsFiat.dailyHitCount}, Monthly avg used: ${credTotalsFiat.monthlyAvgCount}, Missing: ${credTotalsFiat.missingRateCount}`);
  lines.push(`  Total USD: ${formatFiat(credTotalsFiat.usd)} | Total EUR: ${formatFiat(credTotalsFiat.eur)}`);
  lines.push(`Net (Invoices - Credits) USD: ${formatFiat(invTotalsFiat.usd - credTotalsFiat.usd)} | EUR: ${formatFiat(invTotalsFiat.eur - credTotalsFiat.eur)}`);

  // Show a few example rows
  lines.push("");
  lines.push("Sample items (first 20):");
  const sample = allItems.slice(0, 20);
  if (!sample.length) lines.push("  (none)");
  for (const it of sample) {
    const a = parseAssetString(it.quantity);
    const isCyphr = a && String(a.symbol || "").toUpperCase() === "CYPHR";
    const amt = a ? bigIntToFloat(a.amountInt, a.precision) : 0;
    const rate = isCyphr ? getCyphrRateForYmd(priceBook, it.date) : null;
    const mode = rate ? (rate.usedMonthlyAvg ? "avg" : "daily") : "missing";
    const usd = rate ? amt * rate.usd : null;
    const eur = rate ? amt * rate.eur : null;
    lines.push(`  ${String(it.date)} | ${String(it.quantity)} | ${isCyphr ? "CYPHR" : (a ? a.symbol : "?")} | rate=${mode} | USD=${usd==null?"-":formatFiat(usd)} EUR=${eur==null?"-":formatFiat(eur)} | tx=${shortTx(it.txid)}`);
  }

  return lines.join("\n");
}

  async function generateMonthlyStatementPdf() {
    if (!actor) return;

    setBillingError("");
    setBillingPdfName("");
    setBillingPdfUrl("");
    setBillingDebug("");

    const y = Number(String(billingYear || "").trim());
    const m = Number(String(billingMonth || "").trim());
    if (!y || y < 2000 || y > 2100) {
      setBillingError("Invalid year.");
      return;
    }
    if (!m || m < 1 || m > 12) {
      setBillingError("Invalid month.");
      return;
    }

    setBillingBusy(true);
    try {
      const ymKey = `${y}-${String(m).padStart(2, "0")}`;
      const start = new Date(Date.UTC(y, m - 1, 1, 0, 0, 0));
      const end = new Date(Date.UTC(y, m, 1, 0, 0, 0));
      const afterIso = toIsoUtc(start);
      const beforeIso = toIsoUtc(end);

      const raw = await fetchHistoryActionsForPeriod({ account: actor, afterIso, beforeIso });
      const normalized = raw.map(normalizeHistoryAction).filter((x) => x.act && x.act.name);

      // Identify refund transactions (contract action "refund") and pick the corresponding token transfers.
      const refundTxIds = new Set(
        normalized
          .filter((x) => String(x?.act?.account || "") === String(CONTRACT) && String(x?.act?.name || "") === "refund")
          .map((x) => String(x.trx_id || ""))
          .filter(Boolean)
      );

      const depositsIn = normalized
        .filter((x) => {
          const act = x.act;
          if (String(act?.name || "") !== "transfer") return false;
          const d = act?.data || {};
          const from = String(d.from || "");
          const to = String(d.to || "");
          const memo = String(d.memo || "").trim();
          if (from !== String(actor) || to !== String(CONTRACT)) return false;
          return /^ramfee$/i.test(String(memo || "").trim());
        })
        .map((x) => {
          const d = x.act.data || {};
          return {
            date: ymdBerlinFromTimestamp(x.timestamp),
            quantity: String(d.quantity || d.amount || ""),
            tokenContract: String(x.act.account || ""),
            memo: String(d.memo || ""),
            txid: String(x.trx_id || ""),
          };
        })
        .filter((x) => x.quantity);

      let creditsOut = normalized
        .filter((x) => {
          const act = x.act;
          if (String(act?.name || "") !== "transfer") return false;
          const d = act?.data || {};
          const from = String(d.from || "");
          const to = String(d.to || "");
          const txid = String(x.trx_id || "");
          if (from !== String(CONTRACT) || to !== String(actor)) return false;
          return refundTxIds.has(txid);
        })
        .map((x) => {
          const d = x.act.data || {};
          return {
            date: ymdBerlinFromTimestamp(x.timestamp),
            quantity: String(d.quantity || d.amount || ""),
            tokenContract: String(x.act.account || ""),
            memo: String(d.memo || ""),
            txid: String(x.trx_id || ""),
          };
        })
        .filter((x) => x.quantity);

      // Fallback: if refund action isn't available in history, try memo-based detection for outgoing transfers.
      if (creditsOut.length === 0) {
        creditsOut = normalized
          .filter((x) => {
            const act = x.act;
            if (String(act?.name || "") !== "transfer") return false;
            const d = act?.data || {};
            const from = String(d.from || "");
            const to = String(d.to || "");
            const memo = String(d.memo || "");
            if (from !== String(CONTRACT) || to !== String(actor)) return false;
            return /refund/i.test(memo) || /ramfee/i.test(memo);
          })
          .map((x) => {
            const d = x.act.data || {};
            return {
              date: ymdBerlinFromTimestamp(x.timestamp),
              quantity: String(d.quantity || d.amount || ""),
              tokenContract: String(x.act.account || ""),
              memo: String(d.memo || ""),
              txid: String(x.trx_id || ""),
            };
          })
          .filter((x) => x.quantity);
      }

      const issueDate = ymdUtcFromTimestamp(new Date().toISOString());
      const invoiceNo = `NCH-${y}${String(m).padStart(2, "0")}-${String(actor)}`;

      const invTotals = sumAssetsBySymbol(depositsIn);
      const credTotals = sumAssetsBySymbol(creditsOut);

      // net = invoices - credits
      const netTotals = new Map(invTotals);
      for (const [k, v] of credTotals.entries()) {
        netTotals.set(k, (netTotals.get(k) || 0n) - v);
      }

      const priceRes = await fetchCyphrPriceBookForMonth(y, m);
      const priceBook = priceRes && priceRes.book ? priceRes.book : null;
      const invFiatSum = computeCyphrFiatTotals(depositsIn, priceBook);
      const credFiatSum = computeCyphrFiatTotals(creditsOut, priceBook);
      const priceDebug = priceRes && priceRes.debug ? priceRes.debug : null;

      if (PDF_DEBUG_ENABLED) {
        setBillingDebug(buildBillingDebugText({ priceDebug, priceBook, depositsIn, creditsOut }));
      }

      const netFiatUsd = Number(invFiatSum.usd) - Number(credFiatSum.usd);
      const netFiatEur = Number(invFiatSum.eur) - Number(credFiatSum.eur);

      const fmtFiatCell = (n, code) => {
        const v = Number(n);
        if (!Number.isFinite(v)) return "-";
        return `${formatFiat(v)} ${code}`;
      };

      const itemToLine = (it) => {
        const a = parseAssetString(it.quantity);
        const sym = a ? String(a.symbol || "") : "?";
        const token = sym || "?";
        let usd = null;
        let eur = null;

        if (a && sym.toUpperCase() === "CYPHR") {
          const amt = bigIntToFloat(a.amountInt, a.precision);
          const rate = getCyphrRateForYmd(priceBook, it.date);
          if (rate) {
            usd = amt * rate.usd;
            eur = amt * rate.eur;
          }
        }

        return `${String(it.date || "")} | ${String(it.quantity || "")} | ${token} | ${shortTx(it.txid)} | ${usd == null ? "-" : fmtFiatCell(usd, "USD")} | ${eur == null ? "-" : fmtFiatCell(eur, "EUR")}`;
      };

      const lines = [];

      // Page 1
      lines.push("Monthly consolidated statement for RAMFee deposits (invoice items) and refunds (credit note items).");
      lines.push("");
      lines.push("Seller:");
      lines.push("Bloxprod (sole proprietor)");
      
      // Zeige vollständige Adresse oder nur E-Mail basierend auf Umgebungsvariable
      const showFullAddress = process.env.REACT_APP_SHOW_FULL_ADDRESS === "true" || process.env.REACT_APP_SHOW_FULL_ADDRESS === undefined;
      
      if (showFullAddress) {
        // Vollständige Adresse anzeigen
        lines.push("Norbert Beeck");
        lines.push("c/o Address Provider #1681");
        lines.push("An der Alten Ziegelei 38");
        lines.push("48157 Münster");
        lines.push("Germany");
        lines.push("Email: info@bloxprod.io");
      } else {
        // Nur E-Mail anzeigen
        lines.push("Email: info@bloxprod.io");
      }
      
      lines.push("Website: www.bloxprod.io");
      lines.push("VAT: VAT exempt pursuant to Section 19 UStG (Germany). No VAT is charged.");
      lines.push("");
      lines.push("Customer:");
      lines.push(`XPR account (actor): ${String(actor)}`);
      lines.push("Customer address: not provided (on-chain account identifier only)");
      lines.push("");

      // After Customer block (requested)
      lines.push("Data source: On-chain action history (Hyperion). This statement is generated automatically.");
      lines.push(`Fiat totals ${ymKey}:`);
      lines.push(`Total invoices: ${fmtFiatCell(invFiatSum.usd, "USD")} / ${fmtFiatCell(invFiatSum.eur, "EUR")}`);
      lines.push(`Total credits: ${fmtFiatCell(credFiatSum.usd, "USD")} / ${fmtFiatCell(credFiatSum.eur, "EUR")}`);
      lines.push(`Net (Invoices - Credits): ${fmtFiatCell(netFiatUsd, "USD")} / ${fmtFiatCell(netFiatEur, "EUR")}`);

      // Force sections onto page 2 (requested)
      lines.push("__PAGE_BREAK__");

      // Page 2 sections
      lines.push("A) Invoice items - RAMFee deposits received");
      lines.push("Date | Amount | Token | Tx | USD | EUR");
      if (!depositsIn.length) {
        lines.push("(none)");
      } else {
        for (const it of depositsIn) lines.push(itemToLine(it));
      }
      lines.push("");

      lines.push("B) Credit note items - RAMFee refunds paid out");
      lines.push("Date | Amount | Token | Tx | USD | EUR");
      if (!creditsOut.length) {
        lines.push("(none)");
      } else {
        for (const it of creditsOut) lines.push(itemToLine(it));
      }
      lines.push("");

      lines.push("Net totals (Invoices - Credits)");
      lines.push("Invoice totals (by token):");
      for (const s of formatSumMap(invTotals)) lines.push(`- ${s}`);
      lines.push("Credit totals (by token):");
      for (const s of formatSumMap(credTotals)) lines.push(`- ${s}`);
      lines.push("Net totals (by token):");
      for (const s of formatSumMap(netTotals)) lines.push(`- ${s}`);
      lines.push("");
      lines.push(`Fiat totals ${ymKey}:`);
      lines.push(`Total invoices: ${fmtFiatCell(invFiatSum.usd, "USD")} / ${fmtFiatCell(invFiatSum.eur, "EUR")}`);
      lines.push(`Total credits: ${fmtFiatCell(credFiatSum.usd, "USD")} / ${fmtFiatCell(credFiatSum.eur, "EUR")}`);
      lines.push(`Net (Invoices - Credits): ${fmtFiatCell(netFiatUsd, "USD")} / ${fmtFiatCell(netFiatEur, "EUR")}`);

      const headerRightLines = [
        "NFT Claim Hub",
        "Invoice / Statement",
        `Invoice No.: ${invoiceNo}`,
        `Issue date (UTC): ${issueDate}`,
        `Period: ${ymKey}`,
        `Customer: ${String(actor)}`,
      ];

      const logo = await loadImageRgbForPdf(APP_LOGO, 90);
      const pdfBytes = buildPdfFromLinesWithHeader(lines, { logo, headerRightLines });
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const fileName = `nftclaimhub_statement_${String(actor)}_${ymKey}.pdf`;
      setBillingPdfUrl(url);
      setBillingPdfName(fileName);
    } catch (e) {
      setBillingError(serializeError(e).message);
    } finally {
      setBillingBusy(false);
    }
  }

  // Calculate claim statistics with form data (before saving)
  async function calculateClaimPreviewWithFormData(poolFormData, reqFormData = null) {
    console.log("[ClaimCalc DEBUG] calculateClaimPreviewWithFormData called");
    console.log("[ClaimCalc DEBUG] poolFormData:", poolFormData);
    console.log("[ClaimCalc DEBUG] poolFormData.poolType:", poolFormData?.poolType);
    console.log("[ClaimCalc DEBUG] poolFormData.poolKey:", poolFormData?.poolKey);
    
    setClaimCalcError("");
    setClaimCalc(null);
    
    // Check if pool is inactive - set warning but continue calculation
    const isPoolInactive = poolFormData?.active === false || poolFormData?.active === "0" || poolFormData?.active === 0;
    if (isPoolInactive) {
      setClaimCalcError("This pool is inactive. No NFTs are eligible for claims.");
    }
    
    const poolCollectionRaw = String(poolFormData?.collectionName || "").trim();
    if (!poolCollectionRaw) {
      setClaimCalcError("Pool is missing collectionName.");
      return;
    }
    
    // Check if this is an account-based claim (collectionName = "account" for poolType 2, 3, 4, or 6)
    const poolKey = String(poolFormData?.poolKey || selectedPool?.poolKey || "").trim();
    const poolType = Number(poolFormData?.poolType || selectedPool?.poolType || 1);
    const isAccountBasedClaim =
      (poolType === 2 || poolType === 3 || poolType === 4 || poolType === 6) && poolCollectionRaw.toLowerCase() === "account";
    
    const poolNorm = normalizeAtomicCollectionName(poolCollectionRaw);
    const collectionName = poolNorm.atomicName || poolCollectionRaw;
    
    // For account-based claims (poolType 2/3 with collectionName="account"), skip AtomicAssets validation
    // For reward pools (poolType 1), always validate AtomicAssets naming
    if (!isAccountBasedClaim && !poolNorm.atomicName) {
      setClaimCalcError(`Pool collection "${poolCollectionRaw}" does not match AtomicAssets naming rules, so the claim calculator cannot query AtomicAssets.`);
      return;
    }
    
    setClaimCalcLoading(true);
    try {
      const poolImmReqs = asArrayOfNames(poolFormData?.requiredImmutableText || "[]");
      const poolTypeForMut = Number(poolFormData?.poolType || selectedPool?.poolType || 1);
      const poolCollForMut = String(poolFormData?.collectionName || selectedPool?.collectionName || "").trim();
      const poolMutReqs = poolSupportsRequiredMutable(poolTypeForMut, poolCollForMut)
        ? poolFormData && Object.prototype.hasOwnProperty.call(poolFormData, "requiredMutableText")
          ? asArrayOfNames(poolFormData.requiredMutableText || "[]")
          : requiredMutableForSelectedPool
        : [];

      // Build active rules list: existing rules + new/updated rule from form (if reqFormData provided)
      const invalidRuleCollections = [];
      let activeRulesList = (reqForPool || [])
        .filter((r) => !!r?.active)
        .map((r) => {
          const raw = String(r.collectionName || "").trim();
          const norm = normalizeAtomicCollectionName(raw);
          const atomicName = norm.atomicName;
          if (raw && !atomicName) invalidRuleCollections.push(raw);
          return {
            reqId: String(r.reqId || ""),
            collectionNameRaw: raw,
            collectionName: atomicName,
            nftcount: Number(r.nftcount || 1),
            reqPairs: parseImmutableRequirements(r.requiredImmutable || []),
          };
        })
        .filter((r) => r.collectionName && r.nftcount > 0);

      // If reqFormData is provided, add/update that rule in the list
      if (reqFormData) {
        const reqCollectionRaw = String(reqFormData.collectionName || "").trim();
        const reqNorm = normalizeAtomicCollectionName(reqCollectionRaw);
        const reqAtomicName = reqNorm.atomicName;
        if (reqCollectionRaw && !reqAtomicName) {
          invalidRuleCollections.push(reqCollectionRaw);
        }
        if (reqAtomicName && reqFormData.active) {
          // Remove existing rule with same reqKey (if updating)
          const reqKey = String(reqFormData.reqKey || "default").trim();
          activeRulesList = activeRulesList.filter((r) => {
            // Keep rules that don't match the reqKey being updated
            const existingRule = (reqForPool || []).find((r2) => String(r2.reqKey) === reqKey);
            return !existingRule || String(r.reqId) !== String(existingRule.reqId);
          });
          // Add the new/updated rule
          activeRulesList.push({
            reqId: "preview",
            collectionNameRaw: reqCollectionRaw,
            collectionName: reqAtomicName,
            nftcount: Number(reqFormData.nftcount || 1),
            reqPairs: parseImmutableRequirements(asArrayOfNames(reqFormData.requiredImmutableText || "[]")),
          });
        }
      }

      const activeRules = activeRulesList.filter((r) => r.collectionName && r.nftcount > 0);

      if (invalidRuleCollections.length) {
        setClaimCalcError(
          `Claim calculator cannot query AtomicAssets for Dependent NFTs collection(s): ${invalidRuleCollections.join(", ")}. Please enter valid AtomicAssets collection names (lowercase a-z, 1-5, dot, max 12 chars).`
        );
        return;
      }

      // For account-based claims (poolType 2/3 with collectionName="account"), skip NFT fetching/validation
      // For reward pools (poolType 1), always fetch and validate NFTs
      const collectionCache = new Map();
      const excludedIdSet = new Set(
        (Array.isArray(excludedAssets) ? excludedAssets : [])
          .map((r) => String(r?.assetId ?? r?.asset_id ?? ""))
          .filter(Boolean)
      );

      let allPoolAssetsRaw = [];
      let allPoolAssets = [];
      let excludedInPoolCount = 0;
      let poolMatchedAssets = [];

      if (isAccountBasedClaim) {
        // Account-based: No NFT fetching needed
        allPoolAssetsRaw = [];
        allPoolAssets = [];
        excludedInPoolCount = 0;
        poolMatchedAssets = [];
      } else {
        // Normal NFT-based claim: Fetch assets from AtomicAssets
        allPoolAssetsRaw = await fetchAllAtomicAssets({ collectionName, limit: 1000, maxPages: 200 });
        allPoolAssets = excludedIdSet.size
          ? allPoolAssetsRaw.filter((a) => !excludedIdSet.has(String(a?.asset_id || a?.assetId || "")))
          : allPoolAssetsRaw;
        excludedInPoolCount = allPoolAssetsRaw.length - allPoolAssets.length;

        collectionCache.set(collectionName, allPoolAssets);
        poolMatchedAssets = allPoolAssets.filter((a) => matchesPoolAssetRequirements(a, poolImmReqs, poolMutReqs));
      }

      const claimsArr = Array.isArray(claims) ? claims : [];
      const claimsForCalc = excludedIdSet.size ? claimsArr.filter((r) => !excludedIdSet.has(String(r?.assetId || ""))) : claimsArr;

      const claimedSet = new Set(claimsForCalc.map((r) => String(r?.assetId || "")));
      const claimedAccountsSet = new Set(claimsForCalc.map((r) => String(r?.account || "")).filter(Boolean));

      async function getCollectionAssets(coll) {
        const c = String(coll || "").trim();
        if (!c) return [];
        if (collectionCache.has(c)) return collectionCache.get(c);
        const assets = await fetchAllAtomicAssets({ collectionName: c, limit: 1000, maxPages: 200 });
        collectionCache.set(c, assets);
        return assets;
      }

      // For account-based claims, skip dependent NFT rules
      const ruleOwnerCounts = isAccountBasedClaim ? [] : await mapLimit(activeRules, 2, async (rule) => {
        const assets = await getCollectionAssets(rule.collectionName);
        const matched = assets.filter((a) => matchesRequirements(a, rule.reqPairs));
        const counts = new Map();
        for (const a of matched) {
          const o = String(a?.owner || "");
          if (!o) continue;
          counts.set(o, (counts.get(o) || 0) + 1);
        }
        return { rule, counts };
      });

      function getAssetCollectionName(a) {
        return String(a?.collection_name || a?.collection?.collection_name || a?.collection?.collectionName || "");
      }

      function getAssetId(a) {
        return String(a?.asset_id || a?.assetId || "");
      }

      // For account-based claims, eligibleAfterRules is empty (no NFT validation)
      let eligibleAfterRules = isAccountBasedClaim
        ? []
        : (activeRules.length === 0
          ? poolMatchedAssets
          : await (async () => {
              const list = poolMatchedAssets;
              const checks = await mapLimit(list, 10, async (asset) => {
                const owner = String(asset?.owner || "");
                if (!owner) return false;
                for (const item of ruleOwnerCounts) {
                  const rule = item.rule;
                  const baseCount = item.counts.get(owner) || 0;
                  // emulate excludeAssetId behavior (best effort)
                  let effCount = baseCount;
                  if (rule.collectionName === getAssetCollectionName(asset) && matchesRequirements(asset, rule.reqPairs)) {
                    effCount = Math.max(0, effCount - 1);
                  }
                  if (effCount < rule.nftcount) return false;
                }
                return true;
              });
              return list.filter((_, i) => !!checks[i]);
            })());
      
      // DEBUG: Log initial state for poolType 2
      if (poolType === 2) {
        console.log("[ClaimCalc DEBUG poolType 2] poolKey:", poolKey);
        console.log("[ClaimCalc DEBUG poolType 2] eligibleAfterRules BEFORE poolType filter:", eligibleAfterRules.length);
        console.log("[ClaimCalc DEBUG poolType 2] promotions array length:", (promotions || []).length);
        console.log("[ClaimCalc DEBUG poolType 2] wallets array length:", (wallets || []).length);
      }
      
      // For poolType 2, we need to count wallets BEFORE filtering, then filter assets
      let allEligibleWalletsForPoolType2 = new Set();
      if (poolType === 2 && poolKey) {
        if (isAccountBasedClaim) {
          // Account-based: All accounts are potentially eligible (no NFT validation needed)
          // We can't know all possible accounts, so we'll rely on the wallet count from claims/claims table
          // For now, we'll use an empty set and count from claims later if needed
          console.log("[ClaimCalc DEBUG poolType 2] Account-based claim: skipping NFT-based wallet counting");
        } else {
          // NFT-based: Count all wallets that have eligible NFTs (before filtering by claims)
          for (const asset of eligibleAfterRules) {
            const owner = String(asset?.owner || "");
            if (owner) allEligibleWalletsForPoolType2.add(owner);
          }
        }
        console.log("[ClaimCalc DEBUG poolType 2] allEligibleWalletsForPoolType2 size (before claim filter):", allEligibleWalletsForPoolType2.size);
      }
      
      if (poolType === 2 && poolKey) {
        // Pool Type 2 (Promotion Pool): Additional filters
        const promotion = (promotions || []).find((p) => String(p.poolKey || "") === poolKey);
        console.log("[ClaimCalc DEBUG poolType 2] promotion found:", !!promotion);
        if (promotion) {
          console.log("[ClaimCalc DEBUG poolType 2] promotion.promofinished:", promotion.promofinished);
          console.log("[ClaimCalc DEBUG poolType 2] promotion.actualClaimCount:", promotion.actualClaimCount);
          console.log("[ClaimCalc DEBUG poolType 2] promotion.maxClaimCount:", promotion.maxClaimCount);
        }
        if (!promotion) {
          setClaimCalcError(`Promotion not found for poolKey "${poolKey}".`);
          return;
        }
        
        // Check promotion status
        if (promotion.promofinished) {
          setClaimCalcError("Promotion is finished. No more claims allowed.");
          return;
        }
        
        const actualClaimCount = Number(promotion.actualClaimCount || 0);
        const maxClaimCount = Number(promotion.maxClaimCount || 0);
        if (actualClaimCount >= maxClaimCount) {
          setClaimCalcError(`Promotion claim limit reached (${actualClaimCount}/${maxClaimCount}).`);
          return;
        }
        
        // Filter: Only wallets that haven't claimed yet (via NWallet table, cooldownSec = COOLDOWN_FOREVER)
        const walletsForPool = (wallets || []).filter((w) => String(w.poolKey || "") === poolKey);
        console.log("[ClaimCalc DEBUG poolType 2] walletsForPool length:", walletsForPool.length);
        const claimedWalletsSet = new Set(walletsForPool.map((w) => String(w.account || "")));
        console.log("[ClaimCalc DEBUG poolType 2] claimedWalletsSet size:", claimedWalletsSet.size);
        console.log("[ClaimCalc DEBUG poolType 2] claimedWalletsSet sample:", Array.from(claimedWalletsSet).slice(0, 5));
        
        eligibleAfterRules = eligibleAfterRules.filter((asset) => {
          const owner = String(asset?.owner || "");
          return owner && !claimedWalletsSet.has(owner);
        });
        console.log("[ClaimCalc DEBUG poolType 2] eligibleAfterRules AFTER claim filter:", eligibleAfterRules.length);
      } else if (poolType === 3) {
        // Pool Type 3 (Survey Pool): Additional filters
        // Try to find survey by poolKey first (if pool already exists)
        // Otherwise use selectedSurvey (if pool not yet created)
        let survey = null;
        if (poolKey) {
          survey = (surveys || []).find((s) => String(s.poolKey || "") === poolKey);
        }
        // If not found by poolKey, try selectedSurvey (for preview before pool creation)
        if (!survey && selectedSurvey) {
          survey = selectedSurvey;
        }
        if (!survey) {
          setClaimCalcError(`Survey not found. Please select a survey first.`);
          return;
        }
        
        const surveyKey = String(survey.surveyKey || "");
        const surveyType = Number(survey.surveyType || 1);
        
        // Get all votes for this survey
        const surveyVotesForSurvey = (surveyVotes || []).filter((v) => String(v.surveyKey || "") === surveyKey);
        const votesByAccount = new Map();
        for (const vote of surveyVotesForSurvey) {
          const account = String(vote.account || "");
          if (account) {
            votesByAccount.set(account, vote);
          }
        }
        
        // For Survey Pools, we need to check votes, not just assets
        // First, identify all accounts with eligible votes
        const eligibleAccountsSet = new Set();
        for (const vote of surveyVotesForSurvey) {
          const account = String(vote.account || "");
          if (!account) continue;
          
          let isEligible = false;
          if (surveyType === 1) {
            // Normal: Vote exists → Claim allowed
            isEligible = true;
          } else if (surveyType === 2) {
            // Answer-based: Check if vote.answer contains all required answers
            const requiredAnswers = String(survey.requiredAnswers || "").trim();
            if (!requiredAnswers) {
              // If requiredAnswers is empty, all participants can claim
              isEligible = true;
            } else {
              const requiredList = requiredAnswers.split(",").map((s) => s.trim()).filter(Boolean);
              const voteAnswer = String(vote.answer || "").trim();
              const voteAnswers = voteAnswer.split(",").map((s) => s.trim()).filter(Boolean);
              isEligible = requiredList.every((req) => voteAnswers.includes(req));
            }
          } else if (surveyType === 3) {
            // Raffle: Only winners can claim (winnerRank > 0)
            const winnerRank = Number(vote.winnerRank || 0);
            isEligible = winnerRank > 0;
          }
          
          if (isEligible) {
            eligibleAccountsSet.add(account);
          }
        }
        
        // Filter assets: Only assets owned by accounts with eligible votes (only for NFT-based claims)
        if (!isAccountBasedClaim) {
          eligibleAfterRules = eligibleAfterRules.filter((asset) => {
            const owner = String(asset?.owner || "");
            return owner && eligibleAccountsSet.has(owner);
          });
        }
        // For account-based claims, eligibleAfterRules is already empty, eligibleAccountsSet contains all eligible accounts
      } else if (poolType === 4 && poolKey) {
        // Pool Type 4 (Streak Pool): Additional filters
        // Try to find streak by poolKey first (if pool already exists)
        let streak = null;
        if (poolKey) {
          streak = (streaks || []).find((s) => String(s.poolKey || "") === poolKey);
        }
        // If not found by poolKey, try selectedStreak (for preview before pool creation)
        if (!streak && selectedStreak) {
          streak = selectedStreak;
        }
        if (!streak) {
          setClaimCalcError(`Streak not found for poolKey "${poolKey}".`);
          return;
        }
        
        const streakId = String(streak.streakId || "");
        
        // Get all streak states for this streak
        const streakStatesForStreak = (streakStates || []).filter((s) => String(s.streakId || "") === streakId);
        
        // For Streak Pools, eligibility is based on StreakState status:
        // Status 1 (ACTIVE) = Not eligible (streak in progress)
        // Status 2 (COMPLETED) = Eligible for claim
        // Status 3 (CLAIMED) = Already claimed
        const eligibleAccountsSet = new Set();
        for (const state of streakStatesForStreak) {
          const userId = String(state.userId || "");
          if (!userId) continue;
          
          const status = Number(state.status || 0);
          // Only status 2 (COMPLETED) is eligible
          if (status === 2) {
            eligibleAccountsSet.add(userId);
          }
        }
        
        console.log("[ClaimCalc DEBUG poolType 4] streakId:", streakId);
        console.log("[ClaimCalc DEBUG poolType 4] streakStatesForStreak count:", streakStatesForStreak.length);
        console.log("[ClaimCalc DEBUG poolType 4] eligibleAccountsSet size:", eligibleAccountsSet.size);
        
        // Filter assets: Only assets owned by accounts with eligible streak status (only for NFT-based claims)
        if (!isAccountBasedClaim) {
          eligibleAfterRules = eligibleAfterRules.filter((asset) => {
            const owner = String(asset?.owner || "");
            return owner && eligibleAccountsSet.has(owner);
          });
        }
        // For account-based claims, eligibleAfterRules is already empty, eligibleAccountsSet contains all eligible accounts
      } else if (poolType === 6 && poolKey) {
        const stakeScopeRow =
          selectedPool && String(selectedPool.poolKey || "").trim() === poolKey ? selectedPool : { poolHash: "" };
        const scope = derivePoolScope(stakeScopeRow);
        if (!scope) {
          setClaimCalcError(
            "Stake Claim (type 6): calculator needs on-chain poolHash for full eligibility filtering. Save is still possible."
          );
        } else {
          const stakeRows = await fetchAllRows(rpc, { code: CONTRACT, scope, table: "nstakelig", limit: 1000 });
          const eligibleStake = new Set(
            (Array.isArray(stakeRows) ? stakeRows : [])
              .filter((r) => r && (r.eligible === true || r.eligible === 1))
              .map((r) => String(r.account || "").trim())
              .filter(Boolean)
          );
          if (!isAccountBasedClaim) {
            eligibleAfterRules = eligibleAfterRules.filter((asset) => {
              const owner = String(asset?.owner || "").trim();
              return owner && eligibleStake.has(owner);
            });
          } else {
            eligibleAfterRules = Array.from(eligibleStake).map((acct) => {
              const aid = nameToUint64(acct).toString();
              return { owner: acct, asset_id: aid, assetId: aid };
            });
          }
        }
      }

      const eligibleIds = new Set(eligibleAfterRules.map(getAssetId));
      const claimedEligibleCount = Array.from(claimedSet).filter((id) => eligibleIds.has(String(id))).length;
      const remainingEligibleCount = Math.max(0, eligibleAfterRules.length - claimedEligibleCount);
      
      // Count unique wallets that have at least one eligible NFT
      const eligibleWalletsSet = new Set();
      
      // For account-based poolType 2, count from all potential accounts (we can't enumerate all, so we skip this for now)
      // The wallet count will be based on actual claims
      if (isAccountBasedClaim && poolType === 2) {
        // For account-based promotion pools, we can't know all eligible accounts upfront
        // The eligibleWalletsCount will be 0 initially, but that's okay - the calculation is based on maxClaimCount
        console.log("[ClaimCalc DEBUG poolType 2] Account-based: eligibleWalletsSet will remain empty (unlimited potential claims)");
      } else if (poolType === 2 && allEligibleWalletsForPoolType2.size > 0) {
        // Get wallets that have already claimed
        const walletsForPool = (wallets || []).filter((w) => String(w.poolKey || "") === poolKey);
        const claimedWalletsSet = new Set(walletsForPool.map((w) => String(w.account || "")));
        
        console.log("[ClaimCalc DEBUG poolType 2] allEligibleWalletsForPoolType2 size:", allEligibleWalletsForPoolType2.size);
        console.log("[ClaimCalc DEBUG poolType 2] claimedWalletsSet size (in wallet count):", claimedWalletsSet.size);
        
        // Count only wallets that haven't claimed yet
        for (const account of allEligibleWalletsForPoolType2) {
          if (!claimedWalletsSet.has(account)) {
            eligibleWalletsSet.add(account);
          }
        }
        console.log("[ClaimCalc DEBUG poolType 2] eligibleWalletsSet size (after filtering out claimed):", eligibleWalletsSet.size);
        console.log("[ClaimCalc DEBUG poolType 2] eligibleWalletsSet sample:", Array.from(eligibleWalletsSet).slice(0, 5));
      } else {
        // For other pool types, count from eligibleAfterRules
        for (const asset of eligibleAfterRules) {
          const owner = String(asset?.owner || "");
          if (owner) eligibleWalletsSet.add(owner);
        }
        if (poolType === 2) {
          console.log("[ClaimCalc DEBUG poolType 2] WARNING: allEligibleWalletsForPoolType2 is empty, using eligibleAfterRules instead");
          console.log("[ClaimCalc DEBUG poolType 2] eligibleAfterRules length:", eligibleAfterRules.length);
        }
      }
      
      // For Pool Type 3, also count wallets that have eligible votes (even if they don't have assets anymore)
      if (poolType === 3) {
        // Try to find survey by poolKey first (if pool already exists)
        // Otherwise use selectedSurvey (if pool not yet created)
        let survey = null;
        if (poolKey) {
          survey = (surveys || []).find((s) => String(s.poolKey || "") === poolKey);
        }
        // If not found by poolKey, try selectedSurvey (for preview before pool creation)
        if (!survey && selectedSurvey) {
          survey = selectedSurvey;
        }
        if (survey) {
          const surveyKey = String(survey.surveyKey || "");
          const surveyType = Number(survey.surveyType || 1);
          const surveyVotesForSurvey = (surveyVotes || []).filter((v) => String(v.surveyKey || "") === surveyKey);
          
          for (const vote of surveyVotesForSurvey) {
            const account = String(vote.account || "");
            if (!account) continue;
            
            let isEligible = false;
            if (surveyType === 1) {
              isEligible = true;
            } else if (surveyType === 2) {
              const requiredAnswers = String(survey.requiredAnswers || "").trim();
              if (!requiredAnswers) {
                isEligible = true;
              } else {
                const requiredList = requiredAnswers.split(",").map((s) => s.trim()).filter(Boolean);
                const voteAnswer = String(vote.answer || "").trim();
                const voteAnswers = voteAnswer.split(",").map((s) => s.trim()).filter(Boolean);
                isEligible = requiredList.every((req) => voteAnswers.includes(req));
              }
            } else if (surveyType === 3) {
              const winnerRank = Number(vote.winnerRank || 0);
              isEligible = winnerRank > 0;
            }
            
            if (isEligible) {
              eligibleWalletsSet.add(account);
            }
          }
        }
      }
      
      // For Pool Type 4 (Streak), also count wallets that have completed streak status
      if (poolType === 4) {
        let streak = null;
        if (poolKey) {
          streak = (streaks || []).find((s) => String(s.poolKey || "") === poolKey);
        }
        if (!streak && selectedStreak) {
          streak = selectedStreak;
        }
        if (streak) {
          const streakId = String(streak.streakId || "");
          const streakStatesForStreak = (streakStates || []).filter((s) => String(s.streakId || "") === streakId);
          
          for (const state of streakStatesForStreak) {
            const userId = String(state.userId || "");
            if (!userId) continue;
            
            const status = Number(state.status || 0);
            // Only status 2 (COMPLETED) is eligible
            if (status === 2) {
              eligibleWalletsSet.add(userId);
            }
          }
          
          console.log("[ClaimCalc DEBUG poolType 4] eligibleWalletsSet size (after streak state check):", eligibleWalletsSet.size);
        }
      }
      
      // For account-based poolType 2 (Promotion), eligible wallets can't be determined from NFTs
      // Use promotion maxClaimCount as the potential maximum instead
      let eligibleWalletsCount = eligibleWalletsSet.size;
      if (isAccountBasedClaim && poolType === 2) {
        const promotion = (promotions || []).find((p) => String(p.poolKey || "") === poolKey);
        if (promotion) {
          const maxClaimCount = Number(promotion.maxClaimCount || 0);
          // For account-based claims, we can't know all eligible accounts, so use maxClaimCount as estimate
          // The actual eligible count is unlimited in theory, but limited by maxClaimCount
          eligibleWalletsCount = maxClaimCount;
          console.log("[ClaimCalc DEBUG poolType 2] Account-based: Using maxClaimCount as eligibleWalletsCount:", eligibleWalletsCount);
        }
      }
      
      if (poolType === 2) {
        console.log("[ClaimCalc DEBUG poolType 2] FINAL eligibleWalletsCount:", eligibleWalletsCount);
      }

      // Check if cooldown is set to max (4294967295) or poolType is 2, 3, or 4 - if so, calculate based on wallets, not NFTs
      const cooldownSecRaw = String(poolFormData?.cooldownSec || "0").trim();
      const isMaxCooldown = cooldownSecRaw === "4294967295";
      const isPoolType2 = poolType === 2; // Promotion pools are wallet-based (one claim per wallet)
      const isPoolType3 = poolType === 3; // Survey pools are wallet-based (one claim per wallet)
      const isPoolType4 = poolType === 4; // Streak pools are wallet-based (one claim per wallet per streak completion)
      const calculateByWallets =
        isMaxCooldown || isPoolType2 || isPoolType3 || isPoolType4 || (poolType === 6 && isAccountBasedClaim);
      
      // Count unique wallets that have already claimed (for wallet-based calculation)
      const claimedWalletsSet = new Set();
      if (calculateByWallets) {
        for (const claim of claimsForCalc || []) {
          const account = String(claim?.account || "");
          if (account) claimedWalletsSet.add(account);
        }
      }
      const claimedWalletsCount = claimedWalletsSet.size;
      const remainingEligibleWallets = calculateByWallets 
        ? Math.max(0, eligibleWalletsCount - claimedWalletsCount)
        : null;

      // Token sums - use form data for reward
      const rewardStr = normalizeAssetInputForSymbol(
        String(poolFormData?.rewardAmount || "").trim(),
        String(poolFormData?.rewardTokenSymbolRaw || "").trim()
      );
      const rewardParsed = parseAssetString(rewardStr);
      const claimedSum = (claimsForCalc || []).reduce(
        (acc, r) => {
          const p = parseAssetString(r?.amount);
          if (!p || !rewardParsed) return acc;
          if (p.symbol !== rewardParsed.symbol || p.precision !== rewardParsed.precision) return acc;
          return acc + p.amountInt;
        },
        0n
      );
      
      // If max cooldown or poolType 3, calculate based on wallets (one claim per wallet), otherwise based on NFTs
      const totalPotential = rewardParsed 
        ? (calculateByWallets 
          ? rewardParsed.amountInt * BigInt(eligibleWalletsCount)
          : rewardParsed.amountInt * BigInt(eligibleAfterRules.length))
        : 0n;
      const remainingPotential = rewardParsed
        ? (calculateByWallets
          ? rewardParsed.amountInt * BigInt(remainingEligibleWallets || 0)
          : rewardParsed.amountInt * BigInt(remainingEligibleCount))
        : 0n;

      setClaimCalc({
        updatedAt: Date.now(),
        poolCollection: collectionName,
        poolAssetsTotal: allPoolAssetsRaw.length,
        poolAssetsConsidered: allPoolAssets.length,
        excludedAssetsCount: excludedInPoolCount,
        eligibleNfts: eligibleAfterRules.length,
        eligibleWallets: eligibleWalletsCount,
        claimedNfts: claimedSet.size,
        claimedAccounts: claimedAccountsSet.size,
        claimedEligibleNfts: claimedEligibleCount,
        remainingEligibleNfts: remainingEligibleCount,
        reward: rewardParsed,
        totalPotential,
        remainingPotential,
        claimedSum,
        activeRulesCount: activeRules.length,
        poolImmutableRulesCount: poolImmReqs.length,
        poolMutableRulesCount: poolMutReqs.length,
        isMaxCooldown: calculateByWallets,
      });
    } catch (e) {
      setClaimCalcError(serializeError(e).message);
    } finally {
      setClaimCalcLoading(false);
    }
  }

async function refreshClaimCalculator() {
    console.log("[ClaimCalc DEBUG] refreshClaimCalculator called");
    console.log("[ClaimCalc DEBUG] selectedPool:", selectedPool);
    console.log("[ClaimCalc DEBUG] selectedPool.poolType:", selectedPool?.poolType);
    console.log("[ClaimCalc DEBUG] selectedPool.poolKey:", selectedPool?.poolKey);
    
    setClaimCalcError("");
    setClaimCalc(null);
    if (!selectedPool) return;
    
    // Check if pool is inactive - set warning but continue calculation
    const isPoolInactive = selectedPool?.active === false || selectedPool?.active === "0" || selectedPool?.active === 0;
    if (isPoolInactive) {
      setClaimCalcError("This pool is inactive. No NFTs are eligible for claims.");
    }
    
    const poolCollectionRaw = String(selectedPool.collectionName || "").trim();
    if (!poolCollectionRaw) {
      setClaimCalcError("Pool is missing collectionName.");
      return;
    }
    
    // Check if this is an account-based claim (collectionName = "account" for poolType 2, 3, 4, or 6)
    const poolKey = String(selectedPool.poolKey || "").trim();
    const poolType = Number(selectedPool.poolType || 1);
    const isAccountBasedClaim =
      (poolType === 2 || poolType === 3 || poolType === 4 || poolType === 6) && poolCollectionRaw.toLowerCase() === "account";
    
    const poolNorm = normalizeAtomicCollectionName(poolCollectionRaw);
    const collectionName = poolNorm.atomicName || poolCollectionRaw;
    
    // For account-based claims (poolType 2/3 with collectionName="account"), skip AtomicAssets validation
    // For reward pools (poolType 1), always validate AtomicAssets naming
    if (!isAccountBasedClaim && !poolNorm.atomicName) {
      setClaimCalcError(`Pool collection "${poolCollectionRaw}" does not match AtomicAssets naming rules, so the claim calculator cannot query AtomicAssets.`);
      return;
    }
    setClaimCalcLoading(true);
    try {
      const poolImmReqs = selectedPool.requiredImmutable || [];
      const poolMutReqs = poolSupportsRequiredMutable(selectedPool.poolType, selectedPool.collectionName)
        ? requiredMutableForSelectedPool
        : [];

      const invalidRuleCollections = [];
      const activeRules = (reqForPool || [])
        .filter((r) => !!r?.active)
        .map((r) => {
          const raw = String(r.collectionName || "").trim();
          const norm = normalizeAtomicCollectionName(raw);
          const atomicName = norm.atomicName;
          if (raw && !atomicName) invalidRuleCollections.push(raw);
          return {
            reqId: String(r.reqId || ""),
            collectionNameRaw: raw,
            collectionName: atomicName,
            nftcount: Number(r.nftcount || 1),
            reqPairs: parseImmutableRequirements(r.requiredImmutable || []),
          };
        })
        .filter((r) => r.collectionName && r.nftcount > 0);

      if (invalidRuleCollections.length) {
        setClaimCalcError(
          `Claim calculator cannot query AtomicAssets for Dependent NFTs collection(s): ${invalidRuleCollections.join(", ")}. Please enter valid AtomicAssets collection names (lowercase a-z, 1-5, dot, max 12 chars).`
        );
        return;
      }

      // For account-based claims (poolType 2/3 with collectionName="account"), skip NFT fetching/validation
      // For reward pools (poolType 1), always fetch and validate NFTs
      // NOTE: This can be heavy on very large collections.
const collectionCache = new Map();

const excludedIdSet = new Set(
  (Array.isArray(excludedAssets) ? excludedAssets : [])
    .map((r) => String(r?.assetId ?? r?.asset_id ?? ""))
    .filter(Boolean)
);

let allPoolAssetsRaw = [];
let allPoolAssets = [];
let excludedInPoolCount = 0;
let poolMatchedAssets = [];

if (isAccountBasedClaim) {
  // Account-based: No NFT fetching needed
  allPoolAssetsRaw = [];
  allPoolAssets = [];
  excludedInPoolCount = 0;
  poolMatchedAssets = [];
} else {
  // Normal NFT-based claim: Fetch assets from AtomicAssets
  allPoolAssetsRaw = await fetchAllAtomicAssets({ collectionName, limit: 1000, maxPages: 200 });
  allPoolAssets = excludedIdSet.size
    ? allPoolAssetsRaw.filter((a) => !excludedIdSet.has(String(a?.asset_id || a?.assetId || "")))
    : allPoolAssetsRaw;
  excludedInPoolCount = allPoolAssetsRaw.length - allPoolAssets.length;

  collectionCache.set(collectionName, allPoolAssets);
  poolMatchedAssets = allPoolAssets.filter((a) => matchesPoolAssetRequirements(a, poolImmReqs, poolMutReqs));
}


const claimsArr = Array.isArray(claims) ? claims : [];
const claimsForCalc = excludedIdSet.size ? claimsArr.filter((r) => !excludedIdSet.has(String(r?.assetId || ""))) : claimsArr;

const claimedSet = new Set(claimsForCalc.map((r) => String(r?.assetId || "")));
const claimedAccountsSet = new Set(claimsForCalc.map((r) => String(r?.account || "")).filter(Boolean));


      async function getCollectionAssets(coll) {
        const c = String(coll || "").trim();
        if (!c) return [];
        if (collectionCache.has(c)) return collectionCache.get(c);
        const assets = await fetchAllAtomicAssets({ collectionName: c, limit: 1000, maxPages: 200 });
        collectionCache.set(c, assets);
        return assets;
      }

      // For account-based claims, skip dependent NFT rules
      const ruleOwnerCounts = isAccountBasedClaim ? [] : await mapLimit(activeRules, 2, async (rule) => {
        const assets = await getCollectionAssets(rule.collectionName);
        const matched = assets.filter((a) => matchesRequirements(a, rule.reqPairs));
        const counts = new Map();
        for (const a of matched) {
          const o = String(a?.owner || "");
          if (!o) continue;
          counts.set(o, (counts.get(o) || 0) + 1);
        }
        return { rule, counts };
      });

      function getAssetCollectionName(a) {
        return String(a?.collection_name || a?.collection?.collection_name || a?.collection?.collectionName || "");
      }

      function getAssetId(a) {
        return String(a?.asset_id || a?.assetId || "");
      }

      // For account-based claims, eligibleAfterRules is empty (no NFT validation)
      let eligibleAfterRules = isAccountBasedClaim
        ? []
        : (activeRules.length === 0
          ? poolMatchedAssets
          : await (async () => {
              const list = poolMatchedAssets;
              const checks = await mapLimit(list, 10, async (asset) => {
                const owner = String(asset?.owner || "");
                if (!owner) return false;
                for (const item of ruleOwnerCounts) {
                  const rule = item.rule;
                  const baseCount = item.counts.get(owner) || 0;
                  // emulate excludeAssetId behavior (best effort)
                  let effCount = baseCount;
                  if (rule.collectionName === getAssetCollectionName(asset) && matchesRequirements(asset, rule.reqPairs)) {
                    effCount = Math.max(0, effCount - 1);
                  }
                  if (effCount < rule.nftcount) return false;
                }
                return true;
              });
              return list.filter((_, i) => !!checks[i]);
            })());
      
      // DEBUG: Log eligibleAfterRules after dependent NFT rules
      console.log("[ClaimCalc DEBUG] poolType:", poolType);
      console.log("[ClaimCalc DEBUG] eligibleAfterRules length (after dependent NFT rules):", eligibleAfterRules.length);
      console.log("[ClaimCalc DEBUG] activeRules count:", activeRules.length);
      console.log("[ClaimCalc DEBUG] poolKey:", poolKey);
      console.log("[ClaimCalc DEBUG] eligibleAfterRules BEFORE poolType filter:", eligibleAfterRules.length);
      
      if (poolType === 2) {
        console.log("[ClaimCalc DEBUG poolType 2 - refresh] promotions array length:", (promotions || []).length);
        console.log("[ClaimCalc DEBUG poolType 2 - refresh] wallets array length:", (wallets || []).length);
      } else if (poolType === 3) {
        console.log("[ClaimCalc DEBUG poolType 3 - refresh] surveys array length:", (surveys || []).length);
        console.log("[ClaimCalc DEBUG poolType 3 - refresh] surveyVotes array length:", (surveyVotes || []).length);
        console.log("[ClaimCalc DEBUG poolType 3 - refresh] selectedSurvey:", selectedSurvey);
      }
      
      // For poolType 2, we need to count wallets BEFORE filtering, then filter assets
      let allEligibleWalletsForPoolType2 = new Set();
      if (poolType === 2 && poolKey) {
        if (isAccountBasedClaim) {
          // Account-based: All accounts are potentially eligible (no NFT validation needed)
          // We can't know all possible accounts, so we'll rely on the wallet count from claims/claims table
          // For now, we'll use an empty set and count from claims later if needed
          console.log("[ClaimCalc DEBUG poolType 2 - refresh] Account-based claim: skipping NFT-based wallet counting");
        } else {
          // NFT-based: Count all wallets that have eligible NFTs (before filtering by claims)
          for (const asset of eligibleAfterRules) {
            const owner = String(asset?.owner || "");
            if (owner) allEligibleWalletsForPoolType2.add(owner);
          }
        }
        console.log("[ClaimCalc DEBUG poolType 2 - refresh] allEligibleWalletsForPoolType2 size (before claim filter):", allEligibleWalletsForPoolType2.size);
      }
      
      if (poolType === 2 && poolKey) {
        // Pool Type 2 (Promotion Pool): Additional filters
        const promotion = (promotions || []).find((p) => String(p.poolKey || "") === poolKey);
        console.log("[ClaimCalc DEBUG poolType 2 - refresh] promotion found:", !!promotion);
        if (promotion) {
          console.log("[ClaimCalc DEBUG poolType 2 - refresh] promotion.promofinished:", promotion.promofinished);
          console.log("[ClaimCalc DEBUG poolType 2 - refresh] promotion.actualClaimCount:", promotion.actualClaimCount);
          console.log("[ClaimCalc DEBUG poolType 2 - refresh] promotion.maxClaimCount:", promotion.maxClaimCount);
        }
        if (!promotion) {
          setClaimCalcError(`Promotion not found for poolKey "${poolKey}".`);
          return;
        }
        
        // Check promotion status
        if (promotion.promofinished) {
          setClaimCalcError("Promotion is finished. No more claims allowed.");
          return;
        }
        
        const actualClaimCount = Number(promotion.actualClaimCount || 0);
        const maxClaimCount = Number(promotion.maxClaimCount || 0);
        if (actualClaimCount >= maxClaimCount) {
          setClaimCalcError(`Promotion claim limit reached (${actualClaimCount}/${maxClaimCount}).`);
          return;
        }
        
        // Filter: Only wallets that haven't claimed yet (via NWallet table, cooldownSec = COOLDOWN_FOREVER)
        const walletsForPool = (wallets || []).filter((w) => String(w.poolKey || "") === poolKey);
        console.log("[ClaimCalc DEBUG poolType 2 - refresh] walletsForPool length:", walletsForPool.length);
        const claimedWalletsSet = new Set(walletsForPool.map((w) => String(w.account || "")));
        console.log("[ClaimCalc DEBUG poolType 2 - refresh] claimedWalletsSet size:", claimedWalletsSet.size);
        console.log("[ClaimCalc DEBUG poolType 2 - refresh] claimedWalletsSet sample:", Array.from(claimedWalletsSet).slice(0, 5));
        
        eligibleAfterRules = eligibleAfterRules.filter((asset) => {
          const owner = String(asset?.owner || "");
          return owner && !claimedWalletsSet.has(owner);
        });
        console.log("[ClaimCalc DEBUG poolType 2 - refresh] eligibleAfterRules AFTER claim filter:", eligibleAfterRules.length);
      } else if (poolType === 3) {
        // Pool Type 3 (Survey Pool): Additional filters
        console.log("[ClaimCalc DEBUG poolType 3 - refresh] Starting survey pool filter logic");
        
        // Try to find survey by poolKey first (if pool already exists)
        // Otherwise use selectedSurvey (if pool not yet created)
        let survey = null;
        if (poolKey) {
          survey = (surveys || []).find((s) => String(s.poolKey || "") === poolKey);
          console.log("[ClaimCalc DEBUG poolType 3 - refresh] Survey found by poolKey:", !!survey);
        }
        // If not found by poolKey, try selectedSurvey (for preview before pool creation)
        if (!survey && selectedSurvey) {
          survey = selectedSurvey;
          console.log("[ClaimCalc DEBUG poolType 3 - refresh] Using selectedSurvey");
        }
        if (!survey) {
          console.log("[ClaimCalc DEBUG poolType 3 - refresh] ERROR: Survey not found");
          setClaimCalcError(`Survey not found. Please select a survey first.`);
          return;
        }
        
        const surveyKey = String(survey.surveyKey || "");
        const surveyType = Number(survey.surveyType || 1);
        console.log("[ClaimCalc DEBUG poolType 3 - refresh] surveyKey:", surveyKey);
        console.log("[ClaimCalc DEBUG poolType 3 - refresh] surveyType:", surveyType);
        
        // Get all votes for this survey
        const surveyVotesForSurvey = (surveyVotes || []).filter((v) => String(v.surveyKey || "") === surveyKey);
        console.log("[ClaimCalc DEBUG poolType 3 - refresh] surveyVotesForSurvey length:", surveyVotesForSurvey.length);
        
        const votesByAccount = new Map();
        for (const vote of surveyVotesForSurvey) {
          const account = String(vote.account || "");
          if (account) {
            votesByAccount.set(account, vote);
          }
        }
        
        // For Survey Pools, we need to check votes, not just assets
        // First, identify all accounts with eligible votes
        const eligibleAccountsSet = new Set();
        for (const vote of surveyVotesForSurvey) {
          const account = String(vote.account || "");
          if (!account) continue;
          
          let isEligible = false;
          if (surveyType === 1) {
            // Normal: Vote exists → Claim allowed
            isEligible = true;
          } else if (surveyType === 2) {
            // Answer-based: Check if vote.answer contains all required answers
            const requiredAnswers = String(survey.requiredAnswers || "").trim();
            if (!requiredAnswers) {
              // If requiredAnswers is empty, all participants can claim
              isEligible = true;
            } else {
              const requiredList = requiredAnswers.split(",").map((s) => s.trim()).filter(Boolean);
              const voteAnswer = String(vote.answer || "").trim();
              const voteAnswers = voteAnswer.split(",").map((s) => s.trim()).filter(Boolean);
              isEligible = requiredList.every((req) => voteAnswers.includes(req));
            }
          } else if (surveyType === 3) {
            // Raffle: Only winners can claim (winnerRank > 0)
            const winnerRank = Number(vote.winnerRank || 0);
            isEligible = winnerRank > 0;
          }
          
          if (isEligible) {
            eligibleAccountsSet.add(account);
          }
        }
        console.log("[ClaimCalc DEBUG poolType 3 - refresh] eligibleAccountsSet size:", eligibleAccountsSet.size);
        console.log("[ClaimCalc DEBUG poolType 3 - refresh] eligibleAccountsSet sample:", Array.from(eligibleAccountsSet).slice(0, 5));
        
        // Filter assets: Only assets owned by accounts with eligible votes (only for NFT-based claims)
        if (!isAccountBasedClaim) {
          eligibleAfterRules = eligibleAfterRules.filter((asset) => {
            const owner = String(asset?.owner || "");
            return owner && eligibleAccountsSet.has(owner);
          });
        }
        // For account-based claims, eligibleAfterRules is already empty, eligibleAccountsSet contains all eligible accounts
        console.log("[ClaimCalc DEBUG poolType 3 - refresh] eligibleAfterRules AFTER vote filter:", eligibleAfterRules.length);
      } else if (poolType === 6 && poolKey) {
        const scope = derivePoolScope(selectedPool);
        if (!scope) {
          setClaimCalcError("Stake Claim: pool has no poolHash; cannot load nstakelig.");
          return;
        }
        const stakeRows = await fetchAllRows(rpc, { code: CONTRACT, scope, table: "nstakelig", limit: 1000 });
        const eligibleStake = new Set(
          (Array.isArray(stakeRows) ? stakeRows : [])
            .filter((r) => r && (r.eligible === true || r.eligible === 1))
            .map((r) => String(r.account || "").trim())
            .filter(Boolean)
        );
        if (!isAccountBasedClaim) {
          eligibleAfterRules = eligibleAfterRules.filter((asset) => {
            const owner = String(asset?.owner || "").trim();
            return owner && eligibleStake.has(owner);
          });
        } else {
          eligibleAfterRules = Array.from(eligibleStake).map((acct) => {
            const aid = nameToUint64(acct).toString();
            return { owner: acct, asset_id: aid, assetId: aid };
          });
        }
      }

      const eligibleIds = new Set(eligibleAfterRules.map(getAssetId));
      const claimedEligibleCount = Array.from(claimedSet).filter((id) => eligibleIds.has(String(id))).length;
      const remainingEligibleCount = Math.max(0, eligibleAfterRules.length - claimedEligibleCount);
      
      // Count unique wallets that have at least one eligible NFT
      const eligibleWalletsSet = new Set();
      
      // For account-based poolType 2, count from all potential accounts (we can't enumerate all, so we skip this for now)
      // The wallet count will be based on actual claims
      if (isAccountBasedClaim && poolType === 2) {
        // For account-based promotion pools, we can't know all eligible accounts upfront
        // The eligibleWalletsCount will be 0 initially, but that's okay - the calculation is based on maxClaimCount
        console.log("[ClaimCalc DEBUG poolType 2 - refresh] Account-based: eligibleWalletsSet will remain empty (unlimited potential claims)");
      } else if (poolType === 2 && allEligibleWalletsForPoolType2.size > 0) {
        // Get wallets that have already claimed
        const walletsForPool = (wallets || []).filter((w) => String(w.poolKey || "") === poolKey);
        const claimedWalletsSet = new Set(walletsForPool.map((w) => String(w.account || "")));
        
        console.log("[ClaimCalc DEBUG poolType 2 - refresh] allEligibleWalletsForPoolType2 size:", allEligibleWalletsForPoolType2.size);
        console.log("[ClaimCalc DEBUG poolType 2 - refresh] claimedWalletsSet size (in wallet count):", claimedWalletsSet.size);
        
        // Count only wallets that haven't claimed yet
        for (const account of allEligibleWalletsForPoolType2) {
          if (!claimedWalletsSet.has(account)) {
            eligibleWalletsSet.add(account);
          }
        }
        console.log("[ClaimCalc DEBUG poolType 2 - refresh] eligibleWalletsSet size (after filtering out claimed):", eligibleWalletsSet.size);
        console.log("[ClaimCalc DEBUG poolType 2 - refresh] eligibleWalletsSet sample:", Array.from(eligibleWalletsSet).slice(0, 5));
      } else {
        // For other pool types, count from eligibleAfterRules
        for (const asset of eligibleAfterRules) {
          const owner = String(asset?.owner || "");
          if (owner) eligibleWalletsSet.add(owner);
        }
        if (poolType === 2) {
          console.log("[ClaimCalc DEBUG poolType 2 - refresh] WARNING: allEligibleWalletsForPoolType2 is empty, using eligibleAfterRules instead");
          console.log("[ClaimCalc DEBUG poolType 2 - refresh] eligibleAfterRules length:", eligibleAfterRules.length);
        }
      }
      
      // For Pool Type 3, also count wallets that have eligible votes (even if they don't have assets anymore)
      if (poolType === 3) {
        console.log("[ClaimCalc DEBUG poolType 3 - refresh] Counting wallets with eligible votes");
        // Try to find survey by poolKey first (if pool already exists)
        // Otherwise use selectedSurvey (if pool not yet created)
        let survey = null;
        if (poolKey) {
          survey = (surveys || []).find((s) => String(s.poolKey || "") === poolKey);
        }
        // If not found by poolKey, try selectedSurvey (for preview before pool creation)
        if (!survey && selectedSurvey) {
          survey = selectedSurvey;
        }
        if (survey) {
          const surveyKey = String(survey.surveyKey || "");
          const surveyType = Number(survey.surveyType || 1);
          const surveyVotesForSurvey = (surveyVotes || []).filter((v) => String(v.surveyKey || "") === surveyKey);
          console.log("[ClaimCalc DEBUG poolType 3 - refresh] surveyVotesForSurvey length (for wallet count):", surveyVotesForSurvey.length);
          
          for (const vote of surveyVotesForSurvey) {
            const account = String(vote.account || "");
            if (!account) continue;
            
            let isEligible = false;
            if (surveyType === 1) {
              isEligible = true;
            } else if (surveyType === 2) {
              const requiredAnswers = String(survey.requiredAnswers || "").trim();
              if (!requiredAnswers) {
                isEligible = true;
              } else {
                const requiredList = requiredAnswers.split(",").map((s) => s.trim()).filter(Boolean);
                const voteAnswer = String(vote.answer || "").trim();
                const voteAnswers = voteAnswer.split(",").map((s) => s.trim()).filter(Boolean);
                isEligible = requiredList.every((req) => voteAnswers.includes(req));
              }
            } else if (surveyType === 3) {
              const winnerRank = Number(vote.winnerRank || 0);
              isEligible = winnerRank > 0;
            }
            
            if (isEligible) {
              eligibleWalletsSet.add(account);
            }
          }
          console.log("[ClaimCalc DEBUG poolType 3 - refresh] eligibleWalletsSet size (after adding vote-based wallets):", eligibleWalletsSet.size);
        } else {
          console.log("[ClaimCalc DEBUG poolType 3 - refresh] WARNING: Survey not found for wallet count");
        }
      }
      
      // For account-based poolType 2 (Promotion), eligible wallets can't be determined from NFTs
      // Use promotion maxClaimCount as the potential maximum instead
      let eligibleWalletsCount = eligibleWalletsSet.size;
      if (isAccountBasedClaim && poolType === 2) {
        const promotion = (promotions || []).find((p) => String(p.poolKey || "") === poolKey);
        if (promotion) {
          const maxClaimCount = Number(promotion.maxClaimCount || 0);
          // For account-based claims, we can't know all eligible accounts, so use maxClaimCount as estimate
          // The actual eligible count is unlimited in theory, but limited by maxClaimCount
          eligibleWalletsCount = maxClaimCount;
          console.log("[ClaimCalc DEBUG poolType 2 - refresh] Account-based: Using maxClaimCount as eligibleWalletsCount:", eligibleWalletsCount);
        }
      }
      
      console.log("[ClaimCalc DEBUG] FINAL eligibleWalletsCount:", eligibleWalletsCount, "(poolType:", poolType + ")");

      // Check if cooldown is set to max (4294967295) or poolType is 2 or 3 - if so, calculate based on wallets, not NFTs
      const cooldownSecRaw = String(selectedPool.cooldownSec || "0").trim();
      const isMaxCooldown = cooldownSecRaw === "4294967295";
      const isPoolType2 = poolType === 2; // Promotion pools are wallet-based (one claim per wallet)
      const isPoolType3 = poolType === 3; // Survey pools are wallet-based (one claim per wallet)
      const calculateByWallets =
        isMaxCooldown || isPoolType2 || isPoolType3 || (poolType === 6 && isAccountBasedClaim);
      
      // Count unique wallets that have already claimed (for wallet-based calculation)
      const claimedWalletsSet = new Set();
      if (calculateByWallets) {
        for (const claim of claimsForCalc || []) {
          const account = String(claim?.account || "");
          if (account) claimedWalletsSet.add(account);
        }
      }
      const claimedWalletsCount = claimedWalletsSet.size;
      const remainingEligibleWallets = calculateByWallets 
        ? Math.max(0, eligibleWalletsCount - claimedWalletsCount)
        : null;

      // Token sums
      const rewardParsed = parseAssetString(selectedPool.reward);
      const claimedSum = (claimsForCalc || []).reduce(
        (acc, r) => {
          const p = parseAssetString(r?.amount);
          if (!p || !rewardParsed) return acc;
          if (p.symbol !== rewardParsed.symbol || p.precision !== rewardParsed.precision) return acc;
          return acc + p.amountInt;
        },
        0n
      );
      
      // If max cooldown or poolType 3, calculate based on wallets (one claim per wallet), otherwise based on NFTs
      const totalPotential = rewardParsed 
        ? (calculateByWallets 
          ? rewardParsed.amountInt * BigInt(eligibleWalletsCount)
          : rewardParsed.amountInt * BigInt(eligibleAfterRules.length))
        : 0n;
      const remainingPotential = rewardParsed
        ? (calculateByWallets
          ? rewardParsed.amountInt * BigInt(remainingEligibleWallets || 0)
          : rewardParsed.amountInt * BigInt(remainingEligibleCount))
        : 0n;

      setClaimCalc({
        updatedAt: Date.now(),
        poolCollection: collectionName,
        poolAssetsTotal: allPoolAssetsRaw.length,
        poolAssetsConsidered: allPoolAssets.length,
        excludedAssetsCount: excludedInPoolCount,
        eligibleNfts: eligibleAfterRules.length,
        eligibleWallets: eligibleWalletsCount,
        claimedNfts: claimedSet.size,
        claimedAccounts: claimedAccountsSet.size,
        claimedEligibleNfts: claimedEligibleCount,
        remainingEligibleNfts: remainingEligibleCount,
        reward: rewardParsed,
        totalPotential,
        remainingPotential,
        claimedSum,
        activeRulesCount: activeRules.length,
        poolImmutableRulesCount: (poolImmReqs || []).length,
        poolMutableRulesCount: (poolMutReqs || []).length,
        isMaxCooldown: calculateByWallets,
      });
    } catch (e) {
      setClaimCalcError(serializeError(e).message);
    } finally {
      setClaimCalcLoading(false);
    }
  }

  async function signFromTxJson() {
    setError("");
    setNotice("");
    setTxBanner(null);
    if (!session || !auth) {
      setError("Please connect your WebAuth wallet first.");
      setTxBanner({ ok: false, message: "Please connect your WebAuth wallet first." });
      return;
    }
    let tx;
    try {
      tx = JSON.parse(String(txJsonText || ""));
    } catch {
      setError("Invalid JSON.");
      return;
    }
    if (!tx || !Array.isArray(tx.actions) || tx.actions.length === 0) {
      setError("Transaction JSON must contain an actions array.");
      return;
    }
    // Safety: contract is fixed to nftrewards
    const bad = tx.actions.find((a) => String(a?.account || "") && String(a.account) !== CONTRACT);
    if (bad) {
      setError(`This UI is locked to contract '${CONTRACT}'. Remove actions for other contracts.`);
      return;
    }
    // Ensure authorization exists
    const patched = {...tx,
      actions: tx.actions.map((a) => {
        const hasAuth = Array.isArray(a.authorization) && a.authorization.length > 0;
        return hasAuth ? a : {...a, authorization: [auth] };
      }),
    };
    try {
      setLastSignedTxJson(JSON.stringify(patched, null, 2));
    } catch {
      // ignore
    }
    setBusy(true);
    try {
      const txResult = await session.transact(patched, { broadcast: true });
      setNotice("Transaction sent successfully.");
      setTxBanner({ ok: true, message: "Transaction successful.", txid: txResult?.transaction_id || txResult?.processed?.id });
      // Refresh dropdown-backed data after any signed transaction
      try {
        await loadCoreState({ silent: true });
      } catch {
        // ignore
      }
    } catch (e) {
      const err = serializeError(e);
      setError(err.message);
      setTxBanner({ ok: false, message: err.message });
    } finally {
      setBusy(false);
      postWalletUiCleanup();
    }
  }


  async function createManualDeposit2() {
    setError("Manual deposit actions are disabled. Reward funding now comes from project vault wallets.");
  }

  async function updateManualDeposit2() {
    setError("Manual deposit actions are disabled. Reward funding now comes from project vault wallets.");
  }

  async function refreshAll() {
    if (!actor) return;
    setError("");
    setNotice("");
    try {
      await loadCoreState({ silent: true });
      try {
        if (selectedProjectId) await loadExcludedAssetsForProject(selectedProjectId, { silent: true });
      } catch {
        // ignore
      }
      // Reload pool-scoped tables even if pool selection didn't change
      if (selectedPool) {
        const scope = derivePoolScope(selectedPool);
        if (scope) {
          setClaimsLoading(true);
          setWalletsLoading(true);
          try {
            const [cRows, wRows] = await Promise.all([
              fetchAllRows(rpc, { code: CONTRACT, scope, table: "nclaims", limit: 1000 }),
              fetchAllRows(rpc, { code: CONTRACT, scope, table: "nwallets", limit: 1000, index_position: 2, key_type: "i64" }),
            ]);
            const filterActor = (rows) => {
              const arr = Array.isArray(rows) ? rows : [];
              if (contractGodMode) return arr;
              const a = String(effectiveActorForFilters || "").trim();
              if (!a) return [];
              return arr.filter((r) => {
                if (r && Object.prototype.hasOwnProperty.call(r, "actor")) return String(r.actor) === a;
                return true;
              });
            };
            setClaims(filterActor(cRows));
            setWallets(filterActor(wRows));
          } finally {
            setClaimsLoading(false);
            setWalletsLoading(false);
          }
        }
      }
      // Reset claim calculator output so users can refresh it explicitly.
      setClaimCalc(null);
      setClaimCalcError("");
      setTxBanner({ ok: true, message: "Refreshed." });
    } catch (e) {
      const err = serializeError(e);
      setError(err.message);
      setTxBanner({ ok: false, message: err.message });
    }
  }

  // Login screen only
  const accessGranted = loggedIn && termsAccepted;

  const wizFull = adminUiMode === "full";
  const isPoolAssistant = adminUiMode === "wizard";
  const isPoolResetAssistant = adminUiMode === "reset";
  const ws = wizardStep;
  const wizOverview = wizFull || (isPoolAssistant && (ws === 1 || ws === 3));
  const wizProject = wizFull || (isPoolAssistant && ws === 2) || isPoolResetAssistant;
  const wizRamQuick = wizFull || (isPoolAssistant && ws === 3);
  const wizManualDep = false;
  const wizPoolMain = wizFull || (isPoolAssistant && ws === 4);
  const wizPoolSelect = wizFull || (isPoolAssistant && ws === 4) || isPoolResetAssistant;
  const wizPoolAux = wizFull || isPoolResetAssistant;
  const wizDepNft = wizFull || (isPoolAssistant && ws === 5);
  const isStakeClaimPool =
    Number(selectedPool?.poolType ?? poolForm?.poolType ?? 0) === 6;
  const wizStakeClaim = wizDepNft && isStakeClaimPool;
  const wizExcl = wizFull || (isPoolAssistant && ws === 6);
  const wizVerify = wizFull || (isPoolAssistant && ws === 7);
  const wizPermissionGuide = isPoolAssistant && ws === 8;
  const wizRest = wizFull;

  useEffect(() => {
    if (adminUiMode !== "wizard" || !accessGranted) return;
    const stepOpen = {
      1: { allowedTokens: true, ramBudgets: true, ramFees: true, deposits: true },
      2: { project: true },
      3: { allowedTokens: true, ramBudgets: true, ramFees: true, deposits: true, ramQuick: true },
      4: { pool: true },
      5: {
        depNfts: true,
        ...(Number(selectedPool?.poolType ?? poolForm?.poolType ?? 0) === 6
          ? { stakeSnap: true, stakeElig: true }
          : {}),
      },
      6: { exclAssets: true },
      7: { claimCalc: true, claims: true, wallets: true },
      8: { permissionGuide: true },
    };
    const anchor = {
      1: "nr-wiz-overview",
      2: "nr-wiz-project",
      3: "nr-wiz-overview",
      4: "nr-wiz-pool",
      5: "nr-wiz-reqnft",
      6: "nr-wiz-excl",
      7: "nr-wiz-verify",
      8: "nr-wiz-permission",
    };
    const patch = stepOpen[wizardStep];
    if (patch) setOpenCards((prev) => ({ ...prev, ...patch }));
    const id = anchor[wizardStep];
    const t = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 280);
    return () => window.clearTimeout(t);
  }, [adminUiMode, wizardStep, accessGranted, selectedPool, poolForm.poolType]);

  useEffect(() => {
    if (adminUiMode !== "reset" || !accessGranted) return;
    setOpenCards((prev) => ({ ...prev, project: true, poolSelect: true, poolReset: true }));
  }, [adminUiMode, accessGranted]);

  // Keep reset pool dropdown aligned with the global pool selection in reset mode.
  useEffect(() => {
    if (adminUiMode !== "reset") return;
    const key = String(selectedPool?.poolKey || "").trim();
    if (!key) return;
    setResetPoolForm((s) => (s.poolKey === key ? s : { ...s, poolKey: key }));
  }, [adminUiMode, selectedPool?.poolKey]);

  const headerProjectLabel = selectedProject
    ? String(selectedProject.projectName || ("#" + String(selectedProject.projectId ?? "")))
    : "—";
  const headerPoolLabel = selectedPool
    ? (() => {
        const poolKey = String(selectedPool.poolKey ?? selectedPool.pool_key ?? selectedPool.poolkey ?? "").trim() || "—";
        const poolTypeLabel = getPoolTypeLabel(selectedPool.poolType);
        return poolKey !== "—" ? `${poolKey} (${poolTypeLabel})` : poolKey;
      })()
    : "—";
  
  const headerVaultLabel = selectedProject
    ? String((vaultByProjectId.get(String(selectedProject.projectId)) || {}).vaultAccount || "—")
    : "—";
const headerContextTitle = `Project: ${headerProjectLabel} | Pool: ${headerPoolLabel} | Vault: ${headerVaultLabel}`;

  // Claim preview modal handlers
  const handleClaimPreviewConfirm = useCallback(async () => {
    // In info mode, confirm behaves like OK (just close).
    if (claimPreviewMode !== "save") {
      setClaimPreviewModalOpen(false);
      setPendingSaveAction(null);
      setClaimCalc(null);
      setClaimCalcError("");
      return;
    }

    if (!pendingSaveAction || !pendingSaveAction.action) return;

    setBusy(true);
    try {
      setError("");
      const saved = await pendingSaveAction.action();
      if (saved === false) return;
      setClaimPreviewModalOpen(false);
      setPendingSaveAction(null);
      setClaimCalc(null);
      setClaimCalcError("");
    } finally {
      setBusy(false);
    }
  }, [claimPreviewMode, pendingSaveAction]);

  const handleClaimPreviewCancel = useCallback(() => {
    setClaimPreviewModalOpen(false);
    setPendingSaveAction(null);
    setClaimCalc(null);
    setClaimCalcError("");
  }, []);

  return (
    <div className="nr-app">
      <header className="nr-header">
        <div className="nr-headerInner">
          <div className="nr-brand">
            <img className="nr-logo" src={APP_LOGO} alt="NFT Claim Hub" />
            <div className="nr-titleWrap">
              <div className="nr-title">
                NFT Claim Hub
                <span className="nr-pill" style={{ marginLeft: 10 }}>
                  <span className="nr-pillDot" aria-hidden="true" /> by Bloxprod
                </span>
              </div>
              <div className="nr-sub">Manage projects, pools and reward claims on {CONTRACT}.</div>
            </div>
          </div>

          <div className="nr-mid">
            <TransactionBanner txBanner={txBanner} />

            <div className="nr-context" title={headerContextTitle}>
              Project: <b>{headerProjectLabel}</b> <span className="nr-contextSep">•</span> Pool: <b>{headerPoolLabel}</b> <span className="nr-contextSep">•</span> Vault: <b>{headerVaultLabel}</b>
            </div>
          </div>

          <div className="nr-right">
            {isContract && loggedIn ? (
              <div
                className="nr-contractViewAs"
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  alignItems: "center",
                  marginRight: 12,
                  maxWidth: 520,
                }}
              >
                <label className="nr-muted" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                  View as project
                  <select
                    className="nr-input"
                    value={contractViewAsProjectId}
                    onChange={(e) => {
                      setContractViewAsProjectId(String(e.target.value || ""));
                      setContractViewAsAccount("");
                    }}
                    disabled={busy || projectsLoading}
                    aria-label="View as project"
                    style={{ minWidth: 140, maxWidth: 220 }}
                  >
                    <option value="">— (full contract view)</option>
                    {contractViewAsProjectOptions.map((p) => (
                      <option key={String(p.projectId)} value={String(p.projectId)}>
                        #{String(p.projectId)} — {String(p.projectName || "").slice(0, 28)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="nr-muted" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                  Allowed account
                  <select
                    className="nr-input"
                    value={contractViewAsAccount}
                    onChange={(e) => setContractViewAsAccount(String(e.target.value || ""))}
                    disabled={busy || !String(contractViewAsProjectId || "").trim()}
                    aria-label="View as allowed account"
                    style={{ minWidth: 140, maxWidth: 200 }}
                  >
                    <option value="">—</option>
                    {viewAsAllowedAccounts.map((acc) => (
                      <option key={acc.toLowerCase()} value={acc}>
                        {acc}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  className="nr-btn"
                  style={{ fontSize: 12, padding: "6px 10px" }}
                  disabled={!String(contractViewAsProjectId || "").trim() && !String(contractViewAsAccount || "").trim()}
                  onClick={() => {
                    setContractViewAsProjectId("");
                    setContractViewAsAccount("");
                  }}
                >
                  Clear
                </button>
              </div>
            ) : null}
            {accessGranted ? (
              <div className="nr-ui-toggle" role="group" aria-label="Admin UI mode" style={{ marginRight: 10 }}>
                <button
                  type="button"
                  className={adminUiMode === "wizard" ? "is-on" : ""}
                  onClick={() => {
                    setAdminUiMode("wizard");
                    setWizardStep(1);
                  }}
                >
                  Pool Assistent
                </button>
                <button
                  type="button"
                  className={adminUiMode === "reset" ? "is-on" : ""}
                  onClick={() => {
                    setAdminUiMode("reset");
                  }}
                >
                  Pool Reset Assistent
                </button>
                <button
                  type="button"
                  className={adminUiMode === "full" ? "is-on" : ""}
                  onClick={() => setAdminUiMode("full")}
                >
                  Full Console
                </button>
              </div>
            ) : null}
            {loggedIn ? (
              <>
                <button className="nr-btn" style={{ marginRight: 10 }} onClick={refreshAll} disabled={busy || projectsLoading}>
                  Refresh
                </button>
                <button className="nr-btn" style={{ marginRight: 10 }} onClick={toggleAllBoxes} disabled={busy}>
                  {allBoxesExpanded ? "Collapse all" : "Expand all"}
                </button>
              </>
            ) : null}
            <a className="nr-helpLink" href="/help/" target="_blank" rel="noreferrer">
              Help
            </a>
            {loggedIn ? (
              <div className="nr-chip">
                Connected: <b>{actor}</b>
                {contractViewAsActive ? (
                  <>
                    {" "}
                    · UI as <b>{contractViewAsAccount}</b> (project #{contractViewAsProjectId})
                  </>
                ) : null}
              </div>
            ) : (
              <div className="nr-chip">Not connected</div>
            )}
          </div>
        </div>
      </header>

      {isContract && loggedIn && contractViewAsActive ? (
        <div
          role="status"
          style={{
            padding: "8px 16px",
            fontSize: 13,
            background: "rgba(15, 23, 42, 0.92)",
            borderBottom: "1px solid rgba(148, 163, 184, 0.28)",
            color: "rgba(241, 245, 249, 0.96)",
          }}
        >
          UI follows allowed account <strong>{String(contractViewAsAccount || "").trim()}</strong> on project{" "}
          <strong>#{String(contractViewAsProjectId || "").trim()}</strong>. Transactions still sign as{" "}
          <code>{actor}</code>.
        </div>
      ) : null}

      <main className="nr-main">
        {/* LOGIN HERO (shown only when not connected) */}
        {!loggedIn ? (
          <section className="nr-hero" aria-label="NFT Claim Hub">
            <img className="nr-heroLogo" src={APP_LOGO} alt="NFT Claim Hub Logo" />
            <div className="nr-heroTagline">
              Connect your WebAuth Wallet to manage projects and pools and claim rewards securely in one place.
            </div>
          </section>
        ) : null}

        {/* CONNECT */}
        <section className="nr-card">
          <div className="nr-cardHeader">
            <div>
              <div className="nr-cardTitle">Connect</div>
              <div className="nr-muted">WebAuth Wallet</div>
            </div>
            <div className="nr-actions">
              {!loggedIn ? (
                <button className="nr-btn nr-btnPrimary" onClick={handleConnectClick} disabled={busy}>
                  Connect WebAuth Wallet
                </button>
              ) : (
                <button className="nr-btn" onClick={disconnect} disabled={busy}>
                  Disconnect
                </button>
              )}
            </div>
          </div>

          {error ? <div className="nr-error">{error}</div> : null}
          {accessGateMsg ? <div className="nr-error nr-preLine">{accessGateMsg}</div> : null}
          {!termsAccepted ? (
            <div className="nr-error">
              You must accept the Terms of Use to continue.{" "}
              <button className="nr-linkBtn" onClick={openTermsGate}>Review &amp; Accept Terms</button>
            </div>
          ) : null}

          <TermsGate
            isOpen={termsGateOpen && !termsAccepted}
            termsChecked={termsChecked}
            termsVersion={TERMS_VERSION}
            onCheckChange={setTermsChecked}
            onAccept={acceptTermsAndContinue}
            onCancel={() => { setTermsGateOpen(false); void disconnect(); }}
            busy={busy}
          />
          {notice ? <div className="nr-ok">{notice}</div> : null}
        </section>

        {/* Everything else hidden until login */}
        {!accessGranted ? null : (
          <>
            {isPoolAssistant ? (
              <RewardsLaunchWizard
                step={wizardStep}
                setStep={setWizardStep}
                totalSteps={REWARDS_WIZARD_STEP_COUNT}
                stepsMeta={REWARDS_WIZARD_STEPS}
                onRefresh={() => {
                  void refreshAll();
                }}
                busy={busy}
              />
            ) : null}
            {/* OVERVIEW */}
            {wizOverview ? (
            <section id="nr-wiz-overview" className="nr-grid4">
              <div className="nr-card">
                <div className="nr-cardHeaderSmall nr-collapsibleHeader">
                  <div className="nr-headerLeft">
                    <div className="nr-titleRow">
                      <div className="nr-cardTitle">Allowed Tokens</div>
                      <button className="nr-collapseBtn" onClick={() => toggleCard("allowedTokens")} aria-label="Toggle">
                        {openCards.allowedTokens ? "▾" : "▸"}
                      </button>
                    </div>
                    <div className="nr-muted">Read-only overview<br />Approved tokens that pools can pay out.</div>
                  </div>
                </div>
                {openCards.allowedTokens ? <SimpleTable rows={allowTokensView} height={220} /> : null}
              </div>
              <div className="nr-card">
                <div className="nr-cardHeaderSmall nr-collapsibleHeader">
                  <div className="nr-headerLeft">
                    <div className="nr-titleRow">
                      <div className="nr-cardTitle">RAM Fees</div>
                      <button className="nr-collapseBtn" onClick={() => toggleCard("ramFees")} aria-label="Toggle">
                        {openCards.ramFees ? "▾" : "▸"}
                      </button>
                    </div>
                    <div className="nr-muted">Read-only overview<br />Configured RAM fee per claim for this project.</div>
                  </div>
                </div>
                {openCards.ramFees ? <SimpleTable rows={ramFeesView} height={220} /> : null}
              </div>
              <div className="nr-card">
                <div className="nr-cardHeaderSmall nr-collapsibleHeader">
                  <div className="nr-headerLeft">
                    <div className="nr-titleRow">
                      <div className="nr-cardTitle">RAMFee deposits</div>
                      <button className="nr-collapseBtn" onClick={() => toggleCard("deposits")} aria-label="Toggle">
                        {openCards.deposits ? "▾" : "▸"}
                      </button>
                    </div>
                    <div className="nr-muted">Read-only overview<br />RAMFee credit balance and already spent RAMFee entries.</div>
                  </div>
                </div>
                {openCards.deposits ? <SimpleTable rows={depositsView} height={220} /> : null}
              </div>
              <div className="nr-card">
                <div className="nr-cardHeaderSmall nr-collapsibleHeader">
                  <div className="nr-headerLeft">
                    <div className="nr-titleRow">
                      <div className="nr-cardTitle">RAM Budgets</div>
                      <button className="nr-collapseBtn" onClick={() => toggleCard("ramBudgets")} aria-label="Toggle">
                        {openCards.ramBudgets ? "▾" : "▸"}
                      </button>
                    </div>
                    <div className="nr-muted">Read-only overview<br />Claim capacity per project (total vs. used).</div>
                  </div>
                </div>
                {openCards.ramBudgets ? <SimpleTable rows={ramBudgetsView} height={220} /> : null}
              </div>
            </section>
            ) : null}

            {/* Project */}
            {wizProject ? (
              <section id="nr-wiz-project" className="nr-card">
                <div className="nr-cardHeader nr-collapsibleHeader">
                  <div className="nr-headerLeft">
                    <div className="nr-titleRow">
                      <div className="nr-cardTitle">Project</div>
                      <button className="nr-collapseBtn" onClick={() => toggleCard("project")} aria-label="Toggle">
                        {openCards.project ? "▾" : "▸"}
                      </button>
                    </div>
                    <div className="nr-muted">Select and update project metadata</div>
                  </div>
                </div>

                {openCards.project ? (
                <div className="nr-body">
                  <div className="nr-desc">
                    Projects group pools and define which accounts are allowed to manage configuration.
                    If your account is listed in a project's <b>allowedAccounts</b>, you can update the metadata here.
                    Pools belong to a project via <b>projectId</b>, and pool creation is only available to the contract owner (<b>{CONTRACT}</b>).
                  </div>

                  <div className="nr-field">
                    <label>Project</label>
                    <select value={selectedProjectId} onChange={(e) => setSelectedProjectId(e.target.value)} disabled={projectsLoading}>
                      {(allowedProjects || []).map((p) => (
                        <option key={String(p.projectId)} value={String(p.projectId)}>
                          #{String(p.projectId)} — {String(p.projectName || "")}
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedProject ? (
                    <div className="nr-formGrid">
                    <div className="nr-field">
                      <label>Project ID</label>
                      <input value={projectForm.projectId} readOnly />
                    </div>
                    <div className="nr-field">
                      <label>Name</label>
                      <input value={projectForm.projectName} onChange={(e) => setProjectForm((s) => ({...s, projectName: e.target.value }))} />
                    </div>
                    <div className="nr-field">
                      <label>Homepage URL</label>
                      <input value={projectForm.homepageUrl} onChange={(e) => setProjectForm((s) => ({...s, homepageUrl: e.target.value }))} />
                    </div>
                    <div className="nr-field">
                      <label>Image URL</label>
                      <input value={projectForm.projectImageUrl} onChange={(e) => setProjectForm((s) => ({...s, projectImageUrl: e.target.value }))} />
                    </div>
                    <div className="nr-field" style={{ gridColumn: "1 / -1" }}>
                      <label>Description</label>
                      <textarea rows={4} value={projectForm.projectDescription} onChange={(e) => setProjectForm((s) => ({...s, projectDescription: e.target.value }))} />
                    </div>
                    <div className="nr-field" style={{ gridColumn: "1 / -1" }}>
                      <label>Allowed Accounts (JSON array)</label>
                      <textarea rows={6} value={projectForm.allowedAccountsText} onChange={(e) => setProjectForm((s) => ({...s, allowedAccountsText: e.target.value }))} />
                    </div>

                      <div className="nr-actions" style={{ gridColumn: "1 / -1" }}>
                        <button className="nr-btn nr-btnPrimary" onClick={saveProject} disabled={busy}>
                          Save Project
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              
                ) : null}
</section>
            ) : null}

            {/* Pool picker (reset assistant + wizard step 4): select pool without full editor */}
            {wizPoolSelect && !wizPoolMain ? (
              <section id="nr-wiz-pool-select" className="nr-card">
                <div className="nr-cardHeader nr-collapsibleHeader">
                  <div className="nr-headerLeft">
                    <div className="nr-titleRow">
                      <div className="nr-cardTitle">Pools in project</div>
                      <button className="nr-collapseBtn" onClick={() => toggleCard("poolSelect")} aria-label="Toggle">
                        {openCards.poolSelect ? "▾" : "▸"}
                      </button>
                    </div>
                    <div className="nr-muted">
                      Select a pool for reset and related actions ({poolsForProject.length} in this project).
                    </div>
                  </div>
                </div>
                {openCards.poolSelect ? (
                  <div className="nr-body">
                    {!selectedProject ? (
                      <div className="nr-muted">Select a project first.</div>
                    ) : poolsForProject.length === 0 ? (
                      <div className="nr-muted">
                        No pools found for this project. Pools are filtered by your connected account (<b>{actor || "—"}</b>) unless you use contract god mode.
                        Only pools created with your account as <b>actor</b> in <b>npools</b> are listed.
                      </div>
                    ) : (
                      <>
                        <div className="nr-field">
                          <label>Pool</label>
                          <select
                            value={selectedPoolHash}
                            onChange={(e) => {
                              const v = String(e.target.value || "");
                              preferNewPoolRef.current = false;
                              setSelectedPoolHash(v);
                              const p = (poolsForProject || []).find((row) => String(row.poolHash) === v);
                              if (p?.poolKey) {
                                setResetPoolForm((s) => ({ ...s, poolKey: String(p.poolKey) }));
                              }
                            }}
                            disabled={!selectedProject}
                          >
                            {poolsForProject.map((p) => {
                              const poolTypeLabel = getPoolTypeLabel(p.poolType);
                              return (
                                <option key={String(p.poolHash)} value={String(p.poolHash)}>
                                  {String(p.poolKey)} ({poolTypeLabel})
                                </option>
                              );
                            })}
                          </select>
                        </div>
                        {selectedPool ? (
                          <div className="nr-muted" style={{ marginTop: 8 }}>
                            Type <b>{getPoolTypeLabel(selectedPool.poolType)}</b>
                            {Number(selectedPool.poolType) === 6 ? (
                              <>
                                {" "}
                                — Stake Claim uses <b>nclaims</b> per account; reset clears claim rows only (not <b>nwallets</b> / <b>nstakelig</b>).
                              </>
                            ) : null}
                          </div>
                        ) : null}
                        {!actionPermissions.clrclaims ? (
                          <div className="nr-error" style={{ marginTop: 12 }}>
                            Missing <b>clrclaims</b> permission in <b>nauths</b> — Pool Reset is hidden until this action is granted for your account.
                          </div>
                        ) : null}
                      </>
                    )}
                  </div>
                ) : null}
              </section>
            ) : null}

              

            {/* RAM BUDGET / DEPOSIT QUICK ACTIONS */}
            {wizRamQuick ? (
            <section className="nr-card">
              <div className="nr-cardHeader nr-collapsibleHeader">
                <div className="nr-headerLeft">
                  <div className="nr-titleRow">
                    <div className="nr-cardTitle">RAM Budget & RAMFee</div>
                    <button className="nr-collapseBtn" onClick={() => toggleCard("ramQuick")} aria-label="Toggle">
                      {openCards.ramQuick ? "▾" : "▸"}
                    </button>
                  </div>
                  <div className="nr-muted">Quick actions for RAM budgeting and RAMFee refund handling</div>
                </div>
              </div>

              {openCards.ramQuick ? (
                <div className="nr-body">
                  <div className="nr-grid2">
                    {/* Set RAM Budget */}
                    <div className="nr-subcard">
                      <div className="nr-subcardTitle">Set RAM Budget</div>
                      <div className="nr-muted" style={{ marginBottom: 8 }}>
                        Sets the total claim capacity for the selected project's RAM budget.
                        <div style={{ marginTop: 0 }}>
                          To increase your RAM budget capacity, transfer RAMFee tokens to <b>{CONTRACT}</b> with memo <b>ramfee</b> (exact word, case-insensitive ASCII), then click <b>Refresh</b> and select the new entry above.
                        </div>
                      </div>

                      {(preferNewReqRef.current && !String(selectedReqId || "").trim() && !dismissedTips.newRule) ? (
                        <div className="nr-tip">
                          <div className="nr-tipTitle">Tip: Adding a dependent NFT rule</div>
                          <div className="nr-tipText">
                            Pick <b>(new rule)</b>, enter a unique rule key and the required collection/immutable filters, then click <b>Check</b> before saving.
                            Rules are evaluated together: all active rules must pass for a claim to succeed.
                          </div>
                          <button className="nr-tipBtn" onClick={() => setDismissedTips((s) => ({...s, newRule: true }))}>Got it</button>
                        </div>
                      ) : null}

                      <div className="nr-field">
                        <label>RAMFee Deposit</label>
                        <select value={ramFeeDepositId} onChange={(e) => setRamFeeDepositId(e.target.value)}>
                          {ramFeeDepositOptions.map((d) => (
                            <option key={String(d._id)} value={String(d._id)}>
                              #{String(d._id)}{contractGodMode ? ` — ${String(d.actor || "")}` : ""} — {String(d.amount || "")} (remaining {remainingAssetString(d) || "—"})
                            </option>
                          ))}
                          {ramFeeDepositOptions.length === 0 ? <option value="">No RAMFee deposits found</option> : null}
                        </select>
                      </div>

                      <div className="nr-field">
                        <label>Budget claims</label>
                        <div style={{ display: "flex", gap: 8 }}>
                          <input value={ramSetClaims} onChange={(e) => setRamSetClaims(e.target.value)} placeholder="e.g. 5000" />
                          <button
                            type="button"
                            className="nr-btn"
                            onClick={() => {
                              const mc = ramFeeMaxSetBudget?.maxBudgetClaims;
                              if (typeof mc === "bigint") setRamSetClaims(mc.toString());
                            }}
                            disabled={!(typeof ramFeeMaxSetBudget?.maxBudgetClaims === "bigint")}
                          >
                            Use max
                          </button>
                        </div>
                        {ramBudgetRowForProject ? (
                          <div className="nr-muted" style={{ marginTop: 0 }}>
                            Current budget: <b>{String(ramBudgetRowForProject.budgetClaims ?? ramBudgetRowForProject.budget_claims ?? "0")}</b>
                            <span style={{ marginLeft: 10 }}>Used: <b>{String(ramBudgetRowForProject.usedClaims ?? ramBudgetRowForProject.used_claims ?? "0")}</b></span>
                            {typeof ramFeeMaxSetBudget?.maxBudgetClaims === "bigint" ? (
                              <div style={{ marginTop: 0 }}>
                                Max settable with remaining RAMFee: <b>{ramFeeMaxSetBudget.maxBudgetClaims.toString()}</b>
                                <span style={{ marginLeft: 10 }}>Increase available: <b>{ramFeeMaxSetBudget.maxDelta.toString()}</b></span>
                              </div>
                            ) : null}
                          </div>
                        ) : (
                          <div className="nr-muted" style={{ marginTop: 0 }}>No RAM budget row found yet for this project (it will be created on first set/add).</div>
                        )}
                      </div>

                      <div className="nr-muted">
                        Target: <b>#{String(selectedProject?.projectId || "")} — {String(selectedProject?.projectName || "")}</b>
                      </div>

                      <div className="nr-actions" style={{ marginTop: 8 }}>
                        <button className="nr-btn nr-btnPrimary" onClick={setRamBudget} disabled={busy || !selectedProject}>
                          Sign setrambudget
                        </button>
                      </div>
                    </div>

                    {/* Refund deposit */}
                    <div className="nr-subcard">
                      <div className="nr-subcardTitle">Refund Deposit</div>
                      <div className="nr-muted" style={{ marginBottom: 8 }}>
                        Refunds the remaining balance of a deposit back to the deposit owner.
                      </div>

                      <div className="nr-field">
                        <label>RAMFee entry</label>
                        <select value={refundDepositId} onChange={(e) => setRefundDepositId(e.target.value)}>
                          {refundDepositOptions.map((d) => {
                            const typeText = Number(d?.type ?? 0) === 1 ? "RAMFee" : "Legacy";
                            return (
                              <option key={String(d._id)} value={String(d._id)}>
                                #{String(d._id)}{contractGodMode ? ` — ${String(d.actor || "")}` : ""} — {typeText} — {String(d.amount || "")}
                              </option>
                            );
                          })}
                          {refundDepositOptions.length === 0 ? <option value="">No RAMFee entries found</option> : null}
                        </select>
                        {selectedRefundDeposit ? (
                          <div className="nr-muted" style={{ marginTop: 0 }}>
                            Remaining: <b>{remainingAssetString(selectedRefundDeposit) || "—"}</b>
                          </div>
                        ) : null}
                      </div>

                      <div className="nr-actions" style={{ marginTop: 8 }}>
                        <button className="nr-btn nr-btnPrimary" onClick={refundDeposit} disabled={busy || !refundDepositId}>
                          Sign refund
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </section>
            ) : null}

            {/* Manual Deposits (Type 2) */}
            {wizManualDep ? (
            <section className="nr-card">
              <div className="nr-cardHeader nr-collapsibleHeader">
                <div className="nr-headerLeft">
                  <div className="nr-titleRow">
                    <div className="nr-cardTitle">Manual Deposits</div>
                    <button className="nr-collapseBtn" onClick={() => toggleCard("manualDeposits")} aria-label="Toggle">
                      {openCards.manualDeposits ? "▾" : "▸"}
                    </button>
                  </div>
                  <div className="nr-muted">
                    <div style={{ marginTop: 0 }}>
                      Manual deposits are a safety cap / claim limit for a project: claims are only possible up to this configured amount, regardless of how many tokens are currently in the vault.
                      If the manual deposit is exhausted, claiming is no longer possible until it is increased.
                    </div>
                  </div>
                </div>
              </div>

              {openCards.manualDeposits ? (
                <div className="nr-body">
                  <div className="nr-grid2">
                    {/* creatdep2 */}
                    <div className="nr-subcard">
                      <div className="nr-subcardTitle">Create Manual Deposit</div>
                      <div className="nr-muted" style={{ marginBottom: 8, whiteSpace: "pre-line" }}>
                        {"Create Manual Deposit\nCreates a manual deposit row for the selected token. If a manual deposit already exists for that token, use Update instead."}
                      </div>

                      <div className="nr-field">
                        <label>Actor</label>
                        <input value={actor || ""} readOnly />
                      </div>

                      <div className="nr-field">
                        <label>Token</label>
                        <select value={dep2TokenKey} onChange={(e) => setDep2TokenKey(e.target.value)}>
                          {activeAllowTokenOptions.map((t) => (
                            <option key={t.key} value={t.key}>
                              {symbolCodeFrom(t.tokenSymbol)} — {t.tokenContract}
                            </option>
                          ))}
                          {activeAllowTokenOptions.length === 0 ? <option value="">No active allowed tokens</option> : null}
                        </select>
                      </div>

                      <div className="nr-field">
                        <label>Amount</label>
                        <input
                          value={dep2Amount}
                          onChange={(e) => setDep2Amount(e.target.value)}
                          placeholder={selectedDep2Token ? assetPlaceholderForSymbol(selectedDep2Token.tokenSymbol) : "0.0000 TOKEN"}
                        />
                        <div className="nr-muted" style={{ marginTop: 0 }}>
                          Example: <code>{selectedDep2Token ? assetPlaceholderForSymbol(selectedDep2Token.tokenSymbol) : "0.0000 TOKEN"}</code>
                        </div>
                      </div>

                      <div className="nr-actions" style={{ marginTop: 8 }}>
                        <button className="nr-btn nr-btnPrimary" onClick={createManualDeposit2} disabled={busy || !dep2TokenKey}>
                          Sign creatdep2
                        </button>
                      </div>
                    </div>

                    {/* upddep2 */}
                    <div className="nr-subcard">
                      <div className="nr-subcardTitle">Update Manual Deposit</div>
                      <div className="nr-muted" style={{ marginBottom: 8 }}>
                        Updates the amount on an existing type-2 deposit. The amount cannot be set below the already claimed amount.
                      </div>

                      <div className="nr-field">
                        <label>RAMFee entry</label>
                        <select value={dep2UpdateId} onChange={(e) => setDep2UpdateId(e.target.value)}>
                          {dep2DepositOptions.map((d) => {
                            const sym = symbolCodeFrom(d?.tokenSymbol ?? d?.token_symbol ?? "");
                            const actorLabel = contractGodMode ? ` — ${String(d.actor || "")}` : "";
                            return (
                              <option key={String(d._id)} value={String(d._id)}>
                                #{String(d._id)}{actorLabel} — {sym} — {String(d.amount || "")}
                              </option>
                            );
                          })}
                          {dep2DepositOptions.length === 0 ? <option value="">No manual deposits found</option> : null}
                        </select>
                        {selectedDep2Deposit ? (
                          <div className="nr-muted" style={{ marginTop: 0 }}>
                            Claimed: <b>{String(selectedDep2Deposit.claimed || "")}</b>
                            <span style={{ marginLeft: 10 }}>
                              Remaining: <b>{remainingAssetString(selectedDep2Deposit) || "—"}</b>
                            </span>
                          </div>
                        ) : null}
                      </div>

                      <div className="nr-field">
                        <label>New amount</label>
                        <input
                          value={dep2UpdateAmount}
                          onChange={(e) => setDep2UpdateAmount(e.target.value)}
                          placeholder={selectedDep2Deposit ? String(selectedDep2Deposit.amount || "") : ""}
                        />
                      </div>

                      <div className="nr-actions" style={{ marginTop: 8 }}>
                        <button className="nr-btn nr-btnPrimary" onClick={updateManualDeposit2} disabled={busy || !dep2UpdateId}>
                          Sign upddep2
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </section>
            ) : null}


            

            {/* Pool */}
              {actionPermissions.setpool && wizPoolMain ? (
              <section id="nr-wiz-pool" className="nr-card">
                <div className="nr-cardHeader nr-collapsibleHeader">
                  <div className="nr-headerLeft">
                    <div className="nr-titleRow">
                      <div className="nr-cardTitle">Pool</div>
                      <button className="nr-collapseBtn" onClick={() => toggleCard("pool")} aria-label="Toggle">
                        {openCards.pool ? "▾" : "▸"}
                      </button>
                    </div>
                    <div className="nr-muted">Create or select a pool in the current project</div>
                  </div>
                </div>

                {openCards.pool ? (
                <div className="nr-body">
                  <div className="nr-desc">
                    Pools are claim configurations: they link an NFT collection to a reward amount (token + quantity), with optional requirements, time windows and per-wallet cooldowns.
                    Reward claims are paid from the configured project vault wallet (projvault), not from contract reward deposits.
                    Only the project's allowed accounts can create or edit pools.
                  </div>

                  {(preferNewPoolRef.current && !String(selectedPoolHash || "").trim() && !dismissedTips.newPool) ? (
                    <div className="nr-tip">
                      <div className="nr-tipTitle">Tip: Creating a new pool</div>
                      <div className="nr-tipText">
                        Fill out the fields below, then click <b>Check</b> to validate formats and required values before saving.
                        After saving, the new pool will be selected automatically.
                      </div>
                      <button className="nr-tipBtn" onClick={() => setDismissedTips((s) => ({...s, newPool: true }))}>Got it</button>
                    </div>
                  ) : null}

                  <div className="nr-field">
                  <label>Pool</label>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <select value={selectedPoolHash} onChange={(e) => {
                      const v = String(e.target.value || "");
                      if (!v) {
                        preferNewPoolRef.current = true;
                        setSelectedPoolHash("");
                      } else {
                        preferNewPoolRef.current = false;
                        setSelectedPoolHash(v);
                      }
                    }} disabled={!selectedProject} style={{ flex: "1 1 200px" }}>
                      {poolsForProject.map((p) => {
                        const poolTypeLabel = getPoolTypeLabel(p.poolType);
                        return (
                          <option key={String(p.poolHash)} value={String(p.poolHash)}>
                            {String(p.poolKey)} ({poolTypeLabel})
                          </option>
                        );
                      })}
                      <option value="">(new pool)</option>
                    </select>
                    {selectedPool ? (
                      <button
                        type="button"
                        className="nr-btn"
                        onClick={() => {
                          justCopiedPoolRef.current = selectedPool;
                          preferNewPoolRef.current = true;
                          setSelectedPoolHash("");
                        }}
                        disabled={!selectedProject}
                      >
                        Pool Copy
                      </button>
                    ) : null}
                  </div>
                  </div>

                  {!selectedPool && poolsForProject.length === 0 ? (
                    <div className="nr-muted" style={{ padding: 12 }}>
                      No pools found for this project yet. Fill the form below and click <b>Save Pool</b> to create one.
                    </div>
                  ) : null}

                  <div className="nr-formGrid">
                    <div className="nr-field">
                      <label>Pool Key</label>
                      <input
                        placeholder="e.g. promo_jan_2026"
                        value={poolForm.poolKey}
                        onChange={(e) => setPoolForm((s) => ({...s, poolKey: e.target.value }))}
                        readOnly={!isNewPool}
                      />
                      {isNewPool ? (
                        <div className="nr-muted" style={{ marginTop: 0 }}>
                          Example: <b>promo_jan_2026</b> (any unique identifier for this project).
                        </div>
                      ) : (
                        <div className="nr-muted" style={{ marginTop: 0 }}>
                          Pool Key cannot be changed when editing.
                        </div>
                      )}
                    </div>

                    <div className="nr-field">
                      <label>Project ID</label>
                      <input value={poolForm.projectId} readOnly />
                      {isNewPool ? (
                        <div className="nr-muted" style={{ marginTop: 0 }}>
                          Auto-filled from the selected project.
                        </div>
                      ) : null}
                    </div>

                    <div className="nr-field">
                      <label>Collection</label>
                      <input
                        placeholder="e.g. mycollection"
                        value={poolForm.collectionName}
                        onChange={(e) => setPoolForm((s) => ({...s, collectionName: e.target.value }))}
                      />
                      {isNewPool ? (
                        <div className="nr-muted" style={{ marginTop: 0 }}>
                          Example: <b>mycollection</b> (AtomicAssets collection name).
                        </div>
                      ) : null}
                    </div>

                    <div className="nr-field">
                      <label>Reward amount</label>
                      <input
                        placeholder={poolRewardPlaceholder}
                        value={poolForm.rewardAmount}
                        onChange={(e) => setPoolForm((s) => ({...s, rewardAmount: e.target.value }))}
                      />
                      {poolRewardAssetPreview ? (
                        <div className="nr-muted" style={{ marginTop: 0 }}>
                          Reward preview: <b>{poolRewardAssetPreview}</b>
                        </div>
                      ) : isNewPool ? (
                        <div className="nr-muted" style={{ marginTop: 0 }}>
                          {poolRewardHelpText}
                        </div>
                      ) : null}
                    </div>

                    <div className="nr-field">
                      <label>Token symbol</label>
                      <select
                        value={poolForm.rewardTokenSymbolRaw}
                        onChange={(e) => setPoolForm((s) => ({...s, rewardTokenSymbolRaw: e.target.value }))}
                      >
                        <option value="">(select)</option>
                        {poolForm.rewardTokenSymbolRaw &&
                        !poolTokenSymbolOptions.some((o) => String(o.tokenSymbolRaw) === String(poolForm.rewardTokenSymbolRaw)) ? (
                          <option value={poolForm.rewardTokenSymbolRaw}>{poolForm.rewardTokenSymbolRaw}</option>
                        ) : null}
                        {poolTokenSymbolOptions.map((o) => (
                          <option key={String(o.tokenSymbolRaw)} value={String(o.tokenSymbolRaw)}>
                            {String(o.symbol)} ({String(o.precision)} dp)
                          </option>
                        ))}
                        {poolTokenSymbolOptions.length === 0 ? <option value="">No allowed tokens found</option> : null}
                      </select>
                      {isNewPool ? (
                        <div className="nr-muted" style={{ marginTop: 0 }}>
                          Only tokens from <b>Allowed Tokens</b> can be selected.
                        </div>
                      ) : null}
                    </div>

                    <div className="nr-field">
                      <label>Token contract</label>
                      <select
                        value={poolForm.rewardTokenContract}
                        onChange={(e) => setPoolForm((s) => ({...s, rewardTokenContract: e.target.value }))}
                        disabled={!poolForm.rewardTokenSymbolRaw}
                      >
                        <option value="">(select)</option>
                        {poolForm.rewardTokenContract &&
                        !poolTokenContractOptions.some((o) => String(o.tokenContract) === String(poolForm.rewardTokenContract)) ? (
                          <option value={poolForm.rewardTokenContract}>{poolForm.rewardTokenContract}</option>
                        ) : null}
                        {poolTokenContractOptions.map((o) => (
                          <option key={String(o.tokenContract)} value={String(o.tokenContract)}>
                            {String(o.tokenContract)}
                          </option>
                        ))}
                      </select>
                      {isNewPool ? (
                        <div className="nr-muted" style={{ marginTop: 0 }}>
                          The contract must match the selected token symbol entry in <b>Allowed Tokens</b>.
                        </div>
                      ) : null}
                    </div>

                    <div className="nr-field">
                      <label>Pool type</label>
                      <select
                        value={String(poolForm.poolType ?? 1)}
                        onChange={(e) => setPoolForm((s) => ({ ...s, poolType: Number(e.target.value) || 1 }))}
                      >
                        <option value="1">1 – Reward</option>
                        <option value="5">5 – Repeatable</option>
                        <option value="6">6 – Stake Claim</option>
                      </select>
                      <div className="nr-muted" style={{ marginTop: 0 }}>
                        <b>Reward (1):</b> Default one claim per NFT; optional account scope below.{" "}
                        <b>Repeatable (5):</b> Default per NFT per cooldown; account scope = per wallet per period. Cooldown must be &gt; 0.{" "}
                        <b>Stake Claim (6):</b> Always one claim per account (<b>nstakelig</b> + points). Pool <b>reward</b> = per-point payout.
                      </div>
                    </div>

                    {poolSupportsScope ? (
                      <div className="nr-field" style={{ gridColumn: "1 / -1" }}>
                        <label>Claim scope</label>
                        <select
                          value={String(poolForm.claimScope ?? CLAIM_SCOPE_NFT)}
                          onChange={(e) =>
                            setPoolForm((s) => ({
                              ...s,
                              claimScope: Number(e.target.value) === CLAIM_SCOPE_ACCOUNT
                                ? CLAIM_SCOPE_ACCOUNT
                                : CLAIM_SCOPE_NFT,
                            }))
                          }
                          disabled={
                            !actionPermissions.setpoolscope
                            && !contractGodMode
                            && Number(poolForm.claimScope ?? CLAIM_SCOPE_NFT) === CLAIM_SCOPE_ACCOUNT
                          }
                        >
                          <option value={String(CLAIM_SCOPE_NFT)}>Per NFT (default)</option>
                          <option
                            value={String(CLAIM_SCOPE_ACCOUNT)}
                            disabled={!actionPermissions.setpoolscope && !contractGodMode}
                          >
                            Per account (min. 1 qualifying NFT)
                          </option>
                        </select>
                        <div className="nr-muted" style={{ marginTop: 4, fontSize: 12 }}>
                          <b>Per NFT:</b> Each NFT from the collection can claim separately (default; no extra on-chain step).{" "}
                          <b>Per account:</b> One claim identity per wallet; requires <b>setpoolscope</b> permission.
                          {Number(poolForm.claimScope) === CLAIM_SCOPE_ACCOUNT ? (
                            <>
                              {" "}
                              Changing scope on a pool with existing claims can confuse <b>nclaims</b> — prefer <b>clrclaims</b> or a new pool.
                            </>
                          ) : null}
                        </div>
                        {poolClaimScopeNeedsSetpoolscope(
                          poolForm.claimScope,
                          selectedPool,
                          claimScopeForSelectedPool,
                        ) && !actionPermissions.setpoolscope && !contractGodMode ? (
                          <div className="nr-muted" style={{ marginTop: 4, color: "var(--nr-warn, #c90)" }}>
                            Missing <b>setpoolscope</b> permission — cannot save Per account scope or change scope.
                          </div>
                        ) : null}
                        {selectedPool ? (
                          <div className="nr-muted" style={{ marginTop: 4 }}>
                            On-chain: <b>{claimScopeLabel(claimScopeForSelectedPool)}</b>
                            {Number(poolForm.claimScope) !== claimScopeForSelectedPool ? (
                              <span> → will save as <b>{claimScopeLabel(poolForm.claimScope)}</b> on submit</span>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <div className="nr-field" style={{ gridColumn: "1 / -1" }}>
                        <label>Claim scope</label>
                        <div className="nr-muted" style={{ marginTop: 4 }}>
                          Only pool types <b>1</b> and <b>5</b> support claim scope. Type <b>6</b> is always per account.
                        </div>
                      </div>
                    )}

                    {selectedPool != null && (selectedPool.status !== undefined || selectedPool.status === 0) ? (
                    <div className="nr-field">
                      <label>Status</label>
                      <input value={String(selectedPool.status ?? "")} readOnly />
                      <div className="nr-muted" style={{ marginTop: 0 }}>
                        Set automatically by the contract.
                      </div>
                    </div>
                    ) : null}

                    <div className="nr-field">
                      <label>Active</label>
                      <select
                        value={poolForm.active ? "1" : "0"}
                        onChange={(e) => setPoolForm((s) => ({...s, active: e.target.value === "1" }))}
                      >
                        <option value="1">true</option>
                        <option value="0">false</option>
                      </select>
                      {isNewPool ? (
                        <div className="nr-muted" style={{ marginTop: 0 }}>
                          Set to <b>true</b> to allow claims from this pool.
                        </div>
                      ) : null}
                    </div>

                    <div className="nr-field">
                      <label>Creator Fee Enabled</label>
                      <select
                        value={poolForm.creatorFeeEnabled ? "1" : "0"}
                        onChange={(e) => setPoolForm((s) => ({...s, creatorFeeEnabled: e.target.value === "1" }))}
                      >
                        <option value="0">false</option>
                        <option value="1">true</option>
                      </select>
                      <div className="nr-muted" style={{ marginTop: 0 }}>
                        When true, set creator fee via <b>setcrefee</b> (fields below). Save Pool will send setpool and setcrefee in one transaction.
                      </div>
                    </div>

                    {poolForm.creatorFeeEnabled ? (
                    <>
                    <div className="nr-field">
                      <label>Creator Fee % (setcrefee)</label>
                      <input
                        type="number"
                        min="0"
                        max="10000"
                        placeholder="e.g. 500 = 5.00%"
                        value={creatorFeeForm.feePercent}
                        onChange={(e) => setCreatorFeeForm((s) => ({ ...s, feePercent: e.target.value }))}
                      />
                      <div className="nr-muted" style={{ marginTop: 0 }}>
                        Basis points (e.g. 500 = 5%). Required when Creator Fee Enabled is true.
                      </div>
                    </div>
                    <div className="nr-field">
                      <label>Creator Fee Receiver (setcrefee)</label>
                      <input
                        placeholder="Account name"
                        value={creatorFeeForm.receiver}
                        onChange={(e) => setCreatorFeeForm((s) => ({ ...s, receiver: e.target.value }))}
                      />
                      <div className="nr-muted" style={{ marginTop: 0 }}>
                        Account that receives the creator fee. Required when Creator Fee Enabled is true.
                      </div>
                    </div>
                    </>
                    ) : null}

                    <div className="nr-field">
                      <label>Start (UTC)</label>

                      <div className="nr-grid2">
                        <input
                          type="date"
                          value={epochSecToLocalDateValue(poolForm.startTimeSec)}
                          onChange={(e) => {
                            const date = e.target.value;
                            const time = epochSecToLocalTimeValue(poolForm.startTimeSec) || "00:00";
                            const sec = localDateTimePartsToEpochSec(date, time);
                            setPoolForm((s) => ({ ...s, startTimeSec: String(sec || 0) }));
                          }}
                        />
                        <input
                          type="time"
                          step="60"
                          value={epochSecToLocalTimeValue(poolForm.startTimeSec)}
                          onChange={(e) => {
                            const time = e.target.value;
                            const date = epochSecToLocalDateValue(poolForm.startTimeSec);
                            const sec = localDateTimePartsToEpochSec(date, time);
                            setPoolForm((s) => ({ ...s, startTimeSec: String(sec || 0) }));
                          }}
                        />
                      </div>
                      {isNewPool ? (
                        <div className="nr-muted" style={{ marginTop: 0 }}>
                          Leave empty for no start limit.
                        </div>
                      ) : null}
                    </div>

                    <div className="nr-field">
                      <label>End (UTC)</label>

                      <div className="nr-grid2">
                        <input
                          type="date"
                          value={epochSecToLocalDateValue(poolForm.endTimeSec)}
                          onChange={(e) => {
                            const date = e.target.value;
                            const time = epochSecToLocalTimeValue(poolForm.endTimeSec) || "00:00";
                            const sec = localDateTimePartsToEpochSec(date, time);
                            setPoolForm((s) => ({ ...s, endTimeSec: String(sec || 0) }));
                          }}
                        />
                        <input
                          type="time"
                          step="60"
                          value={epochSecToLocalTimeValue(poolForm.endTimeSec)}
                          onChange={(e) => {
                            const time = e.target.value;
                            const date = epochSecToLocalDateValue(poolForm.endTimeSec);
                            const sec = localDateTimePartsToEpochSec(date, time);
                            setPoolForm((s) => ({ ...s, endTimeSec: String(sec || 0) }));
                          }}
                        />
                      </div>
                      {isNewPool ? (
                        <div className="nr-muted" style={{ marginTop: 0 }}>
                          Leave empty for no end limit.
                        </div>
                      ) : null}
                    </div>

                    <div className="nr-field">
                      <label>Cooldown (sec)</label>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <input
                        placeholder="e.g. 86400"
                        value={poolForm.cooldownSec}
                          onChange={(e) => {
                            const val = e.target.value;
                            setPoolForm((s) => ({...s, cooldownSec: val }));
                          }}
                          style={{ flex: 1 }}
                        />
                        <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: Number(poolForm.poolType ?? 1) === 5 ? "not-allowed" : "pointer", whiteSpace: "nowrap", opacity: Number(poolForm.poolType ?? 1) === 5 ? 0.6 : 1 }}>
                          <input
                            type="checkbox"
                            checked={String(poolForm.cooldownSec || "0").trim() === "4294967295"}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setPoolForm((s) => ({...s, cooldownSec: "4294967295" }));
                              } else {
                                setPoolForm((s) => ({...s, cooldownSec: "0" }));
                              }
                            }}
                            disabled={Number(poolForm.poolType ?? 1) === 5}
                          />
                          <span className="nr-muted" style={{ fontSize: 12 }}>Max</span>
                        </label>
                      </div>
                        <div className="nr-muted" style={{ marginTop: 0 }}>
                        {Number(poolForm.poolType ?? 1) === 5 ? (
                          <>
                            For <b>Repeatable</b> pools, cooldown must be &gt; 0 (e.g. <b>86400</b> = 24 hours).
                            {Number(poolForm.claimScope) === CLAIM_SCOPE_ACCOUNT ? (
                              <> With <b>account</b> scope, this limits one claim per wallet per period.</>
                            ) : (
                              <> Applies per NFT when scope is <b>per NFT</b>.</>
                            )}
                          </>
                        ) : Number(poolForm.claimScope) === CLAIM_SCOPE_ACCOUNT ? (
                          <>
                            Account scope: one claim per wallet (NFT is gate only). Use <b>0</b> or <b>Max</b> for wallet cooldown only in <b>per NFT</b> mode.
                          </>
                        ) : (
                          <>
                            Examples: <b>86400</b> = 24 hours, <b>604800</b> = 1 week. Use <b>0</b> for no cooldown.
                            <br />
                            Check <b>Max</b> for one claim per wallet (4294967295).
                          </>
                        )}
                        </div>
                    </div>

                    <div className="nr-field" style={{ gridColumn: "1 / -1" }}>
                      <label>Required Immutable (attribute/value pairs)</label>

                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 8 }}>
                        {poolReqPairs.map((p, i) => (
                          <React.Fragment key={`p-req-${i}`}>
                            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                              <div className="nr-muted" style={{ fontSize: 12 }}>Attribute</div>
                              <input
                                placeholder="e.g. background"
                                value={p.key}
                                onChange={(e) => onPoolReqPairChange(i, "key", e.target.value)}
                              />
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                              <div className="nr-muted" style={{ fontSize: 12 }}>Value</div>
                              <input
                                placeholder="e.g. Purple (Epic)"
                                value={p.value}
                                onChange={(e) => onPoolReqPairChange(i, "value", e.target.value)}
                              />
                            </div>
                          </React.Fragment>
                        ))}
                      </div>

                      <div className="nr-actions" style={{ marginTop: 10, justifyContent: "flex-start" }}>
                        <button type="button" className="nr-btn" onClick={addPoolReqPairRow} disabled={poolReqPairs.length >= 5}>
                          + Add row
                        </button>
                        <button type="button" className="nr-btn nr-btnDanger" onClick={removePoolReqPairRow} disabled={poolReqPairs.length === 0}>
                          Delete row
                        </button>
                        {poolReqPairs.length === 0 ? (
                          <div className="nr-muted" style={{ marginLeft: 10, alignSelf: "center" }}>
                            No immutable filters yet — add a row to require specific attributes.
                          </div>
                        ) : null}
                      </div>

                      <div className="nr-muted" style={{ marginTop: 10 }}>Built JSON array:</div>
                      <pre
                        style={{
                          marginTop: 0,
                          padding: 10,
                          borderRadius: 12,
                          background: "rgba(255,255,255,.04)",
                          overflowX: "auto",
                          whiteSpace: "pre-wrap",
                        }}
                      >
{poolForm.requiredImmutableText}
                      </pre>

                      {isNewPool ? (
                        <div className="nr-muted" style={{ marginTop: 0 }}>
                          Leave all attribute names empty to allow all NFTs in the collection, or add one or more pairs to require specific immutable data.
                        </div>
                      ) : null}
                    </div>

                    {poolSupportsMutable ? (
                      <div className="nr-field" style={{ gridColumn: "1 / -1" }}>
                        <label>Required Mutable (attribute/value or presence-only)</label>
                        <div className="nr-muted" style={{ marginTop: 4, marginBottom: 8, fontSize: 12 }}>
                          Mutable fields are read from the NFT asset only (not the template). Holders can change them before each claim.
                          Leave value empty to require the field to be set (any value). Use <b>key=value</b> for an exact match (string/ipfs/image).
                          Saved via <b>setpoolmut</b> (separate from immutable <b>setpool</b>).
                        </div>

                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 8 }}>
                          {poolMutPairs.map((p, i) => (
                            <React.Fragment key={`p-mut-${i}`}>
                              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                <div className="nr-muted" style={{ fontSize: 12 }}>Attribute</div>
                                <input
                                  placeholder="e.g. level"
                                  value={p.key}
                                  onChange={(e) => onPoolMutPairChange(i, "key", e.target.value)}
                                  disabled={!actionPermissions.setpoolmut}
                                />
                              </div>
                              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                <div className="nr-muted" style={{ fontSize: 12 }}>Value (optional)</div>
                                <input
                                  placeholder="e.g. 10 (empty = any value)"
                                  value={p.value}
                                  onChange={(e) => onPoolMutPairChange(i, "value", e.target.value)}
                                  disabled={!actionPermissions.setpoolmut}
                                />
                              </div>
                            </React.Fragment>
                          ))}
                        </div>

                        <div className="nr-actions" style={{ marginTop: 10, justifyContent: "flex-start" }}>
                          <button
                            type="button"
                            className="nr-btn"
                            onClick={addPoolMutPairRow}
                            disabled={poolMutPairs.length >= 5 || !actionPermissions.setpoolmut}
                          >
                            + Add row
                          </button>
                          <button
                            type="button"
                            className="nr-btn nr-btnDanger"
                            onClick={removePoolMutPairRow}
                            disabled={poolMutPairs.length === 0 || !actionPermissions.setpoolmut}
                          >
                            Delete row
                          </button>
                          {poolMutPairs.length === 0 ? (
                            <div className="nr-muted" style={{ marginLeft: 10, alignSelf: "center" }}>
                              No mutable filters — add a row to require mutable asset fields at claim time.
                            </div>
                          ) : null}
                        </div>

                        <div className="nr-muted" style={{ marginTop: 10 }}>Built JSON array:</div>
                        <pre
                          style={{
                            marginTop: 0,
                            padding: 10,
                            borderRadius: 12,
                            background: "rgba(255,255,255,.04)",
                            overflowX: "auto",
                            whiteSpace: "pre-wrap",
                          }}
                        >
{poolForm.requiredMutableText}
                        </pre>
                      </div>
                    ) : (
                      <div className="nr-field" style={{ gridColumn: "1 / -1" }}>
                        <label>Required Mutable</label>
                        <div className="nr-muted" style={{ marginTop: 4 }}>
                          {Number(poolForm.poolType || 1) === 6 &&
                          String(poolForm.collectionName || "").trim().toLowerCase() === "account"
                            ? "Not available for account-based Stake Claim pools (no AtomicAssets claim NFT)."
                            : "Not used for this pool type. Required mutable filters apply only to Reward (1), Repeatable (5), and Stake Claim (6) pools with an NFT collection."}
                        </div>
                      </div>
                    )}

                    <div className="nr-field" style={{ gridColumn: "1 / -1" }}>
                      <label>Description</label>
                      <textarea
                        placeholder="Optional description for display in NFTClaimHub"
                        rows={3}
                        value={poolForm.poolDescription}
                        onChange={(e) => setPoolForm((s) => ({...s, poolDescription: e.target.value }))}
                      />
                      {isNewPool ? (
                        <div className="nr-muted" style={{ marginTop: 0 }}>
                          Optional description that will be displayed in the NFTClaimHub interface.
                        </div>
                      ) : null}
                    </div>

                    <div className="nr-field" style={{ gridColumn: "1 / -1" }}>
                      <label>Image URL</label>
                      <input
                        placeholder="https://..."
                        value={poolForm.poolImageUrl}
                        onChange={(e) => setPoolForm((s) => ({...s, poolImageUrl: e.target.value }))}
                      />
                      {isNewPool ? (
                        <div className="nr-muted" style={{ marginTop: 0 }}>
                          Optional. Use a direct image link (https://...).
                        </div>
                      ) : null}
                    </div>

                    <div className="nr-actions" style={{ gridColumn: "1 / -1" }}>
                      <button className="nr-btn" onClick={() => { void previewPoolClaimFromCheck(); }} disabled={busy || !selectedProject}>
                        Check
                      </button>
                      <button className="nr-btn nr-btnPrimary" onClick={savePool} disabled={busy || !selectedProject}>
                        Save Pool
                      </button>
                      {!isNewPool && actionPermissions.delpool ? (
                        <button
                          type="button"
                          className="nr-btn nr-btnDanger"
                          onClick={() => setDeletePoolConfirmOpen(true)}
                          disabled={busy || !selectedProject || !selectedPool}
                        >
                          Delete Pool
                        </button>
                      ) : null}
                    </div>

                    {poolCheck ? (
                      <div className="nr-checkPanel" style={{ gridColumn: "1 / -1" }}>
                        <div className={poolCheck.ok ? "nr-checkOk" : "nr-checkErr"}>
                          {poolCheck.ok ? "Looks good. You can save this pool." : "Please fix the issues below before saving."}
                        </div>
                        {poolCheck.errors?.length ? (
                          <ul className="nr-checkList">
                            {poolCheck.errors.map((m, i) => (
                              <li key={`pe-${i}`} className="nr-checkItem nr-checkItemErr">
                                {m}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                        {poolCheck.warnings?.length ? (
                          <ul className="nr-checkList">
                            {poolCheck.warnings.map((m, i) => (
                              <li key={`pw-${i}`} className="nr-checkItem nr-checkItemWarn">
                                {m}
                              </li>
                            ))}
                          </ul>
) : null}
                    </div>
                    ) : null}

                    {deletePoolConfirmOpen && selectedPool ? (
                      <div role="dialog" aria-modal="true" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000 }}>
                        <div style={{ background: "var(--nr-bg, #fff)", padding: 24, borderRadius: 12, maxWidth: 480, boxShadow: "0 4px 20px rgba(0,0,0,0.2)" }}>
                          <h3 style={{ marginTop: 0, color: "var(--nr-danger, #c00)" }}>Delete Pool – Warning</h3>
                          <p>
                            This will <strong>permanently delete</strong> the pool <b>{selectedPool.poolKey}</b>, all associated <b>claim</b> and <b>wallet</b> statistics, and required NFT rules.
                          </p>
                          <p>Only proceed if you are sure. If you need claim or wallet data as reference, export it to CSV yourself before deleting (Claims and Wallets sections below).</p>
                          <p>This action cannot be undone.</p>
                          <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
                            <button className="nr-btn nr-btnDanger" onClick={doDeletePool} disabled={busy}>
                              Export CSV &amp; Delete Pool
                            </button>
                            <button className="nr-btn" onClick={() => setDeletePoolConfirmOpen(false)} disabled={busy}>
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              
                ) : null}
</section>
              ) : null}

            {/* Pool Reset (clrclaims) – only when user has clrclaims in nauth */}
            {actionPermissions.clrclaims && wizPoolAux ? (
            <section className="nr-card">
              <div className="nr-cardHeader nr-collapsibleHeader">
                <div className="nr-headerLeft">
                  <div className="nr-titleRow">
                    <div className="nr-cardTitle">Pool Reset</div>
                    <button className="nr-collapseBtn" onClick={() => toggleCard("poolReset")} aria-label="Toggle">
                      {openCards.poolReset ? "\u25BE" : "\u25B8"}
                    </button>
                  </div>
                  <div className="nr-muted">Remove nclaims for a pool so the same NFTs can claim again. Action: clrclaims.</div>
                </div>
              </div>

              {openCards.poolReset ? (
                <div className="nr-body">
                  <div className="nr-desc" style={{ marginBottom: 16 }}>
                    All claim entries (nclaims) for the selected pool will be deleted (up to max rows per call). Optionally export claims to CSV before reset.
                  </div>

                  <div className="nr-grid2">
                    <div className="nr-field">
                      <label>Pool</label>
                      <select
                        value={resetPoolForm.poolKey}
                        onChange={(e) => setResetPoolForm((s) => ({ ...s, poolKey: String(e.target.value || "").trim() }))}
                      >
                        <option value="">(select pool)</option>
                        {(poolsForProject || []).map((p) => (
                          <option key={String(p.poolHash)} value={String(p.poolKey || "")}>
                            {String(p.poolKey || "")}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="nr-field">
                      <label>Max rows per call</label>
                      <input
                        type="number"
                        min="1"
                        max="10000"
                        value={resetPoolForm.maxRows}
                        onChange={(e) => setResetPoolForm((s) => ({ ...s, maxRows: e.target.value }))}
                        placeholder="500"
                      />
                      <div className="nr-muted" style={{ marginTop: 4 }}>Default 500. Call again until result shows 0 removed.</div>
                    </div>
                  </div>

                  <div className="nr-actions" style={{ marginTop: 16 }}>
                    <button
                      className="nr-btn nr-btnPrimary"
                      onClick={() => setResetPoolConfirmOpen(true)}
                      disabled={!auth || busy || !resetPoolForm.poolKey}
                    >
                      Sign clrclaims
                    </button>
                  </div>

                  {resetPoolResult ? (
                    <div style={{ marginTop: 16, padding: 12, background: "#f0f0f0", borderRadius: 8, color: "#1a1a1a" }}>
                      {typeof resetPoolResult === "object" && resetPoolResult.removed !== undefined ? (
                        <>
                          <strong>Last result:</strong> Deleted <b>{resetPoolResult.removed}</b> claim(s).
                          {resetPoolResult.removed > 0 ? " Call again to delete more (until 0)." : " Done for this batch."}
                        </>
                      ) : (
                        <>
                          <strong>Last result:</strong> {resetPoolResult.message || "Transaction sent; check chain for console output."}
                        </>
                      )}
                    </div>
                  ) : null}

                  {resetPoolConfirmOpen ? (
                    <div role="dialog" aria-modal="true" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000 }}>
                      <div style={{ background: "var(--nr-bg, #fff)", padding: 24, borderRadius: 12, maxWidth: 480, boxShadow: "0 4px 20px rgba(0,0,0,0.2)" }}>
                        <h3 style={{ marginTop: 0 }}>Pool Reset - Warning</h3>
                        <p>
                          All <b>nclaims</b> (claim entries) for pool <b>{resetPoolForm.poolKey}</b> will be deleted (up to {resetPoolForm.maxRows || "500"} rows per call).
                        </p>
                        <p>
                          Before reset, the app automatically exports <b>nclaims</b> and <b>nwallets</b> to CSV files named
                          <b> {toAlnumPoolKey(resetPoolForm.poolKey)}_YYYY-MM-DD_nclaims.csv</b> and <b> {toAlnumPoolKey(resetPoolForm.poolKey)}_YYYY-MM-DD_nwallets.csv</b>.
                        </p>
                        <p>
                          The app triggers both exports automatically before reset. If your browser blocks one of the downloads, allow multiple
                          downloads and verify both CSV files manually.
                        </p>
                        <p>Do you want to continue and sign the clrclaims transaction?</p>
                        <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
                          <button className="nr-btn nr-btnPrimary" onClick={doResetPool} disabled={busy}>
                            Yes, sign
                          </button>
                          <button className="nr-btn" onClick={() => setResetPoolConfirmOpen(false)} disabled={busy}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </section>
            ) : null}

            {/* NFT Creator Fee (npoolcrefee) – only when user has setcrefee in nauth */}
            {actionPermissions.setcrefee && wizPoolAux ? (
            <section className="nr-card">
              <div className="nr-cardHeader nr-collapsibleHeader">
                <div className="nr-headerLeft">
                  <div className="nr-titleRow">
                    <div className="nr-cardTitle">NFT Creator Fee</div>
                    <button className="nr-collapseBtn" onClick={() => toggleCard("poolCreatorFee")} aria-label="Toggle">
                      {openCards.poolCreatorFee ? "▾" : "▸"}
                    </button>
                  </div>
                  <div className="nr-muted">Creator fee configuration per pool. Call setcrefee to set or update.</div>
                </div>
              </div>

              {openCards.poolCreatorFee ? (
                <div className="nr-body">
                  {actionPermissions.setcrefee ? (
                    <div style={{ marginBottom: 20 }}>
                      <div className="nr-muted" style={{ marginBottom: 10 }}>
                        Set or update creator fee for a pool (action <b>setcrefee</b>).
                      </div>
                      <div className="nr-grid2">
                        <div className="nr-field">
                          <label>Pool</label>
                          <select
                            value={creFeeFormAction.poolKey}
                            onChange={(e) => {
                              const key = String(e.target.value || "").trim();
                              const pool = (poolsForProject || []).find((p) => String(p.poolKey) === key);
                              setCreFeeFormAction((s) => ({
                                ...s,
                                poolKey: key,
                                projectId: pool ? String(pool.projectId ?? "") : s.projectId,
                              }));
                            }}
                          >
                            <option value="">(select pool)</option>
                            {(poolsForProject || []).map((p) => (
                              <option key={String(p.poolHash)} value={String(p.poolKey || "")}>
                                {String(p.poolKey || "")}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="nr-field">
                          <label>Project ID</label>
                          <input
                            value={creFeeFormAction.projectId}
                            onChange={(e) => setCreFeeFormAction((s) => ({ ...s, projectId: e.target.value }))}
                            placeholder="from pool or enter"
                          />
                        </div>
                        <div className="nr-field">
                          <label>Fee % (basis points)</label>
                          <input
                            type="number"
                            min="0"
                            max="10000"
                            value={creFeeFormAction.feePercent}
                            onChange={(e) => setCreFeeFormAction((s) => ({ ...s, feePercent: e.target.value }))}
                            placeholder="e.g. 500 = 5%"
                          />
                        </div>
                        <div className="nr-field">
                          <label>Receiver (account)</label>
                          <input
                            value={creFeeFormAction.receiver}
                            onChange={(e) => setCreFeeFormAction((s) => ({ ...s, receiver: e.target.value }))}
                            placeholder="Account name"
                          />
                        </div>
                      </div>
                      <div className="nr-actions" style={{ marginTop: 8 }}>
                        <button className="nr-btn nr-btnPrimary" onClick={setCreFeeAction} disabled={!auth || busy}>
                          Set Creator Fee
                        </button>
                      </div>
                      <div className="nr-muted" style={{ marginTop: 12, marginBottom: 6 }}>Table npoolcrefee:</div>
                    </div>
                  ) : null}
                  {poolCreatorFees.length === 0 ? (
                    <div className="nr-muted" style={{ padding: 12 }}>
                      No npoolcrefee rows. Set Creator Fee via the form above or enable Creator Fee on a pool and save.
                    </div>
                  ) : (
                    <div className="nr-tableWrap" style={{ marginTop: 16 }}>
                      <table className="nr-table">
                        <thead>
                          <tr>
                            {Object.keys(poolCreatorFees[0] || {}).map((col) => (
                              <th key={col}>{col}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {poolCreatorFees.map((row, idx) => (
                            <tr key={idx}>
                              {Object.keys(poolCreatorFees[0] || {}).map((k) => (
                                <td key={k}>{String(row[k] ?? "")}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : null}
            </section>
            ) : null}

{/* Dependent NFTs (setpool) */}
            {actionPermissions.setpool && wizDepNft ? (
            <section id="nr-wiz-reqnft" className="nr-card">
              <div className="nr-cardHeader nr-collapsibleHeader">
                <div className="nr-headerLeft">
                  <div className="nr-titleRow">
                    <div className="nr-cardTitle">Dependent NFTs</div>
                    <button className="nr-collapseBtn" onClick={() => toggleCard("depNfts")} aria-label="Toggle">
                      {openCards.depNfts ? "▾" : "▸"}
                    </button>
                  </div>
                  <div className="nr-muted">Additional claim requirements</div>
                </div>
              </div>

              {openCards.depNfts ? (
                <div className="nr-body">
                  {!selectedPool ? (
                    <div className="nr-muted" style={{ padding: 12 }}>
                      Select a pool first.
                    </div>
                  ) : (
                    <>
                      <div className="nr-desc">
                        Dependent NFTs add extra holding conditions on top of the pool's own NFT rules.
                        Each rule can require the claimer to hold a minimum number of NFTs from another collection and optionally match immutable data keys.
                        All active rules must be satisfied at claim time for an NFT to be eligible.
                      </div>

                      <div className="nr-field">
                        <label style={{ paddingLeft: 6 }}>Rule</label>
                        <select
                          value={selectedReqId}
                          onChange={(e) => {
                            const v = String(e.target.value || "");
                            if (v === "") {
                              preferNewReqRef.current = true;
                              setSelectedReqId("");
    setExclAssetId("");
    setExclReason("");
                            } else {
                              preferNewReqRef.current = false;
                              setSelectedReqId(v);
                            }
                          }}
                        >
                          {reqForPool.map((r) => (
                            <option key={String(r.reqId)} value={String(r.reqId)}>
                              {String(r.reqKey)}
                            </option>
                          ))}
                          <option value="">(new rule)</option>
                        </select>
                      </div>

                      <div className="nr-formGrid">
                        <div className="nr-field">
                          <label>Pool Key</label>
                          <input value={reqForm.poolKey} readOnly />
                          {isNewRule ? (
                            <div className="nr-muted" style={{ marginTop: 0 }}>
                              Auto-filled from the selected pool.
                            </div>
                          ) : null}
                        </div>

                        <div className="nr-field">
                          <label>Req Key</label>
                          <input
                            placeholder="e.g. hold_2"
                            value={reqForm.reqKey}
                            onChange={(e) => setReqForm((s) => ({...s, reqKey: e.target.value }))}
                          />
                          {isNewRule ? (
                            <div className="nr-muted" style={{ marginTop: 0 }}>
                              Example: <b>hold_2</b> (unique rule identifier within this pool).
                            </div>
                          ) : null}
                        </div>

                        <div className="nr-field">
                          <label>Collection</label>
                          <input
                            placeholder="e.g. othercoll"
                            value={reqForm.collectionName}
                            onChange={(e) => setReqForm((s) => ({...s, collectionName: e.target.value }))}
                          />
                          {isNewRule ? (
                            <div className="nr-muted" style={{ marginTop: 0 }}>
                              Example: <b>othercoll</b> (the collection the user must additionally hold).
                            </div>
                          ) : null}
                        </div>

                        <div className="nr-field">
                          <label>NFT count</label>
                          <input
                            placeholder="e.g. 1"
                            value={reqForm.nftcount}
                            onChange={(e) => setReqForm((s) => ({...s, nftcount: e.target.value }))}
                          />
                          {isNewRule ? (
                            <div className="nr-muted" style={{ marginTop: 0 }}>
                              Minimum matching NFTs required (e.g. <b>1</b>, <b>2</b>,...).
                            </div>
                          ) : null}
                        </div>

                        <div className="nr-field">
                          <label>Active</label>
                          <select
                            value={reqForm.active ? "1" : "0"}
                            onChange={(e) => setReqForm((s) => ({...s, active: e.target.value === "1" }))}
                          >
                            <option value="1">true</option>
                            <option value="0">false</option>
                          </select>
                          {isNewRule ? (
                            <div className="nr-muted" style={{ marginTop: 0 }}>
                              Only <b>active</b> rules are enforced at claim time.
                            </div>
                          ) : null}
                        </div>

                                                <div className="nr-field" style={{ gridColumn: "1 / -1" }}>
                          <label>Required Immutable (attribute/value pairs)</label>

                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 8 }}>
                            {reqPairs.map((p, i) => (
                              <React.Fragment key={`r-req-${i}`}>
                                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                  <div className="nr-muted" style={{ fontSize: 12 }}>Attribute</div>
                                  <input
                                    placeholder="e.g. background"
                                    value={p.key}
                                    onChange={(e) => onReqPairChange(i, "key", e.target.value)}
                                  />
                                </div>
                                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                  <div className="nr-muted" style={{ fontSize: 12 }}>Value</div>
                                  <input
                                    placeholder="e.g. Purple (Epic)"
                                    value={p.value}
                                    onChange={(e) => onReqPairChange(i, "value", e.target.value)}
                                  />
                                </div>
                              </React.Fragment>
                            ))}
                          </div>

                          <div className="nr-actions" style={{ marginTop: 10, justifyContent: "flex-start" }}>
                            <button type="button" className="nr-btn" onClick={addReqPairRow} disabled={reqPairs.length >= 5}>
                              + Add row
                            </button>
                            <button type="button" className="nr-btn nr-btnDanger" onClick={removeReqPairRow} disabled={reqPairs.length === 0}>
                              Delete row
                            </button>
                            {reqPairs.length === 0 ? (
                              <div className="nr-muted" style={{ marginLeft: 10, alignSelf: "center" }}>
                                No immutable filters yet — add a row to require specific attributes.
                              </div>
                            ) : null}
                          </div>

                          <div className="nr-muted" style={{ marginTop: 10 }}>Built JSON array:</div>
                          <pre style={{ marginTop: 0, padding: 10, borderRadius: 12, background: "rgba(255,255,255,.04)", overflowX: "auto", whiteSpace: "pre-wrap" }}>
{reqForm.requiredImmutableText}
                          </pre>

                          {isNewRule ? (
                            <div className="nr-muted" style={{ marginTop: 0 }}>
                              Leave all attribute names empty to match any NFTs in the dependent collection, or add one or more pairs to require specific immutable data.
                            </div>
                          ) : null}
                        </div>
                        <div className="nr-actions" style={{ gridColumn: "1 / -1" }}>
                          {isNewRule ? (
                            <button className="nr-btn" onClick={() => { void previewReqClaimFromCheck(); }} disabled={busy}>
                              Check
                            </button>
                          ) : null}
                          <button className="nr-btn nr-btnPrimary" onClick={saveDependentNfts} disabled={busy}>
                            Save Dependent NFTs
                          </button>
                          <button className="nr-btn nr-btnDanger" onClick={deleteDependentNftRule} disabled={busy || !selectedReqId}>
                            Delete Rule
                          </button>
                        </div>

                        {isNewRule && reqCheck ? (
                          <div className="nr-checkPanel" style={{ gridColumn: "1 / -1" }}>
                            <div className={reqCheck.ok ? "nr-checkOk" : "nr-checkErr"}>
                              {reqCheck.ok ? "Looks good. You can save this rule." : "Please fix the issues below before saving."}
                            </div>
                            {reqCheck.errors?.length ? (
                              <ul className="nr-checkList">
                                {reqCheck.errors.map((m, i) => (
                                  <li key={`re-${i}`} className="nr-checkItem nr-checkItemErr">
                                    {m}
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                            {reqCheck.warnings?.length ? (
                              <ul className="nr-checkList">
                                {reqCheck.warnings.map((m, i) => (
                                  <li key={`rw-${i}`} className="nr-checkItem nr-checkItemWarn">
                                    {m}
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </>
                  )}
                </div>
              ) : null}
            </section>
            ) : null}

            {actionPermissions.setpool && wizStakeClaim ? (
            <section id="nr-wiz-stakeelig" className="nr-card">
              <div className="nr-cardHeader nr-collapsibleHeader">
                <div className="nr-headerLeft">
                  <div className="nr-titleRow">
                    <div className="nr-cardTitle">Stake eligibility (nstakelig)</div>
                    <button className="nr-collapseBtn" onClick={() => toggleCard("stakeElig")} aria-label="Toggle">
                      {openCards.stakeElig ? "▾" : "▸"}
                    </button>
                  </div>
                  <div className="nr-muted">
                    Rows from <b>nstakelig</b> (scope = poolHash). Fields include <b>eligible</b>, <b>points</b>, <b>stakedAmount</b>, and <b>updatedAt</b>.{" "}
                    Per-row <b>Delete</b> calls <b>delstakelig</b> (Stake Claim pools, type 6 only; requires nauths).
                  </div>
                </div>
                <div className="nr-actions">
                  <button type="button" className="nr-btn" onClick={() => void refetchStakeEligForSelectedPool()} disabled={!selectedPool || stakeEligLoading}>
                    {stakeEligLoading ? "Loading…" : "Refresh table"}
                  </button>
                </div>
              </div>
              {openCards.stakeElig ? (
                <div className="nr-body">
                  {!selectedPool ? (
                    <div className="nr-muted" style={{ padding: 12 }}>Select a pool first.</div>
                  ) : stakeEligRows.length ? (
                    <SimpleTable
                      rows={stakeEligRows}
                      height={360}
                      renderTrailingCell={(row) => (
                        <button
                          type="button"
                          className="nr-btn nr-btnDanger nr-btnSmall"
                          disabled={
                            busy ||
                            !actionPermissions.delstakelig ||
                            Number(selectedPool?.poolType || 0) !== 6
                          }
                          onClick={() => void deleteStakeEligRow(row?.account)}
                        >
                          Delete
                        </button>
                      )}
                    />
                  ) : (
                    <div className="nr-muted" style={{ padding: 12 }}>No nstakelig rows for this pool.</div>
                  )}
                </div>
              ) : null}
            </section>
            ) : null}

            {actionPermissions.setpool && wizStakeClaim ? (
            <section id="nr-wiz-stakesnap" className="nr-card">
              <div className="nr-cardHeader nr-collapsibleHeader">
                <div className="nr-headerLeft">
                  <div className="nr-titleRow">
                    <div className="nr-cardTitle">Staking snap config (nstaksnap)</div>
                    <button className="nr-collapseBtn" onClick={() => toggleCard("stakeSnap")} aria-label="Toggle">
                      {openCards.stakeSnap ? "▾" : "▸"}
                    </button>
                  </div>
                  <div className="nr-muted">
                    Off-chain <b>Stakingsnap</b> reads this table (scope = poolHash). Claim hub does not use it. Actions:{" "}
                    <b>setstaksnap</b>, <b>delstaksnap</b>, <b>clrstaksnap</b>.
                  </div>
                </div>
                <div className="nr-actions">
                  <button type="button" className="nr-btn" onClick={() => void refetchStakeSnapForSelectedPool()} disabled={!selectedPool || stakeSnapLoading}>
                    {stakeSnapLoading ? "Loading…" : "Refresh table"}
                  </button>
                </div>
              </div>
              {openCards.stakeSnap ? (
                <div className="nr-body">
                  {!selectedPool ? (
                    <div className="nr-muted" style={{ padding: 12 }}>Select a pool first.</div>
                  ) : (
                    <>
                      <div className="nr-desc" style={{ marginBottom: 12 }}>
                        Multiple rows per pool: use <b>pos</b> (1–65535) as the slot id. <b>minQuantity</b> is an on-chain asset (same format as pool reward).
                        <b>pointMultiplier</b> is an integer tier factor (1x / 2x / 3x, on-chain min 1).{" "}
                        <b>tokenContract</b> is the token account to check; <b>stakingPlatform</b> is a free-text id your service interprets (e.g. dex name).{" "}
                        Click a table row to load its values into the form below.
                      </div>
                      {stakeSnapRows.length ? (
                        <div className="nr-tableWrap" style={{ marginBottom: 16 }}>
                          <table className="nr-table">
                            <thead>
                              <tr>
                                <th>pos</th>
                                <th>stakeToken</th>
                                <th>tokenContract</th>
                                <th>minQuantity</th>
                                <th>pointMultiplier</th>
                                <th>stakingPlatform</th>
                                <th>stakeType</th>
                                <th>stakeName</th>
                                <th />
                              </tr>
                            </thead>
                            <tbody>
                              {stakeSnapRows.map((row, idx) => (
                                <tr
                                  key={`ss-${idx}`}
                                  style={{ cursor: "pointer" }}
                                  onClick={() => fillStakeSnapFormFromRow(row)}
                                >
                                  <td>{String(row?.pos ?? "")}</td>
                                  <td>{String(row?.stakeToken ?? "")}</td>
                                  <td>{String(row?.tokenContract ?? "")}</td>
                                  <td>{typeof row?.minQuantity === "string" ? row.minQuantity : JSON.stringify(row?.minQuantity ?? "")}</td>
                                  <td>{String(row?.pointMultiplier ?? "0")}</td>
                                  <td>{String(row?.stakingPlatform ?? "")}</td>
                                  <td>{String(row?.stakeType ?? "")}</td>
                                  <td>{String(row?.stakeName ?? "")}</td>
                                  <td>
                                    <button
                                      type="button"
                                      className="nr-btn nr-btnDanger nr-btnSmall"
                                      disabled={busy || !actionPermissions.delstaksnap}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        void deleteStakeSnapRow(row?.pos);
                                      }}
                                    >
                                      Delete
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="nr-muted" style={{ marginBottom: 12 }}>No nstaksnap rows for this pool.</div>
                      )}

                      <div className="nr-formGrid">
                        <div className="nr-field">
                          <label>pos (slot)</label>
                          <input
                            value={stakeSnapForm.pos}
                            onChange={(e) => setStakeSnapForm((s) => ({ ...s, pos: e.target.value }))}
                          />
                        </div>
                        <div className="nr-field">
                          <label>stakeToken (hint / label)</label>
                          <input
                            placeholder="e.g. XPR"
                            value={stakeSnapForm.stakeToken}
                            onChange={(e) => setStakeSnapForm((s) => ({ ...s, stakeToken: e.target.value }))}
                          />
                        </div>
                        <div className="nr-field">
                          <label>tokenContract</label>
                          <input
                            placeholder="e.g. eosio.token"
                            value={stakeSnapForm.tokenContract}
                            onChange={(e) => setStakeSnapForm((s) => ({ ...s, tokenContract: e.target.value }))}
                          />
                        </div>
                        <div className="nr-field">
                          <label>Min quantity (amount)</label>
                          <input
                            placeholder="e.g. 100.0000"
                            value={stakeSnapForm.minAmount}
                            onChange={(e) => setStakeSnapForm((s) => ({ ...s, minAmount: e.target.value }))}
                          />
                        </div>
                        <div className="nr-field">
                          <label>Min quantity (precision,SYMBOL)</label>
                          <input
                            placeholder="e.g. 4,XPR"
                            value={stakeSnapForm.minTokenSymbolRaw}
                            onChange={(e) => setStakeSnapForm((s) => ({ ...s, minTokenSymbolRaw: e.target.value }))}
                          />
                        </div>
                        <div className="nr-field">
                          <label>pointMultiplier (uint32, min 1)</label>
                          <input
                            type="number"
                            min="1"
                            step="1"
                            placeholder="e.g. 1"
                            value={stakeSnapForm.pointMultiplier}
                            onChange={(e) => setStakeSnapForm((s) => ({ ...s, pointMultiplier: e.target.value }))}
                          />
                        </div>
                        <div className="nr-field">
                          <label>stakingPlatform</label>
                          <input
                            placeholder="service-specific id"
                            value={stakeSnapForm.stakingPlatform}
                            onChange={(e) => setStakeSnapForm((s) => ({ ...s, stakingPlatform: e.target.value }))}
                          />
                        </div>
                        <div className="nr-field">
                          <label>stakeType</label>
                          <input
                            value={stakeSnapForm.stakeType}
                            onChange={(e) => setStakeSnapForm((s) => ({ ...s, stakeType: e.target.value }))}
                          />
                        </div>
                        <div className="nr-field">
                          <label>stakeName</label>
                          <input
                            value={stakeSnapForm.stakeName}
                            onChange={(e) => setStakeSnapForm((s) => ({ ...s, stakeName: e.target.value }))}
                          />
                        </div>
                      </div>
                      <div className="nr-actions" style={{ marginTop: 12, flexWrap: "wrap", gap: 8 }}>
                        <button type="button" className="nr-btn nr-btnPrimary" onClick={() => void saveStakeSnapRow()} disabled={busy || !actionPermissions.setstaksnap}>
                          Save / update row (setstaksnap)
                        </button>
                        <span className="nr-muted" style={{ alignSelf: "center" }}>clrstaksnap max rows:</span>
                        <input
                          style={{ width: 90 }}
                          value={stakeSnapClrMax}
                          onChange={(e) => setStakeSnapClrMax(e.target.value)}
                        />
                        <button type="button" className="nr-btn nr-btnDanger" onClick={() => void clearStakeSnapTable()} disabled={busy || !actionPermissions.clrstaksnap}>
                          Clear table (batch)
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : null}
            </section>
            ) : null}

            {/* Claim Calculator (setpool) */}
            {actionPermissions.setpool && wizVerify ? (
            <section id="nr-wiz-verify" className="nr-card">
              <div className="nr-cardHeader nr-collapsibleHeader">
                <div className="nr-headerLeft">
                  <div className="nr-titleRow">
                    <div className="nr-cardTitle">Claim Calculator</div>
                    <button className="nr-collapseBtn" onClick={() => toggleCard("claimCalc")} aria-label="Toggle">
                      {openCards.claimCalc ? "▾" : "▸"}
                    </button>
                  </div>
                  <div className="nr-muted">
                    Estimate how many NFTs can claim the selected pool and the corresponding token totals.
                    {selectedProject && selectedPool ? (
                      <span style={{ marginLeft: 8 }}>
                        Project: <b>{String(selectedProject.projectName || `#${selectedProject.projectId}`)}</b> • Pool: <b>{String(selectedPool.poolKey || "")}</b>
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className="nr-actions">
                  <button className="nr-btn" onClick={refreshClaimCalculator} disabled={!selectedPool || busy || claimCalcLoading}>
                    {claimCalcLoading ? "Calculating…" : "Refresh"}
                  </button>
                </div>
              </div>
              {openCards.claimCalc ? (
                !selectedPool ? (
                  <div className="nr-muted" style={{ padding: 12 }}>
                    Select a pool first.
                  </div>
                ) : (
                  <div className="nr-body">
                    {Number(selectedPool?.poolType || 0) === 6 ? (
                      <div className="nr-muted" style={{ marginBottom: 10 }}>
                        Stake Claim (type 6): token sum estimates are informational only unless per-account points are fully included in the calculation.
                      </div>
                    ) : null}
                    {claimCalcError ? <div className="nr-error">{claimCalcError}</div> : null}

                    {claimCalc ? (
                      <div className="nr-grid4" style={{ marginTop: 0 }}>
                      <div className="nr-chip" style={{ borderRadius: 14, padding: 12, background: "rgba(255,255,255,.04)" }}>
                        <div className="nr-muted">Eligible NFTs</div>
                        <div style={{ fontWeight: 900, fontSize: 18 }}>{claimCalc.eligibleNfts}</div>
                        <div className="nr-muted" style={{ marginTop: 4 }}>
                          Scanned {claimCalc.poolAssetsTotal} assets in {String(claimCalc.poolCollection)}{claimCalc.excludedAssetsCount ? ` (excluded: ${claimCalc.excludedAssetsCount})` : ""}
                          {claimCalc.poolImmutableRulesCount || claimCalc.poolMutableRulesCount ? (
                            <>
                              <br />
                              Filters: {claimCalc.poolImmutableRulesCount || 0} immutable
                              {claimCalc.poolMutableRulesCount ? `, ${claimCalc.poolMutableRulesCount} mutable` : ""} (asset mutable_data)
                            </>
                          ) : null}
                        </div>
                      </div>
                      <div className="nr-chip" style={{ borderRadius: 14, padding: 12, background: "rgba(255,255,255,.04)" }}>
                        <div className="nr-muted">Eligible Wallets</div>
                        <div style={{ fontWeight: 900, fontSize: 18 }}>{claimCalc.eligibleWallets ?? 0}</div>
                        <div className="nr-muted" style={{ marginTop: 4 }}>
                          Can claim
                        </div>
                      </div>
                      <div className="nr-chip" style={{ borderRadius: 14, padding: 12, background: "rgba(255,255,255,.04)" }}>
                        <div className="nr-muted">Already claimed</div>
                        <div style={{ fontWeight: 900, fontSize: 18 }}>{claimCalc.claimedEligibleNfts}</div>
                        <div className="nr-muted" style={{ marginTop: 4 }}>
                          {claimCalc.claimedAccounts} unique accounts
                        </div>
                      </div>
                      <div className="nr-chip" style={{ borderRadius: 14, padding: 12, background: "rgba(255,255,255,.04)" }}>
                        <div className="nr-muted">Remaining eligible</div>
                        <div style={{ fontWeight: 900, fontSize: 18 }}>{claimCalc.remainingEligibleNfts}</div>
                        <div className="nr-muted" style={{ marginTop: 4 }}>
                          Based on {claimCalc.activeRulesCount} dependent rule(s)
                        </div>
                      </div>
                      <div className="nr-chip" style={{ borderRadius: 14, padding: 12, background: "rgba(255,255,255,.04)" }}>
                        <div className="nr-muted">Token sums</div>
                        {claimCalc.reward ? (
                          <>
                            <div style={{ fontWeight: 900, fontSize: 13, marginTop: 4 }}>
                              Total potential: {formatAssetInt(claimCalc.totalPotential, claimCalc.reward.precision, claimCalc.reward.symbol)}
                            </div>
                            <div style={{ fontWeight: 900, fontSize: 13, marginTop: 0 }}>
                              Already claimed: {formatAssetInt(claimCalc.claimedSum, claimCalc.reward.precision, claimCalc.reward.symbol)}
                            </div>
                            <div className="nr-muted" style={{ marginTop: 4 }}>
                              Remaining: {formatAssetInt(claimCalc.remainingPotential, claimCalc.reward.precision, claimCalc.reward.symbol)}
                            </div>
                            <div className="nr-muted" style={{ marginTop: 8, fontSize: 11 }}>
                              {claimCalc.isMaxCooldown ? "1 Claim per Wallet" : "1 Claim per NFT"}
                            </div>
                          </>
                        ) : (
                          <div className="nr-muted">Reward not available.</div>
                        )}
                      </div>
                      </div>
                    ) : (
                      <div className="nr-muted">
                        Click <b>Refresh</b> to calculate.
                      </div>
                    )}
                  </div>
                )
              ) : null}
            </section>
            ) : null}

            {/* Claims (setpool) */}
            {actionPermissions.setpool && wizVerify ? (
            <section className="nr-card">
              <div className="nr-cardHeader nr-collapsibleHeader">
                <div className="nr-headerLeft">
                  <div className="nr-titleRow">
                    <div className="nr-cardTitle">Claims</div>
                    <button className="nr-collapseBtn" onClick={() => toggleCard("claims")} aria-label="Toggle">
                      {openCards.claims ? "▾" : "▸"}
                    </button>
                  </div>
                </div>
                <div className="nr-actions">
                  <button className="nr-btn" onClick={exportClaimsCsv} disabled={!selectedPool || busy}>
                    Export CSV (all)
                  </button>
                </div>
              </div>

              {openCards.claims ? (
                !selectedPool ? (
                  <div className="nr-muted" style={{ padding: 12 }}>
                    Select a pool first.
                  </div>
                ) : (
                  <>
                    <div className="nr-filters">
                      <div className="nr-field">
                        <label>Filter by account</label>
                        <input value={claimFilterAccount} onChange={(e) => setClaimFilterAccount(e.target.value)} placeholder="e.g. alice" />
                      </div>
                      <div className="nr-field">
                        <label>Filter by assetId</label>
                        <input value={claimFilterAsset} onChange={(e) => setClaimFilterAsset(e.target.value)} placeholder="e.g. 10987654321" />
                      </div>
                      <div className="nr-muted" style={{ alignSelf: "end", paddingBottom: 6 }}>
                        {claimsLoading ? "Loading…" : `${filteredClaims.length} rows`}
                      </div>
                    </div>
                    <SimpleTable rows={filteredClaims} height={420} />
                  </>
                )
              ) : null}
            </section>
            ) : null}

            {/* Wallets (setpool) */}
            {actionPermissions.setpool && wizVerify ? (
            <section className="nr-card">
              <div className="nr-cardHeader nr-collapsibleHeader">
                <div className="nr-headerLeft">
                  <div className="nr-titleRow">
                    <div className="nr-cardTitle">Wallets</div>
                    <button className="nr-collapseBtn" onClick={() => toggleCard("wallets")} aria-label="Toggle">
                      {openCards.wallets ? "▾" : "▸"}
                    </button>
                  </div>
                </div>
                <div className="nr-actions">
                  <button className="nr-btn" onClick={exportWalletsCsv} disabled={!selectedPool || busy}>
                    Export CSV (all)
                  </button>
                </div>
              </div>
              {openCards.wallets ? (
                !selectedPool ? (
                  <div className="nr-muted" style={{ padding: 12 }}>
                    Select a pool first.
                  </div>
                ) : (
                  <>
                    <div className="nr-filters">
                      <div className="nr-field">
                        <label>Filter by account</label>
                        <input value={walletFilterAccount} onChange={(e) => setWalletFilterAccount(e.target.value)} placeholder="e.g. alice" />
                      </div>
                      <div className="nr-muted" style={{ alignSelf: "end", paddingBottom: 6 }}>
                        {walletsLoading ? "Loading…" : `${filteredWallets.length} rows`}
                      </div>
                    </div>
                    <SimpleTable rows={filteredWallets} height={420} />
                  </>
                )
              ) : null}
            </section>
            ) : null}

            {wizPermissionGuide ? (
            <section id="nr-wiz-permission" className="nr-card">
              <ClaimRewardsPermissionGuide
                vaultAccount={headerVaultLabel}
                tokenContract={String(
                  selectedPool?.rewardTokenContract || poolForm.rewardTokenContract || ""
                ).trim()}
                projectName={selectedProject ? String(selectedProject.projectName || "") : ""}
              />
            </section>
            ) : null}

{/* Excluded claim assets (setpool) */}
{actionPermissions.setpool && wizExcl ? (
<section id="nr-wiz-excl" className="nr-card">
  <div className="nr-cardHeader nr-collapsibleHeader">
    <div className="nr-headerLeft">
      <div className="nr-titleRow">
        <div className="nr-cardTitle">Excluded claim assets</div>
        <button className="nr-collapseBtn" onClick={() => toggleCard("exclAssets")} aria-label="Toggle">
          {openCards.exclAssets ? "▾" : "▸"}
        </button>
      </div>
      <div className="nr-muted">
        Block NFT Asset IDs from claiming any pool under this project.
      </div>
    </div>
    <div className="nr-actions">
      <button
        className="nr-btn"
        onClick={() => loadExcludedAssetsForProject(selectedProjectId, { silent: false })}
        disabled={!selectedProjectId || busy || excludedAssetsLoading}
      >
        {excludedAssetsLoading ? "Refreshing…" : "Refresh"}
      </button>
    </div>
  </div>

  {openCards.exclAssets ? (
    !selectedProject ? (
      <div className="nr-muted" style={{ padding: 12 }}>
        Select a project first.
      </div>
    ) : (
      <div style={{ padding: 12 }}>
        <div className="nr-muted" style={{ marginBottom: 10 }}>
          Any asset ID listed here will be ignored by the Claim Calculator and blocked from claiming on-chain.
        </div>

        <div className="nr-grid2">
          <div className="nr-field">
            <label>Asset ID</label>
            <input
              value={exclAssetId}
              onChange={(e) => setExclAssetId(e.target.value)}
              placeholder="e.g. 1099511627776"
            />
          </div>
          <div className="nr-field">
            <label>Pick existing</label>
            <select value={String(exclAssetId || "")} onChange={(e) => {
              const v = String(e.target.value || "");
              setExclAssetId(v);
              const found = (excludedAssetsView || []).find((x) => String(x.assetId) === v);
              setExclReason(found?.reason ? String(found.reason) : "");
            }}>
              <option value="">(select)</option>
              {(excludedAssetOptions || []).map((r) => (
                <option key={String(r.assetId)} value={String(r.assetId)}>
                  {String(r.assetId)}
                  {r.reason ? ` — ${String(r.reason)}` : ""}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="nr-field">
          <label>Reason (optional)</label>
          <input
            value={exclReason}
            onChange={(e) => setExclReason(e.target.value)}
            placeholder="max 256 characters"
          />
        </div>

        <div className="nr-actions" style={{ marginTop: 8 }}>
          <button className="nr-btn" onClick={addExcludedAsset} disabled={!auth || busy}>
            Add / Update
          </button>
          <button className="nr-btn nr-btnDanger" onClick={() => deleteExcludedAsset(exclAssetId)} disabled={!auth || busy}>
            Delete
          </button>
          <button className="nr-btn nr-btnDanger" onClick={clearExcludedAssets} disabled={!auth || busy}>
            Delete all
          </button>
        </div>

        <div className="nr-muted" style={{ marginTop: 12, marginBottom: 6 }}>
          Current rows: {(excludedAssetsView || []).length}
          {excludedAssetsLoading ? " (loading…)" : ""}
        </div>

        <SimpleTable rows={excludedAssetsView || []} />
      </div>
    )
  ) : null}
</section>
) : null}

            {wizRest ? (
            <>
            {/* Promotion Management */}
            {actionPermissions.setpromo ? (
            <section className="nr-card">
              <div className="nr-cardHeader nr-collapsibleHeader">
                <div className="nr-headerLeft">
                  <div className="nr-titleRow">
                    <div className="nr-cardTitle">Promotion Management</div>
                    <button className="nr-collapseBtn" onClick={() => toggleCard("promotions")} aria-label="Toggle">
                      {openCards.promotions ? "▾" : "▸"}
                    </button>
                  </div>
                  <div className="nr-muted">Manage promotion campaigns and create pools from them</div>
                </div>
                <div className="nr-actions">
                  <button
                    className="nr-btn"
                    onClick={() => toggleCard("promotionFilters")}
                    style={{ fontSize: "12px", padding: "6px 10px" }}
                  >
                    {openCards.promotionFilters ? "▾" : "▸"} Filter
                  </button>
                </div>
              </div>

              {openCards.promotions ? (
                <div className="nr-body">
                  {/* Filters and Search */}
                  {openCards.promotionFilters ? (
                  <div className="nr-filters nr-filters-compact" ref={(el) => {
                    if (el) {
                      const styles = window.getComputedStyle(el);
                      const width = window.innerWidth;
                      const parent = el.parentElement;
                      const parentStyles = parent ? window.getComputedStyle(parent) : null;
                      const card = el.closest('.nr-card');
                      const cardStyles = card ? window.getComputedStyle(card) : null;
                      const mediaQuery = window.matchMedia('(max-width: 1200px)');
                      console.log('[Promotion Filters]', {
                        windowWidth: width,
                        cardWidth: cardStyles?.width,
                        cardMaxWidth: cardStyles?.maxWidth,
                        parentWidth: parentStyles?.width,
                        parentMaxWidth: parentStyles?.maxWidth,
                        filtersWidth: styles.width,
                        filtersMaxWidth: styles.maxWidth,
                        filtersOffsetWidth: el.offsetWidth,
                        display: styles.display,
                        flexDirection: styles.flexDirection,
                        flexWrap: styles.flexWrap,
                        boxSizing: styles.boxSizing,
                        overflow: styles.overflow,
                        isMobile: width < 1200,
                        mediaQueryMatches: mediaQuery.matches,
                        padding: styles.padding,
                        gap: styles.gap
                      });
                    }
                  }}>
                    <div className="nr-field" ref={(el) => {
                      if (el) {
                        const styles = window.getComputedStyle(el);
                        const selectEl = el.querySelector('select');
                        const selectStyles = selectEl ? window.getComputedStyle(selectEl) : null;
                        console.log('[Promotion Filter Field]', {
                          fieldWidth: styles.width,
                          fieldMaxWidth: styles.maxWidth,
                          fieldFlex: styles.flex,
                          selectWidth: selectStyles?.width,
                          selectMaxWidth: selectStyles?.maxWidth,
                          selectBoxSizing: selectStyles?.boxSizing
                        });
                      }
                    }}>
                      <label>Filter by Project</label>
                      <select value={promotionFilterProject} onChange={(e) => setPromotionFilterProject(e.target.value)}>
                        <option value="">All Projects</option>
                        {allowedProjects.map((p) => (
                          <option key={String(p.projectId)} value={String(p.projectId)}>
                            {p.projectName || `#${p.projectId}`}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="nr-field">
                      <label>Filter by Status</label>
                      <select value={promotionFilterStatus} onChange={(e) => setPromotionFilterStatus(e.target.value)}>
                        <option value="">All</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="finished">Finished</option>
                      </select>
                    </div>
                    <div className="nr-field">
                      <label>Search</label>
                      <input
                        value={promotionSearch}
                        onChange={(e) => setPromotionSearch(e.target.value)}
                        placeholder="Promotion name..."
                      />
                    </div>
                  </div>
                  ) : null}

                  {/* Promotion List Table */}
                  <div className="nr-tableWrap" style={{ marginTop: 16 }}>
                    <SimpleTable
                      rows={promotionsView || []}
                      height={300}
                      onRowClick={(row) => {
                        // Use _promoteId from the row object (not displayed in table)
                        const promoId = row._promoteId;
                        if (promoId) setSelectedPromotionId(promoId);
                      }}
                    />
                  </div>

                  {/* Promotion Form */}
                  {selectedProject ? (
                    <div style={{ marginTop: 24 }}>
                      <div className="nr-formGrid">
                        <div className="nr-field">
                          <label>Promotion</label>
                          <select value={selectedPromotionId} onChange={(e) => {
                            const v = String(e.target.value || "");
                            if (!v) {
                              preferNewPromotionRef.current = true;
                              setSelectedPromotionId("");
                            } else {
                              preferNewPromotionRef.current = false;
                              setSelectedPromotionId(v);
                            }
                          }} disabled={!selectedProject}>
                            {promotionsForProject.map((p) => (
                              <option key={String(p.promoteId)} value={String(p.promoteId)}>
                                {String(p.promotionName)}
                              </option>
                            ))}
                            <option value="">(new promotion)</option>
                          </select>
                        </div>

                        {!selectedPromotion && promotionsForProject.length === 0 ? (
                          <div className="nr-field" style={{ gridColumn: "1 / -1" }}>
                            <div className="nr-muted" style={{ padding: 12 }}>
                              No promotions found for this project yet. Fill the form below and click <b>Save Promotion</b> to create one.
                            </div>
                          </div>
                        ) : null}

                        <div className="nr-field">
                          <label>Promotion Name *</label>
                          <input
                            value={promotionForm.promotionName}
                            onChange={(e) => setPromotionForm((s) => ({ ...s, promotionName: e.target.value }))}
                            placeholder="e.g. summer-2024"
                            maxLength={70}
                          />
                          <div className="nr-help">Will be used as poolKey. Max 70 characters.</div>
                        </div>

                        <div className="nr-field">
                          <label>Project *</label>
                          <select
                            value={promotionForm.projectId}
                            onChange={(e) => setPromotionForm((s) => ({ ...s, projectId: e.target.value }))}
                            disabled={!!selectedPromotion}
                          >
                            <option value="">Select project...</option>
                            {allowedProjects.map((p) => (
                              <option key={String(p.projectId)} value={String(p.projectId)}>
                                {p.projectName || `#${p.projectId}`}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="nr-field">
                          <label>Collection Name *</label>
                          <input
                            value={promotionForm.collectionName}
                            onChange={(e) => setPromotionForm((s) => ({ ...s, collectionName: e.target.value }))}
                            placeholder="e.g. owlnoctun or account"
                          />
                          <div className="nr-help">AtomicAssets Collection Name. Use <b>"account"</b> to skip NFT validation (account-based claims).</div>
                        </div>

                        <div className="nr-field">
                          <label>Reward Amount *</label>
                          <input
                            value={promotionForm.rewardAmount}
                            onChange={(e) => setPromotionForm((s) => ({ ...s, rewardAmount: e.target.value }))}
                            placeholder={promotionForm.rewardTokenSymbolRaw ? amountPlaceholderForSymbol(promotionForm.rewardTokenSymbolRaw) : "e.g. 1000.000000"}
                          />
                          {promotionForm.rewardTokenSymbolRaw ? (
                            <div className="nr-muted" style={{ marginTop: 0 }}>
                              Enter only the amount (without token symbol). Example: <b>{amountPlaceholderForSymbol(promotionForm.rewardTokenSymbolRaw)}</b>
                            </div>
                          ) : null}
                        </div>

                        <div className="nr-field">
                          <label>Token symbol</label>
                          <select
                            value={promotionForm.rewardTokenSymbolRaw}
                            onChange={(e) => setPromotionForm((s) => ({ ...s, rewardTokenSymbolRaw: e.target.value }))}
                          >
                            <option value="">(select)</option>
                            {promotionForm.rewardTokenSymbolRaw &&
                            !poolTokenSymbolOptions.some((o) => String(o.tokenSymbolRaw) === String(promotionForm.rewardTokenSymbolRaw)) ? (
                              <option value={promotionForm.rewardTokenSymbolRaw}>{promotionForm.rewardTokenSymbolRaw}</option>
                            ) : null}
                            {poolTokenSymbolOptions.map((o) => (
                              <option key={String(o.tokenSymbolRaw)} value={String(o.tokenSymbolRaw)}>
                                {String(o.symbol)} ({String(o.precision)} dp)
                              </option>
                            ))}
                            {poolTokenSymbolOptions.length === 0 ? <option value="">No allowed tokens found</option> : null}
                          </select>
                        </div>

                        <div className="nr-field">
                          <label>Token contract</label>
                          <select
                            value={promotionForm.rewardTokenContract}
                            onChange={(e) => setPromotionForm((s) => ({ ...s, rewardTokenContract: e.target.value }))}
                            disabled={!promotionForm.rewardTokenSymbolRaw}
                          >
                            <option value="">(select)</option>
                            {promotionForm.rewardTokenContract &&
                            !promotionTokenContractOptions.some((o) => String(o.tokenContract) === String(promotionForm.rewardTokenContract)) ? (
                              <option value={promotionForm.rewardTokenContract}>{promotionForm.rewardTokenContract}</option>
                            ) : null}
                            {promotionTokenContractOptions.map((o) => (
                              <option key={String(o.tokenContract)} value={String(o.tokenContract)}>
                                {String(o.tokenContract)}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="nr-field">
                          <label>Max Claim Count *</label>
                          <input
                            type="number"
                            min="1"
                            value={promotionForm.maxClaimCount}
                            onChange={(e) => setPromotionForm((s) => ({ ...s, maxClaimCount: e.target.value }))}
                            placeholder="1"
                          />
                          <div className="nr-help">Maximum number of claims for this promotion</div>
                          {selectedPromotion ? (
                            <div className="nr-muted" style={{ marginTop: 4 }}>
                              Already claimed: {Number(selectedPromotion.actualClaimCount || 0)}
                            </div>
                          ) : null}
                        </div>

                        <div className="nr-field">
                          <label>Start (UTC)</label>
                          <div className="nr-grid2">
                            <input
                              type="date"
                              value={epochSecToLocalDateValue(promotionForm.startTimeSec)}
                              onChange={(e) => {
                                const time = epochSecToLocalTimeValue(promotionForm.startTimeSec) || "00:00";
                                const sec = localDateTimePartsToEpochSec(e.target.value, time);
                                setPromotionForm((s) => ({ ...s, startTimeSec: String(sec) }));
                              }}
                            />
                            <input
                              type="time"
                              step="60"
                              value={epochSecToLocalTimeValue(promotionForm.startTimeSec)}
                              onChange={(e) => {
                                const date = epochSecToLocalDateValue(promotionForm.startTimeSec);
                                const sec = localDateTimePartsToEpochSec(date, e.target.value);
                                setPromotionForm((s) => ({ ...s, startTimeSec: String(sec) }));
                              }}
                            />
                          </div>
                          <div className="nr-muted" style={{ marginTop: 4 }}>
                            Leave empty (0) for no start limit.
                          </div>
                        </div>

                        <div className="nr-field">
                          <label>End (UTC)</label>
                          <div className="nr-grid2">
                            <input
                              type="date"
                              value={epochSecToLocalDateValue(promotionForm.endTimeSec)}
                              onChange={(e) => {
                                const time = epochSecToLocalTimeValue(promotionForm.endTimeSec) || "00:00";
                                const sec = localDateTimePartsToEpochSec(e.target.value, time);
                                setPromotionForm((s) => ({ ...s, endTimeSec: String(sec) }));
                              }}
                            />
                            <input
                              type="time"
                              step="60"
                              value={epochSecToLocalTimeValue(promotionForm.endTimeSec)}
                              onChange={(e) => {
                                const date = epochSecToLocalDateValue(promotionForm.endTimeSec);
                                const sec = localDateTimePartsToEpochSec(date, e.target.value);
                                setPromotionForm((s) => ({ ...s, endTimeSec: String(sec) }));
                              }}
                            />
                          </div>
                          <div className="nr-muted" style={{ marginTop: 4 }}>
                            Leave empty (0) for no end limit.
                          </div>
                        </div>

                        <div className="nr-field" style={{ gridColumn: "1 / -1" }}>
                          <label>Description</label>
                          <textarea
                            value={promotionForm.description}
                            onChange={(e) => setPromotionForm((s) => ({ ...s, description: e.target.value }))}
                            rows={3}
                            maxLength={500}
                            placeholder="Optional description for the promotion"
                          />
                        </div>

                        <div className="nr-field" style={{ gridColumn: "1 / -1" }}>
                          <label>Image URL</label>
                          <input
                            value={promotionForm.imageUrl}
                            onChange={(e) => setPromotionForm((s) => ({ ...s, imageUrl: e.target.value }))}
                            placeholder="https://..."
                            maxLength={256}
                          />
                          {promotionForm.imageUrl ? (
                            <img src={promotionForm.imageUrl} alt="Preview" style={{ maxWidth: 200, marginTop: 8, borderRadius: 4 }} onError={(e) => { e.target.style.display = "none"; }} />
                          ) : null}
                        </div>

                        <div className="nr-field">
                          <label>Active</label>
                          <select
                            value={promotionForm.active ? "1" : "0"}
                            onChange={(e) => setPromotionForm((s) => ({ ...s, active: e.target.value === "1" }))}
                          >
                            <option value="1">true</option>
                            <option value="0">false</option>
                          </select>
                        </div>
                      </div>

                      {promotionCheck ? (
                        <div style={{ marginTop: 16, padding: 12, borderRadius: 4, background: promotionCheck.ok ? "rgba(0, 200, 0, 0.1)" : "rgba(200, 0, 0, 0.1)" }}>
                          <div className={promotionCheck.ok ? "nr-checkOk" : "nr-checkErr"}>
                            {promotionCheck.ok ? "Looks good. You can save this promotion." : "Please fix the issues below before saving."}
                          </div>
                          {promotionCheck.errors?.length ? (
                            <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                              {promotionCheck.errors.map((m, i) => (
                                <li key={i} style={{ color: "#ff6b6b" }}>{m}</li>
                              ))}
                            </ul>
                          ) : null}
                          {promotionCheck.warnings?.length ? (
                            <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                              {promotionCheck.warnings.map((m, i) => (
                                <li key={i} style={{ color: "#ffa500" }}>{m}</li>
                              ))}
                            </ul>
                          ) : null}
                        </div>
                      ) : null}

                      <div className="nr-actions" style={{ marginTop: 16 }}>
                        {selectedPromotion ? (
                          <>
                            <button className="nr-btn" onClick={() => setSelectedPromotionId("")}>
                              Cancel
                            </button>
                            <button className="nr-btn" onClick={() => { void previewPromotionClaimFromCheck(); }} disabled={busy}>
                              Check
                            </button>
                            <button className="nr-btn nr-btnPrimary" onClick={updatePromotion} disabled={busy}>
                              Update Promotion
                            </button>
                            {!selectedPromotion.poolCreated ? (
                              <button className="nr-btn nr-btnPrimary" onClick={() => setPromotionPoolForm({ poolKey: selectedPromotion.promotionName })} disabled={busy}>
                                Create Pool
                              </button>
                            ) : null}
                            <button className="nr-btn nr-btnDanger" onClick={deletePromotion} disabled={busy}>
                              Delete
                            </button>
                          </>
                        ) : (
                          <>
                            <button className="nr-btn" onClick={() => setSelectedPromotionId("")}>
                              Cancel
                            </button>
                            <button className="nr-btn" onClick={() => { void previewPromotionClaimFromCheck(); }} disabled={busy}>
                              Check
                            </button>
                            <button className="nr-btn nr-btnPrimary" onClick={savePromotion} disabled={busy}>
                              Save Promotion
                            </button>
                          </>
                        )}
                      </div>

                      {/* Pool Creation Form (shown when Create Pool button is clicked) */}
                      {selectedPromotion && !selectedPromotion.poolCreated && promotionPoolForm.poolKey !== undefined ? (
                        <div className="nr-subcard" style={{ marginTop: 24 }}>
                          <div className="nr-cardHeaderSmall nr-collapsibleHeader">
                            <div className="nr-headerLeft">
                              <div className="nr-titleRow">
                                <div className="nr-cardTitle">Create Pool from Promotion</div>
                                <button className="nr-collapseBtn" onClick={() => toggleCard("promotionPoolForm")} aria-label="Toggle">
                                  {openCards.promotionPoolForm ? "▾" : "▸"}
                                </button>
                              </div>
                            </div>
                          </div>
                          {openCards.promotionPoolForm ? (
                            <div className="nr-body">
                              <div className="nr-muted" style={{ padding: 12, marginBottom: 16 }}>
                                This will create a pool from the promotion "{selectedPromotion.promotionName}".<br />
                                The pool will be created with:<br />
                                - poolKey: "{selectedPromotion.promotionName}"<br />
                                - cooldownSec: forever (one claim per wallet)<br />
                                - poolType: 2 (Promotion)
                              </div>
                              <div className="nr-actions">
                                <button className="nr-btn" onClick={() => setPromotionPoolForm({ poolKey: undefined })}>
                                  Cancel
                                </button>
                                <button className="nr-btn nr-btnPrimary" onClick={createPromotionPool} disabled={busy}>
                                  Create Pool
                                </button>
                              </div>
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div className="nr-muted" style={{ padding: 12 }}>
                      Select a project first.
                    </div>
                  )}
                </div>
              ) : null}
            </section>
            ) : null}

            {/* Survey Management */}
            {actionPermissions.setsurvey ? (
            <section className="nr-card">
              <div className="nr-cardHeader nr-collapsibleHeader">
                <div className="nr-headerLeft">
                  <div className="nr-titleRow">
                    <div className="nr-cardTitle">Survey Management</div>
                    <button className="nr-collapseBtn" onClick={() => toggleCard("surveys")} aria-label="Toggle">
                      {openCards.surveys ? "▾" : "▸"}
                    </button>
                  </div>
                  <div className="nr-muted">Manage surveys and create pools from them</div>
                </div>
                <div className="nr-actions">
                  <button
                    className="nr-btn"
                    onClick={() => toggleCard("surveyFilters")}
                    style={{ fontSize: "12px", padding: "6px 10px" }}
                  >
                    {openCards.surveyFilters ? "▾" : "▸"} Filter
                  </button>
                </div>
              </div>

              {openCards.surveys ? (
                <div className="nr-body">
                  {/* Filters and Search */}
                  {openCards.surveyFilters ? (
                  <div className="nr-filters nr-filters-compact" ref={(el) => {
                    if (el) {
                      const styles = window.getComputedStyle(el);
                      const width = window.innerWidth;
                      const parent = el.parentElement;
                      const parentStyles = parent ? window.getComputedStyle(parent) : null;
                      const card = el.closest('.nr-card');
                      const cardStyles = card ? window.getComputedStyle(card) : null;
                      const mediaQuery = window.matchMedia('(max-width: 1200px)');
                      console.log('[Survey Filters]', {
                        windowWidth: width,
                        cardWidth: cardStyles?.width,
                        cardMaxWidth: cardStyles?.maxWidth,
                        parentWidth: parentStyles?.width,
                        parentMaxWidth: parentStyles?.maxWidth,
                        filtersWidth: styles.width,
                        filtersMaxWidth: styles.maxWidth,
                        filtersOffsetWidth: el.offsetWidth,
                        display: styles.display,
                        flexDirection: styles.flexDirection,
                        flexWrap: styles.flexWrap,
                        boxSizing: styles.boxSizing,
                        overflow: styles.overflow,
                        isMobile: width < 1200,
                        mediaQueryMatches: mediaQuery.matches,
                        padding: styles.padding,
                        gap: styles.gap
                      });
                    }
                  }}>
                    <div className="nr-field" ref={(el) => {
                      if (el) {
                        const styles = window.getComputedStyle(el);
                        const selectEl = el.querySelector('select');
                        const selectStyles = selectEl ? window.getComputedStyle(selectEl) : null;
                        const parent = el.parentElement;
                        const parentStyles = parent ? window.getComputedStyle(parent) : null;
                        console.log('[Survey Filter Field]', {
                          fieldWidth: styles.width,
                          fieldMaxWidth: styles.maxWidth,
                          fieldFlex: styles.flex,
                          fieldOffsetWidth: el.offsetWidth,
                          fieldMinWidth: styles.minWidth,
                          parentWidth: parentStyles?.width,
                          parentOffsetWidth: parent?.offsetWidth,
                          selectWidth: selectStyles?.width,
                          selectMaxWidth: selectStyles?.maxWidth,
                          selectOffsetWidth: selectEl?.offsetWidth,
                          selectBoxSizing: selectStyles?.boxSizing,
                          selectMinWidth: selectStyles?.minWidth
                        });
                      }
                    }}>
                      <label>Filter by Project</label>
                      <select value={surveyFilterProject} onChange={(e) => setSurveyFilterProject(e.target.value)}>
                        <option value="">All Projects</option>
                        {allowedProjects.map((p) => (
                          <option key={String(p.projectId)} value={String(p.projectId)}>
                            {p.projectName || `#${p.projectId}`}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="nr-field">
                      <label>Filter by Type</label>
                      <select value={surveyFilterType} onChange={(e) => setSurveyFilterType(e.target.value)}>
                        <option value="">All Types</option>
                        <option value="1">Normal</option>
                        <option value="2">Answer-based</option>
                        <option value="3">Raffle</option>
                      </select>
                    </div>
                    <div className="nr-field">
                      <label>Filter by Status</label>
                      <select value={surveyFilterStatus} onChange={(e) => setSurveyFilterStatus(e.target.value)}>
                        <option value="">All</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="closed">Closed</option>
                      </select>
                    </div>
                    <div className="nr-field">
                      <label>Search</label>
                      <input
                        value={surveySearch}
                        onChange={(e) => setSurveySearch(e.target.value)}
                        placeholder="Survey key or text..."
                      />
                    </div>
                  </div>
                  ) : null}

                  {/* Survey List Table */}
                  <div className="nr-tableWrap" style={{ marginTop: 16 }}>
                    <SimpleTable
                      rows={surveysView || []}
                      height={300}
                      onRowClick={(row) => {
                        const surveyKey = row["Survey Key"];
                        if (surveyKey) setSelectedSurveyKey(surveyKey);
                      }}
                    />
                  </div>

                  {/* Survey Form */}
                  {selectedProject ? (
                    <div style={{ marginTop: 24 }}>
                      <div className="nr-formGrid">
                        <div className="nr-field">
                          <label>Survey</label>
                          <select value={selectedSurveyKey} onChange={(e) => {
                            const v = String(e.target.value || "");
                            if (!v) {
                              preferNewSurveyRef.current = true;
                              setSelectedSurveyKey("");
                            } else {
                              preferNewSurveyRef.current = false;
                              setSelectedSurveyKey(v);
                            }
                          }} disabled={!selectedProject}>
                            {surveysForProject.map((s) => (
                              <option key={String(s.surveyKey)} value={String(s.surveyKey)}>
                                {String(s.surveyKey)}
                              </option>
                            ))}
                            <option value="">(new survey)</option>
                          </select>
                        </div>

                        {!selectedSurvey && surveysForProject.length === 0 ? (
                          <div className="nr-field" style={{ gridColumn: "1 / -1" }}>
                            <div className="nr-muted" style={{ padding: 12 }}>
                              No surveys found for this project yet. Fill the form below and click <b>Save Survey</b> to create one.
                            </div>
                          </div>
                        ) : null}
                        <div className="nr-field">
                          <label>Survey Key *</label>
                          <input
                            value={surveyForm.surveyKey}
                            onChange={(e) => setSurveyForm((s) => ({ ...s, surveyKey: e.target.value }))}
                            placeholder="e.g. q1-2024"
                            maxLength={70}
                            disabled={!!selectedSurvey}
                          />
                          <div className="nr-help">Unique key for this survey. Max 70 characters.</div>
                        </div>

                        <div className="nr-field">
                          <label>Project *</label>
                          <select
                            value={surveyForm.projectId}
                            onChange={(e) => setSurveyForm((s) => ({ ...s, projectId: e.target.value }))}
                            disabled={!!selectedSurvey}
                          >
                            <option value="">Select project...</option>
                            {allowedProjects.map((p) => (
                              <option key={String(p.projectId)} value={String(p.projectId)}>
                                {p.projectName || `#${p.projectId}`}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="nr-field" style={{ gridColumn: "1 / -1" }}>
                          <label>Survey Question *</label>
                          <textarea
                            value={surveyForm.surveyText}
                            onChange={(e) => setSurveyForm((s) => ({ ...s, surveyText: e.target.value }))}
                            rows={3}
                            maxLength={500}
                            placeholder="e.g. Which feature do you like best?"
                          />
                        </div>

                        <div className="nr-field" style={{ gridColumn: "1 / -1" }}>
                          <label>Survey Description</label>
                          <textarea
                            value={surveyForm.surveyDescription}
                            onChange={(e) => setSurveyForm((s) => ({ ...s, surveyDescription: e.target.value }))}
                            rows={2}
                            maxLength={500}
                            placeholder="Optional description"
                          />
                        </div>

                        <div className="nr-field" style={{ gridColumn: "1 / -1" }}>
                          <label>Survey Image URL</label>
                          <input
                            value={surveyForm.surveyImageUrl}
                            onChange={(e) => setSurveyForm((s) => ({ ...s, surveyImageUrl: e.target.value }))}
                            placeholder="https://..."
                            maxLength={256}
                          />
                          {surveyForm.surveyImageUrl ? (
                            <img src={surveyForm.surveyImageUrl} alt="Preview" style={{ maxWidth: 200, marginTop: 8, borderRadius: 4 }} onError={(e) => { e.target.style.display = "none"; }} />
                          ) : null}
                        </div>

                        <div className="nr-field">
                          <label>Number of Options *</label>
                          <select
                            value={surveyForm.numOptions}
                            onChange={(e) => setSurveyForm((s) => ({ ...s, numOptions: Number(e.target.value) }))}
                          >
                            {[1, 2, 3, 4, 5, 6].map((n) => (
                              <option key={n} value={n}>
                                {n}
                              </option>
                            ))}
                          </select>
                        </div>

                        {Array.from({ length: surveyForm.numOptions }, (_, i) => i + 1).map((n) => (
                          <div key={n} className="nr-field">
                            <label>Option {n} *</label>
                            <input
                              value={surveyForm[`option${n}`]}
                              onChange={(e) => setSurveyForm((s) => ({ ...s, [`option${n}`]: e.target.value }))}
                              placeholder={`Option ${n} text`}
                              maxLength={200}
                            />
                          </div>
                        ))}

                        <div className="nr-field">
                          <label>Survey Type *</label>
                          <select
                            value={surveyForm.surveyType}
                            onChange={(e) => {
                              const val = Number(e.target.value);
                              setSurveyForm((s) => ({ ...s, surveyType: val, requiredAnswers: val === 2 ? s.requiredAnswers : "" }));
                            }}
                          >
                            <option value="1">Normal (1)</option>
                            <option value="2">Answer-based (2)</option>
                            <option value="3">Raffle (3)</option>
                          </select>
                          <div className="nr-muted" style={{ marginTop: 4 }}>
                            {surveyForm.surveyType === 1 ? "All participants can claim" : surveyForm.surveyType === 2 ? "Only specific answers can claim" : "Only winners can claim"}
                          </div>
                        </div>

                        {surveyForm.surveyType === 2 ? (
                          <div className="nr-field">
                            <label>Required Answers</label>
                            <input
                              value={surveyForm.requiredAnswers}
                              onChange={(e) => setSurveyForm((s) => ({ ...s, requiredAnswers: e.target.value }))}
                              placeholder="e.g. 1,3,5"
                              maxLength={20}
                            />
                            <div className="nr-help">
                              Comma-separated list of option numbers (1-{surveyForm.numOptions}) that are required for claim eligibility. Leave empty if all participants can claim.
                            </div>
                          </div>
                        ) : null}

                        <div className="nr-field">
                          <label>Allow Multiple Selection</label>
                          <select
                            value={surveyForm.allowMultiple ? "1" : "0"}
                            onChange={(e) => setSurveyForm((s) => ({ ...s, allowMultiple: e.target.value === "1" }))}
                          >
                            <option value="0">false</option>
                            <option value="1">true</option>
                          </select>
                        </div>

                        <div className="nr-field">
                          <label>Active</label>
                          <select
                            value={surveyForm.active ? "1" : "0"}
                            onChange={(e) => setSurveyForm((s) => ({ ...s, active: e.target.value === "1" }))}
                          >
                            <option value="1">true</option>
                            <option value="0">false</option>
                          </select>
                        </div>

                        {selectedSurvey ? (
                          <div className="nr-field">
                            <label>Closed</label>
                            <select
                              value={surveyForm.closed ? "1" : "0"}
                              onChange={(e) => setSurveyForm((s) => ({ ...s, closed: e.target.value === "1" }))}
                            >
                              <option value="0">false</option>
                              <option value="1">true</option>
                            </select>
                            <div className="nr-muted" style={{ marginTop: 4 }}>
                              Manually close survey (required for Raffle winner drawing)
                            </div>
                          </div>
                        ) : null}

                        <div className="nr-field">
                          <label>Start (UTC)</label>
                          <div className="nr-grid2">
                            <input
                              type="date"
                              value={epochSecToLocalDateValue(surveyForm.startTimeSec)}
                              onChange={(e) => {
                                const time = epochSecToLocalTimeValue(surveyForm.startTimeSec) || "00:00";
                                const sec = localDateTimePartsToEpochSec(e.target.value, time);
                                setSurveyForm((s) => ({ ...s, startTimeSec: String(sec) }));
                              }}
                            />
                            <input
                              type="time"
                              step="60"
                              value={epochSecToLocalTimeValue(surveyForm.startTimeSec)}
                              onChange={(e) => {
                                const date = epochSecToLocalDateValue(surveyForm.startTimeSec);
                                const sec = localDateTimePartsToEpochSec(date, e.target.value);
                                setSurveyForm((s) => ({ ...s, startTimeSec: String(sec) }));
                              }}
                            />
                          </div>
                          <div className="nr-muted" style={{ marginTop: 4 }}>
                            Leave empty (0) for no start limit.
                          </div>
                        </div>

                        <div className="nr-field">
                          <label>End (UTC)</label>
                          <div className="nr-grid2">
                            <input
                              type="date"
                              value={epochSecToLocalDateValue(surveyForm.endTimeSec)}
                              onChange={(e) => {
                                const time = epochSecToLocalTimeValue(surveyForm.endTimeSec) || "00:00";
                                const sec = localDateTimePartsToEpochSec(e.target.value, time);
                                setSurveyForm((s) => ({ ...s, endTimeSec: String(sec) }));
                              }}
                            />
                            <input
                              type="time"
                              step="60"
                              value={epochSecToLocalTimeValue(surveyForm.endTimeSec)}
                              onChange={(e) => {
                                const date = epochSecToLocalDateValue(surveyForm.endTimeSec);
                                const sec = localDateTimePartsToEpochSec(date, e.target.value);
                                setSurveyForm((s) => ({ ...s, endTimeSec: String(sec) }));
                              }}
                            />
                          </div>
                          <div className="nr-muted" style={{ marginTop: 4 }}>
                            Leave empty (0) for no end limit.
                          </div>
                        </div>

                        {selectedSurvey && Number(selectedSurvey.surveyType) === 3 ? (
                          <div style={{ gridColumn: "1 / -1", marginTop: 8 }}>
                            <div className="nr-actions">
                              <button type="button" className="nr-btn" onClick={() => void drawSurveyWinners()} disabled={busy || !selectedSurvey?.closed}>
                                Draw Winners
                              </button>
                            </div>
                            <div className="nr-muted" style={{ marginTop: 4 }}>
                              {selectedSurvey?.closed ? "Draw winners from participants. This action cannot be undone." : "Only available when the survey is closed (Closed = true). Close the survey first, then draw winners."}
                            </div>
                          </div>
                        ) : null}
                      </div>

                      {/* Pool Creation Form (shown when Create Pool button is clicked) */}
                      {selectedSurvey && !selectedSurvey.poolKey && surveyPoolForm.poolKey !== undefined ? (
                        <div className="nr-subcard" style={{ marginTop: 24 }}>
                          <div className="nr-cardHeaderSmall nr-collapsibleHeader">
                            <div className="nr-headerLeft">
                              <div className="nr-titleRow">
                                <div className="nr-cardTitle">Create Pool from Survey</div>
                                <button className="nr-collapseBtn" onClick={() => toggleCard("surveyPoolForm")} aria-label="Toggle">
                                  {openCards.surveyPoolForm ? "▾" : "▸"}
                                </button>
                              </div>
                            </div>
                          </div>
                          {openCards.surveyPoolForm ? (
                            <div className="nr-body">
                              {!canCreatePoolFromSurvey ? (
                                <div className="nr-muted" style={{ marginBottom: 16, padding: 12, background: "rgba(0,0,0,0.04)", borderRadius: 4 }}>
                                  Create Pool is only available when the survey is <b>closed</b> (Closed = true) and, for <b>Raffle</b> surveys, when <b>Draw Winners</b> has been run. Close the survey first; for Raffle type, draw winners before creating the pool.
                                </div>
                              ) : null}
                              <div className="nr-formGrid" style={!canCreatePoolFromSurvey ? { opacity: 0.7, pointerEvents: "none" } : undefined}>
                            <div className="nr-field">
                              <label>Pool Key *</label>
                              <input
                                value={surveyPoolForm.poolKey}
                                onChange={(e) => setSurveyPoolForm((s) => ({ ...s, poolKey: e.target.value }))}
                                placeholder="e.g. survey-rewards-2024"
                                maxLength={70}
                              />
                            </div>
                            <div className="nr-field">
                              <label>Collection Name *</label>
                              <input
                                value={surveyPoolForm.collectionName}
                                onChange={(e) => setSurveyPoolForm((s) => ({ ...s, collectionName: e.target.value }))}
                                placeholder="e.g. owlnoctun or account"
                              />
                              <div className="nr-help">AtomicAssets Collection Name. Use <b>"account"</b> to skip NFT validation (account-based claims).</div>
                            </div>
                            <div className="nr-field">
                              <label>Reward Amount *</label>
                              <input
                                value={surveyPoolForm.rewardAmount}
                                onChange={(e) => setSurveyPoolForm((s) => ({ ...s, rewardAmount: e.target.value }))}
                                placeholder={surveyPoolForm.rewardTokenSymbolRaw ? amountPlaceholderForSymbol(surveyPoolForm.rewardTokenSymbolRaw) : "e.g. 1000.000000"}
                              />
                              {surveyPoolForm.rewardTokenSymbolRaw ? (
                                <div className="nr-muted" style={{ marginTop: 0 }}>
                                  Enter only the amount (without token symbol). Example: <b>{amountPlaceholderForSymbol(surveyPoolForm.rewardTokenSymbolRaw)}</b>
                                </div>
                              ) : null}
                            </div>
                            <div className="nr-field">
                              <label>Token symbol</label>
                              <select
                                value={surveyPoolForm.rewardTokenSymbolRaw}
                                onChange={(e) => setSurveyPoolForm((s) => ({ ...s, rewardTokenSymbolRaw: e.target.value }))}
                              >
                                <option value="">(select)</option>
                                {surveyPoolForm.rewardTokenSymbolRaw &&
                                !poolTokenSymbolOptions.some((o) => String(o.tokenSymbolRaw) === String(surveyPoolForm.rewardTokenSymbolRaw)) ? (
                                  <option value={surveyPoolForm.rewardTokenSymbolRaw}>{surveyPoolForm.rewardTokenSymbolRaw}</option>
                                ) : null}
                                {poolTokenSymbolOptions.map((o) => (
                                  <option key={String(o.tokenSymbolRaw)} value={String(o.tokenSymbolRaw)}>
                                    {String(o.symbol)} ({String(o.precision)} dp)
                                  </option>
                                ))}
                                {poolTokenSymbolOptions.length === 0 ? <option value="">No allowed tokens found</option> : null}
                              </select>
                            </div>
                            <div className="nr-field">
                              <label>Token contract</label>
                              <select
                                value={surveyPoolForm.rewardTokenContract}
                                onChange={(e) => setSurveyPoolForm((s) => ({ ...s, rewardTokenContract: e.target.value }))}
                                disabled={!surveyPoolForm.rewardTokenSymbolRaw}
                              >
                                <option value="">(select)</option>
                                {surveyPoolForm.rewardTokenContract &&
                                !surveyPoolTokenContractOptions.some((o) => String(o.tokenContract) === String(surveyPoolForm.rewardTokenContract)) ? (
                                  <option value={surveyPoolForm.rewardTokenContract}>{surveyPoolForm.rewardTokenContract}</option>
                                ) : null}
                                {surveyPoolTokenContractOptions.map((o) => (
                                  <option key={String(o.tokenContract)} value={String(o.tokenContract)}>
                                    {String(o.tokenContract)}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="nr-field">
                              <label>Active</label>
                              <select
                                value={surveyPoolForm.active ? "1" : "0"}
                                onChange={(e) => setSurveyPoolForm((s) => ({ ...s, active: e.target.value === "1" }))}
                              >
                                <option value="1">true</option>
                                <option value="0">false</option>
                              </select>
                            </div>
                            <div className="nr-field">
                              <label>Cooldown (sec)</label>
                              <input
                                type="number"
                                min="0"
                                value="4294967295"
                                disabled
                                readOnly
                              />
                              <div className="nr-help">Survey pools always use max cooldown (one claim per wallet)</div>
                            </div>
                            <div className="nr-field" style={{ gridColumn: "1 / -1" }}>
                              <label>Pool Description</label>
                              <textarea
                                value={surveyPoolForm.poolDescription}
                                onChange={(e) => setSurveyPoolForm((s) => ({ ...s, poolDescription: e.target.value }))}
                                rows={2}
                              />
                            </div>
                            <div className="nr-field" style={{ gridColumn: "1 / -1" }}>
                              <label>Pool Image URL</label>
                              <input
                                value={surveyPoolForm.poolImageUrl}
                                onChange={(e) => setSurveyPoolForm((s) => ({ ...s, poolImageUrl: e.target.value }))}
                                placeholder="https://..."
                              />
                            </div>
                              </div>
                              {surveyCheck ? (
                                <div style={{ marginTop: 16, padding: 12, borderRadius: 4, background: surveyCheck.ok ? "rgba(0, 200, 0, 0.1)" : "rgba(200, 0, 0, 0.1)" }}>
                                  <div className={surveyCheck.ok ? "nr-checkOk" : "nr-checkErr"}>
                                    {surveyCheck.ok ? "Looks good. You can save this survey pool." : "Please fix the issues below before saving."}
                                  </div>
                                  {surveyCheck.errors?.length ? (
                                    <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                                      {surveyCheck.errors.map((m, i) => (
                                        <li key={i} style={{ color: "#ff6b6b" }}>{m}</li>
                                      ))}
                                    </ul>
                                  ) : null}
                                  {surveyCheck.warnings?.length ? (
                                    <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                                      {surveyCheck.warnings.map((m, i) => (
                                        <li key={i} style={{ color: "#ffa500" }}>{m}</li>
                                      ))}
                                    </ul>
                                  ) : null}
                                </div>
                              ) : null}
                              <div className="nr-actions" style={{ marginTop: 16 }}>
                                <button className="nr-btn" onClick={() => setSurveyPoolForm({ poolKey: undefined, ...surveyPoolForm })}>
                                  Cancel
                                </button>
                                <button className="nr-btn" onClick={() => { void previewSurveyPoolClaimFromCheck(); }} disabled={busy || !selectedSurvey || !canCreatePoolFromSurvey}>
                                  Check
                                </button>
                                <button className="nr-btn nr-btnPrimary" onClick={createSurveyPool} disabled={busy || !canCreatePoolFromSurvey} title={!canCreatePoolFromSurvey ? "Survey must be closed and (for Raffle) winners drawn first." : undefined}>
                                  Create Pool
                                </button>
                              </div>
                            </div>
                          ) : null}
                        </div>
                      ) : null}

                      {/* Draw Winners Modal (inline form) */}
                      {selectedSurvey && Number(selectedSurvey.surveyType) === 3 ? (
                        <div style={{ marginTop: 24 }}>
                          <div className="nr-cardTitle" style={{ fontSize: 16, marginBottom: 16 }}>
                            Draw Winners (Raffle)
                          </div>
                          <div className="nr-muted" style={{ marginBottom: 12 }}>
                            Only available when the survey is closed (Closed = true). Close the survey first, then draw winners.
                          </div>
                          <div className="nr-formGrid">
                            <div className="nr-field">
                              <label>Number of Winners *</label>
                              <input
                                type="number"
                                min="1"
                                max="6"
                                value={drawWinnersNum}
                                onChange={(e) => setDrawWinnersNum(e.target.value)}
                                disabled={!selectedSurvey?.closed}
                              />
                              <div className="nr-help">Max 6 winners</div>
                            </div>
                            <div className="nr-field">
                              <div className="nr-muted">
                                Participants: {Number(selectedSurvey.voteCount || 0)}
                              </div>
                            </div>
                            <div className="nr-actions" style={{ gridColumn: "1 / -1", marginTop: 8 }}>
                              <button type="button" className="nr-btn nr-btnPrimary" onClick={() => void drawSurveyWinners()} disabled={busy || !selectedSurvey?.closed}>
                                Draw Winners
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : null}

                      {surveyCheck ? (
                        <div style={{ marginTop: 16, padding: 12, borderRadius: 4, background: surveyCheck.ok ? "rgba(0, 200, 0, 0.1)" : "rgba(200, 0, 0, 0.1)" }}>
                          <div className={surveyCheck.ok ? "nr-checkOk" : "nr-checkErr"}>
                            {surveyCheck.ok ? "Looks good. You can save this survey." : "Please fix the issues below before saving."}
                          </div>
                          {surveyCheck.errors?.length ? (
                            <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                              {surveyCheck.errors.map((m, i) => (
                                <li key={i} style={{ color: "#ff6b6b" }}>{m}</li>
                              ))}
                            </ul>
                          ) : null}
                          {surveyCheck.warnings?.length ? (
                            <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                              {surveyCheck.warnings.map((m, i) => (
                                <li key={i} style={{ color: "#ffa500" }}>{m}</li>
                              ))}
                            </ul>
                          ) : null}
                        </div>
                      ) : null}
                      <div className="nr-formGrid" style={{ marginTop: 16 }}>
                        <div className="nr-actions" style={{ gridColumn: "1 / -1" }}>
                          {selectedSurvey ? (
                            <>
                              <button className="nr-btn" onClick={() => setSelectedSurveyKey("")}>
                                Cancel
                              </button>
                              <button className="nr-btn" onClick={() => { void previewSurveyClaimFromCheck(); }} disabled={busy}>
                                Check
                              </button>
                              <button className="nr-btn nr-btnPrimary" onClick={saveSurvey} disabled={busy}>
                                Update Survey
                              </button>
                              <button className="nr-btn nr-btnDanger" onClick={deleteSurvey} disabled={busy}>
                                Delete
                              </button>
                            </>
                          ) : (
                            <>
                              <button className="nr-btn" onClick={() => setSelectedSurveyKey("")}>
                                Cancel
                              </button>
                              <button className="nr-btn" onClick={() => { void previewSurveyClaimFromCheck(); }} disabled={busy}>
                                Check
                              </button>
                              <button className="nr-btn nr-btnPrimary" onClick={saveSurvey} disabled={busy}>
                                Save Survey
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="nr-muted" style={{ padding: 12 }}>
                      Select a project first.
                    </div>
                  )}
                </div>
  ) : null}
</section>
            ) : null}

            {/* Survey Votes (setsurvey) */}
            {actionPermissions.setsurvey ? (
            <section className="nr-card">
              <div className="nr-cardHeader nr-collapsibleHeader">
                <div className="nr-headerLeft">
                  <div className="nr-titleRow">
                    <div className="nr-cardTitle">Survey Votes</div>
                    <button className="nr-collapseBtn" onClick={() => toggleCard("surveyVotes")} aria-label="Toggle">
                      {openCards.surveyVotes ? "▾" : "▸"}
                    </button>
                  </div>
                </div>
                <div className="nr-actions">
                  <button className="nr-btn" onClick={exportSurveyVotesCsv} disabled={busy}>
                    Export CSV (all)
                  </button>
                </div>
              </div>

              {openCards.surveyVotes ? (
                <>
                  <div className="nr-filters">
                    <div className="nr-field">
                      <label>Survey</label>
                      <select value={surveyVoteFilterSurveyKey} onChange={(e) => setSurveyVoteFilterSurveyKey(e.target.value)}>
                        <option value="">All surveys</option>
                        {(surveys || []).map((s) => (
                          <option key={String(s.surveyKey)} value={String(s.surveyKey || "")}>
                            {String(s.surveyKey || "")}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="nr-field">
                      <label>Filter by account</label>
                      <input value={surveyVoteFilterAccount} onChange={(e) => setSurveyVoteFilterAccount(e.target.value)} placeholder="e.g. alice" />
                    </div>
                    <div className="nr-muted" style={{ alignSelf: "end", paddingBottom: 6 }}>
                      {filteredSurveyVotes.length} rows
                    </div>
                  </div>
                  <SimpleTable rows={filteredSurveyVotes} height={420} />
                </>
              ) : null}
            </section>
            ) : null}

            {/* Streak Management */}
            {actionPermissions.setstreak ? (
            <section className="nr-card">
              <div className="nr-cardHeader nr-collapsibleHeader">
                <div className="nr-headerLeft">
                  <div className="nr-titleRow">
                    <div className="nr-cardTitle">Streak Management</div>
                    <button className="nr-collapseBtn" onClick={() => toggleCard("streaks")} aria-label="Toggle">
                      {openCards.streaks ? "▾" : "▸"}
                    </button>
                  </div>
                  <div className="nr-muted">Manage streak challenges and create pools from them</div>
                </div>
                <div className="nr-actions">
                  <button
                    className="nr-btn"
                    onClick={() => toggleCard("streakFilters")}
                    style={{ fontSize: "12px", padding: "6px 10px" }}
                  >
                    {openCards.streakFilters ? "▾" : "▸"} Filter
                  </button>
                </div>
              </div>

              {openCards.streaks ? (
                <div className="nr-body">
                  {/* Filters and Search */}
                  {openCards.streakFilters ? (
                  <div className="nr-filters nr-filters-compact">
                    <div className="nr-field">
                      <label>Filter by Project</label>
                      <select value={streakFilterProject} onChange={(e) => setStreakFilterProject(e.target.value)}>
                        <option value="">All Projects</option>
                        {allowedProjects.map((p) => (
                          <option key={String(p.projectId)} value={String(p.projectId)}>
                            {p.projectName || `#${p.projectId}`}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="nr-field">
                      <label>Filter by Status</label>
                      <select value={streakFilterStatus} onChange={(e) => setStreakFilterStatus(e.target.value)}>
                        <option value="">All</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="poolCreated">Pool Created</option>
                      </select>
                    </div>
                    <div className="nr-field">
                      <label>Search</label>
                      <input
                        value={streakSearch}
                        onChange={(e) => setStreakSearch(e.target.value)}
                        placeholder="Streak ID or name..."
                      />
                    </div>
                  </div>
                  ) : null}

                  {/* Streak List Table */}
                  <div className="nr-tableWrap" style={{ marginTop: 16 }}>
                    <SimpleTable
                      rows={streaksView || []}
                      height={300}
                      onRowClick={(row) => {
                        const streakId = row._streakId;
                        if (streakId) setSelectedStreakId(streakId);
                      }}
                    />
                  </div>

                  {/* Streak Form */}
                  {selectedProject ? (
                    <div style={{ marginTop: 24 }}>
                      <div className="nr-formGrid">
                        <div className="nr-field">
                          <label>Streak</label>
                          <select value={selectedStreakId} onChange={(e) => {
                            const v = String(e.target.value || "");
                            if (!v) {
                              preferNewStreakRef.current = true;
                              setSelectedStreakId("");
                            } else {
                              preferNewStreakRef.current = false;
                              setSelectedStreakId(v);
                            }
                          }} disabled={!selectedProject}>
                            {streaksForProject.map((s) => (
                              <option key={String(s.streakId)} value={String(s.streakId)}>
                                {String(s.streakName || s.streakId)}
                              </option>
                            ))}
                            <option value="">(new streak)</option>
                          </select>
                        </div>

                        {!selectedStreak && streaksForProject.length === 0 ? (
                          <div className="nr-field" style={{ gridColumn: "1 / -1" }}>
                            <div className="nr-muted" style={{ padding: 12 }}>
                              No streaks found for this project yet. Fill the form below and click <b>Save Streak</b> to create one.
                            </div>
                          </div>
                        ) : null}

                        <div className="nr-field">
                          <label>Streak ID *</label>
                          <input
                            value={streakForm.streakId}
                            onChange={(e) => setStreakForm((s) => ({ ...s, streakId: e.target.value }))}
                            placeholder="e.g. 7-day-challenge"
                            maxLength={70}
                            disabled={!!selectedStreak}
                          />
                          <div className="nr-help">Unique identifier for the streak. Max 70 characters.</div>
                        </div>

                        <div className="nr-field">
                          <label>Streak Name *</label>
                          <input
                            value={streakForm.streakName}
                            onChange={(e) => setStreakForm((s) => ({ ...s, streakName: e.target.value }))}
                            placeholder="e.g. 7-Day Login Streak"
                            maxLength={70}
                          />
                        </div>

                        <div className="nr-field">
                          <label>Target Streak Days *</label>
                          <input
                            type="number"
                            min="1"
                            value={streakForm.targetStreakDays}
                            onChange={(e) => setStreakForm((s) => ({ ...s, targetStreakDays: e.target.value }))}
                            placeholder="e.g. 7"
                          />
                          <div className="nr-help">Number of days for the streak to complete.</div>
                        </div>

                        <div className="nr-field">
                          <label>Target In-A-Row</label>
                          <input
                            type="number"
                            min="0"
                            value={streakForm.targetInARow}
                            onChange={(e) => setStreakForm((s) => ({ ...s, targetInARow: e.target.value }))}
                            placeholder="e.g. 0"
                          />
                          <div className="nr-help">Consecutive days required (0 = not required).</div>
                        </div>

                        <div className="nr-field">
                          <label>Collection Name *</label>
                          <input
                            value={streakForm.collectionName}
                            onChange={(e) => setStreakForm((s) => ({ ...s, collectionName: e.target.value }))}
                            placeholder="e.g. owlnoctun or account"
                          />
                          <div className="nr-help">AtomicAssets Collection Name. Use <b>"account"</b> to skip NFT validation.</div>
                        </div>

                        <div className="nr-field">
                          <label>Reward Amount *</label>
                          <input
                            value={streakForm.rewardAmount}
                            onChange={(e) => setStreakForm((s) => ({ ...s, rewardAmount: e.target.value }))}
                            placeholder={streakForm.rewardTokenSymbolRaw ? amountPlaceholderForSymbol(streakForm.rewardTokenSymbolRaw) : "e.g. 100.0000"}
                          />
                          {streakForm.rewardTokenSymbolRaw ? (
                            <div className="nr-muted" style={{ marginTop: 0 }}>
                              Enter only the amount (without token symbol). Example: <b>{amountPlaceholderForSymbol(streakForm.rewardTokenSymbolRaw)}</b>
                            </div>
                          ) : null}
                        </div>

                        <div className="nr-field">
                          <label>Token Symbol *</label>
                          <select
                            value={streakForm.rewardTokenSymbolRaw}
                            onChange={(e) => setStreakForm((s) => ({ ...s, rewardTokenSymbolRaw: e.target.value }))}
                          >
                            <option value="">(select)</option>
                            {streakForm.rewardTokenSymbolRaw &&
                            !poolTokenSymbolOptions.some((o) => String(o.tokenSymbolRaw) === String(streakForm.rewardTokenSymbolRaw)) ? (
                              <option value={streakForm.rewardTokenSymbolRaw}>{streakForm.rewardTokenSymbolRaw}</option>
                            ) : null}
                            {poolTokenSymbolOptions.map((o) => (
                              <option key={String(o.tokenSymbolRaw)} value={String(o.tokenSymbolRaw)}>
                                {String(o.symbol)} ({String(o.precision)} dp)
                              </option>
                            ))}
                            {poolTokenSymbolOptions.length === 0 ? <option value="">No allowed tokens found</option> : null}
                          </select>
                        </div>

                        <div className="nr-field">
                          <label>Token Contract *</label>
                          <select
                            value={streakForm.rewardTokenContract}
                            onChange={(e) => setStreakForm((s) => ({ ...s, rewardTokenContract: e.target.value }))}
                            disabled={!streakForm.rewardTokenSymbolRaw}
                          >
                            <option value="">(select)</option>
                            {streakForm.rewardTokenContract &&
                            !streakTokenContractOptions.some((o) => String(o.tokenContract) === String(streakForm.rewardTokenContract)) ? (
                              <option value={streakForm.rewardTokenContract}>{streakForm.rewardTokenContract}</option>
                            ) : null}
                            {streakTokenContractOptions.map((o) => (
                              <option key={String(o.tokenContract)} value={String(o.tokenContract)}>
                                {String(o.tokenContract)}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="nr-field">
                          <label>Active</label>
                          <select
                            value={streakForm.active ? "1" : "0"}
                            onChange={(e) => setStreakForm((s) => ({ ...s, active: e.target.value === "1" }))}
                          >
                            <option value="1">true</option>
                            <option value="0">false</option>
                          </select>
                        </div>

                        <div className="nr-field">
                          <label>Cooldown (sec)</label>
                          <input
                            type="number"
                            min="0"
                            value={streakForm.cooldownSec}
                            onChange={(e) => setStreakForm((s) => ({ ...s, cooldownSec: e.target.value }))}
                          />
                          <div className="nr-help">Cooldown after claiming. Default: 86400 (1 day).</div>
                        </div>

                        <div className="nr-field">
                          <label>Pool Description</label>
                          <textarea
                            value={streakForm.poolDescription}
                            onChange={(e) => setStreakForm((s) => ({ ...s, poolDescription: e.target.value }))}
                            placeholder="Optional pool description..."
                            maxLength={500}
                            rows={2}
                          />
                        </div>

                        <div className="nr-field">
                          <label>Pool Image URL</label>
                          <input
                            value={streakForm.poolImageUrl}
                            onChange={(e) => setStreakForm((s) => ({ ...s, poolImageUrl: e.target.value }))}
                            placeholder="https://..."
                            maxLength={256}
                          />
                        </div>

                        <div className="nr-field">
                          <label>Required Immutable (JSON)</label>
                          <input
                            value={streakForm.requiredImmutableText}
                            onChange={(e) => setStreakForm((s) => ({ ...s, requiredImmutableText: e.target.value }))}
                            placeholder='["key=value", ...]'
                          />
                          <div className="nr-help">Optional. Example: ["series=Champions"]</div>
                        </div>
                      </div>

                      {/* Create Pool from Streak button */}
                      {selectedStreak && !selectedStreak.poolCreated && selectedStreak.active ? (
                        <div className="nr-formGrid" style={{ marginTop: 16 }}>
                          <div className="nr-actions" style={{ gridColumn: "1 / -1" }}>
                            {streakPoolForm.poolKey === undefined ? (
                              <button className="nr-btn nr-btnSecondary" onClick={() => setStreakPoolForm({ poolKey: selectedStreak.streakId })}>
                                Create Pool from Streak
                              </button>
                            ) : null}
                          </div>
                        </div>
                      ) : null}

                      {/* Pool Creation Form (shown when Create Pool button is clicked) */}
                      {selectedStreak && !selectedStreak.poolCreated && streakPoolForm.poolKey !== undefined ? (
                        <div className="nr-subcard" style={{ marginTop: 24 }}>
                          <div className="nr-cardHeaderSmall nr-collapsibleHeader">
                            <div className="nr-headerLeft">
                              <div className="nr-titleRow">
                                <div className="nr-cardTitle">Create Pool from Streak</div>
                                <button className="nr-collapseBtn" onClick={() => toggleCard("streakPoolForm")} aria-label="Toggle">
                                  {openCards.streakPoolForm ? "▾" : "▸"}
                                </button>
                              </div>
                            </div>
                          </div>
                          {openCards.streakPoolForm ? (
                            <div className="nr-body">
                              <div className="nr-formGrid">
                                <div className="nr-field">
                                  <label>Pool Key *</label>
                                  <input
                                    value={streakPoolForm.poolKey}
                                    onChange={(e) => setStreakPoolForm((s) => ({ ...s, poolKey: e.target.value }))}
                                    placeholder="e.g. streak-pool-7day"
                                    maxLength={70}
                                  />
                                  <div className="nr-help">Defaults to Streak ID if empty.</div>
                                </div>
                              </div>
                              <div className="nr-actions" style={{ marginTop: 16 }}>
                                <button className="nr-btn" onClick={() => setStreakPoolForm({ poolKey: undefined })}>
                                  Cancel
                                </button>
                                <button className="nr-btn nr-btnPrimary" onClick={createStreakPool} disabled={busy}>
                                  Create Pool
                                </button>
                              </div>
                            </div>
                          ) : null}
                        </div>
                      ) : null}

                      {/* Status info */}
                      {selectedStreak && selectedStreak.poolCreated ? (
                        <div style={{ marginTop: 16, padding: 12, borderRadius: 4, background: "rgba(0, 200, 0, 0.1)" }}>
                          <div className="nr-muted">
                            Pool created: <b>{selectedStreak.poolKey}</b>
                          </div>
                        </div>
                      ) : null}

                      {streakCheck ? (
                        <div style={{ marginTop: 16, padding: 12, borderRadius: 4, background: streakCheck.ok ? "rgba(0, 200, 0, 0.1)" : "rgba(200, 0, 0, 0.1)" }}>
                          <div className={streakCheck.ok ? "nr-checkOk" : "nr-checkErr"}>
                            {streakCheck.ok ? "Looks good. You can save this streak." : "Please fix the issues below before saving."}
                          </div>
                          {streakCheck.errors?.length ? (
                            <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                              {streakCheck.errors.map((m, i) => (
                                <li key={i} style={{ color: "#ff6b6b" }}>{m}</li>
                              ))}
                            </ul>
                          ) : null}
                          {streakCheck.warnings?.length ? (
                            <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                              {streakCheck.warnings.map((m, i) => (
                                <li key={i} style={{ color: "#ffa500" }}>{m}</li>
                              ))}
                            </ul>
                          ) : null}
                        </div>
                      ) : null}

                      <div className="nr-formGrid" style={{ marginTop: 16 }}>
                        <div className="nr-actions" style={{ gridColumn: "1 / -1" }}>
                          {selectedStreak ? (
                            <>
                              <button className="nr-btn" onClick={() => setSelectedStreakId("")}>
                                Cancel
                              </button>
                              <button className="nr-btn" onClick={() => { void previewStreakFromCheck(); }} disabled={busy}>
                                Check
                              </button>
                              <button className="nr-btn nr-btnPrimary" onClick={updateStreak} disabled={busy || selectedStreak.poolCreated}>
                                Update Streak
                              </button>
                              {selectedStreak.active ? (
                                <button className="nr-btn nr-btnWarning" onClick={() => setStreakActive(false)} disabled={busy}>
                                  Deactivate
                                </button>
                              ) : (
                                <button className="nr-btn nr-btnSuccess" onClick={() => setStreakActive(true)} disabled={busy}>
                                  Activate
                                </button>
                              )}
                              <button className="nr-btn nr-btnDanger" onClick={deleteStreak} disabled={busy || selectedStreak.poolCreated}>
                                Delete
                              </button>
                            </>
                          ) : (
                            <>
                              <button className="nr-btn" onClick={() => setSelectedStreakId("")}>
                                Cancel
                              </button>
                              <button className="nr-btn" onClick={() => { void previewStreakFromCheck(); }} disabled={busy}>
                                Check
                              </button>
                              <button className="nr-btn nr-btnPrimary" onClick={saveStreak} disabled={busy}>
                                Save Streak
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="nr-muted" style={{ padding: 12 }}>
                      Select a project first.
                    </div>
                  )}
                </div>
              ) : null}
            </section>
            ) : null}

            {/* Streak States (setstreak) */}
            {actionPermissions.setstreak ? (
            <section className="nr-card">
              <div className="nr-cardHeader nr-collapsibleHeader">
                <div className="nr-headerLeft">
                  <div className="nr-titleRow">
                    <div className="nr-cardTitle">Streak States</div>
                    <button className="nr-collapseBtn" onClick={() => toggleCard("streakStates")} aria-label="Toggle">
                      {openCards.streakStates ? "▾" : "▸"}
                    </button>
                  </div>
                  <div className="nr-muted">User progress on streaks (updated by external API)</div>
                </div>
              </div>

              {openCards.streakStates ? (
                <>
                  <div className="nr-filters">
                    <div className="nr-field">
                      <label>Filter by account</label>
                      <input value={streakStateFilterAccount} onChange={(e) => setStreakStateFilterAccount(e.target.value)} placeholder="e.g. alice" />
                    </div>
                    <div className="nr-field">
                      <label>Filter by Status</label>
                      <select value={streakStateFilterStatus} onChange={(e) => setStreakStateFilterStatus(e.target.value)}>
                        <option value="">All</option>
                        <option value="active">Active</option>
                        <option value="completed">Completed</option>
                        <option value="claimed">Claimed</option>
                      </select>
                    </div>
                    <div className="nr-muted" style={{ alignSelf: "end", paddingBottom: 6 }}>
                      {filteredStreakStates.length} rows
                    </div>
                  </div>
                  <SimpleTable rows={streakStatesView} height={420} />
                </>
              ) : null}
            </section>
            ) : null}

            {/* Advanced: tx JSON */}
            <section className="nr-card">
              <div className="nr-cardHeader nr-collapsibleHeader">
                <div className="nr-headerLeft">
                  <div className="nr-titleRow">
                    <div className="nr-cardTitle">Advanced</div>
                    <button className="nr-collapseBtn" onClick={() => setShowTxJson((s) => !s)} aria-label="Toggle">
                      {showTxJson ? "▾" : "▸"}
                    </button>
                  </div>
                  <div className="nr-muted">Transaction JSON (alternative mode)</div>
                </div>
              </div>
              {!showTxJson ? null : (
                <div className="nr-body">
                  <div className="nr-desc">
                    This section lets you work with raw transaction JSON instead of the form-driven editors above.
                    After any signed transaction, the exact JSON is shown here for copying and reuse.
                    You can also paste a JSON transaction to sign and broadcast it with your current wallet.
                    Only actions for the <b>{CONTRACT}</b> contract are accepted.
                  </div>
                  {lastSignedTxJson ? (
                    <div className="nr-field">
                      <div className="nr-row" style={{ gridTemplateColumns: "1fr auto", alignItems: "end" }}>
                        <div>
                          <label>Last signed transaction JSON</label>
                        </div>
                        <div className="nr-actions" style={{ justifyContent: "flex-end" }}>
                          <button
                            className="nr-btn"
                            onClick={async () => {
                              try {
                                await navigator.clipboard.writeText(lastSignedTxJson);
                                setNotice("Transaction JSON copied to clipboard.");
                              } catch {
                                // fallback
                                window.prompt("Copy transaction JSON:", lastSignedTxJson);
                              }
                            }}
                          >
                            Copy
                          </button>
                        </div>
                      </div>
                      <textarea rows={8} value={lastSignedTxJson} readOnly />
                    </div>
                  ) : null}
                  <div className="nr-field">
                    <label>Transaction JSON</label>
                    <textarea rows={10} value={txJsonText} onChange={(e) => setTxJsonText(e.target.value)} placeholder='{"actions":[...]}' />
                  </div>
                  <div className="nr-actions">
                    <button className="nr-btn nr-btnPrimary" onClick={signFromTxJson} disabled={busy}>
                      Sign & Broadcast
                    </button>
                  </div>
                </div>
              )}
            </section>

            {/* Monthly billing (PDF) */}
            <section className="nr-card">
              <div className="nr-cardHeader nr-collapsibleHeader">
                <div className="nr-headerLeft">
                  <div className="nr-titleRow">
                    <div className="nr-cardTitle">Monthly billing (PDF)</div>
                    <button className="nr-collapseBtn" onClick={() => toggleCard("billing")} aria-label="Toggle">
                      {openCards.billing ? "▾" : "▸"}
                    </button>
                  </div>
                  <div className="nr-muted">Generate a consolidated monthly PDF statement for RAMFee deposits (invoice items) and refunds (credit note items).</div>
                </div>
              </div>

              {openCards.billing ? (
                <div className="nr-body">
                  <div className="nr-desc">
                    The statement is generated from on-chain action history via the configured API endpoint.
                    Incoming items are detected as token transfers to <b>{CONTRACT}</b> with memo <b>ramfee</b> (exact 6 letters, ASCII case-insensitive).
                    Refunds are detected by the contract action <b>refund</b> and its related outgoing token transfer.
                  </div>

                  <div className="nr-formGrid">
                    <div className="nr-field">
                      <label>Month</label>
                      <select value={billingMonth} onChange={(e) => setBillingMonth(e.target.value)}>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
                          <option key={n} value={String(n)}>
                            {monthNameEnglish(n)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="nr-field">
                      <label>Year</label>
                      <input
                        type="number"
                        min={2000}
                        max={2100}
                        value={billingYear}
                        onChange={(e) => setBillingYear(e.target.value)}
                        placeholder="e.g. 2026"
                      />
                    </div>
                  </div>

                  {billingError ? <div className="nr-error">{billingError}</div> : null}

                  <div className="nr-actions" style={{ marginTop: 10 }}>
                    <button className="nr-btn nr-btnPrimary" onClick={generateMonthlyStatementPdf} disabled={busy || billingBusy || !actor}>
                      {billingBusy ? "Generating..." : "Generate PDF"}
                    </button>
                    {billingPdfUrl && billingPdfName ? (
                      <a className="nr-btn" href={billingPdfUrl} download={billingPdfName}>
                        Download PDF
                      </a>
                    ) : null}
                  </div>

                  <div className="nr-muted" style={{ marginTop: 10 }}>
                    Seller: <b>Bloxprod (sole proprietor)</b>, Germany. VAT exempt pursuant to <b>Section 19 UStG</b>.
                  </div>
{billingDebug ? (
  <div style={{ marginTop: 12 }}>
    <div className="nr-muted" style={{ marginBottom: 6 }}>
      Billing debug (price file / date matching):
    </div>
    <pre className="nr-pre" style={{ maxHeight: 260, overflow: "auto" }}>{billingDebug}</pre>
  </div>
) : null}
                </div>
              ) : null}
            </section>
            </>
            ) : null}
          </>
        )}
      </main>

      <footer className="nr-footer">
        This page is provided by the XPR Block Producer <b>Bloxprod</b>. Contact &amp; info: <a href="https://www.bloxprod.io" target="_blank" rel="noreferrer">www.bloxprod.io</a>
        <span style={{ margin: "0 10px", opacity: 0.5 }}>•</span>
        <a href="/nftadminhub_terms.html" target="_blank" rel="noreferrer">Terms of Use</a>
      </footer>

      <ClaimPreviewModal
        isOpen={claimPreviewModalOpen}
        claimCalc={claimCalc}
        claimCalcLoading={claimCalcLoading}
        claimCalcError={claimCalcError}
        saveError={claimPreviewModalOpen ? error : ""}
        mode={claimPreviewMode}
        onConfirm={handleClaimPreviewConfirm}
        onCancel={handleClaimPreviewCancel}
        busy={busy}
      />
    </div>
  );
}
